/**
 * Smart Home Dashboard Template - Stitch design aligned
 * Account (appId+apiKey) → MCP QueryLocations/QueryDevices/QueryStatus → Device List
 */

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type CSSProperties,
  type DragEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type TouchEvent as ReactTouchEvent,
  type UIEvent as ReactUIEvent,
} from 'react'
import { io, type Socket } from 'socket.io-client'
import { onAuthStateChanged } from 'firebase/auth'
import {
  queryLocationsWithAuth,
  queryDevicesWithAuth,
  queryStatusWithAuth,
  queryScenesWithAuth,
  configureSceneWithAuth,
  runSceneWithAuth,
  deleteSceneWithAuth,
  queryAutomationsWithAuth,
  configureAutomationWithAuth,
  configureDeviceNicknameWithAuth,
  executeCommandWithAuth,
  type AuthCreds,
  type Location,
  type Device,
  type DeviceStatus,
  type Automation,
  type ConfigureAutomationParams,
  type SceneDeviceCommand,
  type ConfigureSceneResult,
} from '../../../services/turingMcpApi'
import { fetchWeatherByIP } from '../../../services/weatherApi'
import { listDeviceTypesMeta } from '../../../services/deviceTypesStore'
import { loadDeviceTypeTemplateConfig } from '../../../services/deviceTypeTemplateConfigStore'
import { loadGlobalNormalization, type MergeRule } from '../../../services/deviceTypeNormalizationStore'
import {
  resolveDeviceModalRenderConfig,
  resolveModalConfigByDeviceType,
  resolveModalTemplateDeviceType,
  type ModalRenderConfig,
} from '../../appfactory/deviceTypeStyleConfig'
import DeviceTypeModalContent from '../../appfactory/DeviceTypeModalContent'
import { getMockAttributesForTrait, getDeviceTypeIcon } from '../../../components/traitStyles/traitStyleConfig'
import { getCredentialFromUserProfile, type FramePreset } from '../../../services/appfactoryFirestore'
import {
  listDeviceTypeModalOverrides,
  getCustomCss,
  getDesignTokens,
  type DesignTokens,
  type FramerPresetOverrides,
} from '../../../services/appfactoryApi'
import { auth as firebaseAuth } from '../../../services/firebase'
import { getCurrentApiUrl } from '../../../services/environmentStore'
import { getLoginStyleComponent } from '../../appfactory/loginStyleRegistry'
import { PRESET_STYLES } from '../framePresets'
import type { DeviceTypeModalOverrideForContext } from '../../appfactory/DeviceTypeModalOverridesContext'
import powerSettingsNewIcon from './assets/power-settings-new.svg'
import batteryFrameIcon from './assets/battery-android-frame.svg'
import motionPlayIcon from './assets/motion-play.svg'
import doorFrontIcon from './assets/door-front.svg'
import doorOpenIcon from './assets/door-open.svg'
import sensorOccupiedIcon from './assets/sensor-occupied.svg'
import boltIcon from './assets/bolt.svg'
import hubIcon from './assets/hub.svg'
import dineHeartIcon from './assets/dine-heart.svg'
import partnerHeartIcon from './assets/partner-heart.svg'
import './SmartHomeDashboardTemplate.css'
import '../DeviceTypeModalPreview.css'

const DEFAULT_TOKENS = {
  colors: {
    background: '#0B1120',
    backgroundAccent: '#1E293B',
    cardGlass: 'rgba(255,255,255,0.05)',
    cardBorder: 'rgba(255,255,255,0.10)',
    textPrimary: '#FFFFFF',
    textMuted: 'rgba(255,255,255,0.60)',
    primary: '#FBBF24',
  },
  radius: { sceneCard: 24, roomCard: 32, navPill: 40 },
  spacing: { pageX: 24, pageTop: 56, sectionGap: 28 },
  typography: { fontFamily: "'MiSans TC', 'MiSans Latin', sans-serif", titleWeight: 700, bodyWeight: 600 },
}

const DEFAULT_AVATAR =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuBjqlgYj-wiMf8iWJlktrWHPPfZ_0HoF1Nq0C96KXDT3Z8idNxXDYe_Tzq_2di50ipQk92DGdpIWx8g-to-WjaFeHxU9YyzRa5y0D5QPISh2OaQZajVSNRRUoHLlV4BLzizEUDkBFu7n6frY0iIiKYYoNEAbJH9WbLO7v8ZSfqRup10na3-wTP5YHJXewpaVV6mMboFgscIqEK8uvscdvaH3iJ02IH1sSwKc1k0Go-SNpO-JONNx_r1HCbncH2a_jaRSix3Z_qkHQq5'

const DEVICE_CARD_MEDIA_THEME: Record<string, { start: string; end: string; glow: string }> = {
  climate: { start: '#1d4ed8', end: '#38bdf8', glow: '#93c5fd' },
  lighting: { start: '#f59e0b', end: '#facc15', glow: '#fde68a' },
  airflow: { start: '#0ea5e9', end: '#22d3ee', glow: '#7dd3fc' },
  access: { start: '#475569', end: '#94a3b8', glow: '#cbd5e1' },
  utility: { start: '#2563eb', end: '#8b5cf6', glow: '#c4b5fd' },
  sensor: { start: '#059669', end: '#22c55e', glow: '#86efac' },
  generic: { start: '#334155', end: '#64748b', glow: '#cbd5e1' },
}

const DEVICE_IMAGE_CACHE = new Map<string, string>()
const DASHBOARD_SCROLL_CONTAINER_SELECTORS = [
  '.appfactory-preview-embed-viewport',
  '.appfactory-share-preview-content',
  '.appfactory-preview-viewport',
] as const

type DashboardScrollSnapshot = {
  nav: 'home' | 'auto' | 'store'
  containerTops: Partial<Record<(typeof DASHBOARD_SCROLL_CONTAINER_SELECTORS)[number], number>>
  rootTop: number
  winTop: number
}

let hasInitializedDashboardEntry = false
let pendingDashboardScrollSnapshot: DashboardScrollSnapshot | null = null

const TEMPLATE_ITEM_KEY = 'template:dashboard'
type TemplateCssState = 'on' | 'off' | 'selected' | 'unselected'
const ON_OFF_STATE_KEYS = ['OnOff', 'on', 'powerState', 'power', 'isOn'] as const
const NORMALIZED_ON_OFF_STATE_KEYS = new Set(ON_OFF_STATE_KEYS.map((key) => normalizeStateKey(key)))
const PENDING_ONOFF_TTL_MS = 5000
const PULL_REFRESH_TRIGGER_PX = 70
const PULL_REFRESH_MAX_PX = 110

function escapeTemplateCssToken(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizeTemplateCssBody(sourceCss: string): string {
  const source = String(sourceCss || '').trim()
  if (!source) return ''
  const blockMatch = source.match(/\{([^{}]*)\}/)
  if (blockMatch) {
    return String(blockMatch[1] || '').trim()
  }
  return source
}

function buildTemplateCssRules(sourceCss: string, baseSelector: string): string[] {
  const source = String(sourceCss || '').trim()
  if (!source) return []

  const rules: string[] = []
  const blockRegex = /([^{}]+)\{([^{}]*)\}/g
  let match: RegExpExecArray | null = null
  let hasBlock = false

  while ((match = blockRegex.exec(source)) !== null) {
    hasBlock = true
    const rawSelector = String(match[1] || '').trim()
    const body = String(match[2] || '').trim()
    if (!body) continue
    const selector = rawSelector ? rawSelector.replace(/&/g, baseSelector) : baseSelector
    if (!selector) continue
    rules.push(`${selector} {\n${body}\n}`)
  }

  if (rules.length > 0) return rules

  if (hasBlock) {
    const fallbackBody = normalizeTemplateCssBody(source)
    return fallbackBody ? [`${baseSelector} {\n${fallbackBody}\n}`] : []
  }

  return [`${baseSelector} {\n${source}\n}`]
}

function parseTemplateCssBody(sourceCss: string): { body: string; hasBlock: boolean } {
  const source = String(sourceCss || '')
  const match = source.match(/([^{}]+)\{([^{}]*)\}/)
  if (!match) return { body: source.trim(), hasBlock: false }
  return { body: String(match[2] || ''), hasBlock: true }
}

function escapeTemplateRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function upsertTemplateCssDeclaration(body: string, property: string, value: string): string {
  const prop = String(property || '').trim()
  if (!prop) return body
  const entries = String(body || '')
    .split(';')
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !new RegExp(`^${escapeTemplateRegExp(prop)}\\s*:`, 'i').test(line))
  entries.push(`${prop}: ${value}`)
  return entries.join(';\n') + ';'
}

function readTemplateCssDeclaration(body: string, property: string): string | null {
  const prop = String(property || '').trim().toLowerCase()
  if (!prop) return null
  const entries = String(body || '')
    .split(';')
    .map((line) => line.trim())
    .filter(Boolean)
  for (const line of entries) {
    const idx = line.indexOf(':')
    if (idx <= 0) continue
    const key = line.slice(0, idx).trim().toLowerCase()
    if (key !== prop) continue
    return line.slice(idx + 1).trim()
  }
  return null
}

function removeTemplateCssDeclaration(body: string, property: string): string {
  const prop = String(property || '').trim()
  if (!prop) return body
  const entries = String(body || '')
    .split(';')
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !new RegExp(`^${escapeTemplateRegExp(prop)}\\s*:`, 'i').test(line))
  return entries.join(';\n')
}

function normalizeTemplateCssValue(value: string): string {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase()
}

function resolveTemplatePresetVars(
  preset: FramePreset,
  framerPresetOverrides?: FramerPresetOverrides
): Record<string, string> | null {
  if (preset === 'default') return null
  const base = PRESET_STYLES[preset]
  if (!base) return null
  const override = framerPresetOverrides?.[preset] ?? {}
  return {
    ...base,
    ...override,
  } as Record<string, string>
}

function withTemplateCssBody(sourceCss: string, nextBody: string): string {
  const source = String(sourceCss || '')
  const parsed = parseTemplateCssBody(source)
  if (!parsed.hasBlock) {
    const trimmed = String(nextBody || '').trim()
    if (!trimmed) return ''
    return trimmed.endsWith(';') ? trimmed : `${trimmed};`
  }
  const firstBlock = source.match(/([^{}]+)\{([^{}]*)\}/)
  if (!firstBlock || firstBlock.index == null) return nextBody ? `${nextBody};` : ''
  const selector = firstBlock[1].trim() || '&'
  const replacement = nextBody ? `${selector} {\n${nextBody};\n}` : `${selector} {}`
  const start = firstBlock.index
  const end = start + firstBlock[0].length
  return `${source.slice(0, start)}${replacement}${source.slice(end)}`
}

function applyTemplateFramerPresetRuntimeCss(
  sourceCss: string,
  preset: FramePreset,
  framerPresetOverrides?: FramerPresetOverrides
): string {
  if (preset === 'default') return sourceCss
  const vars = resolveTemplatePresetVars(preset, framerPresetOverrides)
  if (!vars) return sourceCss
  const legacyPropMap: Array<[string, keyof typeof vars]> = [
    ['background', '--frame-bg'],
    ['border', '--frame-border'],
    ['box-shadow', '--frame-shadow'],
    ['border-radius', '--frame-radius'],
    ['backdrop-filter', '--frame-blur'],
    ['-webkit-backdrop-filter', '--frame-blur'],
    ['padding', '--frame-padding'],
  ]
  // Legacy cleanup: remove declarations that were previously auto-written by Framer preset mutation.
  let cleanedSource = sourceCss
  {
    const parsedLegacy = parseTemplateCssBody(cleanedSource)
    let legacyBody = String(parsedLegacy.body || '')
    const candidatePresetVars = Object.keys(PRESET_STYLES)
      .map((presetId) => resolveTemplatePresetVars(presetId as FramePreset, framerPresetOverrides))
      .filter((entry): entry is Record<string, string> => Boolean(entry))
    for (const [prop, varKey] of legacyPropMap) {
      const currentValue = readTemplateCssDeclaration(legacyBody, prop)
      if (!currentValue) continue
      const currentNormalized = normalizeTemplateCssValue(currentValue)
      const isLikelyLegacyPresetValue =
        normalizeTemplateCssValue(vars[varKey]) === currentNormalized
        || candidatePresetVars.some(
          (candidateVars) => normalizeTemplateCssValue(candidateVars[varKey]) === currentNormalized
        )
      if (!isLikelyLegacyPresetValue) continue
      legacyBody = removeTemplateCssDeclaration(legacyBody, prop)
    }
    cleanedSource = withTemplateCssBody(cleanedSource, legacyBody)
  }

  const safeVisualPropMap: Array<[string, keyof typeof vars]> = [
    ['background', '--frame-bg'],
    ['border', '--frame-border'],
    ['box-shadow', '--frame-shadow'],
    ['backdrop-filter', '--frame-blur'],
    ['-webkit-backdrop-filter', '--frame-blur'],
  ]
  const parsed = parseTemplateCssBody(cleanedSource)
  let body = String(parsed.body || '')
  // Non-destructive apply: only fill missing visual declarations.
  for (const [prop, varKey] of safeVisualPropMap) {
    if (readTemplateCssDeclaration(body, prop)) continue
    body = upsertTemplateCssDeclaration(body, prop, vars[varKey])
  }
  return withTemplateCssBody(cleanedSource, body)
}

function parseTemplateItemCssKey(
  key: string
): { slotKey: string; cssState: TemplateCssState } | null {
  const raw = String(key || '').trim()
  if (!raw.startsWith(`${TEMPLATE_ITEM_KEY}::`)) return null
  const rest = raw.slice(`${TEMPLATE_ITEM_KEY}::`.length)
  const [slotPart, statePart] = rest.split('@')
  const slotKey = String(slotPart || '').trim()
  if (!slotKey) return null
  const token = String(statePart || 'on').trim().toLowerCase()
  const cssState: TemplateCssState =
    token === 'off' || token === 'selected' || token === 'unselected' ? token : 'on'
  return { slotKey, cssState }
}

function normalizeTemplateSlotToken(slotKey: string): string {
  return String(slotKey || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

function sanitizeGroupCardIconCss(sourceCss: string): string {
  const source = String(sourceCss || '')
  if (!source) return ''
  // Hard-guard: group icon slot is glyph-only, never allow frame-like decorations.
  const isBlockedProp = (propName: string): boolean => {
    const prop = String(propName || '').trim().toLowerCase()
    return (
      prop === 'background'
      || prop.startsWith('background-')
      || prop === 'border'
      || prop.startsWith('border-')
      || prop === 'box-shadow'
      || prop === 'padding'
      || prop.startsWith('padding-')
      || prop === 'backdrop-filter'
      || prop === '-webkit-backdrop-filter'
    )
  }
  const sanitizeBody = (rawBody: string): string => {
    const entries = String(rawBody || '')
      .split(';')
      .map((line) => line.trim())
      .filter(Boolean)
      .filter((line) => {
        const idx = line.indexOf(':')
        if (idx <= 0) return true
        const prop = line.slice(0, idx).trim()
        return !isBlockedProp(prop)
      })
    return entries.join(';\n')
  }

  const blockRegex = /([^{}]+)\{([^{}]*)\}/g
  if (!source.includes('{')) {
    const body = sanitizeBody(source)
    return body ? `${body};` : ''
  }

  let replaced = source
  replaced = replaced.replace(blockRegex, (_full, rawSelector: string, rawBody: string) => {
    const selector = String(rawSelector || '').trim()
    const body = sanitizeBody(String(rawBody || ''))
    if (!selector || !body) return ''
    return `${selector} {\n${body};\n}`
  })
  return replaced.trim()
}

function sanitizeGroupLegacyIconFrameCss(sourceCss: string): string {
  const source = String(sourceCss || '')
  if (!source || !source.includes('{')) return source
  const isBlockedProp = (propName: string): boolean => {
    const prop = String(propName || '').trim().toLowerCase()
    return (
      prop === 'background'
      || prop.startsWith('background-')
      || prop === 'border'
      || prop.startsWith('border-')
      || prop === 'box-shadow'
      || prop === 'padding'
      || prop.startsWith('padding-')
      || prop === 'backdrop-filter'
      || prop === '-webkit-backdrop-filter'
    )
  }
  const sanitizeBody = (rawBody: string): string => {
    const entries = String(rawBody || '')
      .split(';')
      .map((line) => line.trim())
      .filter(Boolean)
      .filter((line) => {
        const idx = line.indexOf(':')
        if (idx <= 0) return true
        const prop = line.slice(0, idx).trim()
        return !isBlockedProp(prop)
      })
    return entries.join(';\n')
  }
  const blockRegex = /([^{}]+)\{([^{}]*)\}/g
  return source.replace(blockRegex, (full, rawSelector: string, rawBody: string) => {
    const selector = String(rawSelector || '')
    if (
      !selector.includes('.smart-home-light-group-icon')
      && !selector.includes('.smart-home-scene-icon')
    ) return full
    const body = sanitizeBody(String(rawBody || ''))
    const normalizedSelector = selector.trim() || '&'
    if (!body) return ''
    return `${normalizedSelector} {\n${body};\n}`
  }).trim()
}

function sanitizeGroupGridSurfaceCss(sourceCss: string): string {
  const source = String(sourceCss || '')
  if (!source) return ''
  const isBlockedProp = (propName: string): boolean => {
    const prop = String(propName || '').trim().toLowerCase()
    return (
      prop === 'backdrop-filter'
      || prop === '-webkit-backdrop-filter'
    )
  }
  const sanitizeBody = (rawBody: string): string => {
    const entries = String(rawBody || '')
      .split(';')
      .map((line) => line.trim())
      .filter(Boolean)
      .filter((line) => {
        const idx = line.indexOf(':')
        if (idx <= 0) return true
        const prop = line.slice(0, idx).trim()
        return !isBlockedProp(prop)
      })
    return entries.join(';\n')
  }
  const blockRegex = /([^{}]+)\{([^{}]*)\}/g
  if (!source.includes('{')) {
    const body = sanitizeBody(source)
    return body ? `${body};` : ''
  }
  return source.replace(blockRegex, (_full, rawSelector: string, rawBody: string) => {
    const selector = String(rawSelector || '').trim()
    const body = sanitizeBody(String(rawBody || ''))
    if (!selector || !body) return ''
    return `${selector} {\n${body};\n}`
  }).trim()
}

function ensureTemplateRootCssBlock(sourceCss: string): string {
  const source = String(sourceCss || '').trim()
  if (!source) return source
  const hasRootBlock = /(^|\n)\s*&\s*\{[^{}]*\}/m.test(source)
  if (hasRootBlock) return source
  if (!source.includes('{')) return source
  return `& {\n}\n${source}`
}

function canApplyFramerPresetToTemplateSlot(slotKey: string): boolean {
  const token = normalizeTemplateSlotToken(slotKey)
  if (!token) return false
  // Icon-only slot must not receive frame/surface preset values.
  if (token === 'groupcardicon') return false
  return true
}

function buildGroupGridTransparencyGuardCss(): string {
  return [
    '.smart-home-dashboard-template [data-template-css-slot="groupGrid"] {',
    '  background: transparent !important;',
    '  background-image: none !important;',
    '  border: 0 !important;',
    '  box-shadow: none !important;',
    '  outline: 0 !important;',
    '  backdrop-filter: none !important;',
    '  -webkit-backdrop-filter: none !important;',
    '}',
  ].join('\n')
}

function buildTemplateIconTransparencyGuardCss(): string {
  return [
    '.smart-home-dashboard-template [data-template-css-slot="sceneCard"] .smart-home-scene-icon,',
    '.smart-home-dashboard-template [data-template-css-slot="groupCard"] .smart-home-scene-icon,',
    '.smart-home-dashboard-template [data-template-css-slot="groupGrid"] .smart-home-scene-icon,',
    '.smart-home-dashboard-template [data-template-css-slot="groupCardIcon"].smart-home-scene-icon,',
    '.smart-home-dashboard-template [data-template-css-slot="groupCard"] .smart-home-light-group-icon,',
    '.smart-home-dashboard-template [data-template-css-slot="groupGrid"] .smart-home-light-group-icon,',
    '.smart-home-dashboard-template [data-template-css-slot="groupCardIcon"].smart-home-light-group-icon {',
    '  background: transparent !important;',
    '  border: 0 !important;',
    '  box-shadow: none !important;',
    '  backdrop-filter: none !important;',
    '  -webkit-backdrop-filter: none !important;',
    '}',
    '.smart-home-dashboard-template [data-template-css-slot="sceneCard"] .smart-home-scene-icon .material-symbols-outlined,',
    '.smart-home-dashboard-template [data-template-css-slot="groupCard"] .smart-home-scene-icon .material-symbols-outlined,',
    '.smart-home-dashboard-template [data-template-css-slot="groupGrid"] .smart-home-scene-icon .material-symbols-outlined,',
    '.smart-home-dashboard-template [data-template-css-slot="groupCard"] .smart-home-light-group-icon .material-symbols-outlined,',
    '.smart-home-dashboard-template [data-template-css-slot="groupGrid"] .smart-home-light-group-icon .material-symbols-outlined,',
    '.smart-home-dashboard-template [data-template-css-slot="groupCardIcon"] .material-symbols-outlined {',
    '  background: transparent !important;',
    '  border: 0 !important;',
    '  box-shadow: none !important;',
    '}',
  ].join('\n')
}

function buildTemplateItemCssRuntime(map: Record<string, string>): string {
  const blocks: string[] = []
  const entries = Object.entries(map || {}).sort(([keyA], [keyB]) => {
    const parsedA = parseTemplateItemCssKey(keyA)
    const parsedB = parseTemplateItemCssKey(keyB)
    const tokenA = normalizeTemplateSlotToken(parsedA?.slotKey || '')
    const tokenB = normalizeTemplateSlotToken(parsedB?.slotKey || '')
    const priority = (token: string): number => {
      if (token === 'groupgrid' || token === 'scenegrid') return 2
      return 1
    }
    return priority(tokenA) - priority(tokenB)
  })
  for (const [key, rawCss] of entries) {
    const parsed = parseTemplateItemCssKey(key)
    if (!parsed) continue
    const escapedSlot = escapeTemplateCssToken(parsed.slotKey)
    const baseSelector = `.smart-home-dashboard-template [data-template-css-slot="${escapedSlot}"]`
    const slotToken = normalizeTemplateSlotToken(parsed.slotKey)
    // Grid-only editing mode: ignore legacy card/icon slot rules.
    if (
      slotToken === 'scenecard'
      || slotToken === 'groupcard'
      || slotToken === 'groupcardicon'
      || slotToken === 'devicecard'
    ) {
      continue
    }
    const scopedSelector =
      slotToken === 'groupgrid'
        ? `${baseSelector} [data-template-css-slot="groupCard"]`
        : slotToken === 'scenegrid'
        ? `${baseSelector} [data-template-css-slot="sceneCard"]`
        : slotToken === 'devicegrid'
        ? `${baseSelector} [data-template-css-slot="deviceCard"]`
        : parsed.cssState === 'on'
        ? baseSelector
        : `${baseSelector}[data-template-css-state="${parsed.cssState}"]`
    const sanitizedCss = slotToken === 'groupcardicon'
      ? sanitizeGroupCardIconCss(rawCss)
      : (slotToken === 'groupgrid')
        ? sanitizeGroupGridSurfaceCss(rawCss)
      : (slotToken === 'groupcard')
        ? sanitizeGroupLegacyIconFrameCss(rawCss)
      : rawCss
    blocks.push(...buildTemplateCssRules(sanitizedCss, scopedSelector))
  }
  blocks.push(buildGroupGridTransparencyGuardCss())
  blocks.push(buildTemplateIconTransparencyGuardCss())
  return blocks.join('\n')
}

function toInlineStyleValue(value: string | null): string {
  return String(value || '').replace(/!important/gi, '').trim()
}

function readTemplateRootCssBody(sourceCss: string): string {
  const source = String(sourceCss || '').trim()
  if (!source) return ''
  const rootMatch = source.match(/(^|\n)\s*&\s*\{([^{}]*)\}/m)
  if (rootMatch) return String(rootMatch[2] || '').trim()
  if (!source.includes('{')) return source
  return ''
}

function resolveDeviceCardThemeKey(deviceType: string): keyof typeof DEVICE_CARD_MEDIA_THEME {
  const normalized = String(deviceType || '').toLowerCase()
  if (!normalized) return 'generic'
  if (
    normalized.includes('ac')
    || normalized.includes('hvac')
    || normalized.includes('heater')
    || normalized.includes('water_heater')
  ) return 'climate'
  if (normalized.includes('light')) return 'lighting'
  if (
    normalized.includes('fan')
    || normalized.includes('air_purifier')
    || normalized.includes('dehumidifier')
    || normalized.includes('humidifier')
  ) return 'airflow'
  if (
    normalized.includes('curtain')
    || normalized.includes('blind')
    || normalized.includes('garage')
    || normalized.includes('valve')
    || normalized.includes('contact')
  ) return 'access'
  if (
    normalized.includes('outlet')
    || normalized.includes('switch')
    || normalized.includes('meter')
    || normalized.includes('electrical')
    || normalized.includes('mattercontroller')
    || normalized.includes('zigbeegateway')
    || normalized.includes('washer')
    || normalized.includes('kettle')
    || normalized.includes('water_dispenser')
    || normalized.includes('water_purifier')
  ) return 'utility'
  if (
    normalized.includes('sensor')
    || normalized.includes('occupancy')
    || normalized.includes('presence')
    || normalized.includes('motion')
  ) return 'sensor'
  return 'generic'
}

function buildDefaultDeviceTypeImageDataUrl(deviceType: string): string {
  const cacheKey = String(deviceType || '').toLowerCase()
  const cached = DEVICE_IMAGE_CACHE.get(cacheKey)
  if (cached) return cached
  const theme = DEVICE_CARD_MEDIA_THEME[resolveDeviceCardThemeKey(cacheKey)]
  const label = formatDeviceTypeForDisplay(cacheKey).toUpperCase() || 'DEVICE'
  const safeLabel = label.replace(/[<>&"]/g, '')
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 480 320">
  <defs>
    <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="${theme.start}" />
      <stop offset="100%" stop-color="${theme.end}" />
    </linearGradient>
    <radialGradient id="glow" cx="55%" cy="40%" r="56%">
      <stop offset="0%" stop-color="${theme.glow}" stop-opacity="0.32" />
      <stop offset="100%" stop-color="${theme.glow}" stop-opacity="0" />
    </radialGradient>
  </defs>
  <rect width="480" height="320" rx="24" fill="url(#bg)" />
  <rect width="480" height="320" rx="24" fill="url(#glow)" />
  <g fill="rgba(255,255,255,0.14)">
    <circle cx="86" cy="90" r="44" />
    <circle cx="406" cy="244" r="60" />
    <circle cx="350" cy="68" r="18" />
  </g>
  <text x="36" y="280" fill="rgba(255,255,255,0.92)" font-family="Inter, Arial, sans-serif" font-size="30" font-weight="700" letter-spacing="1.8">${safeLabel}</text>
</svg>`
  const encoded = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
  DEVICE_IMAGE_CACHE.set(cacheKey, encoded)
  return encoded
}

/** 將 deviceType 轉為顯示名稱，如 AC_UNIT -> AC Unit */
function formatDeviceTypeForDisplay(deviceType: string): string {
  const s = String(deviceType || '').trim()
  if (!s) return ''
  return s
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toSocketOrigin(baseUrl: string): string {
  return String(baseUrl || '').replace(/\/$/, '')
}

function collectRealtimeSnCandidates(payload: unknown): string[] {
  const event = isRecord(payload) ? payload : {}
  const data = isRecord(event.data) ? event.data : {}
  const nested = isRecord(data.data) ? data.data : {}
  return [
    data.deviceSN,
    data.sn,
    data.device,
    data.baseSN,
    nested.deviceSN,
    nested.sn,
    nested.device,
    nested.baseSN,
    event.deviceSN,
    event.sn,
    event.device,
  ]
    .map((item) => String(item || '').trim())
    .filter(Boolean)
}

function collectRealtimeSns(payload: unknown, knownDevices: Device[]): string[] {
  const event = isRecord(payload) ? payload : {}
  const data = isRecord(event.data) ? event.data : {}
  const nested = isRecord(data.data) ? data.data : {}
  const candidates = collectRealtimeSnCandidates(payload)

  if (candidates.length === 0) return []
  const knownSns = knownDevices
    .map((d) => d.deviceSN ?? d.sn)
    .map((sn) => String(sn || '').trim())
    .filter(Boolean)
  const knownSet = new Set(knownSns)
  const knownByBase = new Map<string, string[]>()
  knownSns.forEach((known) => {
    const base = known.split('#')[0]
    if (!base) return
    const list = knownByBase.get(base) ?? []
    list.push(known)
    knownByBase.set(base, list)
  })
  const endpointHints = [data.endpoint, nested.endpoint, event.endpoint, data.endpointId, nested.endpointId, event.endpointId]
    .map((item) => String(item || '').trim())
    .filter(Boolean)
  const matched = new Set<string>()

  candidates.forEach((candidate) => {
    if (knownSet.has(candidate)) matched.add(candidate)
    const [candidateBase, candidateEndpoint] = candidate.split('#')
    if (!candidateBase) return

    if (candidateEndpoint) {
      const endpointSn = `${candidateBase}#${candidateEndpoint}`
      if (knownSet.has(endpointSn)) matched.add(endpointSn)
      if (knownSet.has(candidateBase) && !(knownByBase.get(candidateBase) ?? []).some((sn) => sn.includes('#'))) {
        matched.add(candidateBase)
      }
      return
    }

    // Base SN 只在可精準定位時才回填，避免多 endpoint 一起被誤更新。
    if (knownSet.has(candidateBase)) matched.add(candidateBase)
    if (endpointHints.length > 0) {
      endpointHints.forEach((endpointHint) => {
        const endpointSn = `${candidateBase}#${endpointHint}`
        if (knownSet.has(endpointSn)) matched.add(endpointSn)
      })
      return
    }

    const sameBaseSns = knownByBase.get(candidateBase) ?? []
    if (sameBaseSns.length === 1) {
      matched.add(sameBaseSns[0])
    }
  })

  if (matched.size > 0) return Array.from(matched)
  return candidates.filter((candidate) => knownSet.has(candidate))
}

function collectRealtimeAttributes(payload: unknown): Record<string, unknown> {
  const event = isRecord(payload) ? payload : {}
  const data = isRecord(event.data) ? event.data : {}
  const nested = isRecord(data.data) ? data.data : {}

  const containers: Record<string, unknown>[] = [data, nested, event]
  for (const container of containers) {
    if (isRecord(container.delta)) return container.delta
    if (isRecord(container.attributes)) return container.attributes
    if (isRecord(container.iotState)) return container.iotState
    if (isRecord(container.state)) return container.state
  }

  for (const container of containers) {
    if (isRecord(container.onlineState) && typeof container.onlineState.online === 'boolean') {
      return { online: container.onlineState.online }
    }
    if (typeof container.online === 'boolean') {
      return { online: container.online }
    }
  }

  const fallback = Object.keys(nested).length > 0 ? nested : data
  return Object.fromEntries(
    Object.entries(fallback).filter(([key, value]) => {
      if (
        [
          'device',
          'sn',
          'deviceSN',
          'baseSN',
          'timestamp',
          'eventType',
          'endpoint',
          'groupId',
          'tenantId',
          'type',
          'data',
          'delta',
        ].includes(key)
      ) {
        return false
      }
      return value == null || typeof value !== 'object'
    })
  )
}

function getDeviceImage(d: Device, overrideImage?: string): string {
  const explicit = String(overrideImage || '').trim()
  if (explicit) return explicit
  const type = (d.deviceType ?? d.type ?? '').toLowerCase()
  return buildDefaultDeviceTypeImageDataUrl(type)
}

function toNumberValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const raw = value.trim()
    const parsed = Number(raw)
    if (Number.isFinite(parsed)) return parsed
    const matched = raw.match(/[-+]?\d*\.?\d+(?:e[-+]?\d+)?/i)
    if (matched) {
      const extracted = Number(matched[0])
      if (Number.isFinite(extracted)) return extracted
    }
  }
  return null
}

function formatNumberMaxTwoDecimals(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatTemperatureOneDecimal(value: number): string {
  return `${value.toFixed(1)}°C`
}

function formatScaledUnit(
  value: number,
  units: { base: string; kilo: string; mega: string; giga: string }
): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000_000) return `${formatNumberMaxTwoDecimals(value / 1_000_000_000)}${units.giga}`
  if (abs >= 1_000_000) return `${formatNumberMaxTwoDecimals(value / 1_000_000)}${units.mega}`
  if (abs >= 1_000) return `${formatNumberMaxTwoDecimals(value / 1_000)}${units.kilo}`
  return `${formatNumberMaxTwoDecimals(value)}${units.base}`
}

function toBooleanValue(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value > 0
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (!normalized) return null
    if (['true', '1', 'on', 'open', 'armed', 'active', 'enabled', 'yes'].includes(normalized)) return true
    if (['false', '0', 'off', 'close', 'closed', 'disarmed', 'inactive', 'disabled', 'no'].includes(normalized)) return false
  }
  return null
}

function normalizeStateKey(key: string): string {
  return String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

function normalizeDeviceTypeLookupKey(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
}

function buildDeviceTypeLookupCandidates(value: unknown): string[] {
  const raw = String(value || '').trim()
  if (!raw) return []
  const lower = raw.toLowerCase()
  const normalized = normalizeDeviceTypeLookupKey(raw)
  const candidates = [
    raw,
    lower,
    lower.replace(/[\s-]+/g, '_'),
    lower.replace(/[\s_]+/g, '-'),
    normalized,
    normalized.replace(/_/g, ''),
  ]
    .map((item) => String(item || '').trim())
    .filter(Boolean)
  return Array.from(new Set(candidates))
}

function readStatusValue(record: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (key in record) return record[key]
  }
  const lowered = new Map<string, unknown>()
  Object.entries(record).forEach(([k, v]) => lowered.set(k.toLowerCase(), v))
  const normalized = new Map<string, unknown>()
  Object.entries(record).forEach(([k, v]) => normalized.set(normalizeStateKey(k), v))
  for (const key of keys) {
    const match = lowered.get(key.toLowerCase())
    if (match !== undefined) return match
  }
  for (const key of keys) {
    const match = normalized.get(normalizeStateKey(key))
    if (match !== undefined) return match
  }
  return undefined
}

function extractOnOffValue(record: Record<string, unknown>): boolean | null {
  return toBooleanValue(readStatusValue(record, Array.from(ON_OFF_STATE_KEYS)))
}

function resolveOpenPercentValue(statusRecord: Record<string, unknown>): number | null {
  return toNumberValue(
    readStatusValue(statusRecord, [
      // Prioritize explicit openPercent first (UI/source-of-truth in App flow),
      // then fall back to other legacy/mirrored fields.
      'openPercent',
      'openClosePercent',
      'OpenClosePercent',
      'position',
    ])
  )
}

function stripOnOffFields(record: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(record).filter(([key]) => !NORMALIZED_ON_OFF_STATE_KEYS.has(normalizeStateKey(key)))
  )
}

type CardMetaPair = { label: string; value: string; emphasizeValue?: boolean; icon?: 'battery' }

function formatStatusPair(key: string, value: unknown): CardMetaPair | null {
  const num = toNumberValue(value)
  const normalizedKey = normalizeStateKey(key)
  if (normalizedKey.includes('thermostatmode')) {
    const text = String(value ?? '').trim()
    if (!text) return null
    return { label: '模式', value: text.toUpperCase(), emphasizeValue: true }
  }
  if ((normalizedKey.includes('temperature') || normalizedKey === 'temp') && num !== null) {
    return { label: 'Temperature', value: formatTemperatureOneDecimal(num) }
  }
  if ((normalizedKey.includes('humidity') || normalizedKey === 'rh') && num !== null) {
    return { label: 'Humidity', value: `${formatNumberMaxTwoDecimals(num)}%` }
  }
  if (normalizedKey.includes('brightness') && num !== null) return { label: 'Brightness', value: `${Math.round(num)}%` }
  if (
    (
      normalizedKey.includes('openclosepercent')
      || normalizedKey.includes('openpercent')
      || (normalizedKey.includes('open') && normalizedKey.includes('percent'))
    )
    && num !== null
  ) return { label: 'Open', value: `${Math.round(num)}%` }
  if ((normalizedKey.includes('watthour') || normalizedKey.includes('energy')) && num !== null) {
    return {
      label: 'Energy',
      value: formatScaledUnit(num, { base: 'Wh', kilo: 'kWh', mega: 'MWh', giga: 'GWh' }),
    }
  }
  if ((normalizedKey.includes('watt') || normalizedKey.includes('power')) && num !== null) {
    return {
      label: 'Power',
      value: formatScaledUnit(num, { base: 'W', kilo: 'kW', mega: 'MW', giga: 'GW' }),
    }
  }
  if (normalizedKey.includes('lux') && num !== null) return { label: 'Lux', value: `${num}lx` }
  if (normalizedKey.includes('battery') && num !== null) {
    return { label: 'Battery', value: `${Math.round(num)}%`, emphasizeValue: true, icon: 'battery' }
  }
  if (num !== null) return { label: formatDeviceTypeForDisplay(key), value: formatNumberMaxTwoDecimals(num) }
  if (typeof value === 'boolean') return { label: formatDeviceTypeForDisplay(key), value: value ? 'On' : 'Off' }
  const text = String(value ?? '').trim()
  if (!text) return null
  return { label: formatDeviceTypeForDisplay(key), value: text }
}

function normalizeMetaPairByLabel(pair: CardMetaPair): CardMetaPair {
  const labelKey = normalizeStateKey(pair.label)
  const num = toNumberValue(pair.value)

  if ((labelKey.includes('temperature') || labelKey === 'temp') && num !== null) {
    return { ...pair, label: 'Temperature', value: formatTemperatureOneDecimal(num), icon: undefined }
  }
  if ((labelKey.includes('humidity') || labelKey === 'rh') && num !== null) {
    return { ...pair, label: 'Humidity', value: `${formatNumberMaxTwoDecimals(num)}%`, icon: undefined }
  }
  if ((labelKey.includes('watthour') || labelKey.includes('energy')) && num !== null) {
    return {
      ...pair,
      label: 'Energy',
      value: formatScaledUnit(num, { base: 'Wh', kilo: 'kWh', mega: 'MWh', giga: 'GWh' }),
      icon: undefined,
    }
  }
  if ((labelKey === 'watt' || labelKey.includes('power')) && num !== null) {
    return {
      ...pair,
      label: 'Power',
      value: formatScaledUnit(num, { base: 'W', kilo: 'kW', mega: 'MW', giga: 'GW' }),
      icon: undefined,
    }
  }
  if (labelKey.includes('battery') && num !== null) {
    return { ...pair, label: 'Battery', value: `${Math.round(num)}%`, emphasizeValue: true, icon: 'battery' }
  }
  return pair
}

function normalizePairText(text: string): string {
  return String(text || '').replace(/\s+/g, '').toLowerCase()
}

function resolveBatteryValue(statusRecord: Record<string, unknown>): number | null {
  return toNumberValue(
    readStatusValue(statusRecord, [
      'battery',
      'batteryLevel',
      'batteryPercent',
      'battery_percentage',
      'Battery',
    ])
  )
}

function buildBatteryMetaPair(statusRecord: Record<string, unknown>): CardMetaPair | null {
  const battery = resolveBatteryValue(statusRecord)
  if (battery === null) return null
  return { label: 'Battery', value: `${Math.round(battery)}%`, emphasizeValue: true, icon: 'battery' }
}

function resolveCardPrimaryByDevice(
  deviceType: string,
  statusRecord: Record<string, unknown>,
  enabled: boolean
): { label: string; value: string } {
  const type = String(deviceType || '').toLowerCase()
  const battery = resolveBatteryValue(statusRecord)
  if (type.includes('ac') || type.includes('hvac') || type.includes('heater') || type.includes('water_heater')) {
    const target = toNumberValue(readStatusValue(statusRecord, ['thermostatTemperatureSetpoint', 'targetTemperature', 'setpoint']))
    if (target !== null) return { label: 'Target Temp', value: formatTemperatureOneDecimal(target) }
    const ambient = toNumberValue(readStatusValue(statusRecord, ['temperature', 'thermostatTemperatureAmbient', 'temp']))
    if (ambient !== null) return { label: 'Temperature', value: formatTemperatureOneDecimal(ambient) }
    if (battery !== null) return { label: 'Battery', value: `${Math.round(battery)}%` }
    return { label: '', value: enabled ? 'ON' : 'OFF' }
  }
  if (type.includes('light')) {
    const brightness = toNumberValue(readStatusValue(statusRecord, ['brightness']))
    if (brightness !== null) return { label: 'Brightness', value: `${Math.round(brightness)}%` }
    if (battery !== null) return { label: 'Battery', value: `${Math.round(battery)}%` }
    return { label: '', value: enabled ? 'ON' : 'OFF' }
  }
  if (type.includes('fan')) {
    const speed = readStatusValue(statusRecord, ['fanSpeed', 'currentFanSpeedSetting'])
    if (speed !== undefined && speed !== null && String(speed).trim()) {
      return { label: 'Fan Speed', value: String(speed) }
    }
    if (battery !== null) return { label: 'Battery', value: `${Math.round(battery)}%` }
    return { label: '', value: enabled ? 'ON' : 'OFF' }
  }
  if (type.includes('curtain') || type.includes('blind') || type.includes('garage') || type.includes('valve')) {
    const open = resolveOpenPercentValue(statusRecord)
    if (open !== null) return { label: 'Open', value: `${Math.round(open)}%` }
  }
  const temp = toNumberValue(readStatusValue(statusRecord, ['temperature', 'temp']))
  if (temp !== null) return { label: 'Temperature', value: formatTemperatureOneDecimal(temp) }
  const humidity = toNumberValue(readStatusValue(statusRecord, ['humidity', 'rh']))
  if (humidity !== null) return { label: 'Humidity', value: `${Math.round(humidity)}%` }
  if (battery !== null) return { label: 'Battery', value: `${Math.round(battery)}%` }
  return { label: '', value: enabled ? 'ON' : 'OFF' }
}

function resolveStateBadge(
  device: Device,
  statusRecord: Record<string, unknown>,
  enabled: boolean
): { text: string; tone: 'on' | 'off' | 'open' | 'close' | 'armed' | 'disarmed' | 'offline' } {
  if (device.online === false) return { text: 'OFFLINE', tone: 'offline' }
  const type = String(device.deviceType ?? device.type ?? '').toLowerCase()
  const normalizedType = type.replace(/[^a-z0-9]/g, '')
  const isGenericSwitch = normalizedType.includes('genericswitch')
  const isElectricalMeter = isElectricalMeterLike(device)
  const isNetworkInfra = isNetworkInfraLike(device)
  const traits = (device.traits ?? []).map((t) => String(t).toLowerCase().replace(/[^a-z0-9]/g, ''))
  const hasOnOff = traits.some((t) => t === 'onoff')
  const hasOpenClose = traits.some((t) => t === 'openclose' || t === 'openclosepercent')
  const hasAlarmLikeTrait = traits.some((t) => (
    t.includes('zonealarm')
    || t.includes('smoke')
    || t.includes('security')
    || t.includes('alarm')
  ))
  const isContactSensor = traits.some((t) => t === 'contact') || /contact/.test(normalizedType)
  const isOccupancyLikeSensor = traits.some((t) => (
    t === 'occupancy'
    || t === 'presence'
    || t === 'motionsensor'
    || t === 'motion'
    || t === 'pir'
    || t === 'pirsensor'
  )) || /occupancy|presence|motion|pir/.test(normalizedType)
  const isSensorLikeType = /sensor|occupancy|presence|motion|contact|water|leak|freeze|air_quality|electrical/.test(type)

  if (isGenericSwitch) {
    return { text: 'ONLINE', tone: 'on' }
  }
  if (isNetworkInfra) {
    return { text: 'ONLINE', tone: 'on' }
  }
  if (isElectricalMeter) {
    return { text: 'ONLINE', tone: 'on' }
  }

  if (isContactSensor) {
    const raw = readStatusValue(statusRecord, ['open', 'isOpen', 'openState', 'contact'])
    if (typeof raw === 'string') {
      const normalized = raw.trim().toLowerCase()
      if (normalized.includes('open')) return { text: 'OPEN', tone: 'open' }
      if (normalized.includes('close')) return { text: 'CLOSE', tone: 'close' }
    }
    const isOpen = toBooleanValue(raw)
    return { text: isOpen ? 'OPEN' : 'CLOSE', tone: isOpen ? 'open' : 'close' }
  }

  if (isOccupancyLikeSensor) {
    const occupied = toBooleanValue(
      readStatusValue(statusRecord, [
        'occupied',
        'occupancy',
        'presence',
        'motion',
        'pir',
        'detected',
        'isOccupied',
      ])
    )
    return { text: occupied ? 'OCCUPIED' : 'ABSENCE', tone: occupied ? 'on' : 'off' }
  }

  if (type.includes('curtain') || type.includes('blind') || type.includes('garage') || type.includes('valve')) {
    const open = resolveOpenPercentValue(statusRecord)
    const isOpen = (open ?? 0) > 0
    return { text: isOpen ? 'OPEN' : 'CLOSED', tone: isOpen ? 'open' : 'close' }
  }
  if (hasOpenClose) {
    const open = resolveOpenPercentValue(statusRecord)
    const isOpen = (open ?? 0) > 0
    return { text: isOpen ? 'OPEN' : 'CLOSED', tone: isOpen ? 'open' : 'close' }
  }
  if (hasOnOff) {
    return { text: enabled ? 'ON' : 'OFF', tone: enabled ? 'on' : 'off' }
  }
  if (hasAlarmLikeTrait) {
    const armedState = toBooleanValue(
      readStatusValue(statusRecord, [
        'isArmed',
        'armed',
        'armState',
        'armedState',
        'securityArmed',
        'zoneAlarmArmed',
        'ZoneAlarm',
        'zoneAlarm',
      ])
    )
    if (armedState != null) {
      return { text: armedState ? 'ARMED' : 'DISARMED', tone: armedState ? 'armed' : 'disarmed' }
    }
    const detectorState = toBooleanValue(
      readStatusValue(statusRecord, ['detected', 'occupancy', 'presence', 'motion', 'contact', 'leak'])
    )
    return { text: detectorState ? 'ARMED' : 'DISARMED', tone: detectorState ? 'armed' : 'disarmed' }
  }
  if (isSensorLikeType) {
    return { text: 'ONLINE', tone: 'on' }
  }
  return { text: enabled ? 'ON' : 'OFF', tone: enabled ? 'on' : 'off' }
}

/** 是否可一鍵執行（OnOff / OpenClose） */
function canQuickExecute(d: Device): boolean {
  const traits = (d.traits ?? []).map((t) => String(t).toLowerCase().replace(/[^a-z0-9]/g, ''))
  return traits.some((t) => t === 'onoff') || traits.some((t) => t === 'openclose' || t === 'openclosepercent')
}

function resolvePassiveSensorActionLabel(
  device: Device,
  statusRecord: Record<string, unknown>
): string | null {
  const traits = (device.traits ?? []).map((t) => String(t).toLowerCase().replace(/[^a-z0-9]/g, ''))
  const type = String(device.deviceType ?? device.type ?? '').toLowerCase()
  const normalizedType = type.replace(/[^a-z0-9]/g, '')
  const isNetworkInfra = isNetworkInfraLike(device)
  const isElectricalMeter = isElectricalMeterLike(device)

  if (isNetworkInfra) {
    return null
  }
  if (isElectricalMeter) {
    return null
  }

  const isOccupancyLikeSensor = traits.some((t) => (
    t === 'occupancy'
    || t === 'presence'
    || t === 'motionsensor'
    || t === 'motion'
    || t === 'pir'
    || t === 'pirsensor'
  )) || /occupancy|presence|motion|pir/.test(normalizedType)
  if (isOccupancyLikeSensor) {
    const occupied = toBooleanValue(
      readStatusValue(statusRecord, ['occupied', 'occupancy', 'presence', 'motion', 'pir', 'detected', 'isOccupied'])
    )
    return occupied ? 'OCCUPIED' : 'ABSENCE'
  }

  const isContactSensor = traits.some((t) => t === 'contact') || normalizedType.includes('contact')
  if (isContactSensor) {
    const raw = readStatusValue(statusRecord, ['open', 'isOpen', 'openState', 'contact'])
    if (typeof raw === 'string') {
      const normalized = raw.trim().toLowerCase()
      if (normalized.includes('open')) return 'OPEN'
      if (normalized.includes('close')) return 'CLOSE'
    }
    const isOpen = toBooleanValue(raw)
    return isOpen ? 'OPEN' : 'CLOSE'
  }

  const isTemperatureSensor = traits.some((t) => t === 'temperaturemeasurement')
    || (type.includes('temperature') && !/ac|hvac|heater|water_heater|thermostat/.test(type))
  if (isTemperatureSensor) {
    return null
  }

  return 'VIEW'
}

function isGenericSwitchType(deviceType: string): boolean {
  return String(deviceType || '').toLowerCase().replace(/[^a-z0-9]/g, '').includes('genericswitch')
}

function isElectricalMeterLike(device: Device): boolean {
  const type = String(device.deviceType ?? device.type ?? '').toLowerCase()
  const normalizedType = type.replace(/[^a-z0-9]/g, '')
  const traits = (device.traits ?? []).map((t) => String(t).toLowerCase().replace(/[^a-z0-9]/g, ''))
  const hasPowerOrEnergyTrait = traits.some((t) => (
    t.includes('powermeasurement')
    || t.includes('energy')
    || t.includes('watt')
    || t.includes('electrical')
  ))

  return (
    normalizedType.includes('electricalsensor')
    || normalizedType.includes('electricalmeter')
    || normalizedType.includes('powermeter')
    || normalizedType.includes('energymeter')
    || (normalizedType.includes('electrical') && hasPowerOrEnergyTrait)
  )
}

function isNetworkInfraLike(device: Device): boolean {
  const type = String(device.deviceType ?? device.type ?? '').toLowerCase()
  const normalizedType = type.replace(/[^a-z0-9]/g, '')
  const tokens = type.split(/[^a-z0-9]+/).filter(Boolean)
  const hasApToken = tokens.includes('ap') || tokens.includes('accesspoint')

  return (
    normalizedType.includes('gateway')
    || normalizedType.includes('router')
    || normalizedType.includes('bridge')
    || normalizedType.includes('accesspoint')
    || normalizedType.includes('wifiap')
    || normalizedType.includes('hub')
    || hasApToken
  )
}

function resolveActionIconTone(
  iconName: string,
  quickActionLabel: string | null,
  stateBadgeTone: 'on' | 'off' | 'open' | 'close' | 'armed' | 'disarmed' | 'offline'
): 'solid' | 'hollow' | 'alert-solid' | null {
  const normalizedLabel = String(quickActionLabel || '').trim().toUpperCase()
  if (iconName === 'sensors') {
    if (normalizedLabel === 'OCCUPIED') return 'alert-solid'
    if (normalizedLabel === 'ABSENCE') return 'hollow'
    return 'solid'
  }
  if (iconName === 'blinds') {
    return stateBadgeTone === 'open' ? 'solid' : 'hollow'
  }
  if (iconName === 'network_infra') return 'solid'
  if (iconName === 'electrical_meter') return 'solid'
  if (iconName === 'contact_open') return 'solid'
  if (iconName === 'contact_close') return 'hollow'
  return null
}

/** 依 traits 決定卡片右上角圖示：OnOff→電源、Pure Sensor→感測器、OpenClose→窗簾 */
function getDeviceCardIcon(
  d: Device,
  status: DeviceStatus | undefined
): { icon: string; isOn?: boolean; showGlow?: boolean; useGenericSwitchIcon?: boolean } {
  const traits = (d.traits ?? []).map((t) => String(t).toLowerCase().replace(/[^a-z0-9]/g, ''))
  const type = (d.deviceType ?? d.type ?? '').toLowerCase()
  const isGenericSwitch = isGenericSwitchType(type)
  const normalizedType = type.replace(/[^a-z0-9]/g, '')
  const isContactSensor = traits.some((t) => t === 'contact') || normalizedType.includes('contact')
  const isNetworkInfra = isNetworkInfraLike(d)

  const hasOnOff = traits.some((t) => t === 'onoff')
  const hasOpenClose = traits.some((t) => t === 'openclose' || t === 'openclosepercent')
  const sensorTraits = ['occupancy', 'temperaturemeasurement', 'humidity', 'illuminance', 'powermeasurement', 'contact', 'motionsensor']
  const isPureSensor = traits.some((t) => sensorTraits.includes(t)) || /sensor|occupancy|motion/.test(type)
  const statusRecord = (status?.attributes ?? status?.iotState ?? {}) as Record<string, unknown>
  const isElectricalMeter = isElectricalMeterLike(d)

  // 0. Network Infra（Hub/Gateway/Router/AP）→ 網路圖示
  if (isNetworkInfra) {
    return { icon: 'network_infra' }
  }

  // 1. OnOff → 電源圖示（power / power_off）
  if (hasOnOff) {
    const onOff = status?.attributes?.['OnOff'] ?? status?.iotState?.['OnOff']
    const isOn = onOff === true || onOff === 1 || onOff === 'on'
    return {
      icon: isOn ? 'power' : 'power_off',
      isOn,
      showGlow: isOn,
      useGenericSwitchIcon: isGenericSwitch,
    }
  }

  // 1.5 Contact Sensor → 開關門圖示
  if (isContactSensor) {
    const raw = readStatusValue(statusRecord, ['open', 'isOpen', 'openState', 'contact'])
    if (typeof raw === 'string') {
      const normalized = raw.trim().toLowerCase()
      if (normalized.includes('open')) return { icon: 'contact_open', showGlow: true }
      if (normalized.includes('close')) return { icon: 'contact_close' }
    }
    const isOpen = toBooleanValue(raw)
    return { icon: isOpen ? 'contact_open' : 'contact_close', showGlow: isOpen ?? false }
  }

  // 2. OpenClose → 窗簾圖示
  if (hasOpenClose) {
    return { icon: 'blinds' }
  }

  // 2.5 Electrical Meter/Sensor → 電力閃電圖示
  if (isElectricalMeter && !hasOnOff) {
    return { icon: 'electrical_meter' }
  }

  // 3. Pure Sensor（無 OnOff）→ 感測器圖示
  if (isPureSensor && !hasOnOff) {
    return { icon: 'sensors' }
  }

  // 4. 預設：電源（插座、開關等）
  const onOff = status?.attributes?.['OnOff'] ?? status?.iotState?.['OnOff']
  const isOn = onOff === true || onOff === 1 || onOff === 'on'
  return { icon: isOn ? 'power' : 'power_off', isOn, showGlow: isOn }
}

function resolveDeviceProductIcon(
  device: Device,
  status?: DeviceStatus
): string {
  const normalizedType = normalizeDeviceTypeLookupKey(device.deviceType ?? device.type ?? '')
  if (normalizedType === 'generic_switch' || normalizedType === 'genericswitch') return 'toggle_on'
  if (normalizedType.includes('fan')) return 'mode_fan'

  const typeIcon = getDeviceTypeIcon({ type: normalizedType })
  if (typeIcon && typeIcon !== 'power_settings_new') return typeIcon

  const iconName = getDeviceCardIcon(device, status).icon
  if (iconName === 'network_infra') return 'router'
  if (iconName === 'electrical_meter') return 'bolt'
  if (iconName === 'contact_open' || iconName === 'contact_close') return 'sensor_door'
  if (iconName === 'blinds') return 'blinds'
  if (iconName === 'sensors') return 'sensors'
  if (iconName === 'power' || iconName === 'power_off') return 'toggle_on'
  return 'devices'
}

const STORAGE_KEY = 'smart_home_dashboard_auth'
const SELECTED_LOCATION_KEY_PREFIX = 'smart_home_selected_location_'
const SCENE_ORDER_KEY_PREFIX = 'smart_home_scene_order_'
const LIGHT_GROUPS_KEY_PREFIX = 'smart_home_light_groups_'
const DEVICE_ORDER_KEY_PREFIX = 'smart_home_device_order_'

function getSelectedLocationStorageKey(appId: string) {
  return `${SELECTED_LOCATION_KEY_PREFIX}${appId}`
}

function loadStoredSelectedLocation(appId: string): string | null {
  try {
    return localStorage.getItem(getSelectedLocationStorageKey(appId))
  } catch {
    return null
  }
}

function saveSelectedLocation(appId: string, locationId: string) {
  try {
    localStorage.setItem(getSelectedLocationStorageKey(appId), locationId)
  } catch (_e) {
    // ignore
  }
}

function getSceneOrderStorageKey(appId: string, locationId: string) {
  return `${SCENE_ORDER_KEY_PREFIX}${appId}_${locationId}`
}

function loadStoredSceneOrder(appId: string, locationId: string): string[] | null {
  try {
    const raw = localStorage.getItem(getSceneOrderStorageKey(appId, locationId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    return parsed.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
  } catch (_e) {
    return null
  }
}

function saveSceneOrder(appId: string, locationId: string, sceneIds: string[]) {
  try {
    localStorage.setItem(getSceneOrderStorageKey(appId, locationId), JSON.stringify(sceneIds))
  } catch (_e) {
    // ignore
  }
}

function getLightGroupsStorageKey(appId: string, locationId: string) {
  return `${LIGHT_GROUPS_KEY_PREFIX}${appId}_${locationId}`
}

function loadStoredLightGroups(appId: string, locationId: string): LightControlGroup[] {
  try {
    const raw = localStorage.getItem(getLightGroupsStorageKey(appId, locationId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
      .map((entry) => ({
        id: String(entry.id || '').trim(),
        name: String(entry.name || '').trim() || '燈光群組',
        icon: String(entry.icon || 'lightbulb').trim() || 'lightbulb',
        deviceSns: Array.isArray(entry.deviceSns)
          ? entry.deviceSns.map((sn) => String(sn || '').trim()).filter(Boolean)
          : [],
        createdAt: Number(entry.createdAt) || Date.now(),
        updatedAt: Number(entry.updatedAt) || Date.now(),
      }))
      .filter((entry) => Boolean(entry.id))
  } catch (_e) {
    return []
  }
}

function saveStoredLightGroups(appId: string, locationId: string, groups: LightControlGroup[]) {
  try {
    localStorage.setItem(getLightGroupsStorageKey(appId, locationId), JSON.stringify(groups))
  } catch (_e) {
    // ignore
  }
}

function getDeviceOrderStorageKey(appId: string, locationId: string) {
  return `${DEVICE_ORDER_KEY_PREFIX}${appId}_${locationId}`
}

function loadStoredDeviceOrder(appId: string, locationId: string): string[] | null {
  try {
    const raw = localStorage.getItem(getDeviceOrderStorageKey(appId, locationId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    return parsed.map((id) => String(id || '').trim()).filter(Boolean)
  } catch (_e) {
    return null
  }
}

function saveStoredDeviceOrder(appId: string, locationId: string, orderedDeviceSns: string[]) {
  try {
    localStorage.setItem(getDeviceOrderStorageKey(appId, locationId), JSON.stringify(orderedDeviceSns))
  } catch (_e) {
    // ignore
  }
}

function hapticTap() {
  if ('vibrate' in navigator) {
    navigator.vibrate(10)
  }
}

function loadStoredAuth(): { appId: string; apiKey: string } | null {
  try {
    const s = localStorage.getItem(STORAGE_KEY)
    if (!s) return null
    const o = JSON.parse(s)
    if (o?.appId && o?.apiKey) return { appId: o.appId, apiKey: o.apiKey }
  } catch (_e) {
    // ignore
  }
  return null
}

function clearAuth() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (_e) {
    // ignore
  }
}

function isValidAuthOverride(input: AuthCreds | null | undefined): input is AuthCreds {
  if (!input) return false
  const appId = String(input.appId || '').trim()
  if (!appId) return false
  if ('accessToken' in input) {
    return Boolean(String(input.accessToken || '').trim())
  }
  return Boolean(String(input.apiKey || '').trim())
}

const DEFAULT_STORE_LINK = 'https://store.ultronsmart.com'
const DEFAULT_QUICK_SCENES = [
  { id: 'movie-night', label: 'Movie Night', icon: 'movie', active: true },
  { id: 'away', label: 'Away', icon: 'directions_run', active: false },
  { id: 'sleep', label: 'Sleep', icon: 'dark_mode', active: false },
]
const SCENE_ICON_OPTIONS = [
  'home',
  'directions_run',
  'movie',
  'music_note',
  'mic',
  'local_cafe',
  'ut_dine_heart',
  'ut_partner_heart',
  'menu_book',
  'local_bar',
  'bed',
  'eco',
  'wb_sunny',
  'favorite',
  'local_fire_department',
  'spa',
  'restaurant',
  'self_improvement',
  'celebration',
  'sports_esports',
  'fitness_center',
  'cleaning_services',
  'shield',
  'pets',
  'work',
  'weekend',
]
const LIGHT_GROUP_ICON_OPTIONS = [
  'lightbulb',
  'wb_incandescent',
  'emoji_objects',
  'highlight',
  'flare',
  'bolt',
]

const SCENE_ICON_ALIASES: Record<string, string> = {
  logout: 'directions_run',
  dark_mode: 'favorite',
  bedtime: 'bed',
  whatshot: 'local_fire_department',
  ut_intimacy: 'favorite',
  ut_lips: 'favorite',
}

function normalizeSceneIconName(iconName: string): string {
  const key = String(iconName || '').trim()
  if (!key) return 'auto_awesome'
  return SCENE_ICON_ALIASES[key] ?? key
}

type SceneIconTone = {
  bg: string
  border: string
  fg: string
  activeBg: string
  activeBorder: string
  activeFg: string
  ring: string
}

const SCENE_ICON_TONES: Record<string, SceneIconTone> = {
  home: { bg: 'rgba(245, 158, 11, 0.16)', border: 'rgba(245, 158, 11, 0.46)', fg: '#fcd34d', activeBg: 'rgba(245, 158, 11, 0.30)', activeBorder: 'rgba(245, 158, 11, 0.76)', activeFg: '#fde68a', ring: 'rgba(245, 158, 11, 0.42)' },
  directions_run: { bg: 'rgba(59, 130, 246, 0.16)', border: 'rgba(59, 130, 246, 0.46)', fg: '#93c5fd', activeBg: 'rgba(59, 130, 246, 0.30)', activeBorder: 'rgba(59, 130, 246, 0.76)', activeFg: '#dbeafe', ring: 'rgba(59, 130, 246, 0.42)' },
  movie: { bg: 'rgba(168, 85, 247, 0.16)', border: 'rgba(168, 85, 247, 0.46)', fg: '#d8b4fe', activeBg: 'rgba(168, 85, 247, 0.30)', activeBorder: 'rgba(168, 85, 247, 0.76)', activeFg: '#f3e8ff', ring: 'rgba(168, 85, 247, 0.42)' },
  music_note: { bg: 'rgba(236, 72, 153, 0.16)', border: 'rgba(236, 72, 153, 0.46)', fg: '#f9a8d4', activeBg: 'rgba(236, 72, 153, 0.30)', activeBorder: 'rgba(236, 72, 153, 0.76)', activeFg: '#fce7f3', ring: 'rgba(236, 72, 153, 0.42)' },
  mic: { bg: 'rgba(139, 92, 246, 0.16)', border: 'rgba(139, 92, 246, 0.46)', fg: '#c4b5fd', activeBg: 'rgba(139, 92, 246, 0.30)', activeBorder: 'rgba(139, 92, 246, 0.76)', activeFg: '#ede9fe', ring: 'rgba(139, 92, 246, 0.42)' },
  local_cafe: { bg: 'rgba(180, 83, 9, 0.16)', border: 'rgba(180, 83, 9, 0.46)', fg: '#fdba74', activeBg: 'rgba(180, 83, 9, 0.30)', activeBorder: 'rgba(180, 83, 9, 0.76)', activeFg: '#ffedd5', ring: 'rgba(180, 83, 9, 0.42)' },
  ut_dine_heart: { bg: 'rgba(244, 63, 94, 0.16)', border: 'rgba(244, 63, 94, 0.46)', fg: '#fda4af', activeBg: 'rgba(244, 63, 94, 0.30)', activeBorder: 'rgba(244, 63, 94, 0.76)', activeFg: '#ffe4e6', ring: 'rgba(244, 63, 94, 0.42)' },
  ut_partner_heart: { bg: 'rgba(236, 72, 153, 0.16)', border: 'rgba(236, 72, 153, 0.46)', fg: '#f9a8d4', activeBg: 'rgba(236, 72, 153, 0.30)', activeBorder: 'rgba(236, 72, 153, 0.76)', activeFg: '#fce7f3', ring: 'rgba(236, 72, 153, 0.42)' },
  menu_book: { bg: 'rgba(99, 102, 241, 0.16)', border: 'rgba(99, 102, 241, 0.46)', fg: '#c7d2fe', activeBg: 'rgba(99, 102, 241, 0.30)', activeBorder: 'rgba(99, 102, 241, 0.76)', activeFg: '#e0e7ff', ring: 'rgba(99, 102, 241, 0.42)' },
  local_bar: { bg: 'rgba(244, 63, 94, 0.16)', border: 'rgba(244, 63, 94, 0.46)', fg: '#fda4af', activeBg: 'rgba(244, 63, 94, 0.30)', activeBorder: 'rgba(244, 63, 94, 0.76)', activeFg: '#ffe4e6', ring: 'rgba(244, 63, 94, 0.42)' },
  bed: { bg: 'rgba(139, 92, 246, 0.16)', border: 'rgba(139, 92, 246, 0.46)', fg: '#c4b5fd', activeBg: 'rgba(139, 92, 246, 0.30)', activeBorder: 'rgba(139, 92, 246, 0.76)', activeFg: '#ede9fe', ring: 'rgba(139, 92, 246, 0.42)' },
  eco: { bg: 'rgba(34, 197, 94, 0.16)', border: 'rgba(34, 197, 94, 0.46)', fg: '#86efac', activeBg: 'rgba(34, 197, 94, 0.30)', activeBorder: 'rgba(34, 197, 94, 0.76)', activeFg: '#dcfce7', ring: 'rgba(34, 197, 94, 0.42)' },
  wb_sunny: { bg: 'rgba(249, 115, 22, 0.16)', border: 'rgba(249, 115, 22, 0.46)', fg: '#fdba74', activeBg: 'rgba(249, 115, 22, 0.30)', activeBorder: 'rgba(249, 115, 22, 0.76)', activeFg: '#ffedd5', ring: 'rgba(249, 115, 22, 0.42)' },
  favorite: { bg: 'rgba(236, 72, 153, 0.16)', border: 'rgba(236, 72, 153, 0.46)', fg: '#f9a8d4', activeBg: 'rgba(236, 72, 153, 0.30)', activeBorder: 'rgba(236, 72, 153, 0.76)', activeFg: '#fce7f3', ring: 'rgba(236, 72, 153, 0.42)' },
  local_fire_department: { bg: 'rgba(249, 115, 22, 0.16)', border: 'rgba(249, 115, 22, 0.46)', fg: '#fdba74', activeBg: 'rgba(249, 115, 22, 0.30)', activeBorder: 'rgba(249, 115, 22, 0.76)', activeFg: '#ffedd5', ring: 'rgba(249, 115, 22, 0.42)' },
  spa: { bg: 'rgba(20, 184, 166, 0.16)', border: 'rgba(20, 184, 166, 0.46)', fg: '#99f6e4', activeBg: 'rgba(20, 184, 166, 0.30)', activeBorder: 'rgba(20, 184, 166, 0.76)', activeFg: '#ccfbf1', ring: 'rgba(20, 184, 166, 0.42)' },
  restaurant: { bg: 'rgba(239, 68, 68, 0.16)', border: 'rgba(239, 68, 68, 0.46)', fg: '#fca5a5', activeBg: 'rgba(239, 68, 68, 0.30)', activeBorder: 'rgba(239, 68, 68, 0.76)', activeFg: '#fee2e2', ring: 'rgba(239, 68, 68, 0.42)' },
  self_improvement: { bg: 'rgba(20, 184, 166, 0.16)', border: 'rgba(20, 184, 166, 0.46)', fg: '#99f6e4', activeBg: 'rgba(20, 184, 166, 0.30)', activeBorder: 'rgba(20, 184, 166, 0.76)', activeFg: '#ccfbf1', ring: 'rgba(20, 184, 166, 0.42)' },
  celebration: { bg: 'rgba(217, 70, 239, 0.16)', border: 'rgba(217, 70, 239, 0.46)', fg: '#f0abfc', activeBg: 'rgba(217, 70, 239, 0.30)', activeBorder: 'rgba(217, 70, 239, 0.76)', activeFg: '#fae8ff', ring: 'rgba(217, 70, 239, 0.42)' },
  sports_esports: { bg: 'rgba(6, 182, 212, 0.16)', border: 'rgba(6, 182, 212, 0.46)', fg: '#67e8f9', activeBg: 'rgba(6, 182, 212, 0.30)', activeBorder: 'rgba(6, 182, 212, 0.76)', activeFg: '#cffafe', ring: 'rgba(6, 182, 212, 0.42)' },
  fitness_center: { bg: 'rgba(132, 204, 22, 0.16)', border: 'rgba(132, 204, 22, 0.46)', fg: '#bef264', activeBg: 'rgba(132, 204, 22, 0.30)', activeBorder: 'rgba(132, 204, 22, 0.76)', activeFg: '#ecfccb', ring: 'rgba(132, 204, 22, 0.42)' },
  cleaning_services: { bg: 'rgba(14, 165, 233, 0.16)', border: 'rgba(14, 165, 233, 0.46)', fg: '#7dd3fc', activeBg: 'rgba(14, 165, 233, 0.30)', activeBorder: 'rgba(14, 165, 233, 0.76)', activeFg: '#e0f2fe', ring: 'rgba(14, 165, 233, 0.42)' },
  shield: { bg: 'rgba(16, 185, 129, 0.16)', border: 'rgba(16, 185, 129, 0.46)', fg: '#6ee7b7', activeBg: 'rgba(16, 185, 129, 0.30)', activeBorder: 'rgba(16, 185, 129, 0.76)', activeFg: '#d1fae5', ring: 'rgba(16, 185, 129, 0.42)' },
  pets: { bg: 'rgba(234, 88, 12, 0.16)', border: 'rgba(234, 88, 12, 0.46)', fg: '#fdba74', activeBg: 'rgba(234, 88, 12, 0.30)', activeBorder: 'rgba(234, 88, 12, 0.76)', activeFg: '#ffedd5', ring: 'rgba(234, 88, 12, 0.42)' },
  work: { bg: 'rgba(37, 99, 235, 0.16)', border: 'rgba(37, 99, 235, 0.46)', fg: '#93c5fd', activeBg: 'rgba(37, 99, 235, 0.30)', activeBorder: 'rgba(37, 99, 235, 0.76)', activeFg: '#dbeafe', ring: 'rgba(37, 99, 235, 0.42)' },
  weekend: { bg: 'rgba(250, 204, 21, 0.16)', border: 'rgba(250, 204, 21, 0.46)', fg: '#fde047', activeBg: 'rgba(250, 204, 21, 0.30)', activeBorder: 'rgba(250, 204, 21, 0.76)', activeFg: '#fef9c3', ring: 'rgba(250, 204, 21, 0.42)' },
}

const SCENE_ICON_FALLBACK_TONES: SceneIconTone[] = Object.values(SCENE_ICON_TONES)

function resolveSceneIconTone(iconName: string, index: number): SceneIconTone {
  return SCENE_ICON_TONES[iconName] ?? SCENE_ICON_FALLBACK_TONES[index % SCENE_ICON_FALLBACK_TONES.length]
}

function renderSceneIconGlyph(iconName: string) {
  const normalized = normalizeSceneIconName(iconName)
  if (normalized === 'ut_dine_heart') {
    return <img src={dineHeartIcon} alt="" className="smart-home-scene-icon-custom-image" />
  }
  if (normalized === 'ut_partner_heart') {
    return <img src={partnerHeartIcon} alt="" className="smart-home-scene-icon-custom-image" />
  }
  return <span className="material-symbols-outlined">{normalized}</span>
}

function buildPublicDemoMockData(): {
  locations: Location[]
  devices: Device[]
  statuses: DeviceStatus[]
} {
  const locationId = 'demo-home'
  const locations: Location[] = [
    { id: locationId, groupId: locationId, name: 'Demo Home', deviceCount: 5 },
  ]
  const devices: Device[] = [
    {
      sn: 'DEMO-LIGHT-001',
      deviceSN: 'DEMO-LIGHT-001',
      name: 'Living Room Light',
      nickname: 'Living Room Light',
      displayName: 'Living Room Light',
      deviceType: 'LIGHT',
      type: 'LIGHT',
      traits: ['OnOff', 'Brightness', 'ColorTemperature'],
      online: true,
    },
    {
      sn: 'DEMO-AC-001',
      deviceSN: 'DEMO-AC-001',
      name: 'Master Bedroom AC',
      nickname: 'Master Bedroom AC',
      displayName: 'Master Bedroom AC',
      deviceType: 'AC_UNIT',
      type: 'AC_UNIT',
      traits: ['OnOff', 'TemperatureSetting', 'TemperatureMeasurement', 'Humidity'],
      online: true,
    },
    {
      sn: 'DEMO-CURTAIN-001',
      deviceSN: 'DEMO-CURTAIN-001',
      name: 'Main Curtain',
      nickname: 'Main Curtain',
      displayName: 'Main Curtain',
      deviceType: 'CURTAIN',
      type: 'CURTAIN',
      traits: ['OpenClose'],
      online: true,
    },
    {
      sn: 'DEMO-SENSOR-001',
      deviceSN: 'DEMO-SENSOR-001',
      name: 'Hallway Sensor',
      nickname: 'Hallway Sensor',
      displayName: 'Hallway Sensor',
      deviceType: 'OCCUPANCY_SENSOR',
      type: 'OCCUPANCY_SENSOR',
      traits: ['OccupancySensing', 'TemperatureMeasurement'],
      online: true,
    },
    {
      sn: 'DEMO-OUTLET-001',
      deviceSN: 'DEMO-OUTLET-001',
      name: 'Coffee Outlet',
      nickname: 'Coffee Outlet',
      displayName: 'Coffee Outlet',
      deviceType: 'OUTLET',
      type: 'OUTLET',
      traits: ['OnOff', 'ElectricalMeasurement'],
      online: true,
    },
  ]
  const statuses: DeviceStatus[] = [
    { sn: 'DEMO-LIGHT-001', name: 'Living Room Light', online: true, attributes: { OnOff: true, brightness: 68, colorTemperature: 4200 } },
    { sn: 'DEMO-AC-001', name: 'Master Bedroom AC', online: true, attributes: { OnOff: true, thermostatMode: 'cool', temperature: 24.6, humidity: 47, fanSpeed: 'medium' } },
    { sn: 'DEMO-CURTAIN-001', name: 'Main Curtain', online: true, attributes: { OpenClosePercent: 35 } },
    { sn: 'DEMO-SENSOR-001', name: 'Hallway Sensor', online: true, attributes: { occupied: true, temperature: 26.2 } },
    { sn: 'DEMO-OUTLET-001', name: 'Coffee Outlet', online: true, attributes: { OnOff: false, watt: 0, watt_hour: 12.4 } },
  ]
  return { locations, devices, statuses }
}

const AUTOMATION_CONDITION_METRIC_LABELS: Record<string, string> = {
  on: '電源',
  onoff: '電源',
  powerstate: '電源',
  power: '電源',
  ison: '電源',
  openpercent: '窗簾開合',
  openclosepercent: '窗簾開合',
  position: '窗簾開合',
  running: '運轉狀態',
  startstop: '運轉狀態',
  isrunning: '運轉狀態',
  runstate: '運轉狀態',
  temperature: '溫度',
  temp: '溫度',
  humidity: '濕度',
  rh: '濕度',
  lux: '照度',
  illuminance: '照度',
  watt: '功率',
  realpower: '功率',
  current: '電流',
  voltage: '電壓',
  watthour: '累積電量',
  energy: '累積電量',
  occupied: '人體',
  occupancy: '人體',
  presence: '人體',
  motion: '人體',
  online: '連線狀態',
}

const AUTOMATION_CONDITION_UNIT_BY_METRIC: Record<string, string> = {
  temperature: '°C',
  temp: '°C',
  humidity: '%',
  rh: '%',
  lux: 'Lux',
  illuminance: 'Lux',
  watt: 'W',
  realpower: 'W',
  current: 'A',
  voltage: 'V',
  watthour: 'Wh',
  energy: 'Wh',
}

function formatAutomationConditionText(metricRaw: string, operatorRaw: string, threshold: unknown): string | null {
  const metricKey = normalizeStateKey(metricRaw)
  const operator = String(operatorRaw || '').trim().toLowerCase()
  const thresholdText = String(threshold ?? '').trim()
  const boolThreshold = toBooleanValue(threshold)
  const metricLabel = AUTOMATION_CONDITION_METRIC_LABELS[metricKey] || metricRaw

  const isOnMetric = ['on', 'onoff', 'powerstate', 'power', 'ison'].includes(metricKey)
  if (operator === 'eq' && isOnMetric && boolThreshold !== null) {
    return boolThreshold ? '設備開啟' : '設備關閉'
  }

  const isRunningMetric = ['running', 'startstop', 'isrunning', 'runstate'].includes(metricKey)
  if (operator === 'eq' && isRunningMetric && boolThreshold !== null) {
    return boolThreshold ? '設備啟動' : '設備停止'
  }

  const isOccupancyMetric = ['occupied', 'occupancy', 'presence', 'motion'].includes(metricKey)
  if (operator === 'eq' && isOccupancyMetric && boolThreshold !== null) {
    return boolThreshold ? '偵測到人員' : '未偵測到人員'
  }

  if (operator === 'eq' && metricKey === 'online' && boolThreshold !== null) {
    return boolThreshold ? '設備在線' : '設備離線'
  }

  const numericThreshold = toNumberValue(threshold)
  const unit = AUTOMATION_CONDITION_UNIT_BY_METRIC[metricKey] || ''
  const valueText = numericThreshold !== null
    ? `${formatNumberMaxTwoDecimals(numericThreshold)}${unit}`
    : thresholdText

  if (!metricLabel || !operator || threshold === undefined || valueText === '') return null

  if (operator === 'eq') return `${metricLabel}為 ${valueText}`
  if (operator === 'neq') return `${metricLabel}不為 ${valueText}`
  if (operator === 'gt') return `${metricLabel}高於 ${valueText}`
  if (operator === 'gte') return `${metricLabel}大於等於 ${valueText}`
  if (operator === 'lt') return `${metricLabel}低於 ${valueText}`
  if (operator === 'lte') return `${metricLabel}小於等於 ${valueText}`

  return `${metricLabel} ${operator} ${valueText}`
}

function resolveAutomationConditionDeviceSn(conditions: unknown): string {
  if (!conditions || typeof conditions !== 'object') return ''
  const cond = conditions as { type?: string; device?: unknown; conditions?: unknown[] }
  if (cond.type === 'simple') {
    return String(cond.device || '').trim()
  }
  if (cond.type === 'compound' && Array.isArray(cond.conditions)) {
    for (const child of cond.conditions) {
      const found = resolveAutomationConditionDeviceSn(child)
      if (found) return found
    }
  }
  return ''
}

function isEndpointDeviceSnValue(raw: unknown): boolean {
  const sn = String(raw || '').trim()
  return Boolean(sn) && sn.includes('#')
}

function toStrictEndpointDeviceSn(raw: unknown): string {
  const sn = String(raw || '').trim()
  return isEndpointDeviceSnValue(sn) ? sn : ''
}

function resolveAutomationDeviceLabel(deviceSN: string, labelBySn?: Map<string, string>): string {
  const sn = String(deviceSN || '').trim()
  if (!sn || !labelBySn) return ''
  const direct = labelBySn.get(sn)
  if (direct) return direct
  const baseSN = sn.split('#')[0]
  return labelBySn.get(baseSN) ?? ''
}

function formatAutomationSummary(a: Automation, deviceLabelBySn?: Map<string, string>): string {
  const mode = String(a.triggerMode || '').trim().toLowerCase()
  const trigger = (a.trigger && typeof a.trigger === 'object') ? (a.trigger as Record<string, unknown>) : {}
  const triggerType = String(trigger.type || '').trim().toLowerCase()

  if (a.schedule?.enabled || (a.schedule?.timeRanges?.length ?? 0) > 0 || mode === 'schedule' || triggerType === 'schedule') {
    const triggerStart = String(trigger.time || '').trim()
    const start = a.schedule?.timeRanges?.[0]?.start ?? triggerStart ?? '—'
    const days = a.schedule.daysOfWeek
    const dayLabel = !days || days.length === 7 ? '每日' : days.length === 5 && [1,2,3,4,5].every(d => days.includes(d)) ? '平日' : `${days.length} 天`
    return start && start !== '—' ? `${start} ${dayLabel}` : '定時觸發'
  }
  if (a.conditions && typeof a.conditions === 'object' && 'type' in a.conditions) {
    const c = a.conditions as { type?: string; metric?: string; operator?: string; threshold?: unknown }
    const metric = String(c.metric || '').trim()
    const op = String(c.operator || '').trim()
    const threshold = c.threshold
    const thresholdText = String(threshold ?? '').trim().toLowerCase()
    const metricKey = metric.toLowerCase()
    if (metric.toLowerCase() === 'online' && op === 'eq' && (threshold === true || thresholdText === 'true' || threshold === 1 || thresholdText === '1')) {
      const targetDeviceSn = resolveAutomationConditionDeviceSn(a.conditions)
      const targetLabel = resolveAutomationDeviceLabel(targetDeviceSn, deviceLabelBySn)
      return targetLabel ? `當「${targetLabel}」在線時` : '設備在線時'
    }
    if (metric.toLowerCase() === 'online' && op === 'eq' && (threshold === false || thresholdText === 'false' || threshold === 0 || thresholdText === '0')) {
      const targetDeviceSn = resolveAutomationConditionDeviceSn(a.conditions)
      const targetLabel = resolveAutomationDeviceLabel(targetDeviceSn, deviceLabelBySn)
      return targetLabel ? `當「${targetLabel}」離線時` : '離線通知'
    }
    if (op === 'eq' && ['openpercent', 'openclosepercent', 'position'].includes(metricKey)) {
      const pct = Number(threshold)
      const targetDeviceSn = resolveAutomationConditionDeviceSn(a.conditions)
      const targetLabel = resolveAutomationDeviceLabel(targetDeviceSn, deviceLabelBySn)
      if (Number.isFinite(pct)) {
        if (pct <= 0) return targetLabel ? `當「${targetLabel}」關閉時` : '窗簾關閉時'
        if (pct >= 100) return targetLabel ? `當「${targetLabel}」開啟時` : '窗簾開啟時'
        return targetLabel
          ? `當「${targetLabel}」開合度為 ${Math.round(pct)}% 時`
          : `窗簾 ${Math.round(pct)}% 時`
      }
    }
    const humanReadable = formatAutomationConditionText(metric, op, threshold)
    if (humanReadable) {
      const targetDeviceSn = resolveAutomationConditionDeviceSn(a.conditions)
      const targetLabel = resolveAutomationDeviceLabel(targetDeviceSn, deviceLabelBySn)
      if (targetLabel) return `當「${targetLabel}」${humanReadable}時`
      return `當${humanReadable}時`
    }
    return '條件觸發'
  }
  return '—'
}

type AutomationCardCopy = {
  title: string
  subtitle: string
}

function buildAutomationCardCopy(
  automation: Automation,
  deviceLabelBySn?: Map<string, string>
): AutomationCardCopy {
  const userTitle = String(automation.name || '').trim()
  const triggerTitle = userTitle || '未命名自動化'
  const triggerDetail = formatAutomationSummary(automation, deviceLabelBySn)
  return {
    title: triggerTitle,
    subtitle: automation.enabled
      ? (triggerDetail && triggerDetail !== '—' ? triggerDetail : '—')
      : `已停用 · ${triggerDetail && triggerDetail !== '—' ? triggerDetail : '—'}`,
  }
}

type AutomationTriggerType = 'schedule' | 'sensor' | 'device_state' | 'offline_notify'
type AutomationTriggerOperator = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte'
type DeviceStateTriggerCondition = 'on' | 'off' | 'open' | 'close' | 'start' | 'stop'
type TriggerMetricOption = {
  value: string
  label: string
  aliases?: string[]
  traits?: string[]
}

type TriggerThresholdMeta = {
  min?: number
  max?: number
  step?: number
  unit?: string
  options?: string[]
  sample?: unknown
  isBoolean?: boolean
}

type TriggerThresholdChoice = {
  value: string
  label: string
}

type AutomationRecommendation = {
  id: string
  triggerType: AutomationTriggerType
  title: string
  subtitle: string
  icon: string
  suggestedName?: string
  prefillTriggerDeviceSn?: string
  prefillTriggerMetric?: string
  prefillTriggerOperator?: AutomationTriggerOperator
  prefillTriggerThresholdInput?: string
  prefillDeviceStateCondition?: DeviceStateTriggerCondition
  prefillScheduleStart?: string
  prefillScheduleEnd?: string
  prefillScheduleDays?: number[]
  prefillActionDeviceSns?: string[]
  prefillDeviceActions?: Record<string, { command: string; params?: Record<string, unknown> }>
}

const AUTOMATION_WEEK_DAYS = [
  { value: 0, label: '日' },
  { value: 1, label: '一' },
  { value: 2, label: '二' },
  { value: 3, label: '三' },
  { value: 4, label: '四' },
  { value: 5, label: '五' },
  { value: 6, label: '六' },
] as const

const AUTOMATION_SCHEDULE_HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) =>
  String(hour).padStart(2, '0')
)
const AUTOMATION_SCHEDULE_MINUTE_OPTIONS = Array.from({ length: 60 }, (_, minute) =>
  String(minute).padStart(2, '0')
)
const AUTOMATION_SCHEDULE_WHEEL_ITEM_HEIGHT = 36
const AUTOMATION_SCHEDULE_DEFAULT_HOUR = '18'
const AUTOMATION_SCHEDULE_DEFAULT_MINUTE = '00'

function parseScheduleTimeParts(value: string): { hour: string; minute: string } {
  const normalized = String(value || '').trim()
  const match = normalized.match(/^([01]\d|2[0-3]):([0-5]\d)$/)
  if (!match) return { hour: '', minute: '' }
  return { hour: match[1], minute: match[2] }
}

function shouldUseIosScheduleWheelPicker(): boolean {
  if (typeof window === 'undefined') return false
  const nav = window.navigator
  const ua = String(nav.userAgent || '')
  const isIOS = /iPhone|iPad|iPod/i.test(ua)
  const isIPadDesktopMode = nav.platform === 'MacIntel' && (nav.maxTouchPoints || 0) > 1
  return isIOS || isIPadDesktopMode
}

const SENSOR_TRIGGER_METRICS: TriggerMetricOption[] = [
  { value: 'temperature', label: '溫度', aliases: ['temp'], traits: ['TemperatureMeasurement'] },
  { value: 'humidity', label: '濕度', aliases: ['rh'], traits: ['HumidityMeasurement', 'Humidity'] },
  { value: 'lux', label: '照度 (Lux)', aliases: ['illuminance'], traits: ['IlluminanceMeasurement'] },
  {
    value: 'watt',
    label: '即時功率 (W)',
    aliases: ['power', 'realpower'],
    traits: ['ElectricalMeasurement', 'PowerMeasurement', 'PowerConsumption'],
  },
  {
    value: 'watt_hour',
    label: '累積電量 (Wh)',
    aliases: ['energy', 'wh', 'kwh'],
    traits: ['ElectricalMeasurement', 'EnergyMeasurement', 'Metering'],
  },
  {
    value: 'current',
    label: '電流 (A)',
    aliases: ['amp', 'ampere', 'realcurrent'],
    traits: ['ElectricalMeasurement', 'CurrentMeasurement', 'CurrentSensor'],
  },
  {
    value: 'voltage',
    label: '電壓 (V)',
    aliases: ['volt', 'realvoltage'],
    traits: ['ElectricalMeasurement', 'VoltageMeasurement', 'VoltageSensor'],
  },
  {
    value: 'occupied',
    label: '有人/無人',
    aliases: ['occupancy', 'presence', 'motion'],
    traits: ['OccupancySensing', 'MotionSensor', 'PresenceSensing'],
  },
]

const DEVICE_STATE_TRIGGER_METRICS: TriggerMetricOption[] = [
  { value: 'on', label: '電源狀態', aliases: ['onoff', 'powerstate'], traits: ['OnOff'] },
  { value: 'openPercent', label: '開合度', aliases: ['openclosepercent', 'position'], traits: ['OpenClose'] },
  { value: 'running', label: '啟停狀態', aliases: ['startstop', 'isrunning', 'runstate'], traits: ['StartStop'] },
]

const AUTOMATION_TRIGGER_ENTRY_CONFIG: Record<
  AutomationTriggerType,
  { title: string; example: string; icon: string }
> = {
  schedule: {
    title: '某個特定時間',
    example: '例如：「上午 8:00」或「日落」',
    icon: 'schedule',
  },
  device_state: {
    title: '設備被控制時',
    example: '例如：「電燈開啟」',
    icon: 'hub',
  },
  sensor: {
    title: '感測器有變化時',
    example: '例如：「偵測到動作」',
    icon: 'sensors',
  },
  offline_notify: {
    title: '離線通知',
    example: '例如：「設備離線時」',
    icon: 'cloud_off',
  },
}

function isOfflineThresholdValue(value: unknown): boolean {
  if (value === false || value === 0) return true
  const text = String(value ?? '').trim().toLowerCase()
  return text === 'false' || text === '0'
}

function normalizeConditionOperator(raw: unknown): AutomationTriggerOperator {
  const value = String(raw || '').trim().toLowerCase()
  if (value === 'neq') return 'neq'
  if (value === 'gt') return 'gt'
  if (value === 'gte') return 'gte'
  if (value === 'lt') return 'lt'
  if (value === 'lte') return 'lte'
  return 'eq'
}

function inferAutomationTriggerType(automation?: Automation | null): AutomationTriggerType {
  if (!automation) return 'schedule'
  if (automation.schedule?.enabled || (automation.schedule?.timeRanges?.length ?? 0) > 0) return 'schedule'
  const mode = String(automation.triggerMode || '').trim().toLowerCase()
  if (mode === 'schedule') return 'schedule'
  const condition = (automation.conditions && typeof automation.conditions === 'object')
    ? (automation.conditions as Record<string, unknown>)
    : {}
  const metric = String(condition.metric || '').trim().toLowerCase()
  const operator = normalizeConditionOperator(condition.operator)
  if (metric === 'online' && operator === 'eq' && isOfflineThresholdValue(condition.threshold)) {
    return 'offline_notify'
  }
  if ([
    'temperature',
    'humidity',
    'lux',
    'illuminance',
    'occupied',
    'watt',
    'watt_hour',
    'realpower',
    'current',
    'ampere',
    'amp',
    'realcurrent',
    'voltage',
    'volt',
    'realvoltage',
  ].includes(metric)) {
    return 'sensor'
  }
  if (mode === 'event') return 'device_state'
  return metric ? 'device_state' : 'schedule'
}

function parseAutomationThresholdInput(input: string, operator: AutomationTriggerOperator): unknown {
  const raw = String(input || '').trim()
  if (!raw) return undefined
  const lower = raw.toLowerCase()
  if (lower === 'true') return true
  if (lower === 'false') return false
  if (operator === 'gt' || operator === 'gte' || operator === 'lt' || operator === 'lte') {
    const numeric = Number(raw)
    if (Number.isFinite(numeric)) return numeric
    return undefined
  }
  const numeric = Number(raw)
  if (Number.isFinite(numeric)) return numeric
  return raw
}

function normalizeMetricLookupKey(input: unknown): string {
  return String(input || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '')
}

function resolveSystemTimezone(fallback = 'Asia/Taipei'): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (typeof tz === 'string' && tz.trim()) return tz.trim()
  } catch (_e) {
    // ignore
  }
  return fallback
}

function buildRuleAliasTokenMap(rules: MergeRule[] | undefined): Map<string, string> {
  const map = new Map<string, string>()
  for (const rule of rules ?? []) {
    const normalizedMain = normalizeMetricLookupKey(rule.main)
    if (!normalizedMain) continue
    if (!map.has(normalizedMain)) map.set(normalizedMain, normalizedMain)
    for (const key of rule.keys ?? []) {
      const normalizedKey = normalizeMetricLookupKey(key)
      if (!normalizedKey) continue
      if (!map.has(normalizedKey)) map.set(normalizedKey, normalizedMain)
    }
  }
  return map
}

function resolveMergedMetricToken(raw: unknown, aliasMap?: Map<string, string>): string {
  const normalized = normalizeMetricLookupKey(raw)
  if (!normalized) return ''
  if (!aliasMap) return normalized
  return aliasMap.get(normalized) ?? normalized
}

function buildMergedMetricAliasMap(options?: {
  sensorDataRules?: MergeRule[]
  stateRules?: MergeRule[]
}): Map<string, string> {
  const map = new Map<string, string>()
  // sensorData rules first; state rules act as fallback
  for (const [k, v] of buildRuleAliasTokenMap(options?.sensorDataRules)) {
    if (!map.has(k)) map.set(k, v)
  }
  for (const [k, v] of buildRuleAliasTokenMap(options?.stateRules)) {
    if (!map.has(k)) map.set(k, v)
  }
  return map
}

function collectCapabilityTokensFromUnknown(
  value: unknown,
  out: Set<string>,
  depth = 0,
  metricAliasMap?: Map<string, string>
): void {
  if (depth > 4 || value == null) return
  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === 'string') {
        const rawToken = normalizeMetricLookupKey(item)
        const mergedToken = resolveMergedMetricToken(item, metricAliasMap)
        if (rawToken) out.add(rawToken)
        if (mergedToken) out.add(mergedToken)
      } else {
        collectCapabilityTokensFromUnknown(item, out, depth + 1, metricAliasMap)
      }
    }
    return
  }
  if (typeof value !== 'object') return
  const record = value as Record<string, unknown>
  for (const [key, nested] of Object.entries(record)) {
    const rawToken = normalizeMetricLookupKey(key)
    const mergedToken = resolveMergedMetricToken(key, metricAliasMap)
    if (rawToken) out.add(rawToken)
    if (mergedToken) out.add(mergedToken)
    if (nested != null && typeof nested === 'object') {
      collectCapabilityTokensFromUnknown(nested, out, depth + 1, metricAliasMap)
    }
  }
}

function collectDeviceCapabilityTokens(
  device?: Device,
  metricAliasMap?: Map<string, string>
): Set<string> {
  const out = new Set<string>()
  if (!device) return out
  for (const trait of device.traits ?? []) {
    out.add(normalizeMetricLookupKey(trait))
  }
  // QueryDevices capability payload (traits + attributes) 是唯一能力來源。
  collectCapabilityTokensFromUnknown(device.attributes, out, 0, metricAliasMap)
  collectCapabilityTokensFromUnknown(device.capabilities, out, 0, metricAliasMap)
  out.add('online')
  return out
}

function collectDeviceTraitTokens(device?: Device): Set<string> {
  const out = new Set<string>()
  if (!device) return out
  for (const trait of device.traits ?? []) {
    out.add(normalizeMetricLookupKey(trait))
  }
  return out
}

const ACTION_REQUIRED_CONTROL_TRAIT_TOKENS = new Set([
  'OnOff',
  'OpenClose',
  'StartStop',
  'Brightness',
  'ColorSetting',
  'ColorTemperature',
  'Mode',
  'FanSpeed',
  'TemperatureSetting',
  'ThermostatTemperatureSetpoint',
  'HumiditySetting',
].map((value) => normalizeMetricLookupKey(value)))

function collectInferredControlTraitTokensFromCapability(
  device: Device,
  metricAliasMap?: Map<string, string>
): Set<string> {
  const inferred = new Set<string>()
  const capabilityTokens = collectDeviceCapabilityTokens(device, metricAliasMap)
  const hasAny = (...tokens: string[]) => (
    tokens.some((token) => capabilityTokens.has(normalizeMetricLookupKey(token)))
  )

  if (hasAny('on', 'onoff', 'powerstate')) {
    inferred.add(normalizeMetricLookupKey('OnOff'))
  }
  if (hasAny('openclose', 'openpercent', 'openclosepercent', 'position')) {
    inferred.add(normalizeMetricLookupKey('OpenClose'))
  }
  if (hasAny('startstop', 'running', 'isrunning', 'runstate')) {
    inferred.add(normalizeMetricLookupKey('StartStop'))
  }
  if (hasAny('brightness', 'brightnessabsolute', 'level')) {
    inferred.add(normalizeMetricLookupKey('Brightness'))
  }
  if (hasAny('colortemperature', 'setcolortemperature', 'colortemp', 'kelvin')) {
    inferred.add(normalizeMetricLookupKey('ColorTemperature'))
  }
  if (hasAny('mode', 'thermostatmode', 'setthermostatmode', 'setmode', 'setmodes')) {
    inferred.add(normalizeMetricLookupKey('Mode'))
  }
  if (hasAny('fanspeed', 'setfanspeed', 'currentspeedsettings', 'currentfanspeedsetting', 'speed')) {
    inferred.add(normalizeMetricLookupKey('FanSpeed'))
  }
  if (hasAny('thermostattemperaturesetpoint', 'setpoint', 'targettemperature', 'settemperature', 'temperaturesetting')) {
    inferred.add(normalizeMetricLookupKey('TemperatureSetting'))
    inferred.add(normalizeMetricLookupKey('ThermostatTemperatureSetpoint'))
  }

  return inferred
}

function isDeviceActionControllable(
  device: Device,
  metricAliasMap?: Map<string, string>
): boolean {
  const traitTokens = new Set<string>([
    ...collectDeviceTraitTokens(device),
    ...collectInferredControlTraitTokensFromCapability(device, metricAliasMap),
  ])
  for (const token of ACTION_REQUIRED_CONTROL_TRAIT_TOKENS) {
    if (traitTokens.has(token)) return true
  }
  return false
}

function deriveAutomationInitialSettingsFromState(
  deviceState?: Record<string, unknown>
): DeviceActionConfig {
  const state = (deviceState && typeof deviceState === 'object')
    ? (deviceState as Record<string, unknown>)
    : {}

  const readValue = (keys: string[]): unknown => {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(state, key)) {
        return state[key]
      }
    }
    return undefined
  }

  const onOff = toBooleanValue(readValue(['OnOff', 'on', 'powerState', 'isOn']))
  const openPercent = toNumberValue(readValue(['openPercent', 'openClosePercent', 'position']))
  const brightness = toNumberValue(readValue(['brightness', 'Brightness']))
  const colorTemp = toNumberValue(
    readValue(['colorTemp', 'color_temperature', 'colorTemperature'])
      ?? ((state.color && typeof state.color === 'object')
        ? (
            (state.color as Record<string, unknown>).temperatureK
            ?? (state.color as Record<string, unknown>).temperature
          )
        : undefined)
  )
  const setpoint = toNumberValue(
    readValue([
      'thermostatTemperatureSetpoint',
      'setpoint',
      'targetTemperature',
      'temperatureSetting',
    ])
  )
  const fanSpeed = normalizeFanSpeedPreset(readValue(['fanSpeed', 'currentFanSpeedSetting', 'speed']))
  const modeRaw = readValue(['thermostatMode', 'mode'])
  const mode = String(modeRaw ?? '').trim().toLowerCase()

  if (openPercent !== null && Number.isFinite(openPercent)) {
    return normalizeSceneCommandFromModal('openClose', { openPercent })
  }

  if (setpoint !== null && Number.isFinite(setpoint)) {
    return normalizeSceneCommandFromModal('setTemperature', { temperature: setpoint })
  }

  if (fanSpeed) {
    return normalizeSceneCommandFromModal('setFanSpeed', { fanSpeed })
  }

  if (mode) {
    return normalizeSceneCommandFromModal('setMode', { mode })
  }

  if (brightness !== null && Number.isFinite(brightness)) {
    if (onOff === false) {
      return normalizeSceneCommandFromModal('off')
    }
    return normalizeSceneCommandFromModal('on', { brightness })
  }

  if (colorTemp !== null && Number.isFinite(colorTemp)) {
    return normalizeSceneCommandFromModal('setColorTemperature', { temperature: colorTemp, colorTemp })
  }

  if (onOff !== null) {
    return normalizeSceneCommandFromModal(onOff ? 'on' : 'off')
  }

  // Fallback: keep action configured as ON for devices without clear live state signal.
  return normalizeSceneCommandFromModal('on')
}

function inferDeviceStateTriggerCondition(
  metricRaw: unknown,
  thresholdRaw: unknown
): DeviceStateTriggerCondition | '' {
  const metric = normalizeMetricLookupKey(metricRaw)
  const thresholdText = String(thresholdRaw ?? '').trim().toLowerCase()
  const thresholdNumber = Number(thresholdText)
  const isFalsy = (
    thresholdRaw === false
    || thresholdText === 'false'
    || thresholdText === '0'
    || thresholdText === 'off'
    || thresholdText === 'close'
    || thresholdText === 'stop'
    || (Number.isFinite(thresholdNumber) && thresholdNumber === 0)
  )

  if (metric === 'on' || metric === 'onoff' || metric === 'powerstate') {
    return isFalsy ? 'off' : 'on'
  }
  if (metric === 'openpercent' || metric === 'openclosepercent' || metric === 'position') {
    return isFalsy ? 'close' : 'open'
  }
  if (metric === 'running' || metric === 'isrunning' || metric === 'startstop' || metric === 'runstate') {
    return isFalsy ? 'stop' : 'start'
  }
  return ''
}

function mapDeviceStateConditionToRule(
  condition: DeviceStateTriggerCondition | ''
): { metric: string; threshold: unknown } | null {
  if (condition === 'on') return { metric: 'on', threshold: true }
  if (condition === 'off') return { metric: 'on', threshold: false }
  if (condition === 'open') return { metric: 'openPercent', threshold: 100 }
  if (condition === 'close') return { metric: 'openPercent', threshold: 0 }
  if (condition === 'start') return { metric: 'running', threshold: true }
  if (condition === 'stop') return { metric: 'running', threshold: false }
  return null
}

function metricMatchesCapability(
  option: TriggerMetricOption,
  capabilityTokens: Set<string>,
  traitTokens: Set<string>
): boolean {
  if (capabilityTokens.size === 0) return false
  const normalizedMetric = normalizeMetricLookupKey(option.value)
  const isPowerMetric = (
    normalizedMetric === 'watt'
    || normalizedMetric === 'watthour'
    || normalizedMetric === 'current'
    || normalizedMetric === 'voltage'
  )

  const expectedTraits = (option.traits ?? []).map((value) => normalizeMetricLookupKey(value))
  const expectedValues = [
    option.value,
    ...(option.aliases ?? []),
  ].map((value) => normalizeMetricLookupKey(value))
  const valueMatched = expectedValues.some((token) => capabilityTokens.has(token))

  const hasTraitConstraint = expectedTraits.length > 0
  const traitMatched = hasTraitConstraint && expectedTraits.some((token) => traitTokens.has(token))
  if (!hasTraitConstraint) {
    return valueMatched
  }
  if (traitMatched) {
    return true
  }
  if (valueMatched) {
    // 非電力欄位允許 key fallback，避免 trait 命名差異造成全部被過濾。
    // 電力欄位仍要求 trait 命中（除非設備本身未提供任何 trait）。
    if (traitTokens.size === 0) return true
    if (isPowerMetric) return false
    return true
  }
  return false
}

const DEFAULT_THRESHOLD_PLACEHOLDER = '例如：30、true、on'

const DEFAULT_THRESHOLD_CHOICES_BY_METRIC: Record<string, Array<string | number | boolean>> = {
  temperature: [16, 18, 20, 22, 24, 26, 28, 30],
  humidity: [30, 40, 50, 60, 70, 80],
  lux: [5, 10, 50, 100, 300, 500, 1000],
  watt: [1, 5, 10, 50, 100, 300, 500, 1000],
  watthour: [1, 10, 50, 100, 500, 1000, 5000],
  current: [0.1, 0.5, 1, 2, 5, 10],
  voltage: [100, 110, 120, 220, 230, 240],
  occupied: [true, false],
}

const METRIC_DEFAULT_UNIT_MAP: Record<string, string> = {
  temperature: '°C',
  humidity: '%',
  lux: 'lux',
  watt: 'W',
  watthour: 'Wh',
  current: 'A',
  voltage: 'V',
}

const BOOLEAN_METRIC_KEYS = new Set([
  'occupied',
  'presence',
  'motion',
  'detected',
  'on',
  'online',
  'running',
])

function readNumberByKeys(record: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    if (!(key in record)) continue
    const value = toNumberValue(record[key])
    if (value !== null) return value
  }
  return undefined
}

function toPlainNumberString(value: number): string {
  if (!Number.isFinite(value)) return ''
  if (Number.isInteger(value)) return String(value)
  return value.toFixed(4).replace(/\.?0+$/, '')
}

function readStringByKeys(record: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const raw = record[key]
    if (typeof raw !== 'string') continue
    const text = raw.trim()
    if (text) return text
  }
  return undefined
}

function collectStringOptionsFromUnknown(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const values = raw
    .map((item) => String(item ?? '').trim())
    .filter(Boolean)
  return Array.from(new Set(values))
}

function collectBooleanOptionsFromUnknown(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const values = raw
    .map((item) => {
      if (typeof item === 'boolean') return item ? 'true' : 'false'
      const normalized = String(item ?? '').trim().toLowerCase()
      if (!normalized) return ''
      if (['true', '1', 'on', 'open', 'start', 'yes', 'enabled', 'active'].includes(normalized)) return 'true'
      if (['false', '0', 'off', 'close', 'stop', 'no', 'disabled', 'inactive'].includes(normalized)) return 'false'
      return ''
    })
    .filter(Boolean)
  return Array.from(new Set(values))
}

function extractThresholdMetaFromNode(rawNode: unknown): TriggerThresholdMeta {
  const meta: TriggerThresholdMeta = {}

  if (typeof rawNode === 'boolean') {
    meta.isBoolean = true
    meta.sample = rawNode
    meta.options = ['true', 'false']
    return meta
  }

  if (typeof rawNode === 'number' || typeof rawNode === 'string') {
    meta.sample = rawNode
    const boolValue = toBooleanValue(rawNode)
    if (boolValue !== null) {
      meta.isBoolean = true
      meta.options = ['true', 'false']
    }
    return meta
  }

  if (!isRecord(rawNode)) return meta

  const directMin = readNumberByKeys(rawNode, ['min', 'minimum', 'minValue', 'from', 'start'])
  const directMax = readNumberByKeys(rawNode, ['max', 'maximum', 'maxValue', 'to', 'end'])
  const directStep = readNumberByKeys(rawNode, ['step', 'resolution', 'increment', 'precision'])
  const directUnit = readStringByKeys(rawNode, ['unit', 'uom', 'symbol'])
  const directValue = rawNode.value ?? rawNode.current ?? rawNode.default

  if (directMin !== undefined) meta.min = directMin
  if (directMax !== undefined) meta.max = directMax
  if (directStep !== undefined) meta.step = directStep
  if (directUnit) meta.unit = directUnit
  if (directValue !== undefined) meta.sample = directValue

  const range = isRecord(rawNode.range) ? rawNode.range : isRecord(rawNode.limits) ? rawNode.limits : null
  if (range) {
    const rangeMin = readNumberByKeys(range, ['min', 'minimum', 'from', 'start'])
    const rangeMax = readNumberByKeys(range, ['max', 'maximum', 'to', 'end'])
    const rangeStep = readNumberByKeys(range, ['step', 'resolution', 'increment'])
    if (rangeMin !== undefined && meta.min === undefined) meta.min = rangeMin
    if (rangeMax !== undefined && meta.max === undefined) meta.max = rangeMax
    if (rangeStep !== undefined && meta.step === undefined) meta.step = rangeStep
  }

  const enumCandidates = [
    ...collectStringOptionsFromUnknown(rawNode.enum),
    ...collectStringOptionsFromUnknown(rawNode.options),
    ...collectStringOptionsFromUnknown(rawNode.values),
    ...collectStringOptionsFromUnknown(rawNode.allowedValues),
    ...collectStringOptionsFromUnknown(rawNode.supportedValues),
  ]
  const booleanEnumCandidates = [
    ...collectBooleanOptionsFromUnknown(rawNode.enum),
    ...collectBooleanOptionsFromUnknown(rawNode.options),
    ...collectBooleanOptionsFromUnknown(rawNode.values),
    ...collectBooleanOptionsFromUnknown(rawNode.allowedValues),
    ...collectBooleanOptionsFromUnknown(rawNode.supportedValues),
  ]
  const mergedOptions = Array.from(new Set([...enumCandidates, ...booleanEnumCandidates]))
  if (mergedOptions.length > 0) meta.options = mergedOptions
  if (mergedOptions.includes('true') || mergedOptions.includes('false')) meta.isBoolean = true

  const valueType = String(rawNode.valueType ?? rawNode.type ?? '').trim().toLowerCase()
  if (valueType.includes('bool')) meta.isBoolean = true

  if (meta.sample !== undefined) {
    const boolValue = toBooleanValue(meta.sample)
    if (boolValue !== null) meta.isBoolean = true
  }

  return meta
}

function mergeThresholdMeta(base: TriggerThresholdMeta, incoming: TriggerThresholdMeta): TriggerThresholdMeta {
  const next: TriggerThresholdMeta = { ...base }
  if (incoming.min !== undefined) next.min = next.min === undefined ? incoming.min : Math.min(next.min, incoming.min)
  if (incoming.max !== undefined) next.max = next.max === undefined ? incoming.max : Math.max(next.max, incoming.max)
  if (next.step === undefined && incoming.step !== undefined) next.step = incoming.step
  if (!next.unit && incoming.unit) next.unit = incoming.unit
  if (next.sample === undefined && incoming.sample !== undefined) next.sample = incoming.sample
  next.isBoolean = Boolean(next.isBoolean || incoming.isBoolean)
  const mergedOptions = Array.from(new Set([...(next.options ?? []), ...(incoming.options ?? [])]))
  if (mergedOptions.length > 0) next.options = mergedOptions
  return next
}

function collectMetricNodesFromUnknown(
  raw: unknown,
  candidateTokens: Set<string>,
  out: unknown[],
  metricAliasMap?: Map<string, string>,
  depth = 0
): void {
  if (depth > 6 || raw == null) return
  if (Array.isArray(raw)) {
    for (const item of raw) {
      collectMetricNodesFromUnknown(item, candidateTokens, out, metricAliasMap, depth + 1)
    }
    return
  }
  if (!isRecord(raw)) return
  for (const [key, value] of Object.entries(raw)) {
    const keyToken = normalizeMetricLookupKey(key)
    const mergedToken = resolveMergedMetricToken(key, metricAliasMap)
    if (candidateTokens.has(keyToken) || candidateTokens.has(mergedToken)) {
      out.push(value)
    }
    if (value != null && typeof value === 'object') {
      collectMetricNodesFromUnknown(value, candidateTokens, out, metricAliasMap, depth + 1)
    }
  }
}

function collectMetricCandidateTokens(
  metricOption: TriggerMetricOption,
  metricAliasMap?: Map<string, string>
): Set<string> {
  const out = new Set<string>()
  const candidates = [metricOption.value, ...(metricOption.aliases ?? [])]
  for (const candidate of candidates) {
    const rawToken = normalizeMetricLookupKey(candidate)
    const mergedToken = resolveMergedMetricToken(candidate, metricAliasMap)
    if (rawToken) out.add(rawToken)
    if (mergedToken) out.add(mergedToken)
  }
  return out
}

function buildChoicesFromOptions(
  values: string[],
  unit?: string
): TriggerThresholdChoice[] {
  return values.map((value) => {
    const num = toNumberValue(value)
    const unitText = unit && num !== null ? ` ${unit}` : ''
    return { value, label: `${value}${unitText}` }
  })
}

function buildNumericChoices(meta: TriggerThresholdMeta): TriggerThresholdChoice[] {
  const points = new Set<number>()
  const min = meta.min
  const max = meta.max
  const step = meta.step
  if (min !== undefined && max !== undefined) {
    if (step !== undefined && step > 0) {
      const estimatedSteps = Math.floor((max - min) / step)
      if (estimatedSteps >= 1 && estimatedSteps <= 14) {
        for (let value = min; value <= max + step / 1000; value += step) {
          points.add(Number(toPlainNumberString(value)))
        }
      }
    }
    if (points.size === 0) {
      const segments = 6
      for (let i = 0; i <= segments; i += 1) {
        const ratio = i / segments
        points.add(Number(toPlainNumberString(min + (max - min) * ratio)))
      }
    }
  }
  if (meta.sample !== undefined) {
    const sampleNumber = toNumberValue(meta.sample)
    if (sampleNumber !== null) points.add(Number(toPlainNumberString(sampleNumber)))
  }
  if (min !== undefined) points.add(Number(toPlainNumberString(min)))
  if (max !== undefined) points.add(Number(toPlainNumberString(max)))

  const sorted = Array.from(points).filter(Number.isFinite).sort((a, b) => a - b)
  return sorted.map((value) => ({
    value: toPlainNumberString(value),
    label: `${toPlainNumberString(value)}${meta.unit ? ` ${meta.unit}` : ''}`,
  }))
}

function buildFallbackChoices(metricKey: string): TriggerThresholdChoice[] {
  const raw = DEFAULT_THRESHOLD_CHOICES_BY_METRIC[metricKey] ?? [true, false]
  return raw.map((item) => {
    const value = typeof item === 'boolean' ? (item ? 'true' : 'false') : String(item)
    return { value, label: value }
  })
}

function deriveTriggerThresholdUiModel(options: {
  device?: Device
  metricOption?: TriggerMetricOption
  metricAliasMap?: Map<string, string>
}): { placeholder: string; helper: string; choices: TriggerThresholdChoice[] } {
  const metricOption = options.metricOption
  const device = options.device
  if (!metricOption || !device) {
    return {
      placeholder: DEFAULT_THRESHOLD_PLACEHOLDER,
      helper: '請先選擇觸發設備與欄位',
      choices: [],
    }
  }

  const metricKey = normalizeMetricLookupKey(metricOption.value)
  const candidateTokens = collectMetricCandidateTokens(metricOption, options.metricAliasMap)
  const nodes: unknown[] = []

  collectMetricNodesFromUnknown(device.attributes, candidateTokens, nodes, options.metricAliasMap)
  collectMetricNodesFromUnknown(device.capabilities, candidateTokens, nodes, options.metricAliasMap)

  let meta: TriggerThresholdMeta = {}
  for (const node of nodes) {
    meta = mergeThresholdMeta(meta, extractThresholdMetaFromNode(node))
  }

  if (!meta.unit && METRIC_DEFAULT_UNIT_MAP[metricKey]) {
    meta.unit = METRIC_DEFAULT_UNIT_MAP[metricKey]
  }
  if (!meta.isBoolean && BOOLEAN_METRIC_KEYS.has(metricKey)) {
    meta.isBoolean = true
  }
  if ((meta.options?.length ?? 0) === 0 && meta.isBoolean) {
    meta.options = ['true', 'false']
  }

  let choices: TriggerThresholdChoice[] = []

  if (meta.options && meta.options.length > 0) {
    const preview = meta.options.slice(0, 4)
    return {
      placeholder: `例如：${preview.join('、')}`,
      helper: `可用值：${meta.options.join(' / ')}（來源：QueryDevices attributes）`,
      choices: buildChoicesFromOptions(meta.options, meta.unit),
    }
  }

  if (meta.min !== undefined && meta.max !== undefined) {
    const unitText = meta.unit ? ` ${meta.unit}` : ''
    const mid = (meta.min + meta.max) / 2
    const stepText = meta.step !== undefined ? `，步進 ${formatNumberMaxTwoDecimals(meta.step)}` : ''
    choices = buildNumericChoices(meta)
    return {
      placeholder: `例如：${formatNumberMaxTwoDecimals(mid)}`,
      helper: `建議範圍：${formatNumberMaxTwoDecimals(meta.min)} ~ ${formatNumberMaxTwoDecimals(meta.max)}${unitText}${stepText}（來源：QueryDevices attributes）`,
      choices,
    }
  }

  if (meta.min !== undefined || meta.max !== undefined) {
    const unitText = meta.unit ? ` ${meta.unit}` : ''
    const minText = meta.min !== undefined ? `>= ${formatNumberMaxTwoDecimals(meta.min)}` : ''
    const maxText = meta.max !== undefined ? `<= ${formatNumberMaxTwoDecimals(meta.max)}` : ''
    const joiner = minText && maxText ? ' 且 ' : ''
    choices = buildNumericChoices(meta)
    return {
      placeholder: `例如：${formatNumberMaxTwoDecimals(meta.min ?? meta.max ?? 0)}`,
      helper: `建議值：${minText}${joiner}${maxText}${unitText}（來源：QueryDevices attributes）`,
      choices,
    }
  }

  if (meta.sample !== undefined) {
    const sampleText = typeof meta.sample === 'string' ? meta.sample : String(meta.sample)
    const unitText = meta.unit && toNumberValue(meta.sample) !== null ? ` ${meta.unit}` : ''
    const sampleNumeric = toNumberValue(meta.sample)
    choices = sampleNumeric !== null
      ? [{ value: toPlainNumberString(sampleNumeric), label: `${toPlainNumberString(sampleNumeric)}${unitText}` }]
      : [{ value: sampleText, label: `${sampleText}${unitText}` }]
    return {
      placeholder: `例如：${sampleText}`,
      helper: `目前值：${sampleText}${unitText}（來源：QueryDevices attributes）`,
      choices,
    }
  }

  return {
    placeholder: DEFAULT_THRESHOLD_PLACEHOLDER,
    helper: '已套用預設候選值，請直接選擇',
    choices: buildFallbackChoices(metricKey),
  }
}

const AUTOMATION_SWIPE_ACTION_WIDTH = 92
const AUTOMATION_SWIPE_ACTION_GAP = 8
const AUTOMATION_SWIPE_REVEAL_WIDTH = AUTOMATION_SWIPE_ACTION_WIDTH + AUTOMATION_SWIPE_ACTION_GAP
const AUTOMATION_SWIPE_OPEN_THRESHOLD = 46

function SwipeableAutomationCard({
  automation,
  title,
  subtitle,
  opened,
  onOpenCard,
  onToggleEnabled,
  onDelete,
  onSwipeOpen,
  onSwipeClose,
}: {
  automation: Automation
  title: string
  subtitle: string
  opened: boolean
  onOpenCard: (automation: Automation) => void
  onToggleEnabled: (automation: Automation) => void
  onDelete: (automation: Automation) => void
  onSwipeOpen: (automationId: string) => void
  onSwipeClose: () => void
}) {
  const [offsetX, setOffsetX] = useState(0)
  const [dragging, setDragging] = useState(false)
  const dragStartXRef = useRef(0)
  const dragStartYRef = useRef(0)
  const isDraggingRef = useRef(false)
  const movedRef = useRef(false)
  const blockClickUntilRef = useRef(0)

  // Sync opened state down to internal offset
  useEffect(() => {
    // Only update if not dragging to avoid interfering with user gesture
    if (dragging) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOffsetX(opened ? -AUTOMATION_SWIPE_REVEAL_WIDTH : 0)
  }, [opened, dragging])

  const stopDragging = useCallback(() => {
    isDraggingRef.current = false
    setDragging(false)
  }, [])

  const handlePointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null
    if (target?.closest('button')) return
    if (event.pointerType === 'mouse' && event.button !== 0) return
    isDraggingRef.current = true
    movedRef.current = false
    dragStartXRef.current = event.clientX
    dragStartYRef.current = event.clientY
    setDragging(true)
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }, [])

  const handlePointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return
    const dx = event.clientX - dragStartXRef.current
    const dy = event.clientY - dragStartYRef.current

    // Let vertical scrolling pass through.
    if (!movedRef.current && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) {
      stopDragging()
      setOffsetX(opened ? -AUTOMATION_SWIPE_REVEAL_WIDTH : 0)
      return
    }

    if (Math.abs(dx) > 6) movedRef.current = true

    const base = opened ? -AUTOMATION_SWIPE_REVEAL_WIDTH : 0
    const next = Math.max(-AUTOMATION_SWIPE_REVEAL_WIDTH - 12, Math.min(0, base + dx))
    setOffsetX(next)
  }, [opened, stopDragging])

  const handlePointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return
    const moved = movedRef.current
    const finalOffset = offsetX
    stopDragging()
    event.currentTarget.releasePointerCapture?.(event.pointerId)

    if (moved) {
      blockClickUntilRef.current = Date.now() + 220
    }

    const shouldOpen = finalOffset <= -AUTOMATION_SWIPE_OPEN_THRESHOLD
    if (shouldOpen) {
      onSwipeOpen(automation.id)
      setOffsetX(-AUTOMATION_SWIPE_REVEAL_WIDTH)
    } else {
      onSwipeClose()
      setOffsetX(0)
    }
  }, [automation.id, offsetX, onSwipeClose, onSwipeOpen, stopDragging])

  return (
    <div className={`smart-home-auto-swipe-item ${opened ? 'is-open' : ''} ${dragging ? 'is-dragging' : ''}`}>
      <button
        type="button"
        className="smart-home-auto-delete-action"
        onClick={(event) => {
          event.stopPropagation()
          onDelete(automation)
        }}
        aria-label={`刪除自動化 ${automation.name}`}
      >
        <span className="material-symbols-outlined">delete</span>
        <span>刪除</span>
      </button>
      <div
        role="button"
        tabIndex={0}
        className="smart-home-auto-card glass"
        style={{ transform: `translateX(${offsetX}px)` }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClick={() => {
          if (Date.now() < blockClickUntilRef.current) return
          if (opened) {
            onSwipeClose()
            return
          }
          onOpenCard(automation)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            if (opened) {
              onSwipeClose()
              return
            }
            onOpenCard(automation)
          }
        }}
      >
        <div className="smart-home-auto-card-left">
          <div className="smart-home-auto-card-name">{title}</div>
          <div className="smart-home-auto-card-summary">{subtitle}</div>
        </div>
        <div className="smart-home-auto-card-right">
          <button
            type="button"
            className="smart-home-auto-toggle"
            onClick={(event) => {
              event.stopPropagation()
              onToggleEnabled(automation)
            }}
            aria-label={automation.enabled ? '停用' : '啟用'}
          >
            <span className={`material-symbols-outlined ${automation.enabled ? 'auto-toggle-on' : ''}`}>
              {automation.enabled ? 'toggle_on' : 'toggle_off'}
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

function AutoRoutinesContent({
  auth,
  selectedLocationId,
  devices,
  scenes,
  hapticTap,
  resolveModalConfigForDevice,
  resolveDeviceStateBySN,
  onEmptyScrollLockChange,
}: {
  auth: AuthCreds | null
  selectedLocationId: string | null
  devices: Device[]
  scenes: QuickSceneItem[]
  hapticTap: () => void
  resolveModalConfigForDevice: (device: Device) => ModalConfig
  resolveDeviceStateBySN: (sn: string) => Record<string, unknown>
  onEmptyScrollLockChange?: (locked: boolean) => void
}) {
  const [automations, setAutomations] = useState<Automation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editModal, setEditModal] = useState<Automation | null>(null)
  const [createModal, setCreateModal] = useState(false)
  const [openedSwipeId, setOpenedSwipeId] = useState<string | null>(null)

  const fetchAutomations = useCallback(async () => {
    if (!auth) return
    setLoading(true)
    setError(null)
    try {
      const list = await queryAutomationsWithAuth(auth, selectedLocationId ?? undefined)
      setAutomations(list)
      setOpenedSwipeId(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : '載入失敗')
    } finally {
      setLoading(false)
    }
  }, [auth, selectedLocationId])

  useEffect(() => {
    fetchAutomations()
  }, [fetchAutomations])

  const hasAutoList = Boolean(auth) && !loading && !error && automations.length > 0
  const shouldLockParentScroll = !hasAutoList
  const automationDeviceLabelBySn = useMemo(() => {
    const map = new Map<string, string>()
    for (const device of devices) {
      const sn = String(device.deviceSN ?? device.sn ?? '').trim()
      if (!sn) continue
      const label = String(device.nickname ?? device.displayName ?? device.name ?? sn).trim() || sn
      if (!map.has(sn)) map.set(sn, label)
      const baseSN = sn.split('#')[0]
      if (baseSN && !map.has(baseSN)) map.set(baseSN, label)
    }
    return map
  }, [devices])
  const getAutomationCardCopy = useCallback((automation: Automation): AutomationCardCopy => (
    buildAutomationCardCopy(automation, automationDeviceLabelBySn)
  ), [automationDeviceLabelBySn])

  useEffect(() => {
    onEmptyScrollLockChange?.(shouldLockParentScroll)
    return () => {
      onEmptyScrollLockChange?.(false)
    }
  }, [onEmptyScrollLockChange, shouldLockParentScroll])

  const handleToggleEnabled = async (a: Automation) => {
    if (!auth) return
    hapticTap()
    try {
      await configureAutomationWithAuth(auth, {
        action: 'update',
        id: a.id,
        name: a.name,
        enabled: !a.enabled,
        conditions: a.conditions ?? { type: 'simple', device: '', metric: 'online', operator: 'eq', threshold: true },
        actions: a.actions ?? [{ type: 'control', devices: [], command: 'on' }],
      })
      setAutomations((prev) => prev.map((x) => (x.id === a.id ? { ...x, enabled: !x.enabled } : x)))
    } catch (e) {
      setError(e instanceof Error ? e.message : '更新失敗')
    }
  }

  const handleDeleteAutomation = async (a: Automation) => {
    if (!auth || !a.id) return
    hapticTap()
    setError(null)
    try {
      await configureAutomationWithAuth(auth, {
        action: 'delete',
        id: a.id,
        name: a.name,
        conditions: a.conditions ?? {
          type: 'simple',
          device: 'placeholder',
          metric: 'online',
          operator: 'eq',
          threshold: true,
        },
        actions: a.actions ?? [{ type: 'control', devices: [], command: 'on' }],
      })
      setAutomations((prev) => prev.filter((item) => item.id !== a.id))
      setOpenedSwipeId((current) => (current === a.id ? null : current))
    } catch (e) {
      setError(e instanceof Error ? e.message : '刪除失敗')
    }
  }

  if (!auth) {
    return (
      <section className="smart-home-section smart-home-auto-section">
        <div className="smart-home-section-header">
          <h2 className="smart-home-section-title">Automations</h2>
        </div>
        <div className="smart-home-auto-content">
          <p className="smart-home-empty">請先登入以管理自動化規則</p>
        </div>
      </section>
    )
  }

  return (
    <section className="smart-home-section smart-home-auto-section">
      <div className="smart-home-section-header">
        <h2 className="smart-home-section-title">Automations</h2>
        <button
          type="button"
          className="smart-home-add-scene glass"
          onClick={() => {
            hapticTap()
            setCreateModal(true)
          }}
          aria-label="新增自動化"
        >
          <span className="material-symbols-outlined smart-home-icon">add</span>
        </button>
      </div>
      <div className="smart-home-auto-content">
        {loading ? (
          <p className="smart-home-loading">載入中...</p>
        ) : error ? (
          <p className="smart-home-error">{error}</p>
        ) : automations.length === 0 ? (
          <p className="smart-home-empty">尚無自動化規則</p>
        ) : (
          <div className="smart-home-auto-list">
            {automations.map((a) => {
              const cardCopy = getAutomationCardCopy(a)
              return (
              <SwipeableAutomationCard
                key={a.id}
                automation={a}
                title={cardCopy.title}
                subtitle={cardCopy.subtitle}
                opened={openedSwipeId === a.id}
                onSwipeOpen={(automationId) => setOpenedSwipeId(automationId)}
                onSwipeClose={() => setOpenedSwipeId(null)}
                onOpenCard={(automation) => {
                  hapticTap()
                  setEditModal(automation)
                }}
                onToggleEnabled={(automation) => {
                  setOpenedSwipeId(null)
                  handleToggleEnabled(automation)
                }}
                onDelete={(automation) => {
                  handleDeleteAutomation(automation)
                }}
              />
              )
            })}
          </div>
        )}
      </div>
      {editModal && (
        <AutomationModal
          auth={auth}
          automation={editModal}
          devices={devices}
          availableScenes={scenes}
          selectedLocationId={selectedLocationId}
          onClose={() => setEditModal(null)}
          onSaved={() => {
            setEditModal(null)
            fetchAutomations()
          }}
          hapticTap={hapticTap}
          resolveModalConfigForDevice={resolveModalConfigForDevice}
          resolveDeviceStateBySN={resolveDeviceStateBySN}
        />
      )}
      {createModal && (
        <AutomationModal
          auth={auth}
          devices={devices}
          availableScenes={scenes}
          selectedLocationId={selectedLocationId}
          onClose={() => setCreateModal(false)}
          onSaved={() => {
            setCreateModal(false)
            fetchAutomations()
          }}
          hapticTap={hapticTap}
          resolveModalConfigForDevice={resolveModalConfigForDevice}
          resolveDeviceStateBySN={resolveDeviceStateBySN}
        />
      )}
    </section>
  )
}

type ModalConfig = ModalRenderConfig
type DeviceActionConfig = { command: string; params?: Record<string, unknown> }
type QuickSceneItem = {
  id: string
  sceneId: string
  groupId?: string
  label: string
  icon: string
  active?: boolean
  devices?: SceneDeviceCommand[]
}
type LightControlGroup = {
  id: string
  name: string
  icon: string
  deviceSns: string[]
  createdAt: number
  updatedAt: number
}

function resolveDeviceSn(device: { sn?: string; deviceSN?: string }): string {
  return String(device.deviceSN ?? device.sn ?? '').trim()
}

function resolveStatusSn(status?: DeviceStatus | null): string {
  if (!status || typeof status !== 'object') return ''
  return String(
    (status as { sn?: string }).sn
    ?? (status as { deviceSN?: string }).deviceSN
    ?? (status as { deviceSn?: string }).deviceSn
    ?? ''
  ).trim()
}

function pickRealtimeStateFromStatuses(statuses: DeviceStatus[], targetSn: string): Record<string, unknown> | null {
  const normalizedTargetSn = String(targetSn || '').trim()
  if (!normalizedTargetSn || !Array.isArray(statuses) || statuses.length === 0) return null
  const targetBaseSn = normalizedTargetSn.split('#')[0]
  const matchedStatus = statuses.find((status) => resolveStatusSn(status) === normalizedTargetSn)
    ?? statuses.find((status) => resolveStatusSn(status) === targetBaseSn)
    ?? null
  if (!matchedStatus) return null
  const state = (matchedStatus.attributes ?? matchedStatus.iotState ?? {}) as Record<string, unknown>
  if (!state || typeof state !== 'object') return null
  return state
}

function isLightCandidateDevice(device: {
  sn?: string
  deviceSN?: string
  name?: string
  displayName?: string
  nickname?: string
  deviceType?: string
  type?: string
  traits?: string[]
}): boolean {
  const rawType = String(device.deviceType ?? device.type ?? '').trim().toLowerCase()
  if (!rawType) return false
  // 僅以 device type 判斷，避免 Outlet/Switch 因名稱或 traits 被誤判成 Light。
  return rawType === 'light' || rawType.includes('light')
}

function sanitizeLightGroupsForDevices(groups: LightControlGroup[], devices: Device[]): LightControlGroup[] {
  if (groups.length === 0) return []
  const lightSnSet = new Set(
    devices
      .filter((device) => isLightCandidateDevice(device))
      .map((device) => resolveDeviceSn(device))
      .filter(Boolean)
  )
  const occupied = new Set<string>()
  const normalized: LightControlGroup[] = []
  for (const group of groups) {
    const groupId = String(group.id || '').trim()
    if (!groupId) continue
    const orderedUniqueMembers: string[] = []
    for (const rawSn of group.deviceSns ?? []) {
      const sn = String(rawSn || '').trim()
      if (!sn) continue
      if (!lightSnSet.has(sn)) continue
      if (occupied.has(sn)) continue
      occupied.add(sn)
      orderedUniqueMembers.push(sn)
    }
    if (orderedUniqueMembers.length === 0) continue
    normalized.push({
      id: groupId,
      name: String(group.name || '').trim() || '燈光群組',
      icon: String(group.icon || 'lightbulb').trim() || 'lightbulb',
      deviceSns: orderedUniqueMembers,
      createdAt: Number(group.createdAt) || Date.now(),
      updatedAt: Number(group.updatedAt) || Date.now(),
    })
  }
  return normalized
}

function applySceneOrder(items: QuickSceneItem[], orderedIds: string[] | null | undefined): QuickSceneItem[] {
  if (!orderedIds || orderedIds.length === 0) return items
  const rank = new Map<string, number>()
  orderedIds.forEach((id, idx) => {
    if (!rank.has(id)) rank.set(id, idx)
  })
  return [...items].sort((a, b) => {
    const aRank = rank.has(a.id) ? rank.get(a.id)! : Number.MAX_SAFE_INTEGER
    const bRank = rank.has(b.id) ? rank.get(b.id)! : Number.MAX_SAFE_INTEGER
    return aRank - bRank
  })
}

function applyDeviceOrder(items: Device[], orderedDeviceSns: string[] | null | undefined): Device[] {
  if (!orderedDeviceSns || orderedDeviceSns.length === 0) return items
  const rank = new Map<string, number>()
  orderedDeviceSns.forEach((sn, index) => {
    const normalized = String(sn || '').trim()
    if (normalized && !rank.has(normalized)) rank.set(normalized, index)
  })
  return [...items].sort((a, b) => {
    const aSn = resolveDeviceSn(a)
    const bSn = resolveDeviceSn(b)
    const aRank = rank.has(aSn) ? rank.get(aSn)! : Number.MAX_SAFE_INTEGER
    const bRank = rank.has(bSn) ? rank.get(bSn)! : Number.MAX_SAFE_INTEGER
    return aRank - bRank
  })
}

function applyLightGroupOrder(items: LightControlGroup[], orderedIds: string[] | null | undefined): LightControlGroup[] {
  if (!orderedIds || orderedIds.length === 0) return items
  const rank = new Map<string, number>()
  orderedIds.forEach((id, index) => {
    const normalized = String(id || '').trim()
    if (normalized && !rank.has(normalized)) rank.set(normalized, index)
  })
  return [...items].sort((a, b) => {
    const aRank = rank.has(a.id) ? rank.get(a.id)! : Number.MAX_SAFE_INTEGER
    const bRank = rank.has(b.id) ? rank.get(b.id)! : Number.MAX_SAFE_INTEGER
    return aRank - bRank
  })
}

function normalizeFanSpeedPreset(value: unknown): string | null {
  const raw = String(value ?? '').trim().toLowerCase()
  if (!raw) return null
  if (raw === '0' || raw === 'auto' || raw === 'automatic') return 'auto'
  if (raw === '1' || raw === 'low') return 'low'
  if (raw === '2' || raw === 'medium' || raw === 'mid') return 'medium'
  if (raw === '3' || raw === 'high') return 'high'
  return raw
}

function normalizeSceneCommandFromModal(command: string, params?: Record<string, unknown>): DeviceActionConfig {
  const raw = String(command || '').trim()
  const lower = raw.toLowerCase()
  let nextCommand = raw
  let nextParams: Record<string, unknown> | undefined = params

  if (lower === 'onoff') {
    const onLike = params?.on
    const isOff = onLike === false || onLike === 0 || String(onLike).toLowerCase() === 'false'
    nextCommand = isOff ? 'off' : 'on'
    nextParams = undefined
  } else if (lower === 'setthermostatmode' || lower === 'thermostatsetmode') {
    nextCommand = 'setMode'
    nextParams = { mode: String(params?.mode ?? 'auto') }
  } else if (lower === 'setfanspeed') {
    nextCommand = 'setFanSpeed'
    nextParams = { fanSpeed: normalizeFanSpeedPreset(params?.fanSpeed ?? params?.speed ?? 'medium') ?? 'medium' }
  } else if (lower === 'setcustommode') {
    const modeName = String(params?.modeName ?? params?.mode ?? params?.key ?? '').trim().toLowerCase()
    const modeValue = params?.modeValue ?? params?.value ?? params?.setting ?? params?.mode
    const normalizedModeValue = String(modeValue ?? '').trim().toLowerCase()
    const normalizedFanSpeed = normalizeFanSpeedPreset(modeValue)
    if (modeName.includes('fan') || normalizedFanSpeed !== null) {
      nextCommand = 'setFanSpeed'
      nextParams = { fanSpeed: normalizedFanSpeed ?? 'medium' }
    } else if (normalizedModeValue) {
      nextCommand = 'setMode'
      nextParams = { mode: normalizedModeValue }
    } else {
      nextCommand = 'on'
      nextParams = undefined
    }
  } else if (lower === 'setmodes') {
    const updateModeSettings = (
      params?.updateModeSettings
      && typeof params.updateModeSettings === 'object'
      && !Array.isArray(params.updateModeSettings)
    ) ? (params.updateModeSettings as Record<string, unknown>) : {}
    const firstModeEntry = Object.entries(updateModeSettings)[0]
    const modeKey = String(firstModeEntry?.[0] ?? '').trim().toLowerCase()
    const modeValue = firstModeEntry?.[1]
    const normalizedFanSpeed = normalizeFanSpeedPreset(modeValue)
    if (modeKey.includes('fan') || normalizedFanSpeed !== null) {
      nextCommand = 'setFanSpeed'
      nextParams = { fanSpeed: normalizedFanSpeed ?? 'medium' }
    } else {
      const normalizedModeValue = String(modeValue ?? '').trim().toLowerCase()
      if (normalizedModeValue) {
        nextCommand = 'setMode'
        nextParams = { mode: normalizedModeValue }
      } else {
        nextCommand = 'on'
        nextParams = undefined
      }
    }
  } else if (lower === 'setsetpoint' || lower === 'setthermostattemperaturesetpoint') {
    nextCommand = 'setTemperature'
    nextParams = { temperature: Number(params?.setpoint ?? params?.temperature ?? 24) }
  } else if (lower === 'setcolortemperature' || lower === 'setcolortemp') {
    nextCommand = 'setColorTemperature'
    const colorTemp = Number(params?.temperature ?? params?.colorTemp ?? 4000)
    nextParams = { temperature: colorTemp, colorTemp }
  } else if (lower === 'setposition' || lower === 'setopenclosepercent' || lower === 'setopenpercent') {
    nextCommand = 'openClose'
    const openPercent = Number(params?.openPercent ?? params?.openClosePercent ?? params?.position ?? 100)
    nextParams = Number.isFinite(openPercent) ? { openPercent } : {}
  } else if (lower === 'openclose') {
    nextCommand = 'openClose'
    const openPercent = Number(params?.openPercent ?? params?.openClosePercent ?? params?.position)
    nextParams = Number.isFinite(openPercent) ? { openPercent } : {}
  } else if (lower === 'toggle') {
    nextCommand = 'on'
    nextParams = undefined
  } else if (lower === 'on' || lower === 'open' || lower === 'start' || lower === 'resume' || lower === 'run') {
    // Keep params for stateful "on/open" scenes (e.g. brightness, color temp, mode).
    nextCommand = lower
    nextParams = params && Object.keys(params).length > 0 ? params : undefined
  } else if (
    lower === 'off'
    || lower === 'close'
    || lower === 'stop'
    || lower === 'pause'
    || lower === 'lock'
    || lower === 'unlock'
    || lower === 'arm'
    || lower === 'disarm'
    || lower === 'cancel'
  ) {
    nextCommand = lower
    nextParams = undefined
  }

  const normalizedCommand = String(nextCommand || '').trim()
  const normalizedLower = normalizedCommand.toLowerCase()
  const hasMeaningfulParams = Boolean(nextParams && Object.keys(nextParams).length > 0)
  const isInvalidCommandToken = !normalizedCommand || normalizedLower === 'undefined' || normalizedLower === 'null'
  const knownCommands = new Set([
    'on', 'off', 'open', 'close', 'start', 'stop', 'resume', 'run', 'pause',
    'lock', 'unlock', 'arm', 'disarm', 'cancel', 'openclose',
    'setfanspeed', 'setmode', 'setbrightness', 'settemperature', 'setcolortemperature',
  ])
  const hasControlParams = Boolean(
    nextParams
    && (
      nextParams.fanSpeed != null
      || nextParams.speed != null
      || nextParams.mode != null
      || nextParams.brightness != null
      || nextParams.openPercent != null
      || nextParams.openClosePercent != null
      || nextParams.colorTemp != null
      || nextParams.temperature != null
      || nextParams.modeValue != null
      || nextParams.updateModeSettings != null
      || nextParams.thermostatMode != null
      || nextParams.thermostatTemperatureSetpoint != null
    )
  )

  if (isInvalidCommandToken) {
    nextCommand = 'on'
  } else if (!knownCommands.has(normalizedLower) && hasControlParams) {
    // For legacy/unknown command names with actionable params, fallback to "on"
    // so the action remains configured and executable.
    nextCommand = 'on'
  } else if (!knownCommands.has(normalizedLower) && !hasMeaningfulParams) {
    nextCommand = 'on'
    nextParams = undefined
  }

  return {
    command: nextCommand || 'on',
    ...(nextParams && Object.keys(nextParams).length > 0 ? { params: nextParams } : {}),
  }
}

function parseSceneDevicesFromAny(rawDevices: unknown): SceneDeviceCommand[] {
  if (Array.isArray(rawDevices)) {
    return rawDevices
      .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
      .map((entry) => {
        const sn = String(entry.sn || entry.deviceSN || entry.deviceSn || '')
        const commandRaw = String(entry.command || entry.cmd || entry.action || entry.Command || 'on')
        const rawParams =
          entry.params
          ?? entry.parameters
          ?? entry.param
          ?? entry.Paras
          ?? entry.paras
          ?? {}
        const params = (rawParams && typeof rawParams === 'object')
          ? { ...(rawParams as Record<string, unknown>) }
          : {}

        // Some payloads flatten trait values at top-level instead of params object.
        const topLevelBrightness = Number(entry.brightness ?? entry.Brightness ?? entry.level)
        if (!Number.isNaN(topLevelBrightness) && Number.isFinite(topLevelBrightness) && params.brightness == null) {
          params.brightness = topLevelBrightness
        }
        const topLevelColorTemp = Number(
          entry.colorTemp
          ?? entry.color_temperature
          ?? entry.colorTemperature
          ?? entry.ColorTemperature
        )
        if (!Number.isNaN(topLevelColorTemp) && Number.isFinite(topLevelColorTemp)) {
          if (params.colorTemp == null) params.colorTemp = topLevelColorTemp
          if (params.temperature == null) params.temperature = topLevelColorTemp
        }
        const topLevelOpenPercent = Number(
          entry.openPercent
          ?? entry.openClosePercent
          ?? entry.OpenPercent
          ?? entry.OpenClosePercent
          ?? entry.position
        )
        if (!Number.isNaN(topLevelOpenPercent) && Number.isFinite(topLevelOpenPercent) && params.openPercent == null) {
          params.openPercent = topLevelOpenPercent
        }

        const normalized = normalizeSceneCommandFromModal(
          commandRaw,
          Object.keys(params).length > 0 ? params : undefined
        )

        return {
          sn,
          command: normalized.command,
          params: normalized.params as SceneDeviceCommand['params'],
        }
      })
      .filter((entry) => entry.sn.length > 0)
  }

  if (!rawDevices || typeof rawDevices !== 'object') return []
  const materialized = rawDevices as Record<string, unknown>
  const out: SceneDeviceCommand[] = []

  for (const [baseSn, endpointMapRaw] of Object.entries(materialized)) {
    if (!endpointMapRaw || typeof endpointMapRaw !== 'object') continue
    const endpointMap = endpointMapRaw as Record<string, unknown>

    for (const [endpoint, endpointRaw] of Object.entries(endpointMap)) {
      if (!endpointRaw || typeof endpointRaw !== 'object') continue
      const endpointRecord = endpointRaw as Record<string, unknown>
      const iotCmds = Array.isArray(endpointRecord.IotCmds)
        ? endpointRecord.IotCmds
        : Array.isArray(endpointRecord.iotCmds)
          ? endpointRecord.iotCmds
          : Array.isArray(endpointRecord.commands)
            ? endpointRecord.commands
            : []

      let inferredCommand = 'on'
      const inferredParams: Record<string, unknown> = {}

      if (iotCmds.length > 0) {
        for (const cmdRaw of iotCmds) {
          if (!cmdRaw || typeof cmdRaw !== 'object') continue
          const cmdRecord = cmdRaw as Record<string, unknown>
          const cmd = String(
            cmdRecord.command
            ?? cmdRecord.Command
            ?? cmdRecord.iotCmd
            ?? cmdRecord.IotCmd
            ?? ''
          ).toLowerCase()
          const rawParams =
            cmdRecord.params
            ?? cmdRecord.Params
            ?? cmdRecord.paras
            ?? cmdRecord.Paras
            ?? cmdRecord.param
            ?? cmdRecord.Param
            ?? cmdRecord.arguments
            ?? {}
          const params = (rawParams && typeof rawParams === 'object')
            ? (rawParams as Record<string, unknown>)
            : {}

          if (cmd === 'onoff') {
            const onValue = params.on
            if (onValue === false || onValue === 0 || String(onValue).toLowerCase() === 'false') inferredCommand = 'off'
            else if (onValue === true || onValue === 1 || String(onValue).toLowerCase() === 'true') inferredCommand = 'on'
          } else if (cmd === 'brightnessabsolute' || cmd === 'setbrightness') {
            const brightness = Number(params.brightness ?? params.Brightness ?? params.level ?? params.value)
            if (Number.isFinite(brightness)) inferredParams.brightness = brightness
          } else if (cmd === 'colorabsolute' || cmd === 'setcolortemperature' || cmd === 'colortemperature') {
            const color = (params.color && typeof params.color === 'object')
              ? (params.color as Record<string, unknown>)
              : {}
            const colorTemp = Number(
              color.temperature
              ?? params.temperature
              ?? params.colorTemp
              ?? params.color_temperature
              ?? params.colorTemperature
              ?? params.kelvin
            )
            if (Number.isFinite(colorTemp)) {
              inferredParams.colorTemp = colorTemp
              inferredParams.temperature = colorTemp
            }
          } else if (cmd === 'thermostattemperaturesetpoint') {
            const temperature = Number(
              params.thermostatTemperatureSetpoint
              ?? params.temperature
              ?? params.setpoint
              ?? params.targetTemperature
            )
            if (Number.isFinite(temperature)) inferredParams.temperature = temperature
          } else if (cmd === 'thermostatsetmode') {
            if (params.thermostatMode != null || params.mode != null) {
              inferredParams.mode = String(params.thermostatMode ?? params.mode)
            }
          } else if (cmd === 'setfanspeed') {
            if (params.fanSpeed != null || params.speed != null) {
              inferredParams.fanSpeed = normalizeFanSpeedPreset(params.fanSpeed ?? params.speed)
                ?? String(params.fanSpeed ?? params.speed).toLowerCase()
            }
          } else if (cmd === 'setcustommode') {
            const modeName = String(params.modeName ?? params.mode ?? params.key ?? '').trim().toLowerCase()
            const modeValue = params.modeValue ?? params.value ?? params.setting ?? params.mode
            const normalizedFanSpeed = normalizeFanSpeedPreset(modeValue)
            if (modeName.includes('fan') || normalizedFanSpeed !== null) {
              inferredParams.fanSpeed = normalizedFanSpeed ?? 'medium'
            } else if (modeValue != null) {
              inferredParams.mode = String(modeValue).trim().toLowerCase()
            }
          } else if (cmd === 'setmodes') {
            const updateModeSettings = (
              params.updateModeSettings
              && typeof params.updateModeSettings === 'object'
              && !Array.isArray(params.updateModeSettings)
            ) ? (params.updateModeSettings as Record<string, unknown>) : {}
            const firstModeEntry = Object.entries(updateModeSettings)[0]
            const modeKey = String(firstModeEntry?.[0] ?? '').trim().toLowerCase()
            const modeValue = firstModeEntry?.[1]
            const normalizedFanSpeed = normalizeFanSpeedPreset(modeValue)
            if (modeKey.includes('fan') || normalizedFanSpeed !== null) {
              inferredParams.fanSpeed = normalizedFanSpeed ?? 'medium'
            } else if (modeValue != null) {
              inferredParams.mode = String(modeValue).trim().toLowerCase()
            }
          } else if (
            cmd === 'openclose'
            || cmd === 'setposition'
            || cmd === 'setopenclosepercent'
            || cmd === 'setopenpercent'
          ) {
            inferredCommand = 'openClose'
            const openPercent = Number(
              params.openPercent
              ?? params.openClosePercent
              ?? params.OpenPercent
              ?? params.OpenClosePercent
              ?? params.position
              ?? params.percent
              ?? params.value
            )
            if (Number.isFinite(openPercent)) inferredParams.openPercent = openPercent
          } else if (cmd === 'lockunlock') {
            const lockValue = params.lock
            inferredCommand = (lockValue === false || lockValue === 0 || String(lockValue).toLowerCase() === 'false')
              ? 'unlock'
              : 'lock'
          } else if (cmd === 'pause' || cmd === 'resume' || cmd === 'start' || cmd === 'cancel' || cmd === 'run') {
            inferredCommand = cmd
          }
        }
      } else {
        // Backward compatibility: some scene payloads return direct trait-like values without IotCmds
        const onLike = endpointRecord.on ?? endpointRecord.OnOff ?? endpointRecord.power ?? endpointRecord.On
        if (onLike === false || onLike === 0 || String(onLike).toLowerCase() === 'false') inferredCommand = 'off'
        else if (onLike === true || onLike === 1 || String(onLike).toLowerCase() === 'true') inferredCommand = 'on'

        const brightness = Number(endpointRecord.brightness ?? endpointRecord.Brightness ?? endpointRecord.level)
        if (Number.isFinite(brightness)) inferredParams.brightness = brightness

        const colorTemp = Number(
          endpointRecord.colorTemp
          ?? endpointRecord.color_temperature
          ?? endpointRecord.colorTemperature
          ?? endpointRecord.ColorTemperature
          ?? (endpointRecord.color && typeof endpointRecord.color === 'object'
            ? (endpointRecord.color as Record<string, unknown>).temperature
            : undefined)
        )
        if (Number.isFinite(colorTemp)) {
          inferredParams.colorTemp = colorTemp
          inferredParams.temperature = colorTemp
        }

        const temperature = Number(endpointRecord.temperature ?? endpointRecord.thermostatTemperatureSetpoint)
        if (Number.isFinite(temperature)) inferredParams.temperature = temperature

        if (endpointRecord.mode != null || endpointRecord.thermostatMode != null) {
          inferredParams.mode = String(endpointRecord.mode ?? endpointRecord.thermostatMode)
        }
        if (endpointRecord.fanSpeed != null || endpointRecord.speed != null) {
          inferredParams.fanSpeed = String(endpointRecord.fanSpeed ?? endpointRecord.speed).toLowerCase()
        }
        const openClosePayload = (
          endpointRecord.openClose && typeof endpointRecord.openClose === 'object'
        )
          ? (endpointRecord.openClose as Record<string, unknown>)
          : {}
        const openPercent = Number(
          endpointRecord.openPercent
          ?? endpointRecord.openClosePercent
          ?? endpointRecord.OpenPercent
          ?? endpointRecord.OpenClosePercent
          ?? endpointRecord.position
          ?? openClosePayload.openPercent
          ?? openClosePayload.openClosePercent
          ?? openClosePayload.position
        )
        if (Number.isFinite(openPercent)) {
          inferredCommand = 'openClose'
          inferredParams.openPercent = openPercent
        }
        if (endpointRecord.lock != null) {
          inferredCommand = (endpointRecord.lock === false || endpointRecord.lock === 0 || String(endpointRecord.lock).toLowerCase() === 'false')
            ? 'unlock'
            : 'lock'
        }
      }

      const normalized = normalizeSceneCommandFromModal(inferredCommand, inferredParams)
      const endpointSuffix = String(endpoint || '').trim()
      const sn = endpointSuffix ? `${baseSn}#${endpointSuffix}` : baseSn
      if (sn) {
        out.push({
          sn,
          command: normalized.command,
          params: normalized.params as SceneDeviceCommand['params'],
        })
      }
    }
  }

  return out
}

function getSceneIdFromRow(row: Record<string, unknown>): string {
  const nestedScene = (row.scene && typeof row.scene === 'object')
    ? (row.scene as Record<string, unknown>)
    : null
  return String(
    row.id
    || row.sceneId
    || row.SceneId
    || row.SceneID
    || row.scene_id
    || row.sceneID
    || nestedScene?.SceneId
    || nestedScene?.SceneID
    || nestedScene?.sceneId
    || nestedScene?.sceneID
    || nestedScene?.id
    || ''
  ).trim()
}

function getSceneGroupIdFromRow(row: Record<string, unknown>): string {
  const nestedScene = (row.scene && typeof row.scene === 'object')
    ? (row.scene as Record<string, unknown>)
    : null
  return String(
    row.groupId
    || row.groupID
    || row.GroupId
    || row.GroupID
    || row.group_id
    || row.locationId
    || row.LocationId
    || nestedScene?.groupId
    || nestedScene?.groupID
    || nestedScene?.GroupId
    || nestedScene?.GroupID
    || nestedScene?.locationId
    || nestedScene?.LocationId
    || ''
  ).trim()
}

function toSceneNumber(value: unknown): number | undefined {
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

function mergeSceneActionDraft(
  prev: DeviceActionConfig | undefined,
  command: string,
  params?: Record<string, unknown>
): DeviceActionConfig {
  const normalized = normalizeSceneCommandFromModal(command, params)
  const incomingCommandRaw = String(normalized.command || 'on')
  const incomingCommand = incomingCommandRaw.toLowerCase()
  const prevCommandRaw = String(prev?.command || '')
  const prevCommand = prevCommandRaw.toLowerCase()
  const prevParams = { ...(prev?.params ?? {}) }
  const incomingParams = { ...(normalized.params ?? {}) } as Record<string, unknown>
  const hardOffCommands = new Set(['off', 'close', 'stop', 'cancel'])
  const statefulOnCommands = new Set(['on', 'open', 'start', 'resume', 'run', 'lock', 'unlock', 'arm', 'disarm'])
  const paramOnlyCommands = new Set([
    'setbrightness',
    'setcolortemperature',
    'settemperature',
    'setmode',
    'setfanspeed',
  ])

  if (incomingCommand === 'setbrightness') {
    const brightness = toSceneNumber(incomingParams.brightness)
    if (brightness != null) incomingParams.brightness = brightness
  }
  if (incomingCommand === 'setcolortemperature') {
    const colorTemp = toSceneNumber(incomingParams.colorTemp ?? incomingParams.temperature)
    if (colorTemp != null) {
      incomingParams.colorTemp = colorTemp
      incomingParams.temperature = colorTemp
    }
  }
  if (incomingCommand === 'settemperature') {
    const temperature = toSceneNumber(incomingParams.temperature)
    if (temperature != null) incomingParams.temperature = temperature
  }
  if (incomingCommand === 'openclose') {
    const openPercent = toSceneNumber(incomingParams.openPercent)
    if (openPercent != null) incomingParams.openPercent = openPercent
  }

  if (hardOffCommands.has(incomingCommand)) {
    return { command: incomingCommandRaw }
  }

  if (incomingCommand === 'openclose') {
    const merged = { ...prevParams, ...incomingParams }
    return {
      command: 'openClose',
      ...(Object.keys(merged).length > 0 ? { params: merged } : {}),
    }
  }

  if (paramOnlyCommands.has(incomingCommand)) {
    const merged = { ...prevParams, ...incomingParams }
    const baseCommand = statefulOnCommands.has(prevCommand) ? prevCommandRaw : 'on'
    return {
      command: baseCommand,
      ...(Object.keys(merged).length > 0 ? { params: merged } : {}),
    }
  }

  if (statefulOnCommands.has(incomingCommand)) {
    const merged = { ...prevParams, ...incomingParams }
    return {
      command: incomingCommandRaw,
      ...(Object.keys(merged).length > 0 ? { params: merged } : {}),
    }
  }

  return normalized
}

function applySceneActionDraftToDeviceState(
  baseState: Record<string, unknown> | undefined,
  action?: DeviceActionConfig
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(baseState ?? {}) }
  if (!action) return out

  const normalized = normalizeSceneCommandFromModal(
    action.command,
    (action.params ?? {}) as Record<string, unknown>
  )
  const command = String(normalized.command || '').trim().toLowerCase()
  const params = (normalized.params ?? {}) as Record<string, unknown>
  const asNumber = (value: unknown): number | undefined => {
    const n = Number(value)
    return Number.isFinite(n) ? n : undefined
  }
  const setOpenPercent = (value: number) => {
    const clamped = Math.max(0, Math.min(100, Math.round(value)))
    out.openPercent = clamped
  }

  const openPercent = asNumber(params.openPercent ?? params.openClosePercent ?? params.position)
  if (openPercent != null) setOpenPercent(openPercent)

  const brightness = asNumber(params.brightness)
  if (brightness != null) {
    const clamped = Math.max(0, Math.min(100, Math.round(brightness)))
    out.brightness = clamped
    out.Brightness = clamped
  }

  const colorTemp = asNumber(params.colorTemp ?? params.colorTemperature)
  if (colorTemp != null || command === 'setcolortemperature') {
    const next = colorTemp ?? asNumber(params.temperature)
    if (next != null) {
      out.colorTemperature = next
      out.colorTemp = next
      out.color_temperature = next
      out.temperature = next
    }
  }

  const setpoint = asNumber(params.temperature ?? params.setpoint)
  if (setpoint != null && command === 'settemperature') {
    out.temperature = setpoint
    out.setpoint = setpoint
    out.thermostatTemperatureSetpoint = setpoint
  }

  if (params.fanSpeed != null || params.speed != null || command === 'setfanspeed') {
    const speed = String(params.fanSpeed ?? params.speed ?? '').trim().toLowerCase()
    if (speed) {
      out.currentFanSpeedSetting = speed
      out.fanSpeed = speed
      out.fan_speed = speed
    }
  }

  if (params.mode != null || command === 'setmode') {
    const mode = String(params.mode ?? '').trim().toLowerCase()
    if (mode) {
      out.mode = mode
      out.thermostatMode = mode
    }
  }

  if (command === 'on') {
    out.on = true
    out.OnOff = true
  } else if (command === 'off') {
    out.on = false
    out.OnOff = false
  } else if (command === 'start' || command === 'run' || command === 'resume') {
    out.running = true
    out.status = 'running'
  } else if (command === 'stop' || command === 'pause' || command === 'cancel') {
    out.running = false
    out.status = 'stopped'
  } else if (command === 'open') {
    if (openPercent == null) setOpenPercent(100)
  } else if (command === 'close') {
    if (openPercent == null) setOpenPercent(0)
  } else if (command === 'openclose' && openPercent == null) {
    const fallbackFromParams = asNumber(params.openPercent ?? params.openClosePercent ?? params.position)
    if (fallbackFromParams != null) setOpenPercent(fallbackFromParams)
  }

  return out
}

function extractSceneRowsFromPayload(payload: ConfigureSceneResult | null): Array<Record<string, unknown>> {
  const rows: Array<Record<string, unknown>> = []
  const visit = (node: unknown, groupHint?: string, depth = 0) => {
    if (depth > 8 || node == null) return
    if (Array.isArray(node)) {
      for (const item of node) visit(item, groupHint, depth + 1)
      return
    }
    if (typeof node !== 'object') return

    const raw = node as Record<string, unknown>
    const sceneNode = raw.scene
    const scenePart = (sceneNode && typeof sceneNode === 'object' && !Array.isArray(sceneNode))
      ? (sceneNode as Record<string, unknown>)
      : {}
    const row: Record<string, unknown> = { ...scenePart, ...raw }
    delete row.scene

    const inferredGroup = String(
      row.groupId
      || row.groupID
      || row.GroupId
      || row.GroupID
      || row.group_id
      || row.locationId
      || groupHint
      || ''
    ).trim()
    if (inferredGroup && !row.groupId && !row.groupID && !row.locationId) {
      row.groupId = inferredGroup
    }

    const hasSceneShape =
      row.id != null
      || row.sceneId != null
      || row.SceneId != null
      || row.SceneID != null
      || row.scene_id != null
      || row.sceneID != null
      || row.devices != null
      || row.Materialized != null
      || row.materialized != null
      || row.name != null
      || row.Name != null
      || row.label != null
    if (hasSceneShape) rows.push(row)

    visit(row.scenes, inferredGroup || groupHint, depth + 1)
    visit(raw.scene, inferredGroup || groupHint, depth + 1)
    visit(row.Scene, inferredGroup || groupHint, depth + 1)
    visit(row.data, inferredGroup || groupHint, depth + 1)
    visit(row.result, inferredGroup || groupHint, depth + 1)

    const groups = Array.isArray(row.groups) ? row.groups : []
    for (const group of groups) {
      const g = (group && typeof group === 'object') ? (group as Record<string, unknown>) : {}
      const gId = String(g.groupId || g.groupID || g.locationId || inferredGroup || groupHint || '').trim()
      if (Array.isArray(g.scenes)) {
        visit(g.scenes, gId, depth + 1)
      } else {
        visit(g, gId, depth + 1)
      }
    }

    const locations = Array.isArray(row.locations) ? row.locations : []
    for (const location of locations) {
      const loc = (location && typeof location === 'object') ? (location as Record<string, unknown>) : {}
      const locId = String(loc.groupId || loc.groupID || loc.locationId || inferredGroup || groupHint || '').trim()
      if (Array.isArray(loc.scenes)) {
        visit(loc.scenes, locId, depth + 1)
      } else {
        visit(loc, locId, depth + 1)
      }
    }
  }

  visit(payload)

  const dedup = new Map<string, Record<string, unknown>>()
  for (const row of rows) {
    const key = `${getSceneIdFromRow(row)}|${getSceneGroupIdFromRow(row)}|${String(row.name || row.Name || row.label || '')}`
    if (!dedup.has(key)) dedup.set(key, row)
  }
  return [...dedup.values()]
}

function extractSceneDevicesFromQueryPayload(
  payload: ConfigureSceneResult,
  targetSceneId: string,
  targetGroupId?: string
): SceneDeviceCommand[] {
  const sceneRows = extractSceneRowsFromPayload(payload)
  const matchBySceneIdAndGroup = sceneRows.find((item) => {
    const id = getSceneIdFromRow(item)
    if (id !== targetSceneId) return false
    if (!targetGroupId) return true
    const group = getSceneGroupIdFromRow(item)
    return !group || group === targetGroupId
  })
  const target = matchBySceneIdAndGroup ?? sceneRows.find((item) => {
    const id = getSceneIdFromRow(item)
    return id === targetSceneId
  })

  return parseSceneDevicesFromAny(target?.devices ?? target?.Materialized ?? target?.materialized)
}

function buildSceneItemsFromPayload(
  payload: ConfigureSceneResult | null,
  fallbackScenes: Array<{ id: string; label: string; icon: string; active?: boolean }>,
  locationId?: string | null,
  useFallbackWhenEmpty = true
): QuickSceneItem[] {
  const sceneRows = extractSceneRowsFromPayload(payload)

  const allFromApi = sceneRows
    .map((row) => {
      const sceneId = getSceneIdFromRow(row)
      const label = String(row.name || row.Name || row.label || sceneId || '').trim() || 'Scene'
      const icon = normalizeSceneIconName(String(row.icon || row.Icon || 'auto_awesome'))
      const devices = parseSceneDevicesFromAny(row.devices ?? row.Materialized ?? row.materialized)
      const groupId = getSceneGroupIdFromRow(row)
      return {
        id: sceneId || `scene-${Math.random().toString(36).slice(2, 8)}`,
        sceneId: sceneId || '',
        groupId: groupId || undefined,
        label,
        icon: icon || 'auto_awesome',
        active: Boolean(row.enabled ?? row.Enabled ?? true),
        devices,
      } as QuickSceneItem
    })
    .filter((scene) => scene.sceneId)

  const fromApi = allFromApi
    .filter((row) => {
      if (!locationId) return true
      const group = row.groupId ?? ''
      return !group || group === locationId
    })

  if (fromApi.length > 0) return fromApi
  if (!useFallbackWhenEmpty) return []

  return fallbackScenes.map((scene) => ({
    id: scene.id,
    sceneId: scene.id,
    groupId: locationId || undefined,
    label: scene.label,
    icon: normalizeSceneIconName(scene.icon || 'auto_awesome'),
    active: scene.active,
    devices: [],
  }))
}

function formatSceneDeviceActionSummary(action?: DeviceActionConfig): string {
  if (!action) return 'Not configured'
  const normalized = normalizeSceneCommandFromModal(
    action.command,
    (action.params ?? {}) as Record<string, unknown>
  )
  const command = String(normalized.command || '').toLowerCase()
  const params = normalized.params || {}
  if (command === 'off' || command === 'close' || command === 'stop') return 'Off'
  if (command === 'lock') return 'Locked'
  if (command === 'unlock') return 'Unlocked'
  if (command === 'arm') return 'Armed'
  if (command === 'disarm') return 'Disarmed'
  const parts: string[] = []
  if (params.mode != null) parts.push(`Mode ${String(params.mode).toUpperCase()}`)
  if (params.fanSpeed != null) parts.push(`Fan ${String(params.fanSpeed)}`)
  if (params.brightness != null) parts.push(`Brightness ${Math.round(Number(params.brightness))}%`)
  if (params.openPercent != null || params.openClosePercent != null) {
    const openPercent = Number(params.openPercent ?? params.openClosePercent)
    if (Number.isFinite(openPercent)) parts.push(`Open ${Math.round(openPercent)}%`)
  }
  if (params.colorTemp != null) parts.push(`Color Temp ${Math.round(Number(params.colorTemp))}K`)
  else if (command === 'setcolortemperature' && params.temperature != null) {
    parts.push(`Color Temp ${Math.round(Number(params.temperature))}K`)
  } else if (command === 'settemperature' && params.temperature != null) {
    parts.push(`${Number(params.temperature).toFixed(1)}°C`)
  }
  if (parts.length > 0) {
    if (command === 'on' || command === 'open' || command === 'run' || command === 'start' || command === 'resume') {
      return `On · ${parts.join(' · ')}`
    }
    return parts.join(' · ')
  }
  return command === 'on' || command === 'open' || command === 'run' || command === 'start' || command === 'resume'
    ? 'On'
    : command === 'pause'
      ? 'Paused'
      : 'Not configured'
}

function DeviceModal({
  device,
  auth,
  readOnly = false,
  selectedLocationId: _selectedLocationId,
  _selectedLocationId: _legacySelectedLocationId,
  initialSettings,
  mode,
  onSave,
  onClose,
  hapticTap,
  modalConfig,
  deviceState = {},
  wrapMode = 'overlay',
  onDeviceNicknameUpdated,
}: {
  device: Device
  auth: AuthCreds | null
  readOnly?: boolean
  selectedLocationId?: string | null
  _selectedLocationId?: string | null
  initialSettings?: { command: string; params?: Record<string, unknown> }
  mode: 'automation' | 'control'
  onSave?: (settings: { command: string; params?: Record<string, unknown> }) => void
  onClose: () => void
  hapticTap: () => void
  modalConfig?: ModalConfig
  deviceState?: Record<string, unknown>
  wrapMode?: 'overlay' | 'preview'
  onDeviceNicknameUpdated?: (deviceSN: string, nickname: string) => void
}) {
  const sn = device.deviceSN ?? device.sn
  const config: ModalConfig = modalConfig ?? {}
  const useConfigDriven =
    (mode === 'control' || mode === 'automation') &&
    (
      (config.enabledTraits?.length ?? 0) > 0 ||
      (config.enabledStates?.length ?? 0) > 0 ||
      (config.enabledCharts?.length ?? 0) > 0 ||
      (config.modalItemOrder?.length ?? 0) > 0
    )
  const configForRender: ModalConfig = mode === 'automation'
    ? {
        ...config,
        enabledStates: config.enabledStates,
        enabledCharts: [],
        modalStateStyles: config.modalStateStyles,
        modalChartStyles: {},
        modalItemOrder: (config.modalItemOrder ?? []).filter((itemKey) => (
          itemKey.startsWith('trait:') || itemKey.startsWith('state:')
        )),
        modalItemVisible: config.modalItemVisible
          ? Object.fromEntries(
              Object.entries(config.modalItemVisible).filter(([itemKey]) => (
                itemKey.startsWith('trait:') || itemKey.startsWith('state:')
              ))
            )
          : undefined,
        modalItemSpacing: config.modalItemSpacing
          ? Object.fromEntries(
              Object.entries(config.modalItemSpacing).filter(([itemKey]) => (
                itemKey.startsWith('trait:') || itemKey.startsWith('state:')
              ))
            )
          : undefined,
        modalFrameStyles: config.modalFrameStyles
          ? Object.fromEntries(
              Object.entries(config.modalFrameStyles).filter(([itemKey]) => (
                itemKey.startsWith('trait:') || itemKey.startsWith('state:')
              ))
            )
          : undefined,
        modalItemCss: config.modalItemCss
          ? Object.fromEntries(
              Object.entries(config.modalItemCss).filter(([itemKey]) => (
                itemKey.startsWith('trait:') || itemKey.startsWith('state:')
              ))
            )
          : undefined,
      }
    : config

  const [command, setCommand] = useState<'on' | 'off'>(initialSettings?.command === 'off' ? 'off' : 'on')
  const [brightness, setBrightness] = useState(
    (initialSettings?.params?.brightness as number) ?? (deviceState?.brightness as number) ?? 100
  )
  const normalizedInitialSettings = useMemo(
    () =>
      normalizeSceneCommandFromModal(
        initialSettings?.command || 'on',
        (initialSettings?.params ?? {}) as Record<string, unknown>
      ),
    [initialSettings]
  )
  const [automationDraftState, setAutomationDraftState] = useState<DeviceActionConfig>(
    normalizedInitialSettings
  )
  const effectiveDeviceState = useMemo(
    () => (mode === 'automation'
      ? applySceneActionDraftToDeviceState(deviceState, automationDraftState)
      : { ...(deviceState ?? {}) }),
    [mode, deviceState, automationDraftState]
  )
  const automationDraftRef = useRef<DeviceActionConfig>(
    normalizedInitialSettings
  )
  const automationDraftTouchedRef = useRef(false)

  const updateAutomationDraft = useCallback((next: DeviceActionConfig, options?: { touched?: boolean }) => {
    if (options?.touched !== false) {
      automationDraftTouchedRef.current = true
    }
    automationDraftRef.current = next
    setAutomationDraftState(next)
  }, [])

  useEffect(() => {
    // Re-sync initial settings only before user starts editing.
    if (automationDraftTouchedRef.current) return
    automationDraftRef.current = normalizedInitialSettings
    setAutomationDraftState(normalizedInitialSettings)
  }, [normalizedInitialSettings])

  useEffect(() => {
    // Reset touched state when switching device/modal session.
    automationDraftTouchedRef.current = false
  }, [sn, mode])

  const handleCommand = async (cmd: string, params?: Record<string, unknown>) => {
    if (mode === 'automation') {
      updateAutomationDraft(mergeSceneActionDraft(automationDraftRef.current, cmd, params), { touched: true })
      return
    }
    if (!auth || readOnly) return
    hapticTap()
    try {
      await executeCommandWithAuth(auth, [sn], cmd, params)
    } catch {
      // ignore
    }
  }

  const deviceType = (device.deviceType ?? device.type ?? '').toLowerCase()
  const deviceName =
    (device.nickname && String(device.nickname).trim()) ||
    device.displayName ||
    device.name ||
    device.sn ||
    sn ||
    formatDeviceTypeForDisplay(device.deviceType ?? device.type ?? '')
  const nicknameEditable = mode === 'control'

  const handleSaveNickname = useCallback(
    async (nextNickname: string) => {
      const next = String(nextNickname || '').trim()
      if (!next) return
      const result = await configureDeviceNicknameWithAuth(auth, sn, next)
      if (!result.success) {
        throw new Error(result.error || 'Failed to save nickname')
      }
      onDeviceNicknameUpdated?.(sn, next)
    },
    [auth, onDeviceNicknameUpdated, sn]
  )

  const applyAutomationDraftAndClose = useCallback(() => {
    if (mode === 'automation') {
      onSave?.(automationDraftRef.current)
    }
    onClose()
  }, [mode, onSave, onClose])

  if (useConfigDriven) {
    const configDrivenContent = (
      <DeviceTypeModalContent
        device={device}
        deviceState={effectiveDeviceState}
        onCommand={handleCommand}
        onClose={mode === 'automation' ? applyAutomationDraftAndClose : onClose}
        deviceType={deviceType}
        styleKey={configForRender.modalStyleId}
        config={configForRender}
        getAttributesForTrait={(traitId) => {
          const base = (device.attributes ?? {}) as Record<string, unknown>
          const mock = getMockAttributesForTrait(traitId)
          return { ...mock, ...base }
        }}
        getStateValue={(key) => effectiveDeviceState[key]}
        showFooterActions={false}
        wrapMode={wrapMode}
        modalBackground={configForRender.modalBackground}
        editableDeviceNickname={nicknameEditable}
        onSaveDeviceNickname={nicknameEditable ? handleSaveNickname : undefined}
        showDeviceInfoButton={mode === 'control'}
        chartCredentials={
          auth
            ? ('accessToken' in auth
                ? { accessToken: auth.accessToken, appId: auth.appId }
                : { apiKey: auth.apiKey, appId: auth.appId })
            : undefined
        }
        enforceCapabilityVisibility
      />
    )
    if (mode === 'control') {
      return configDrivenContent
    }
    const configDrivenModalInner = (
      <div
        className={
          wrapMode === 'preview'
            ? 'device-type-modal-preview-modal-wrap smart-home-device-modal glass'
            : 'smart-home-device-modal glass'
        }
        onClick={(e) => e.stopPropagation()}
      >
        {configDrivenContent}
        <div className="smart-home-automation-actions">
          <button
            type="button"
            className="smart-home-automation-btn smart-home-automation-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="smart-home-automation-btn smart-home-automation-primary"
            onClick={applyAutomationDraftAndClose}
          >
            Apply
          </button>
        </div>
      </div>
    )
    if (wrapMode === 'preview') {
      return (
        <div className="device-type-modal-preview-viewport-402">
          {configDrivenModalInner}
        </div>
      )
    }
    return (
      <div className="smart-home-device-modal-overlay">
        {configDrivenModalInner}
      </div>
    )
  }

  const hasBrightness = (device.traits ?? []).some(
    (t) => typeof t === 'string' && t.toLowerCase().includes('brightness')
  )

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (mode !== 'automation' || useConfigDriven) return
    const cmd = command === 'off' ? 'off' : 'on'
    const params = cmd === 'on' && hasBrightness ? { brightness } : undefined
    updateAutomationDraft(normalizeSceneCommandFromModal(cmd, params), { touched: true })
  }, [mode, useConfigDriven, command, brightness, hasBrightness, updateAutomationDraft])

  const handleSave = async () => {
    hapticTap()
    const cmd = command === 'off' ? 'off' : 'on'
    const params = command === 'on' && hasBrightness ? { brightness } : undefined
    const settings = { command: cmd, params }
    if (mode === 'automation' && onSave) {
      onSave(settings)
      onClose()
      return
    }
    if (mode === 'control') {
      if (!auth || readOnly) {
        onClose()
        return
      }
      try {
        await executeCommandWithAuth(auth, [sn], cmd, params)
      } catch {
        // ignore
      }
      onClose()
    }
  }

  const fallbackModalInner = (
    <div
      className={
        wrapMode === 'preview'
          ? 'device-type-modal-preview-modal-wrap smart-home-device-modal glass'
          : 'smart-home-device-modal glass'
      }
      onClick={(e) => e.stopPropagation()}
    >
      <div className="smart-home-device-modal-header">
        <button
          type="button"
          className="smart-home-device-modal-back-btn"
          onClick={mode === 'automation' ? applyAutomationDraftAndClose : onClose}
          aria-label="Back"
          title="Back"
        >
          <span className="material-symbols-rounded">arrow_back</span>
        </button>
        <h2 className="smart-home-automation-title">{deviceName}</h2>
      </div>
      <div className="smart-home-device-modal-controls">
        <div className="smart-home-automation-field">
          <label>Power</label>
          <div className="smart-home-automation-command-row">
            <button
              type="button"
              className={`smart-home-automation-command-btn glass ${command === 'on' ? 'active' : ''}`}
              onClick={() => { hapticTap(); setCommand('on') }}
            >
              <span className="material-symbols-outlined">power</span>
              On
            </button>
            <button
              type="button"
              className={`smart-home-automation-command-btn glass ${command === 'off' ? 'active' : ''}`}
              onClick={() => { hapticTap(); setCommand('off') }}
            >
              <span className="material-symbols-outlined">power_off</span>
              Off
            </button>
          </div>
        </div>
        {hasBrightness && command === 'on' && (
          <div className="smart-home-automation-field">
            <label>Brightness {brightness}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={brightness}
              onChange={(e) => setBrightness(Number(e.target.value))}
              className="smart-home-device-modal-range"
            />
          </div>
        )}
      </div>
      <div className="smart-home-automation-actions">
        <button
          type="button"
          className="smart-home-automation-btn smart-home-automation-cancel"
          onClick={mode === 'automation' ? applyAutomationDraftAndClose : onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="smart-home-automation-btn smart-home-automation-primary"
          onClick={handleSave}
        >
          Done
        </button>
      </div>
    </div>
  )

  if (wrapMode === 'preview') {
    return (
      <div className="device-type-modal-preview-viewport-402">
        {fallbackModalInner}
      </div>
    )
  }

  return (
    <div className="smart-home-device-modal-overlay">
      {fallbackModalInner}
    </div>
  )
}

function AutomationModal({
  auth,
  automation,
  devices,
  availableScenes,
  selectedLocationId,
  onClose,
  onSaved,
  hapticTap,
  resolveModalConfigForDevice,
  resolveDeviceStateBySN,
}: {
  auth: AuthCreds
  automation?: Automation | null
  devices: Array<{
    sn: string
    name?: string
    displayName?: string
    nickname?: string
    deviceSN?: string
    traits?: string[]
    attributes?: Record<string, unknown>
    deviceType?: string
    type?: string
  }>
  availableScenes: QuickSceneItem[]
  selectedLocationId: string | null
  onClose: () => void
  onSaved: () => void
  hapticTap: () => void
  resolveModalConfigForDevice: (device: Device) => ModalConfig
  resolveDeviceStateBySN: (sn: string) => Record<string, unknown>
}) {
  const isEdit = !!automation
  const [step, setStep] = useState(isEdit ? 1 : 0)
  const [name, setName] = useState(automation?.name ?? '')
  const inferredTriggerType = inferAutomationTriggerType(automation)
  const rawConditions = (automation?.conditions && typeof automation.conditions === 'object')
    ? (automation.conditions as Record<string, unknown>)
    : {}
  const initialMetric = String(rawConditions.metric || '').trim()
  const initialOperator = normalizeConditionOperator(rawConditions.operator)
  const initialThreshold = rawConditions.threshold
  const initialThresholdInput = initialThreshold == null ? '' : String(initialThreshold)
  const initialTriggerDeviceSn = String(rawConditions.device || '').trim()
  const initialScheduleStart = String(automation?.schedule?.timeRanges?.[0]?.start ?? '').trim()
  const initialScheduleEnd = String(automation?.schedule?.timeRanges?.[0]?.end ?? '').trim()
  const initialScheduleTime = initialScheduleStart || initialScheduleEnd
  const [scheduleStart, setScheduleStart] = useState(
    initialScheduleTime || '18:00'
  )
  const scheduleTimeParts = useMemo(() => parseScheduleTimeParts(scheduleStart), [scheduleStart])
  const [useIosScheduleWheelPicker, setUseIosScheduleWheelPicker] = useState(false)
  const [isScheduleTimePickerOpen, setIsScheduleTimePickerOpen] = useState(false)
  const [isDesktopSchedulePickerOpen, setIsDesktopSchedulePickerOpen] = useState(false)
  const scheduleHourWheelRef = useRef<HTMLDivElement | null>(null)
  const scheduleMinuteWheelRef = useRef<HTMLDivElement | null>(null)
  const scheduleWheelDebounceRef = useRef<{ hour: number | null; minute: number | null }>({
    hour: null,
    minute: null,
  })
  const [scheduleDays, setScheduleDays] = useState<number[]>(
    automation?.schedule?.daysOfWeek?.length
      ? [...automation.schedule.daysOfWeek].sort((a, b) => a - b)
      : [0, 1, 2, 3, 4, 5, 6]
  )
  const [triggerType, setTriggerType] = useState<AutomationTriggerType>(inferredTriggerType)
  const [triggerDeviceSn, setTriggerDeviceSn] = useState(initialTriggerDeviceSn)
  const [triggerMetric, setTriggerMetric] = useState(
    initialMetric || (inferredTriggerType === 'sensor' ? 'temperature' : 'on')
  )
  const [triggerOperator, setTriggerOperator] = useState<AutomationTriggerOperator>(initialOperator)
  const [triggerThresholdInput, setTriggerThresholdInput] = useState(initialThresholdInput)
  const [deviceStateCondition, setDeviceStateCondition] = useState<DeviceStateTriggerCondition | ''>(
    inferredTriggerType === 'device_state'
      ? inferDeviceStateTriggerCondition(initialMetric, initialThreshold)
      : ''
  )
  const [selectedDeviceSns, setSelectedDeviceSns] = useState<string[]>(() => {
    const out = new Set<string>()
    for (const action of automation?.actions ?? []) {
      if (action.type !== 'control') continue
      for (const deviceSn of action.devices ?? []) {
        const value = String(deviceSn || '').trim()
        if (value) out.add(value)
      }
    }
    return Array.from(out)
  })
  const [selectedSceneActionIds, setSelectedSceneActionIds] = useState<string[]>(() => {
    const out = new Set<string>()
    for (const action of automation?.actions ?? []) {
      if (action.type !== 'scene') continue
      const idList = Array.isArray(action.sceneIds) && action.sceneIds.length > 0
        ? action.sceneIds
        : (action.sceneId ? [action.sceneId] : [])
      for (const rawId of idList) {
        const sceneId = String(rawId || '').trim()
        if (sceneId) out.add(sceneId)
      }
    }
    return Array.from(out)
  })
  const [isDeviceSectionCollapsed, setIsDeviceSectionCollapsed] = useState(false)
  const [isSceneSectionCollapsed, setIsSceneSectionCollapsed] = useState(false)
  const [deviceActions, setDeviceActions] = useState<Record<string, { command: string; params?: Record<string, unknown> }>>(() => {
    const out: Record<string, { command: string; params?: Record<string, unknown> }> = {}
    const acts = automation?.actions ?? []
    for (const a of acts) {
      if (a.type !== 'control') continue
      const devs = a.devices ?? []
      const cmd = a.command ?? 'on'
      const params = a.params ?? {}
      for (const sn of devs) out[sn] = { command: cmd, params }
    }
    return out
  })
  const [deviceModalSn, setDeviceModalSn] = useState<string | null>(null)
  const [liveDeviceStatesBySn, setLiveDeviceStatesBySn] = useState<Record<string, Record<string, unknown>>>({})
  const [saving, setSaving] = useState(false)
  const [testingAutomation, setTestingAutomation] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [globalNormalizationRules, setGlobalNormalizationRules] = useState<{
    stateRules: MergeRule[]
    sensorDataRules: MergeRule[]
  }>({
    stateRules: [],
    sensorDataRules: [],
  })

  const deviceSn = (d: { sn: string; deviceSN?: string }) => d.deviceSN ?? d.sn
  const deviceName = (d: { sn: string; name?: string; displayName?: string; nickname?: string; deviceSN?: string }) =>
    d.nickname ?? d.displayName ?? d.name ?? d.sn ?? d.deviceSN ?? '—'
  useEffect(() => {
    let cancelled = false
    loadGlobalNormalization()
      .then((doc) => {
        if (cancelled) return
        setGlobalNormalizationRules({
          stateRules: doc.stateRules ?? [],
          sensorDataRules: doc.sensorDataRules ?? [],
        })
      })
      .catch(() => {
        if (cancelled) return
        setGlobalNormalizationRules({ stateRules: [], sensorDataRules: [] })
      })
    return () => {
      cancelled = true
    }
  }, [])
  const mergedMetricAliasMap = useMemo(
    () => buildMergedMetricAliasMap(globalNormalizationRules),
    [globalNormalizationRules]
  )
  const systemTimezone = useMemo(() => resolveSystemTimezone(), [])
  const deviceCapabilityTokensBySn = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const device of devices) {
      const sn = deviceSn(device)
      map.set(sn, collectDeviceCapabilityTokens(device as Device, mergedMetricAliasMap))
    }
    return map
  }, [devices, mergedMetricAliasMap])
  const deviceTraitTokensBySn = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const device of devices) {
      const sn = deviceSn(device)
      map.set(sn, collectDeviceTraitTokens(device as Device))
    }
    return map
  }, [devices])
  const actionSelectableDevices = useMemo(() => {
    const baseHasEndpointMap = new Map<string, boolean>()
    for (const device of devices) {
      const sn = String(deviceSn(device) || '').trim()
      if (!sn) continue
      const base = sn.split('#')[0]
      if (sn.includes('#')) {
        baseHasEndpointMap.set(base, true)
      } else if (!baseHasEndpointMap.has(base)) {
        baseHasEndpointMap.set(base, false)
      }
    }
    return devices.filter((device) => {
      const sn = String(deviceSn(device) || '').trim()
      if (!sn) return false
      const base = sn.split('#')[0]
      const endpointCompatible = sn.includes('#') || !(baseHasEndpointMap.get(base) ?? false)
      return endpointCompatible && isDeviceActionControllable(device as Device, mergedMetricAliasMap)
    })
  }, [devices, mergedMetricAliasMap])
  const actionSelectableDeviceSnSet = useMemo(
    () => new Set(actionSelectableDevices.map((device) => deviceSn(device))),
    [actionSelectableDevices]
  )
  const selectedActionDeviceSns = useMemo(
    () => selectedDeviceSns.filter((sn) => actionSelectableDeviceSnSet.has(sn)),
    [selectedDeviceSns, actionSelectableDeviceSnSet]
  )
  const availableActionScenes = useMemo(() => {
    const filtered = availableScenes.filter((scene) => {
      const sceneId = String(scene.sceneId || scene.id || '').trim()
      if (!sceneId) return false
      return true
    })
    const dedup = new Map<string, QuickSceneItem>()
    for (const scene of filtered) {
      const sceneId = String(scene.sceneId || scene.id || '').trim()
      if (!sceneId || dedup.has(sceneId)) continue
      dedup.set(sceneId, scene)
    }
    return Array.from(dedup.values())
  }, [availableScenes])
  const hasAnyActionSelection = selectedActionDeviceSns.length > 0 || selectedSceneActionIds.length > 0
  const fallbackConditionDeviceSn = useMemo(() => {
    const candidates = [
      triggerDeviceSn,
      selectedActionDeviceSns[0],
      selectedDeviceSns[0],
      actionSelectableDevices[0]?.deviceSN ?? actionSelectableDevices[0]?.sn,
      devices[0]?.deviceSN ?? devices[0]?.sn,
    ]
    for (const raw of candidates) {
      const sn = toStrictEndpointDeviceSn(raw)
      if (sn) return sn
    }
    return ''
  }, [triggerDeviceSn, selectedActionDeviceSns, selectedDeviceSns, actionSelectableDevices, devices])
  const statusQueryLocationId = useMemo(
    () => String(selectedLocationId || automation?.groupId || '').trim(),
    [selectedLocationId, automation?.groupId]
  )
  const resolveLiveDeviceStateBySN = useCallback(
    (sn: string): Record<string, unknown> => liveDeviceStatesBySn[sn] ?? resolveDeviceStateBySN(sn),
    [liveDeviceStatesBySn, resolveDeviceStateBySN]
  )
  const actionSceneLabelById = useMemo(() => {
    const map = new Map<string, string>()
    for (const scene of availableActionScenes) {
      const sceneId = String(scene.sceneId || scene.id || '').trim()
      if (!sceneId || map.has(sceneId)) continue
      map.set(sceneId, String(scene.label || sceneId))
    }
    return map
  }, [availableActionScenes])
  const selectedTriggerDevice = useMemo(
    () => devices.find((device) => deviceSn(device) === triggerDeviceSn),
    [devices, triggerDeviceSn]
  )
  const selectedTriggerDeviceCapability = useMemo(
    () => (triggerDeviceSn ? deviceCapabilityTokensBySn.get(triggerDeviceSn) : undefined),
    [deviceCapabilityTokensBySn, triggerDeviceSn]
  )
  const selectedTriggerDeviceTraits = useMemo(
    () => (triggerDeviceSn ? deviceTraitTokensBySn.get(triggerDeviceSn) : undefined),
    [deviceTraitTokensBySn, triggerDeviceSn]
  )
  const availableTriggerDevices = useMemo(() => {
    const endpointDevices = devices.filter((device) => isEndpointDeviceSnValue(deviceSn(device)))
    if (triggerType === 'sensor') {
      return endpointDevices.filter((device) => {
        const sn = deviceSn(device)
        const capability = deviceCapabilityTokensBySn.get(sn)
        const traits = deviceTraitTokensBySn.get(sn)
        if (!capability) return false
        return SENSOR_TRIGGER_METRICS.some((metricOption) =>
          metricMatchesCapability(
            metricOption,
            capability,
            traits ?? new Set()
          )
        )
      })
    }
    if (triggerType === 'device_state') {
      return endpointDevices.filter((device) => {
        const sn = deviceSn(device)
        const capability = deviceCapabilityTokensBySn.get(sn)
        const traits = deviceTraitTokensBySn.get(sn)
        if (!capability) return false
        // 設備被控制時：僅顯示支援 OnOff / OpenClose / StartStop 的設備
        return DEVICE_STATE_TRIGGER_METRICS.some((metricOption) =>
          metricMatchesCapability(
            metricOption,
            capability,
            traits ?? new Set()
          )
        )
      })
    }
    return endpointDevices
  }, [
    devices,
    triggerType,
    deviceCapabilityTokensBySn,
    deviceTraitTokensBySn,
  ])
  const triggerEntryOptions = useMemo(() => {
    const supportsMetrics = (metrics: TriggerMetricOption[]) =>
      devices.some((device) => {
        const sn = deviceSn(device)
        const capability = deviceCapabilityTokensBySn.get(sn)
        const traits = deviceTraitTokensBySn.get(sn)
        if (!capability) return false
        return metrics.some((metricOption) =>
          metricMatchesCapability(
            metricOption,
            capability,
            traits ?? new Set()
          )
        )
      })
    const supportMap: Record<AutomationTriggerType, boolean> = {
      schedule: true,
      sensor: supportsMetrics(SENSOR_TRIGGER_METRICS),
      device_state: supportsMetrics(DEVICE_STATE_TRIGGER_METRICS),
      offline_notify: devices.length > 0,
    }
    return (Object.keys(AUTOMATION_TRIGGER_ENTRY_CONFIG) as AutomationTriggerType[]).map((type) => ({
      type,
      ...AUTOMATION_TRIGGER_ENTRY_CONFIG[type],
      enabled: supportMap[type],
    }))
  }, [devices, deviceCapabilityTokensBySn, deviceTraitTokensBySn])
  const recommendedAutomationEntries = useMemo<AutomationRecommendation[]>(() => {
    const enabledTypeSet = new Set(
      triggerEntryOptions.filter((item) => item.enabled).map((item) => item.type)
    )
    const getName = (device: typeof devices[number]) =>
      String(device.nickname ?? device.displayName ?? device.name ?? device.deviceSN ?? device.sn ?? '')
        .trim()
    const getTraits = (device: typeof devices[number]) =>
      new Set((device.traits ?? []).map((trait) => normalizeMetricLookupKey(trait)))
    const getSn = (device: typeof devices[number]) => deviceSn(device)
    const roomKeywords = ['客廳', '臥室', '主臥', '書房', '餐廳', '廚房', '陽台', '玄關', '浴室', '走廊']
    const allNames = devices.map(getName).filter(Boolean)
    const roomScore = new Map<string, number>()
    for (const room of roomKeywords) {
      const score = allNames.filter((name) => name.includes(room)).length
      if (score > 0) roomScore.set(room, score)
    }
    const preferredRoom = Array.from(roomScore.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? ''
    const lightingDevices = devices.filter((device) => {
      const typeText = String(device.deviceType ?? device.type ?? '').toLowerCase()
      const nameText = getName(device).toLowerCase()
      const traits = getTraits(device)
      return (
        typeText.includes('light')
        || nameText.includes('light')
        || nameText.includes('燈')
        || traits.has('brightness')
        || traits.has('colortemperature')
      )
    })
    const sensorDevices = devices.filter((device) => {
      const traits = getTraits(device)
      const typeText = String(device.deviceType ?? device.type ?? '').toLowerCase()
      const nameText = getName(device).toLowerCase()
      return (
        traits.has('occupancysensing')
        || traits.has('motionsensor')
        || traits.has('presencesensing')
        || traits.has('temperaturemeasurement')
        || traits.has('humiditymeasurement')
        || traits.has('illuminancemeasurement')
        || typeText.includes('sensor')
        || nameText.includes('sensor')
        || nameText.includes('感測')
      )
    })
    const controllableDevices = devices.filter((device) => {
      const traits = getTraits(device)
      return (
        traits.has('onoff')
        || traits.has('openclose')
        || traits.has('startstop')
      )
    })
    const onoffDevices = controllableDevices.filter((device) => getTraits(device).has('onoff'))
    const primaryActionDevices = lightingDevices.length > 0 ? lightingDevices : onoffDevices
    const deviceLabelBySn = new Map<string, string>()
    for (const device of devices) {
      const sn = getSn(device)
      if (!sn) continue
      deviceLabelBySn.set(sn, getName(device) || sn)
    }

    const pickActionTargets = (options?: { excludeSn?: string; max?: number }): string[] => {
      const max = options?.max ?? 3
      const out: string[] = []
      for (const device of primaryActionDevices) {
        const sn = getSn(device)
        if (!sn || sn === options?.excludeSn) continue
        if (!out.includes(sn)) out.push(sn)
        if (out.length >= max) break
      }
      if (out.length === 0) {
        for (const device of onoffDevices) {
          const sn = getSn(device)
          if (!sn || sn === options?.excludeSn) continue
          if (!out.includes(sn)) out.push(sn)
          if (out.length >= max) break
        }
      }
      return out
    }

    const buildOnActions = (sns: string[]) => {
      const out: Record<string, { command: string; params?: Record<string, unknown> }> = {}
      for (const sn of sns) {
        out[sn] = { command: 'on', params: {} }
      }
      return out
    }

    const formatActionSummary = (sns: string[]): string => {
      if (sns.length === 0) return '你可在下一步設定要連動的設備'
      const names = sns.map((sn) => deviceLabelBySn.get(sn) || sn).filter(Boolean)
      if (names.length === 1) return `打開「${names[0]}」`
      if (names.length === 2) return `打開「${names[0]}」與「${names[1]}」`
      return `打開「${names[0]}」等 ${names.length} 個設備`
    }

    const suggestions: AutomationRecommendation[] = []

    if (enabledTypeSet.has('schedule')) {
      const morningActionTargets = pickActionTargets({ max: 3 })
      suggestions.push({
        id: 'rec-schedule-morning',
        triggerType: 'schedule',
        title: preferredRoom ? `${preferredRoom}早晨模式` : '早晨模式',
        subtitle: `在上午 7:00，${formatActionSummary(morningActionTargets)}`,
        icon: AUTOMATION_TRIGGER_ENTRY_CONFIG.schedule.icon,
        suggestedName: preferredRoom ? `${preferredRoom}早晨自動化` : '早晨自動化',
        prefillScheduleStart: '07:00',
        prefillScheduleEnd: '',
        prefillScheduleDays: [1, 2, 3, 4, 5],
        prefillActionDeviceSns: morningActionTargets,
        prefillDeviceActions: buildOnActions(morningActionTargets),
      })

      const actionTargets = pickActionTargets({ max: 3 })
      suggestions.push({
        id: 'rec-schedule-evening',
        triggerType: 'schedule',
        title: preferredRoom ? `${preferredRoom}晚上時段` : '晚上固定時段',
        subtitle: `在下午 6:00，${formatActionSummary(actionTargets)}`,
        icon: AUTOMATION_TRIGGER_ENTRY_CONFIG.schedule.icon,
        suggestedName: preferredRoom ? `${preferredRoom}晚間自動化` : '晚間自動化',
        prefillScheduleStart: '18:00',
        prefillScheduleEnd: '',
        prefillScheduleDays: [0, 1, 2, 3, 4, 5, 6],
        prefillActionDeviceSns: actionTargets,
        prefillDeviceActions: buildOnActions(actionTargets),
      })
    }

    if (enabledTypeSet.has('sensor') && sensorDevices.length > 0) {
      const sensor = sensorDevices[0]
      const sensorSn = getSn(sensor)
      const sensorName = getName(sensor) || '感測器'
      const sensorTokens = deviceCapabilityTokensBySn.get(sensorSn) ?? new Set<string>()
      const suggestedMetric = sensorTokens.has('occupied')
        ? 'occupied'
        : sensorTokens.has('temperature')
          ? 'temperature'
          : sensorTokens.has('humidity')
            ? 'humidity'
            : sensorTokens.has('lux')
              ? 'lux'
              : 'temperature'
      const actionTargets = pickActionTargets({ excludeSn: sensorSn, max: 3 })
      suggestions.push({
        id: 'rec-sensor-motion',
        triggerType: 'sensor',
        title: `在${sensorName}偵測到變化時`,
        subtitle: formatActionSummary(actionTargets),
        icon: AUTOMATION_TRIGGER_ENTRY_CONFIG.sensor.icon,
        suggestedName: `${sensorName}變化自動化`,
        prefillTriggerDeviceSn: sensorSn,
        prefillTriggerMetric: suggestedMetric,
        prefillTriggerOperator: suggestedMetric === 'occupied' ? 'eq' : undefined,
        prefillTriggerThresholdInput: suggestedMetric === 'occupied' ? 'true' : undefined,
        prefillActionDeviceSns: actionTargets,
        prefillDeviceActions: buildOnActions(actionTargets),
      })
    }

    if (enabledTypeSet.has('device_state') && controllableDevices.length > 0) {
      const target = controllableDevices[0]
      const targetSn = getSn(target)
      const targetName = getName(target) || '設備'
      const actionTargets = pickActionTargets({ excludeSn: targetSn, max: 3 })
      suggestions.push({
        id: 'rec-device-link',
        triggerType: 'device_state',
        title: `當${targetName}被控制時`,
        subtitle: formatActionSummary(actionTargets),
        icon: AUTOMATION_TRIGGER_ENTRY_CONFIG.device_state.icon,
        suggestedName: `${targetName}連動`,
        prefillTriggerDeviceSn: targetSn,
        prefillDeviceStateCondition: 'on',
        prefillActionDeviceSns: actionTargets,
        prefillDeviceActions: buildOnActions(actionTargets),
      })
    }

    if (enabledTypeSet.has('offline_notify') && devices.length > 0) {
      const notifyTarget = getSn(devices[0])
      suggestions.push({
        id: 'rec-offline',
        triggerType: 'offline_notify',
        title: '設備離線時即時通知',
        subtitle: '避免配件斷線造成自動化失效',
        icon: AUTOMATION_TRIGGER_ENTRY_CONFIG.offline_notify.icon,
        suggestedName: '離線通知',
        prefillTriggerDeviceSn: notifyTarget,
        prefillTriggerOperator: 'eq',
        prefillTriggerThresholdInput: 'false',
      })
    }

    if (suggestions.length > 0) return suggestions.slice(0, 4)

    return triggerEntryOptions
      .filter((item) => item.enabled)
      .slice(0, 4)
      .map((item) => ({
        id: `fallback-${item.type}`,
        triggerType: item.type,
        title: item.title,
        subtitle: item.example,
        icon: item.icon,
        suggestedName: undefined,
      }))
  }, [devices, triggerEntryOptions, deviceCapabilityTokensBySn])
  const selectTriggerType = useCallback((nextType: AutomationTriggerType, recommendation?: AutomationRecommendation) => {
    hapticTap()
    setTriggerType(nextType)
    if (recommendation?.prefillTriggerDeviceSn) {
      setTriggerDeviceSn(recommendation.prefillTriggerDeviceSn)
    }
    if (nextType === 'schedule') {
      if (recommendation?.prefillScheduleStart !== undefined) {
        setScheduleStart(recommendation.prefillScheduleStart)
      }
      if (recommendation?.prefillScheduleEnd !== undefined) {
        // setScheduleEnd not implemented yet — skip
      }
      if (recommendation?.prefillScheduleDays && recommendation.prefillScheduleDays.length > 0) {
        const sortedDays = Array.from(new Set(recommendation.prefillScheduleDays)).sort((a, b) => a - b)
        setScheduleDays(sortedDays)
      }
    }
    if (nextType === 'offline_notify') {
      setTriggerMetric('online')
      setTriggerOperator(recommendation?.prefillTriggerOperator ?? 'eq')
      setTriggerThresholdInput(recommendation?.prefillTriggerThresholdInput ?? 'false')
    }
    if (nextType === 'device_state') {
      setDeviceStateCondition(recommendation?.prefillDeviceStateCondition ?? 'on')
    }
    if (nextType === 'sensor') {
      setTriggerMetric(
        recommendation?.prefillTriggerMetric
          ?? ((prev) => (!prev || prev === 'online') ? 'temperature' : prev)
      )
      if (recommendation?.prefillTriggerOperator) {
        setTriggerOperator(recommendation.prefillTriggerOperator)
      }
      if (recommendation?.prefillTriggerThresholdInput !== undefined) {
        setTriggerThresholdInput(recommendation.prefillTriggerThresholdInput)
      }
    } else if (recommendation?.prefillTriggerOperator) {
      setTriggerOperator(recommendation.prefillTriggerOperator)
      if (recommendation.prefillTriggerThresholdInput !== undefined) {
        setTriggerThresholdInput(recommendation.prefillTriggerThresholdInput)
      }
    }
    if (recommendation?.prefillActionDeviceSns) {
      const sns = Array.from(new Set(
        recommendation.prefillActionDeviceSns
          .map((sn) => String(sn || '').trim())
          .filter((sn) => Boolean(sn) && actionSelectableDeviceSnSet.has(sn))
      ))
      setSelectedDeviceSns(sns)
      setDeviceActions(() => {
        const out: Record<string, { command: string; params?: Record<string, unknown> }> = {}
        for (const sn of sns) {
          const seed = recommendation.prefillDeviceActions?.[sn] ?? { command: 'on', params: {} }
          out[sn] = normalizeSceneCommandFromModal(seed.command, seed.params ?? {})
        }
        return out
      })
    }
    if (recommendation?.suggestedName) {
      setName((prev) => {
        if (String(prev || '').trim()) return prev
        return recommendation.suggestedName as string
      })
    }
    setErr(null)
    setStep(1)
  }, [hapticTap, actionSelectableDeviceSnSet])
  const triggerMetricOptions = useMemo(() => {
    if (triggerType === 'schedule' || triggerType === 'offline_notify') return [] as TriggerMetricOption[]
    if (!selectedTriggerDevice || !selectedTriggerDeviceCapability) return [] as TriggerMetricOption[]
    const base = triggerType === 'sensor' ? SENSOR_TRIGGER_METRICS : DEVICE_STATE_TRIGGER_METRICS
    return base.filter((option) =>
      metricMatchesCapability(
        option,
        selectedTriggerDeviceCapability,
        selectedTriggerDeviceTraits ?? new Set()
      )
    )
  }, [
    triggerType,
    selectedTriggerDevice,
    selectedTriggerDeviceCapability,
    selectedTriggerDeviceTraits,
  ])
  const selectedTriggerMetricOption = useMemo(
    () => triggerMetricOptions.find((option) => option.value === triggerMetric),
    [triggerMetricOptions, triggerMetric]
  )
  const triggerThresholdUi = useMemo(
    () => deriveTriggerThresholdUiModel({
      device: selectedTriggerDevice as Device | undefined,
      metricOption: selectedTriggerMetricOption,
      metricAliasMap: mergedMetricAliasMap,
    }),
    [selectedTriggerDevice, selectedTriggerMetricOption, mergedMetricAliasMap]
  )
  const triggerThresholdChoices = useMemo(() => {
    const map = new Map<string, TriggerThresholdChoice>()
    for (const choice of triggerThresholdUi.choices) {
      const value = String(choice.value || '').trim()
      if (!value || map.has(value)) continue
      map.set(value, choice)
    }
    const existing = String(triggerThresholdInput || '').trim()
    if (existing && !map.has(existing)) {
      map.set(existing, { value: existing, label: `目前設定：${existing}` })
    }
    return Array.from(map.values())
  }, [triggerThresholdUi.choices, triggerThresholdInput])
  const deviceStateConditionOptions = useMemo(() => {
    if (triggerType !== 'device_state') return [] as Array<{ value: DeviceStateTriggerCondition; label: string }>
    const supportedMetricKeys = new Set(
      triggerMetricOptions.map((metric) => normalizeMetricLookupKey(metric.value))
    )
    const out: Array<{ value: DeviceStateTriggerCondition; label: string }> = []
    if (supportedMetricKeys.has('on')) {
      out.push({ value: 'on', label: 'On' }, { value: 'off', label: 'Off' })
    }
    if (supportedMetricKeys.has('openpercent')) {
      out.push({ value: 'open', label: 'Open' }, { value: 'close', label: 'Close' })
    }
    if (supportedMetricKeys.has('running')) {
      out.push({ value: 'start', label: 'Start' }, { value: 'stop', label: 'Stop' })
    }
    return out
  }, [triggerType, triggerMetricOptions])

  useEffect(() => {
    if (triggerType === 'schedule' || triggerType === 'offline_notify') return
    if (triggerMetricOptions.length === 0) {
      if (triggerMetric) setTriggerMetric('')
      return
    }
    if (triggerMetricOptions.some((option) => option.value === triggerMetric)) return
    setTriggerMetric(triggerMetricOptions[0]?.value ?? '')
  }, [triggerType, triggerMetric, triggerMetricOptions])

  useEffect(() => {
    if (triggerType === 'schedule' || triggerType === 'offline_notify' || triggerType === 'device_state') return
    if (!triggerDeviceSn || !triggerMetric) return
    if (triggerThresholdChoices.length === 0) {
      if (triggerThresholdInput) setTriggerThresholdInput('')
      return
    }
    if (triggerThresholdChoices.some((choice) => choice.value === triggerThresholdInput)) return
    setTriggerThresholdInput(triggerThresholdChoices[0]?.value ?? '')
  }, [
    triggerType,
    triggerDeviceSn,
    triggerMetric,
    triggerThresholdChoices,
    triggerThresholdInput,
  ])

  useEffect(() => {
    if (triggerType !== 'device_state') return
    if (deviceStateConditionOptions.length === 0) {
      if (deviceStateCondition) setDeviceStateCondition('')
      return
    }
    if (deviceStateConditionOptions.some((option) => option.value === deviceStateCondition)) return
    setDeviceStateCondition(deviceStateConditionOptions[0]?.value ?? '')
  }, [triggerType, deviceStateCondition, deviceStateConditionOptions])

  useEffect(() => {
    if (triggerType === 'schedule') return
    if (!triggerDeviceSn) return
    if (!isEndpointDeviceSnValue(triggerDeviceSn)) {
      setTriggerDeviceSn('')
      return
    }
    if (availableTriggerDevices.some((device) => deviceSn(device) === triggerDeviceSn)) return
    setTriggerDeviceSn('')
  }, [triggerType, triggerDeviceSn, availableTriggerDevices])

  useEffect(() => {
    setSelectedDeviceSns((prev) => {
      const next = prev.filter((sn) => actionSelectableDeviceSnSet.has(sn))
      return next.length === prev.length ? prev : next
    })
  }, [actionSelectableDeviceSnSet])

  useEffect(() => {
    if (!deviceModalSn) return
    if (actionSelectableDeviceSnSet.has(deviceModalSn)) return
    setDeviceModalSn(null)
  }, [deviceModalSn, actionSelectableDeviceSnSet])

  useEffect(() => {
    if (!deviceModalSn) return
    const seedState = resolveDeviceStateBySN(deviceModalSn)
    if (seedState && Object.keys(seedState).length > 0) {
      setLiveDeviceStatesBySn((prev) => ({ ...prev, [deviceModalSn]: seedState }))
    }
    if (!statusQueryLocationId) return
    let cancelled = false
    queryStatusWithAuth(auth, statusQueryLocationId, [deviceModalSn])
      .then((statuses) => {
        if (cancelled) return
        const realtimeState = pickRealtimeStateFromStatuses(statuses, deviceModalSn)
        if (!realtimeState || Object.keys(realtimeState).length === 0) return
        setLiveDeviceStatesBySn((prev) => ({ ...prev, [deviceModalSn]: realtimeState }))
      })
      .catch(() => {
        // ignore real-time refresh failures, keep existing snapshot
      })
    return () => {
      cancelled = true
    }
  }, [auth, deviceModalSn, resolveDeviceStateBySN, statusQueryLocationId])

  const toggleDevice = (sn: string) => {
    hapticTap()
    setSelectedDeviceSns((prev) =>
      prev.includes(sn) ? prev.filter((s) => s !== sn) : [...prev, sn]
    )
  }
  const toggleSceneAction = (sceneId: string) => {
    hapticTap()
    setSelectedSceneActionIds((prev) =>
      prev.includes(sceneId) ? prev.filter((id) => id !== sceneId) : [...prev, sceneId]
    )
  }

  const toggleScheduleDay = (day: number) => {
    hapticTap()
    setScheduleDays((prev) => {
      if (prev.includes(day)) return prev.filter((d) => d !== day)
      return [...prev, day].sort((a, b) => a - b)
    })
  }

  useEffect(() => {
    setUseIosScheduleWheelPicker(shouldUseIosScheduleWheelPicker())
  }, [])

  useEffect(() => {
    if (useIosScheduleWheelPicker) return
    if (!isScheduleTimePickerOpen) return
    setIsScheduleTimePickerOpen(false)
  }, [useIosScheduleWheelPicker, isScheduleTimePickerOpen])

  useEffect(() => {
    if (useIosScheduleWheelPicker) {
      if (isDesktopSchedulePickerOpen) setIsDesktopSchedulePickerOpen(false)
      return
    }
    if (isScheduleTimePickerOpen) setIsScheduleTimePickerOpen(false)
  }, [useIosScheduleWheelPicker, isDesktopSchedulePickerOpen, isScheduleTimePickerOpen])

  const updateScheduleTimePart = useCallback((nextPart: Partial<{ hour: string; minute: string }>) => {
    const hour = String(
      nextPart.hour ?? scheduleTimeParts.hour ?? AUTOMATION_SCHEDULE_DEFAULT_HOUR
    ).trim()
    const minute = String(
      nextPart.minute ?? scheduleTimeParts.minute ?? AUTOMATION_SCHEDULE_DEFAULT_MINUTE
    ).trim()
    if (!hour || !minute) {
      setScheduleStart('')
      return
    }
    setScheduleStart(`${hour}:${minute}`)
  }, [scheduleTimeParts.hour, scheduleTimeParts.minute])

  const schedulePickerHour = scheduleTimeParts.hour || AUTOMATION_SCHEDULE_DEFAULT_HOUR
  const schedulePickerMinute = scheduleTimeParts.minute || AUTOMATION_SCHEDULE_DEFAULT_MINUTE

  const centerScheduleWheel = useCallback((
    part: 'hour' | 'minute',
    value: string,
    behavior: ScrollBehavior = 'smooth'
  ) => {
    const container = part === 'hour' ? scheduleHourWheelRef.current : scheduleMinuteWheelRef.current
    if (!container) return
    const target = container.querySelector<HTMLElement>(`[data-wheel-value="${value}"]`)
    if (!target) return
    target.scrollIntoView({ block: 'center', behavior })
  }, [])

  const handleScheduleWheelSelect = useCallback((
    part: 'hour' | 'minute',
    value: string,
    behavior: ScrollBehavior = 'smooth'
  ) => {
    if (part === 'hour') updateScheduleTimePart({ hour: value })
    else updateScheduleTimePart({ minute: value })
    centerScheduleWheel(part, value, behavior)
  }, [centerScheduleWheel, updateScheduleTimePart])

  const handleScheduleWheelScroll = useCallback((part: 'hour' | 'minute', event: ReactUIEvent<HTMLDivElement>) => {
    const container = event.currentTarget
    const timerKey = part === 'hour' ? 'hour' : 'minute'
    const existingTimer = scheduleWheelDebounceRef.current[timerKey]
    if (existingTimer != null) window.clearTimeout(existingTimer)
    scheduleWheelDebounceRef.current[timerKey] = window.setTimeout(() => {
      const options = part === 'hour'
        ? AUTOMATION_SCHEDULE_HOUR_OPTIONS
        : AUTOMATION_SCHEDULE_MINUTE_OPTIONS
      const rawIndex = Math.round(container.scrollTop / AUTOMATION_SCHEDULE_WHEEL_ITEM_HEIGHT)
      const index = Math.max(0, Math.min(options.length - 1, rawIndex))
      const value = options[index]
      handleScheduleWheelSelect(part, value)
      scheduleWheelDebounceRef.current[timerKey] = null
    }, 90)
  }, [handleScheduleWheelSelect])

  useEffect(() => {
    return () => {
      if (scheduleWheelDebounceRef.current.hour != null) window.clearTimeout(scheduleWheelDebounceRef.current.hour)
      if (scheduleWheelDebounceRef.current.minute != null) window.clearTimeout(scheduleWheelDebounceRef.current.minute)
    }
  }, [])

  useEffect(() => {
    if (!isScheduleTimePickerOpen) return
    const frame = window.requestAnimationFrame(() => {
      centerScheduleWheel('hour', schedulePickerHour, 'auto')
      centerScheduleWheel('minute', schedulePickerMinute, 'auto')
    })
    return () => window.cancelAnimationFrame(frame)
  }, [isScheduleTimePickerOpen, centerScheduleWheel, schedulePickerHour, schedulePickerMinute])

  const getStepOneValidationError = useCallback((): string | null => {
    if (!name.trim()) return '請輸入名稱'
    if (triggerType === 'schedule') {
      if (!String(scheduleStart || '').trim()) return '請設定執行時間'
      if (scheduleDays.length === 0) return '請至少選擇一個執行日'
      return null
    }
    if (availableTriggerDevices.length === 0) {
      return triggerType === 'sensor'
        ? '目前沒有可用的感測器設備'
        : '請先在首頁登入並載入設備'
    }
    if (devices.length === 0) return '請先在首頁登入並載入設備'
    if (!triggerDeviceSn) return '請選擇觸發設備'
    if (!isEndpointDeviceSnValue(triggerDeviceSn)) return '請選擇 endpoint 設備（deviceSN）'
    if (triggerType === 'offline_notify') return null
    if (triggerType === 'device_state') {
      if (deviceStateConditionOptions.length === 0) return '所選設備不支援 OnOff / OpenClose / StartStop'
      if (!deviceStateCondition) return '請選擇觸發條件'
      return null
    }
    if (!triggerMetric) return '請選擇觸發條件欄位'
    if (triggerMetricOptions.length === 0) return '所選設備沒有可用的觸發欄位'
    if (!triggerThresholdInput.trim()) return '請設定觸發門檻值'
    return null
  }, [
    name,
    triggerType,
    scheduleStart,
    scheduleDays,
    availableTriggerDevices,
    devices.length,
    triggerDeviceSn,
    deviceStateConditionOptions,
    deviceStateCondition,
    triggerMetric,
    triggerMetricOptions,
    triggerThresholdInput,
  ])

  const stepOneValidationError = useMemo(
    () => (step === 1 ? getStepOneValidationError() : null),
    [step, getStepOneValidationError]
  )
  const headerPrimaryLabel = step === 3 ? (isEdit ? '儲存' : '完成') : '下一步'
  const headerPrimaryDisabled = (
    saving
    || (
      step === 1
        ? stepOneValidationError !== null
        : step === 2
          ? !hasAnyActionSelection
          : false
    )
  )

  const handleNext = () => {
    hapticTap()
    setErr(null)
    if (step === 1) {
      const validationError = getStepOneValidationError()
      if (validationError) {
        setErr(validationError)
        return
      }
      setStep(2)
    } else if (step === 2) {
      if (!hasAnyActionSelection) {
        setErr('請至少選擇一個設備或情境')
        return
      }
      setStep(3)
    }
  }

  const buildAutomationActionPayload = useCallback(() => {
    const controlActions = selectedActionDeviceSns.map((sn) => {
      const cfg = deviceActions[sn] ?? { command: 'on', params: {} }
      const normalizedCfg = normalizeSceneCommandFromModal(
        cfg.command,
        (cfg.params ?? {}) as Record<string, unknown>
      )
      return {
        type: 'control' as const,
        devices: [sn],
        command: normalizedCfg.command,
        params: normalizedCfg.params,
      }
    })
    const sceneActions = selectedSceneActionIds.map((sceneId) => ({
      type: 'scene' as const,
      sceneId,
    }))
    return [...controlActions, ...sceneActions]
  }, [deviceActions, selectedActionDeviceSns, selectedSceneActionIds])

  const handleTestAutomation = useCallback(async () => {
    if (saving || testingAutomation) return
    if (!hasAnyActionSelection) {
      setErr('此自動化沒有可執行動作')
      return
    }
    hapticTap()
    setErr(null)
    setTestingAutomation(true)
    try {
      const actions = buildAutomationActionPayload()
      const sceneGroupMap = new Map<string, string>()
      for (const scene of availableScenes) {
        const sceneId = String(scene.sceneId || scene.id || '').trim()
        const groupId = String(scene.groupId || '').trim()
        if (sceneId && groupId && !sceneGroupMap.has(sceneId)) {
          sceneGroupMap.set(sceneId, groupId)
        }
      }
      const fallbackGroupId = String(selectedLocationId || automation?.groupId || '').trim() || undefined

      let executed = 0
      for (const action of actions) {
        if (action.type === 'control') {
          const devices = (action.devices ?? []).map((sn) => String(sn || '').trim()).filter(Boolean)
          const command = String(action.command || '').trim()
          if (devices.length === 0 || !command) continue
          const result = await executeCommandWithAuth(auth, devices, command, action.params)
          if (!result.success) throw new Error(result.error || '設備控制執行失敗')
          executed += 1
          continue
        }
        if (action.type === 'scene') {
          const sceneIds = Array.isArray((action as Record<string, unknown>).sceneIds) && ((action as Record<string, unknown>).sceneIds as string[]).length > 0
            ? (action as Record<string, unknown>).sceneIds as string[]
            : (action.sceneId ? [action.sceneId] : [])
          for (const rawSceneId of sceneIds) {
            const sceneId = String(rawSceneId || '').trim()
            if (!sceneId) continue
            const groupId = sceneGroupMap.get(sceneId) || fallbackGroupId
            const result = await runSceneWithAuth(auth, sceneId, groupId)
            if (!result.success) throw new Error(result.error || `情境 ${sceneId} 執行失敗`)
            executed += 1
          }
        }
      }

      if (executed === 0) {
        throw new Error('此自動化沒有可執行的控制或情境動作')
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : '測試執行失敗')
    } finally {
      setTestingAutomation(false)
    }
  }, [
    auth,
    automation?.groupId,
    availableScenes,
    buildAutomationActionPayload,
    hasAnyActionSelection,
    hapticTap,
    saving,
    selectedLocationId,
    testingAutomation,
  ])

  const handleSave = async () => {
    if (!hasAnyActionSelection) {
      setErr('請至少選擇一個設備或情境')
      return
    }
    hapticTap()
    setSaving(true)
    setErr(null)
    try {
      const existingSimpleCondition = (
        isEdit
        && automation?.conditions
        && typeof automation.conditions === 'object'
        && (automation.conditions as Record<string, unknown>).type === 'simple'
      ) ? (automation.conditions as {
        type: 'simple'
        device?: string
        metric?: string
        operator?: string
        threshold?: unknown
      }) : null
      const existingNormalizedSimpleCondition = existingSimpleCondition
        ? {
            ...existingSimpleCondition,
            device: toStrictEndpointDeviceSn(existingSimpleCondition.device),
          }
        : null
      const existingTrigger = (
        isEdit
        && automation?.trigger
        && typeof automation.trigger === 'object'
      ) ? (automation.trigger as Record<string, unknown>) : null
      const useExistingTriggerCondition = (
        existingNormalizedSimpleCondition
        && String(existingNormalizedSimpleCondition.device || '').trim()
        && String(existingNormalizedSimpleCondition.metric || '').trim()
      )
      const actions = buildAutomationActionPayload()
      const params: ConfigureAutomationParams = {
        action: isEdit ? 'update' : 'create',
        name: name.trim(),
        enabled: automation?.enabled ?? true,
        conditions: {
          type: 'simple',
          device: fallbackConditionDeviceSn,
          metric: 'online',
          operator: 'eq',
          threshold: true,
        },
        actions,
      }
      const normalizedAutomationGroupId = String(selectedLocationId || automation?.groupId || '').trim()
      if (normalizedAutomationGroupId) {
        params.locationId = normalizedAutomationGroupId
        params.groupId = normalizedAutomationGroupId
      }
      if (triggerType === 'schedule') {
        if (!fallbackConditionDeviceSn) {
          setErr('找不到可用設備，請先選擇至少一個設備或確認設備清單已載入')
          setSaving(false)
          return
        }
        const normalizedScheduleTime = String(scheduleStart || '').trim()
        if (!normalizedScheduleTime) {
          setErr('請設定執行時間')
          setSaving(false)
          return
        }
        params.triggerMode = 'schedule'
        params.trigger = { type: 'schedule' }
        params.evaluationStrategy = { type: 'schedule' }
        params.conditions = {
          type: 'simple',
          device: fallbackConditionDeviceSn,
          metric: 'online',
          operator: 'eq',
          threshold: true,
        }
        const schedulePayload: NonNullable<ConfigureAutomationParams['schedule']> = {
          enabled: true,
          timezone: systemTimezone,
          daysOfWeek: [...scheduleDays].sort((a, b) => a - b),
          // Keep backend compatibility: single-point time encoded as start === end.
          timeRanges: [{ start: normalizedScheduleTime, end: normalizedScheduleTime }],
        }
        params.schedule = schedulePayload
      } else {
        params.triggerMode = 'event'
        if (triggerType === 'offline_notify') {
          const normalizedTriggerDeviceSn = toStrictEndpointDeviceSn(triggerDeviceSn)
          if (normalizedTriggerDeviceSn) {
            params.trigger = { type: 'device_state' }
            params.conditions = {
              type: 'simple',
              device: normalizedTriggerDeviceSn,
              metric: 'online',
              operator: 'eq',
              threshold: false,
            }
          } else if (useExistingTriggerCondition) {
            params.trigger = existingTrigger ?? { type: 'device_state' }
            params.conditions = existingNormalizedSimpleCondition as typeof params.conditions
          } else {
            setErr('請選擇 endpoint 設備（deviceSN）')
            setSaving(false)
            return
          }
        } else if (triggerType === 'device_state') {
          const mappedCondition = mapDeviceStateConditionToRule(deviceStateCondition)
          const normalizedTriggerDeviceSn = toStrictEndpointDeviceSn(triggerDeviceSn)
          if (!mappedCondition || !normalizedTriggerDeviceSn) {
            if (useExistingTriggerCondition) {
              params.trigger = existingTrigger ?? { type: 'device_state' }
              params.conditions = existingNormalizedSimpleCondition as typeof params.conditions
            } else {
              setErr('請選擇 endpoint 設備與觸發條件')
              setSaving(false)
              return
            }
          } else {
            params.trigger = { type: 'device_state' }
            params.conditions = {
              type: 'simple',
              device: normalizedTriggerDeviceSn,
              metric: mappedCondition.metric,
              operator: 'eq',
              threshold: mappedCondition.threshold,
            }
          }
        } else {
          const threshold = parseAutomationThresholdInput(triggerThresholdInput, triggerOperator)
          const normalizedTriggerDeviceSn = toStrictEndpointDeviceSn(triggerDeviceSn)
          const normalizedTriggerMetric = String(triggerMetric || '').trim()
          if (
            threshold === undefined
            || !normalizedTriggerDeviceSn
            || !normalizedTriggerMetric
          ) {
            if (useExistingTriggerCondition) {
              params.trigger = existingTrigger ?? { type: triggerType }
              params.conditions = existingNormalizedSimpleCondition as typeof params.conditions
            } else {
              setErr('請選擇 endpoint 設備並確認門檻值格式')
              setSaving(false)
              return
            }
          } else {
            params.trigger = { type: triggerType }
            params.conditions = {
              type: 'simple',
              device: normalizedTriggerDeviceSn,
              metric: normalizedTriggerMetric,
              operator: triggerOperator,
              threshold,
            }
          }
        }
      }
      if (isEdit && automation?.id) params.id = automation.id
      await configureAutomationWithAuth(auth, params)
      onSaved()
    } catch (e) {
      setErr(e instanceof Error ? e.message : '儲存失敗')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!isEdit || !automation?.id) return
    hapticTap()
    setSaving(true)
    setErr(null)
    try {
      await configureAutomationWithAuth(auth, {
        action: 'delete',
        id: automation.id,
        name: automation.name,
        conditions: {
          type: 'simple',
          device: 'placeholder',
          metric: 'online',
          operator: 'eq',
          threshold: true,
        },
        actions: automation.actions ?? [{ type: 'control', devices: [], command: 'on' }],
      })
      onSaved()
    } catch (e) {
      setErr(e instanceof Error ? e.message : '刪除失敗')
    } finally {
      setSaving(false)
    }
  }

  const [dragY, setDragY] = useState(0)
  const isDragging = useRef(false)
  const startY = useRef(0)

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const target = e.target
    if (!(target instanceof Element)) return
    if (target.closest('.smart-home-automation-sheet-header')) return
    if (target.closest('button, input, select, textarea, a, [role="button"]')) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    isDragging.current = true
    startY.current = e.clientY
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return
    const currentY = e.clientY
    const delta = Math.max(0, currentY - startY.current)
    setDragY(delta)
  }

  const [isClosing, setIsClosing] = useState(false)

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      onClose()
    }, 250) // Match the animation duration
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return
    isDragging.current = false
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture?.(e.pointerId)
    }
    if (dragY > 120) {
      handleClose()
    } else {
      setDragY(0)
    }
  }

  return (
    <div 
      className={`smart-home-automation-overlay ${isClosing ? 'is-closing' : ''}`} 
      onClick={handleClose}
    >
      <div 
        className={`smart-home-automation-modal glass ${isClosing ? 'is-closing' : ''}`} 
        onClick={(e) => e.stopPropagation()}
        style={isClosing ? undefined : {
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: isDragging.current ? 'none' : 'transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)'
        }}
      >
        <div 
          className="smart-home-automation-sheet-top"
        >
          <div
            className="smart-home-automation-sheet-handle"
            aria-hidden="true"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          />
          <div className="smart-home-automation-sheet-header">
            <button
              type="button"
              className="smart-home-automation-sheet-close"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                hapticTap()
                if (step === 0 && !isEdit) {
                  handleClose()
                  return
                }
                if (!isEdit && step === 1) {
                  setErr(null)
                  setStep(0)
                  return
                }
                if (step > 1) {
                  setErr(null)
                  setStep(step - 1)
                  return
                }
                handleClose()
              }}
              aria-label={(!isEdit && step === 0) || (isEdit && step === 1) ? '關閉' : '返回'}
              title={(!isEdit && step === 0) || (isEdit && step === 1) ? '關閉' : '返回'}
            >
              <span className="material-symbols-outlined">
                {(!isEdit && step === 0) || (isEdit && step === 1) ? 'close' : 'chevron_left'}
              </span>
            </button>
            <h2 className="smart-home-automation-title">
              {step === 0 && !isEdit ? '新增自動化操作' : (isEdit ? '編輯自動化' : '新增自動化')}
            </h2>
            {step > 0 ? (
              <button
                type="button"
                className="smart-home-automation-sheet-next"
                disabled={headerPrimaryDisabled}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => {
                  if (step === 3) {
                    handleSave()
                    return
                  }
                  handleNext()
                }}
              >
                {headerPrimaryLabel}
              </button>
            ) : (
              <span className="smart-home-automation-sheet-header-spacer" aria-hidden="true" />
            )}
          </div>
        </div>

        <div className="smart-home-automation-sheet-content">
          {step === 0 && (
            <>
              <h3 className="smart-home-automation-intro-title">事件</h3>
              <p className="smart-home-automation-intro-desc">選擇此自動化操作的發生時間。</p>
              <div className="smart-home-automation-event-list">
                {triggerEntryOptions.map((entry) => (
                  <button
                    key={entry.type}
                    type="button"
                    className={`smart-home-automation-event-item ${triggerType === entry.type ? 'active' : ''}`}
                    disabled={!entry.enabled}
                    onClick={() => selectTriggerType(entry.type)}
                  >
                    <span className="material-symbols-outlined smart-home-automation-event-item-icon">{entry.icon}</span>
                    <span className="smart-home-automation-event-item-main">
                      <span className="smart-home-automation-event-item-title">{entry.title}</span>
                      <span className="smart-home-automation-event-item-desc">{entry.example}</span>
                    </span>
                    <span className="material-symbols-outlined smart-home-automation-event-item-chevron">chevron_right</span>
                  </button>
                ))}
              </div>
              <h3 className="smart-home-automation-intro-title smart-home-automation-intro-title-secondary">建議</h3>
              <p className="smart-home-automation-intro-desc">根據你的家庭配件所製作。</p>
              <div className="smart-home-automation-event-list smart-home-automation-event-list-recommend">
                {recommendedAutomationEntries.length === 0 ? (
                  <div className="smart-home-automation-event-item static">
                    <span className="material-symbols-outlined smart-home-automation-event-item-icon">tips_and_updates</span>
                    <span className="smart-home-automation-event-item-main">
                      <span className="smart-home-automation-event-item-title">登入並載入設備後可產生建議</span>
                      <span className="smart-home-automation-event-item-desc">你可以先從「某個特定時間」開始建立。</span>
                    </span>
                  </div>
                ) : (
                  recommendedAutomationEntries.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      className="smart-home-automation-event-item"
                      onClick={() => selectTriggerType(entry.triggerType, entry)}
                    >
                      <span className="material-symbols-outlined smart-home-automation-event-item-icon">{entry.icon}</span>
                      <span className="smart-home-automation-event-item-main">
                        <span className="smart-home-automation-event-item-title">{entry.title}</span>
                        <span className="smart-home-automation-event-item-desc">{entry.subtitle}</span>
                      </span>
                      <span className="material-symbols-outlined smart-home-automation-event-item-chevron">chevron_right</span>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
          {step === 1 && (
            <>
            <div className="smart-home-automation-field">
              <label>名稱</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="例如：下班自動關燈"
                className="smart-home-automation-input"
              />
            </div>
            <div className="smart-home-automation-field">
              <label>觸發條件</label>
              {isEdit ? (
                <div className="smart-home-automation-command-row smart-home-automation-trigger-row">
                  <button
                    type="button"
                    className={`smart-home-automation-command-btn ${triggerType === 'schedule' ? 'active' : ''}`}
                    onClick={() => {
                      hapticTap()
                      setTriggerType('schedule')
                      setErr(null)
                    }}
                  >
                    某個特定時間
                  </button>
                  <button
                    type="button"
                    className={`smart-home-automation-command-btn ${triggerType === 'device_state' ? 'active' : ''}`}
                    onClick={() => {
                      hapticTap()
                      setTriggerType('device_state')
                      if (!deviceStateCondition) setDeviceStateCondition('on')
                      setErr(null)
                    }}
                  >
                    設備被控制時
                  </button>
                  <button
                    type="button"
                    className={`smart-home-automation-command-btn ${triggerType === 'sensor' ? 'active' : ''}`}
                    onClick={() => {
                      hapticTap()
                      setTriggerType('sensor')
                      if (!triggerMetric || triggerMetric === 'online') setTriggerMetric('temperature')
                      setErr(null)
                    }}
                  >
                    感測器有變化時
                  </button>
                  <button
                    type="button"
                    className={`smart-home-automation-command-btn ${triggerType === 'offline_notify' ? 'active' : ''}`}
                    onClick={() => {
                      hapticTap()
                      setTriggerType('offline_notify')
                      setTriggerMetric('online')
                      setTriggerOperator('eq')
                      setTriggerThresholdInput('false')
                      setErr(null)
                    }}
                  >
                    離線通知
                  </button>
                </div>
              ) : (
                <div className="smart-home-automation-selected-trigger">
                  <span className="material-symbols-outlined smart-home-automation-selected-trigger-icon">
                    {AUTOMATION_TRIGGER_ENTRY_CONFIG[triggerType].icon}
                  </span>
                  <div className="smart-home-automation-selected-trigger-main">
                    <p className="smart-home-automation-selected-trigger-title">
                      {AUTOMATION_TRIGGER_ENTRY_CONFIG[triggerType].title}
                    </p>
                    <p className="smart-home-automation-selected-trigger-desc">
                      {AUTOMATION_TRIGGER_ENTRY_CONFIG[triggerType].example}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="smart-home-automation-selected-trigger-change"
                    onClick={() => {
                      hapticTap()
                      setErr(null)
                      setStep(0)
                    }}
                  >
                    更換
                  </button>
                </div>
              )}
            </div>
            {triggerType === 'schedule' ? (
              <>
                <div className="smart-home-automation-field">
                  <div>
                    <label>執行時間</label>
                    {useIosScheduleWheelPicker ? (
                      <div className="smart-home-automation-time-chip-row">
                        <button
                          type="button"
                          className="smart-home-automation-time-chip"
                          onClick={() => {
                            hapticTap()
                            setIsScheduleTimePickerOpen(true)
                          }}
                        >
                          {scheduleStart || '選擇時間'}
                        </button>
                      </div>
                    ) : (
                      <div className="smart-home-automation-time-chip-row">
                        <button
                          type="button"
                          className="smart-home-automation-time-chip smart-home-automation-time-chip-desktop"
                          onClick={() => {
                            hapticTap()
                            setIsDesktopSchedulePickerOpen(true)
                          }}
                        >
                          {scheduleStart || '選擇時間'}
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      className="smart-home-automation-time-clear"
                      onClick={() => {
                        hapticTap()
                        setScheduleStart('')
                      }}
                    >
                      清除時間
                    </button>
                    {useIosScheduleWheelPicker && isScheduleTimePickerOpen && (
                      <div
                        className="smart-home-automation-time-wheel-backdrop"
                        onClick={() => setIsScheduleTimePickerOpen(false)}
                      >
                        <div
                          className="smart-home-automation-time-wheel-panel"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <div className="smart-home-automation-time-wheel-toolbar">
                            <span className="smart-home-automation-time-wheel-title">選擇時間</span>
                            <button
                              type="button"
                              className="smart-home-automation-time-wheel-done"
                              onClick={() => {
                                hapticTap()
                                setIsScheduleTimePickerOpen(false)
                              }}
                            >
                              完成
                            </button>
                          </div>
                          <div className="smart-home-automation-time-wheel-columns">
                            <div className="smart-home-automation-time-wheel-col">
                              <div
                                ref={scheduleHourWheelRef}
                                className="smart-home-automation-time-wheel-list"
                                onScroll={(event) => handleScheduleWheelScroll('hour', event)}
                              >
                                {AUTOMATION_SCHEDULE_HOUR_OPTIONS.map((hour) => (
                                  <button
                                    key={hour}
                                    type="button"
                                    className={`smart-home-automation-time-wheel-item ${schedulePickerHour === hour ? 'active' : ''}`}
                                    data-wheel-value={hour}
                                    onClick={() => {
                                      hapticTap()
                                      handleScheduleWheelSelect('hour', hour)
                                    }}
                                  >
                                    {hour}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="smart-home-automation-time-wheel-separator">:</div>
                            <div className="smart-home-automation-time-wheel-col">
                              <div
                                ref={scheduleMinuteWheelRef}
                                className="smart-home-automation-time-wheel-list"
                                onScroll={(event) => handleScheduleWheelScroll('minute', event)}
                              >
                                {AUTOMATION_SCHEDULE_MINUTE_OPTIONS.map((minute) => (
                                  <button
                                    key={minute}
                                    type="button"
                                    className={`smart-home-automation-time-wheel-item ${schedulePickerMinute === minute ? 'active' : ''}`}
                                    data-wheel-value={minute}
                                    onClick={() => {
                                      hapticTap()
                                      handleScheduleWheelSelect('minute', minute)
                                    }}
                                  >
                                    {minute}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="smart-home-automation-time-wheel-highlight" aria-hidden />
                          </div>
                        </div>
                      </div>
                    )}
                    {!useIosScheduleWheelPicker && isDesktopSchedulePickerOpen && (
                      <div
                        className="smart-home-automation-time-wheel-backdrop"
                        onClick={() => setIsDesktopSchedulePickerOpen(false)}
                      >
                        <div
                          className="smart-home-automation-time-desktop-panel"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <div className="smart-home-automation-time-wheel-toolbar">
                            <span className="smart-home-automation-time-wheel-title">選擇時間</span>
                            <button
                              type="button"
                              className="smart-home-automation-time-wheel-done"
                              onClick={() => {
                                hapticTap()
                                setIsDesktopSchedulePickerOpen(false)
                              }}
                            >
                              完成
                            </button>
                          </div>
                          <div className="smart-home-automation-time-desktop-columns">
                            <div className="smart-home-automation-time-desktop-column">
                              <div className="smart-home-automation-time-desktop-column-title">時</div>
                              <div className="smart-home-automation-time-desktop-list">
                                {AUTOMATION_SCHEDULE_HOUR_OPTIONS.map((hour) => (
                                  <button
                                    key={hour}
                                    type="button"
                                    className={`smart-home-automation-time-desktop-item ${schedulePickerHour === hour ? 'active' : ''}`}
                                    onClick={() => {
                                      hapticTap()
                                      updateScheduleTimePart({ hour })
                                    }}
                                  >
                                    {hour}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="smart-home-automation-time-desktop-column">
                              <div className="smart-home-automation-time-desktop-column-title">分</div>
                              <div className="smart-home-automation-time-desktop-list">
                                {AUTOMATION_SCHEDULE_MINUTE_OPTIONS.map((minute) => (
                                  <button
                                    key={minute}
                                    type="button"
                                    className={`smart-home-automation-time-desktop-item ${schedulePickerMinute === minute ? 'active' : ''}`}
                                    onClick={() => {
                                      hapticTap()
                                      updateScheduleTimePart({ minute })
                                    }}
                                  >
                                    {minute}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="smart-home-automation-field">
                  <label>執行日</label>
                  <div className="smart-home-automation-days">
                    {AUTOMATION_WEEK_DAYS.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        className={`smart-home-automation-day-btn ${scheduleDays.includes(day.value) ? 'active' : ''}`}
                        onClick={() => toggleScheduleDay(day.value)}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="smart-home-automation-field">
                  <label>觸發設備</label>
                  <select
                    value={triggerDeviceSn}
                    onChange={(e) => setTriggerDeviceSn(e.target.value)}
                    className="smart-home-automation-input"
                    disabled={availableTriggerDevices.length === 0}
                  >
                    <option value="">
                      {triggerType === 'sensor'
                        ? (availableTriggerDevices.length === 0 ? '沒有可用感測器設備' : '請選擇感測器設備')
                        : '請選擇設備'}
                    </option>
                    {availableTriggerDevices.map((d) => {
                      const sn = deviceSn(d)
                      return (
                        <option key={sn} value={sn}>
                          {deviceName(d)}
                        </option>
                      )
                    })}
                  </select>
                </div>
                {triggerType === 'offline_notify' ? (
                  <div className="smart-home-automation-field">
                    <label>條件</label>
                    <input
                      type="text"
                      value="設備離線 (online = false)"
                      className="smart-home-automation-input"
                      disabled
                    />
                  </div>
                ) : triggerType === 'device_state' ? (
                  <div className="smart-home-automation-field">
                    <label>觸發條件</label>
                    <select
                      value={deviceStateCondition}
                      onChange={(e) => setDeviceStateCondition(e.target.value as DeviceStateTriggerCondition)}
                      className="smart-home-automation-input"
                      disabled={!triggerDeviceSn || deviceStateConditionOptions.length === 0}
                    >
                      {!triggerDeviceSn ? (
                        <option value="">請先選擇觸發設備</option>
                      ) : deviceStateConditionOptions.length === 0 ? (
                        <option value="">此設備不支援 OnOff / OpenClose / StartStop</option>
                      ) : null}
                      {deviceStateConditionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <>
                    <div className="smart-home-automation-field smart-home-automation-trigger-grid">
                      <div>
                        <label>欄位</label>
                        <select
                          value={triggerMetric}
                          onChange={(e) => setTriggerMetric(e.target.value)}
                          className="smart-home-automation-input"
                          disabled={!triggerDeviceSn || triggerMetricOptions.length === 0}
                        >
                          {!triggerDeviceSn ? (
                            <option value="">請先選擇觸發設備</option>
                          ) : triggerMetricOptions.length === 0 ? (
                            <option value="">此設備沒有可用欄位</option>
                          ) : null}
                          {triggerMetricOptions.map((metricOption) => (
                            <option key={metricOption.value} value={metricOption.value}>
                              {metricOption.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label>比較條件</label>
                        <select
                          value={triggerOperator}
                          onChange={(e) => setTriggerOperator(e.target.value as AutomationTriggerOperator)}
                          className="smart-home-automation-input"
                        >
                          <option value="eq">=</option>
                          <option value="neq">!=</option>
                          <option value="gt">&gt;</option>
                          <option value="gte">&gt;=</option>
                          <option value="lt">&lt;</option>
                          <option value="lte">&lt;=</option>
                        </select>
                      </div>
                    </div>
                    <div className="smart-home-automation-field">
                      <label>門檻值</label>
                      <select
                        value={triggerThresholdInput}
                        onChange={(e) => setTriggerThresholdInput(e.target.value)}
                        className="smart-home-automation-input"
                        disabled={!triggerDeviceSn || !triggerMetric || triggerThresholdChoices.length === 0}
                      >
                        {!triggerDeviceSn ? (
                          <option value="">請先選擇觸發設備</option>
                        ) : !triggerMetric ? (
                          <option value="">請先選擇欄位</option>
                        ) : triggerThresholdChoices.length === 0 ? (
                          <option value="">目前無可選門檻值</option>
                        ) : null}
                        {triggerThresholdChoices.map((choice) => (
                          <option key={choice.value} value={choice.value}>
                            {choice.label}
                          </option>
                        ))}
                      </select>
                      <p className="smart-home-automation-threshold-hint">
                        {triggerThresholdUi.helper}
                      </p>
                    </div>
                  </>
                )}
              </>
            )}
            {isEdit && (
              <div className="smart-home-automation-actions smart-home-automation-actions-inline smart-home-automation-test-actions">
                <button
                  type="button"
                  className="smart-home-automation-btn smart-home-automation-cancel smart-home-automation-test-btn"
                  onClick={() => {
                    void handleTestAutomation()
                  }}
                  disabled={saving || testingAutomation || !hasAnyActionSelection}
                >
                  <span className="material-symbols-outlined">play_arrow</span>
                  {testingAutomation ? '測試中...' : '測試本自動化'}
                </button>
              </div>
            )}
            </>
          )}

          {step === 2 && (
            <div className="smart-home-automation-field smart-home-automation-action-picker">
              <div className="smart-home-automation-picker-toolbar">
                <label>{`設備（已選 ${selectedActionDeviceSns.length}）`}</label>
                <button
                  type="button"
                  className="smart-home-automation-picker-collapse"
                  aria-expanded={!isDeviceSectionCollapsed}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    setIsDeviceSectionCollapsed((prev) => {
                      const nextCollapsed = !prev
                      if (!nextCollapsed) setIsSceneSectionCollapsed(true)
                      return nextCollapsed
                    })
                    requestAnimationFrame(hapticTap)
                  }}
                >
                  {isDeviceSectionCollapsed ? '展開' : '收折'}
                  <span className="material-symbols-outlined">
                    {isDeviceSectionCollapsed ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}
                  </span>
                </button>
              </div>
              <div className={`smart-home-automation-device-list ${isDeviceSectionCollapsed ? 'is-collapsed' : ''}`}>
                {actionSelectableDevices.map((d) => {
                  const sn = deviceSn(d)
                  const selected = selectedDeviceSns.includes(sn)
                  return (
                    <button
                      key={sn}
                      type="button"
                      className={`smart-home-automation-device-item glass ${selected ? 'active' : ''}`}
                      onClick={() => toggleDevice(sn)}
                    >
                      <span className="material-symbols-outlined">
                        {selected ? 'check_circle' : 'radio_button_unchecked'}
                      </span>
                      {deviceName(d)}
                    </button>
                  )
                })}
                {actionSelectableDevices.length === 0 && (
                  <p className="smart-home-empty">沒有可用的可控設備（traits 為空）</p>
                )}
              </div>
              <div className="smart-home-automation-picker-toolbar">
                <label>{`情境（已選 ${selectedSceneActionIds.length}）`}</label>
                <button
                  type="button"
                  className="smart-home-automation-picker-collapse"
                  aria-expanded={!isSceneSectionCollapsed}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    setIsSceneSectionCollapsed((prev) => {
                      const nextCollapsed = !prev
                      if (!nextCollapsed) setIsDeviceSectionCollapsed(true)
                      return nextCollapsed
                    })
                    requestAnimationFrame(hapticTap)
                  }}
                >
                  {isSceneSectionCollapsed ? '展開' : '收折'}
                  <span className="material-symbols-outlined">
                    {isSceneSectionCollapsed ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}
                  </span>
                </button>
              </div>
              <div className={`smart-home-automation-device-list ${isSceneSectionCollapsed ? 'is-collapsed' : ''}`}>
                {availableActionScenes.map((scene) => {
                  const sceneId = String(scene.sceneId || scene.id || '').trim()
                  const selected = selectedSceneActionIds.includes(sceneId)
                  return (
                    <button
                      key={sceneId}
                      type="button"
                      className={`smart-home-automation-device-item glass ${selected ? 'active' : ''}`}
                      onClick={() => toggleSceneAction(sceneId)}
                    >
                      <span className="material-symbols-outlined">
                        {selected ? 'check_circle' : 'radio_button_unchecked'}
                      </span>
                      {scene.label}
                    </button>
                  )
                })}
                {availableActionScenes.length === 0 && (
                  <p className="smart-home-empty">目前沒有可用情境</p>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <>
              <div className="smart-home-automation-field">
                <label>將執行情境</label>
                <div className="smart-home-automation-device-list">
                  {selectedSceneActionIds.map((sceneId) => (
                    <div key={sceneId} className="smart-home-automation-device-item glass active">
                      <span className="material-symbols-outlined">auto_awesome</span>
                      {actionSceneLabelById.get(sceneId) || sceneId}
                    </div>
                  ))}
                  {selectedSceneActionIds.length === 0 && (
                    <p className="smart-home-empty">未選擇情境（可只執行設備控制）</p>
                  )}
                </div>
              </div>
              <div className="smart-home-automation-field smart-home-automation-field-fill">
                <label>設定每個設備的動作（點擊設備開啟設定）</label>
                <div className="smart-home-automation-device-list">
                  {selectedActionDeviceSns.map((sn) => {
                    const d = devices.find((x) => (x.deviceSN ?? x.sn) === sn)
                    const cfg = deviceActions[sn]
                    const summary = cfg ? formatSceneDeviceActionSummary(cfg as DeviceActionConfig) : '尚未設定'
                    const state = resolveLiveDeviceStateBySN(sn)
                    const leadingIcon = resolveDeviceProductIcon(
                      (d as Device) ?? ({ sn } as Device),
                      {
                        sn,
                        attributes: state,
                        iotState: state,
                      }
                    )
                    return (
                      <button
                        key={sn}
                        type="button"
                        className="smart-home-automation-device-item smart-home-automation-device-config glass"
                        onClick={() => {
                          hapticTap()
                          setDeviceModalSn(sn)
                        }}
                      >
                        <span className="material-symbols-outlined smart-home-automation-device-config-icon">{leadingIcon}</span>
                        <span className="smart-home-automation-device-config-name">
                          {d ? deviceName(d) : sn}
                        </span>
                        <span className="smart-home-automation-device-config-summary">{summary}</span>
                      </button>
                    )
                  })}
                  {selectedActionDeviceSns.length === 0 && (
                    <p className="smart-home-empty">未選擇設備控制（可只執行情境）</p>
                  )}
                </div>
              </div>
            </>
          )}

          {err && <p className="smart-home-automation-error">{err}</p>}
        </div>

        {deviceModalSn && (() => {
          const d = devices.find((x) => (x.deviceSN ?? x.sn) === deviceModalSn)
          if (!d) return null
          const liveState = resolveLiveDeviceStateBySN(deviceModalSn)
          const initialAutomationSettings = deviceActions[deviceModalSn]
            ?? deriveAutomationInitialSettingsFromState(liveState)
          return (
            <DeviceModal
              device={d as Device}
              auth={auth}
              selectedLocationId={selectedLocationId}
              initialSettings={initialAutomationSettings}
              mode="automation"
              onSave={(settings) => {
                const normalized = normalizeSceneCommandFromModal(
                  settings.command,
                  (settings.params ?? {}) as Record<string, unknown>
                )
                setDeviceActions((prev) => ({ ...prev, [deviceModalSn]: normalized }))
                setDeviceModalSn(null)
              }}
              onClose={() => setDeviceModalSn(null)}
              hapticTap={hapticTap}
              modalConfig={resolveModalConfigForDevice(d as Device)}
              deviceState={liveState}
            />
          )
        })()}

        {step === 3 && isEdit && (
          <div className="smart-home-automation-actions smart-home-automation-actions-inline">
            <button
              type="button"
              className="smart-home-automation-btn smart-home-automation-delete"
              onClick={handleDelete}
              disabled={saving}
            >
              刪除
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function SceneEditorModal({
  auth,
  scene,
  devices,
  selectedLocationId,
  onClose,
  onSaved,
  hapticTap,
  resolveModalConfigForDevice,
  resolveDeviceStateBySN,
}: {
  auth: AuthCreds
  scene?: QuickSceneItem | null
  devices: Array<{ sn: string; name?: string; displayName?: string; nickname?: string; deviceSN?: string; traits?: string[]; deviceType?: string; type?: string }>
  selectedLocationId: string | null
  onClose: () => void
  onSaved: () => void
  hapticTap: () => void
  resolveModalConfigForDevice: (device: Device) => ModalConfig
  resolveDeviceStateBySN: (sn: string) => Record<string, unknown>
}) {
  const isEdit = Boolean(scene)
  const [step, setStep] = useState(1)
  const [name, setName] = useState(scene?.label ?? '')
  const [icon, setIcon] = useState(normalizeSceneIconName(scene?.icon ?? 'auto_awesome'))
  
  const actionSelectableDevices = useMemo(() => {
    const baseHasEndpointMap = new Map<string, boolean>()
    for (const device of devices) {
      const sn = String(device.deviceSN ?? device.sn ?? '').trim()
      if (!sn) continue
      const base = sn.split('#')[0]
      if (sn.includes('#')) {
        baseHasEndpointMap.set(base, true)
      } else if (!baseHasEndpointMap.has(base)) {
        baseHasEndpointMap.set(base, false)
      }
    }
    return devices.filter((device) => {
      const sn = String(device.deviceSN ?? device.sn ?? '').trim()
      if (!sn) return false
      const base = sn.split('#')[0]
      const endpointCompatible = sn.includes('#') || !(baseHasEndpointMap.get(base) ?? false)
      return endpointCompatible && isDeviceActionControllable(device as Device)
    })
  }, [devices])
  const actionSelectableDeviceSnSet = useMemo(
    () => new Set(actionSelectableDevices.map((device) => device.deviceSN ?? device.sn)),
    [actionSelectableDevices]
  )

  const [selectedDeviceSns, setSelectedDeviceSns] = useState<string[]>(() => {
    const fromScene = scene?.devices?.map((d) => d.sn).filter(Boolean) ?? []
    if (fromScene.length > 0) return fromScene.filter(sn => actionSelectableDeviceSnSet.has(sn))
    if (isEdit) return []
    return []
  })
  const orderedActionSelectableDevices = useMemo(() => {
    const selectedSet = new Set(selectedDeviceSns)
    return actionSelectableDevices
      .map((device, index) => ({ device, index }))
      .sort((a, b) => {
        const aSelected = selectedSet.has(a.device.deviceSN ?? a.device.sn)
        const bSelected = selectedSet.has(b.device.deviceSN ?? b.device.sn)
        if (aSelected === bSelected) return a.index - b.index
        return aSelected ? -1 : 1
      })
      .map((entry) => entry.device)
  }, [actionSelectableDevices, selectedDeviceSns])
  const [deviceActions, setDeviceActions] = useState<Record<string, DeviceActionConfig>>(() => {
    const out: Record<string, DeviceActionConfig> = {}
    for (const item of scene?.devices ?? []) {
      if (!item?.sn) continue
      out[item.sn] = normalizeSceneCommandFromModal(item.command, (item.params || {}) as Record<string, unknown>)
    }
    return out
  })
  const [deviceModalSn, setDeviceModalSn] = useState<string | null>(null)
  const [liveDeviceStatesBySn, setLiveDeviceStatesBySn] = useState<Record<string, Record<string, unknown>>>({})
  const sceneStep2ListRef = useRef<HTMLDivElement | null>(null)
  const [saving, setSaving] = useState(false)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const normalizedIcon = normalizeSceneIconName(icon)
  const sceneIconOptions = normalizedIcon && !SCENE_ICON_OPTIONS.includes(normalizedIcon)
    ? [normalizedIcon, ...SCENE_ICON_OPTIONS]
    : SCENE_ICON_OPTIONS
  const editingSceneId = String(scene?.sceneId || scene?.id || '').trim()
  const editingGroupId = String(scene?.groupId || selectedLocationId || '').trim()
  const statusQueryLocationId = useMemo(
    () => String(editingGroupId || selectedLocationId || '').trim(),
    [editingGroupId, selectedLocationId]
  )
  const resolveLiveDeviceStateBySN = useCallback(
    (sn: string): Record<string, unknown> => liveDeviceStatesBySn[sn] ?? resolveDeviceStateBySN(sn),
    [liveDeviceStatesBySn, resolveDeviceStateBySN]
  )

  const deviceName = (d: { sn: string; name?: string; displayName?: string; nickname?: string; deviceSN?: string }) =>
    d.nickname ?? d.displayName ?? d.name ?? d.sn ?? d.deviceSN ?? '—'
  const resolveSceneDeviceConfigIcon = (device: Device, sn: string): string => {
    const state = resolveDeviceStateBySN(sn)
    return resolveDeviceProductIcon(
      device,
      {
        sn,
        attributes: state,
        iotState: state,
      } as DeviceStatus
    )
  }

  useEffect(() => {
    if (!isEdit || !editingSceneId) return
    let cancelled = false
    setLoadingDetail(true)
    queryScenesWithAuth(auth, true, editingGroupId || selectedLocationId || undefined)
      .then((payload) => {
        if (cancelled) return
        const details = extractSceneDevicesFromQueryPayload(payload, editingSceneId, editingGroupId || undefined)
        if (details.length === 0) return
        const allowedSns = details
          .map((d) => d.sn)
          .filter((sn) => actionSelectableDeviceSnSet.has(sn))
        setSelectedDeviceSns(allowedSns)
        setDeviceActions((prev) => {
          const next = { ...prev }
          for (const item of details) {
            next[item.sn] = normalizeSceneCommandFromModal(
              item.command,
              (item.params || {}) as Record<string, unknown>
            )
          }
          return next
        })
      })
      .catch(() => {
        // ignore
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false)
      })
    return () => {
      cancelled = true
    }
  }, [auth, isEdit, editingSceneId, editingGroupId, selectedLocationId, actionSelectableDeviceSnSet])

  useEffect(() => {
    if (!deviceModalSn) return
    const seedState = resolveDeviceStateBySN(deviceModalSn)
    if (seedState && Object.keys(seedState).length > 0) {
      setLiveDeviceStatesBySn((prev) => ({ ...prev, [deviceModalSn]: seedState }))
    }
    if (!statusQueryLocationId) return
    let cancelled = false
    queryStatusWithAuth(auth, statusQueryLocationId, [deviceModalSn])
      .then((statuses) => {
        if (cancelled) return
        const realtimeState = pickRealtimeStateFromStatuses(statuses, deviceModalSn)
        if (!realtimeState || Object.keys(realtimeState).length === 0) return
        setLiveDeviceStatesBySn((prev) => ({ ...prev, [deviceModalSn]: realtimeState }))
      })
      .catch(() => {
        // ignore real-time refresh failures, keep existing snapshot
      })
    return () => {
      cancelled = true
    }
  }, [auth, deviceModalSn, resolveDeviceStateBySN, statusQueryLocationId])

  const getStep2ScrollContainer = (fromEl?: HTMLElement | null): HTMLElement | null => {
    const listEl = sceneStep2ListRef.current
    const sheetContentEl = (
      fromEl?.closest('.smart-home-automation-sheet-content')
      ?? listEl?.closest('.smart-home-automation-sheet-content')
    ) as HTMLElement | null
    if (sheetContentEl && sheetContentEl.scrollHeight > sheetContentEl.clientHeight + 1) {
      return sheetContentEl
    }
    if (listEl && listEl.scrollHeight > listEl.clientHeight + 1) {
      return listEl
    }
    return sheetContentEl ?? listEl ?? null
  }

  const toggleDevice = (sn: string, triggerEl?: HTMLElement | null) => {
    const scrollContainer = getStep2ScrollContainer(triggerEl)
    const scrollTopBefore = scrollContainer?.scrollTop ?? 0
    const activeEl = document.activeElement
    if (activeEl instanceof HTMLElement) {
      activeEl.blur()
    }
    if (triggerEl instanceof HTMLElement) {
      triggerEl.blur()
    }

    hapticTap()
    setSelectedDeviceSns((prev) => (
      prev.includes(sn)
        ? prev.filter((s) => s !== sn)
        : [...prev, sn]
    ))

    // Keep viewport position stable after resorting selected items to top.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const latestScrollContainer = getStep2ScrollContainer(triggerEl)
        if (!latestScrollContainer) return
        latestScrollContainer.scrollTop = scrollTopBefore
      })
    })
  }

  const buildSceneDevicePayload = (): SceneDeviceCommand[] => (
    selectedDeviceSns.map((sn) => {
      const cfg = deviceActions[sn] ?? { command: 'on' }
      const normalizedCfg = normalizeSceneCommandFromModal(
        cfg.command,
        (cfg.params ?? {}) as Record<string, unknown>
      )
      return {
        sn,
        command: normalizedCfg.command || 'on',
        ...(normalizedCfg.params && Object.keys(normalizedCfg.params).length > 0
          ? { params: normalizedCfg.params }
          : {}),
      }
    })
  )

  const [isClosing, setIsClosing] = useState(false)
  const [dragY, setDragY] = useState(0)
  const isDragging = useRef(false)
  const startY = useRef(0)

  const handleClose = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      onClose()
    }, 250)
  }, [onClose])

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null
    if (target?.closest('button, input, select, textarea, a, [role="button"]')) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    isDragging.current = true
    startY.current = e.clientY
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return
    const currentY = e.clientY
    const delta = Math.max(0, currentY - startY.current)
    setDragY(delta)
  }

  const handlePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return
    isDragging.current = false
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture?.(e.pointerId)
    }
    if (dragY > 120) {
      handleClose()
    } else {
      setDragY(0)
    }
  }

  const handleSave = async () => {
    if (!name.trim()) {
      setErr('請輸入情境名稱')
      return
    }
    if (selectedDeviceSns.length === 0) {
      setErr('請至少選擇一個設備')
      return
    }
    hapticTap()
    setSaving(true)
    setErr(null)
    try {
      const payloadDevices = buildSceneDevicePayload()
      if (!isEdit) {
        await configureSceneWithAuth(auth, {
          action: 'create',
          name: name.trim(),
          icon: icon.trim() || 'auto_awesome',
          enabled: true,
          devices: payloadDevices,
          ...(editingGroupId ? { groupId: editingGroupId } : {}),
        })
      } else {
        if (!editingSceneId) throw new Error('缺少 sceneId')
        await configureSceneWithAuth(auth, {
          action: 'update',
          id: editingSceneId,
          sceneId: editingSceneId,
          name: name.trim(),
          icon: icon.trim() || 'auto_awesome',
          devices: payloadDevices,
          ...(editingGroupId ? { groupId: editingGroupId } : {}),
        })
      }
      onSaved()
    } catch (e) {
      setErr(e instanceof Error ? e.message : '儲存失敗')
    } finally {
      setSaving(false)
    }
  }

  const handleHeaderBack = () => {
    hapticTap()
    if (step > 1) {
      setErr(null)
      setStep((prev) => Math.max(1, prev - 1))
      return
    }
    handleClose()
  }

  const handleHeaderPrimary = () => {
    if (saving) return
    hapticTap()
    if (step === 1) {
      if (!name.trim()) {
        setErr('請輸入情境名稱')
        return
      }
      setErr(null)
      setStep(2)
      return
    }
    if (step === 2) {
      if (selectedDeviceSns.length === 0) {
        setErr('請至少選擇一個設備')
        return
      }
      setErr(null)
      setStep(3)
      return
    }
    void handleSave()
  }

  const headerPrimaryLabel = step === 3
    ? (saving ? '儲存中...' : (isEdit ? '儲存' : '建立'))
    : '下一步'
  const headerPrimaryDisabled = saving
    || (step === 1 && !name.trim())
    || (step === 2 && selectedDeviceSns.length === 0)

  const handleDelete = async () => {
    if (!isEdit || !editingSceneId) return
    hapticTap()
    setSaving(true)
    setErr(null)
    try {
      await deleteSceneWithAuth(auth, editingSceneId, editingGroupId || undefined)
      onSaved()
    } catch (e) {
      setErr(e instanceof Error ? e.message : '刪除失敗')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={`smart-home-automation-overlay ${isClosing ? 'is-closing' : ''}`} onClick={handleClose}>
      <div
        className={`smart-home-automation-modal glass ${isClosing ? 'is-closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        style={isClosing ? undefined : {
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: isDragging.current ? 'none' : 'transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <div
          className="smart-home-automation-sheet-top"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <div className="smart-home-automation-sheet-handle" aria-hidden="true" />
          <div className="smart-home-automation-sheet-header">
            <button
              type="button"
              className="smart-home-automation-sheet-close"
              onClick={handleHeaderBack}
              aria-label={step === 1 ? '關閉' : '返回'}
              title={step === 1 ? '關閉' : '返回'}
            >
              <span className="material-symbols-outlined">{step === 1 ? 'close' : 'chevron_left'}</span>
            </button>
            <h2 className="smart-home-automation-title">
              {isEdit ? '編輯情境' : '建立情境'}
            </h2>
            <button
              type="button"
              className="smart-home-automation-sheet-next"
              disabled={headerPrimaryDisabled}
              onClick={handleHeaderPrimary}
            >
              {headerPrimaryLabel}
            </button>
          </div>
        </div>

        <div className="smart-home-automation-sheet-content">
          <div className="smart-home-scene-sheet-content">
            {step === 1 && (
              <section className="smart-home-scene-sheet-section">
                <div className="smart-home-scene-name-card">
                  <button
                    type="button"
                    className="smart-home-scene-name-icon active"
                    onClick={() => {
                      hapticTap()
                    }}
                    aria-label="Scene icon"
                  >
                    {renderSceneIconGlyph(icon)}
                  </button>
                  <div className="smart-home-scene-name-input-wrap">
                    <label>情境名稱</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="例如：電影模式"
                      className="smart-home-automation-input"
                    />
                  </div>
                </div>
                <div className="smart-home-scene-icon-selector smart-home-scene-icon-selector--stitch" role="listbox" aria-label="選擇情境圖示">
                  {sceneIconOptions.map((iconName, iconIndex) => {
                    const selected = normalizeSceneIconName(icon) === iconName
                    const tone = resolveSceneIconTone(iconName, iconIndex)
                    const toneStyle = {
                      '--scene-icon-bg': tone.bg,
                      '--scene-icon-border': tone.border,
                      '--scene-icon-fg': tone.fg,
                      '--scene-icon-bg-active': tone.activeBg,
                      '--scene-icon-border-active': tone.activeBorder,
                      '--scene-icon-fg-active': tone.activeFg,
                      '--scene-icon-ring': tone.ring,
                    } as CSSProperties
                    return (
                      <button
                        key={iconName}
                        type="button"
                        className={`smart-home-scene-icon-option ${selected ? 'active' : ''}`}
                        style={toneStyle}
                        onClick={() => {
                          hapticTap()
                          setIcon(normalizeSceneIconName(iconName))
                        }}
                        aria-label={`Icon ${iconName}`}
                        aria-pressed={selected}
                      >
                        {renderSceneIconGlyph(iconName)}
                      </button>
                    )
                  })}
                </div>
                {isEdit && (
                  <button
                    type="button"
                    className="smart-home-automation-btn smart-home-automation-delete smart-home-scene-delete-btn"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    刪除
                  </button>
                )}
              </section>
            )}

            {step === 2 && (
              <div className="smart-home-automation-field smart-home-automation-field-fill">
                <label>選擇配件</label>
                <div className="smart-home-automation-device-list" ref={sceneStep2ListRef}>
                  {orderedActionSelectableDevices.map((device) => {
                    const sn = device.deviceSN ?? device.sn
                    const selected = selectedDeviceSns.includes(sn)
                    return (
                      <button
                        key={sn}
                        data-scene-device-sn={sn}
                        type="button"
                        className={`smart-home-automation-device-item glass ${selected ? 'active' : ''}`}
                        onClick={(e) => toggleDevice(sn, e.currentTarget)}
                      >
                        <span className="material-symbols-outlined">
                          {selected ? 'check_circle' : 'radio_button_unchecked'}
                        </span>
                        {deviceName(device)}
                      </button>
                    )
                  })}
                  {actionSelectableDevices.length === 0 && (
                    <p className="smart-home-empty">沒有可用的可控設備（traits 為空）</p>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="smart-home-automation-field smart-home-automation-field-fill">
                <label>設定每個設備的動作（點擊設備開啟設定）</label>
                <div className="smart-home-automation-device-list">
                  {selectedDeviceSns.map((sn) => {
                    const d = devices.find((x) => (x.deviceSN ?? x.sn) === sn)
                    if (!d) return null
                    const summary = formatSceneDeviceActionSummary(deviceActions[sn])
                    const leadingIcon = resolveSceneDeviceConfigIcon(d as Device, sn)
                    return (
                      <button
                        key={sn}
                        type="button"
                        className="smart-home-automation-device-item smart-home-automation-device-config glass"
                        onClick={() => {
                          hapticTap()
                          setDeviceModalSn(sn)
                        }}
                      >
                        <span className="material-symbols-outlined smart-home-automation-device-config-icon">{leadingIcon}</span>
                        <span className="smart-home-automation-device-config-main">
                          <span className="smart-home-automation-device-config-name">
                            {deviceName(d)}
                          </span>
                          <span className="smart-home-automation-device-config-summary">{summary}</span>
                        </span>
                      </button>
                    )
                  })}
                  {selectedDeviceSns.length === 0 && (
                    <p className="smart-home-empty">請先在上一步選擇至少一個可控設備</p>
                  )}
                </div>
              </div>
            )}

            {loadingDetail && <p className="smart-home-loading">載入既有情境設定中...</p>}
            {err && <p className="smart-home-automation-error">{err}</p>}
          </div>

        </div>

        {deviceModalSn && (() => {
          const d = devices.find((x) => (x.deviceSN ?? x.sn) === deviceModalSn)
          if (!d) return null
          const liveState = resolveLiveDeviceStateBySN(deviceModalSn)
          const initialAutomationSettings = deviceActions[deviceModalSn]
            ?? deriveAutomationInitialSettingsFromState(liveState)
          return (
            <DeviceModal
              device={d as Device}
              auth={auth}
              selectedLocationId={selectedLocationId}
              initialSettings={initialAutomationSettings}
              mode="automation"
              onSave={(settings) => {
                const normalized = normalizeSceneCommandFromModal(
                  settings.command,
                  (settings.params ?? {}) as Record<string, unknown>
                )
                setDeviceActions((prev) => ({ ...prev, [deviceModalSn]: normalized }))
                setDeviceModalSn(null)
              }}
              onClose={() => setDeviceModalSn(null)}
              hapticTap={hapticTap}
              modalConfig={resolveModalConfigForDevice(d as Device)}
              deviceState={liveState}
            />
          )
        })()}
      </div>
    </div>
  )
}

function SceneOrderModal({
  scenes,
  onClose,
  onApply,
  hapticTap,
}: {
  scenes: QuickSceneItem[]
  onClose: () => void
  onApply: (orderedIds: string[]) => void
  hapticTap: () => void
}) {
  const [orderedIds, setOrderedIds] = useState<string[]>(scenes.map((s) => s.id))
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dragOverMarker, setDragOverMarker] = useState<{ id: string; position: 'before' | 'after' } | null>(null)

  // Use a ref to store scenes.map to avoid infinite render loop in effect
  const initialIds = useMemo(() => scenes.map((s) => s.id), [scenes])

  useEffect(() => {
    setOrderedIds(initialIds)
  }, [initialIds])

  const clearDragState = () => {
    setDraggingId(null)
    setDragOverMarker(null)
  }

  const moveByDrag = (fromId: string, toId: string, position: 'before' | 'after') => {
    if (fromId === toId) return
    setOrderedIds((prev) => {
      const withoutSource = prev.filter((id) => id !== fromId)
      const targetIndex = withoutSource.indexOf(toId)
      if (targetIndex < 0) return prev
      const insertIndex = position === 'before' ? targetIndex : targetIndex + 1
      const next = [...withoutSource]
      next.splice(insertIndex, 0, fromId)
      return next
    })
  }

  const handleDragStart = (id: string) => {
    setDraggingId(id)
    setDragOverMarker(null)
    hapticTap()
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>, targetId: string) => {
    if (!draggingId) return
    event.preventDefault()
    const bounds = event.currentTarget.getBoundingClientRect()
    const position = event.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after'
    setDragOverMarker((prev) => {
      if (prev && prev.id === targetId && prev.position === position) return prev
      return { id: targetId, position }
    })
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>, targetId: string) => {
    event.preventDefault()
    if (!draggingId) return
    const position = dragOverMarker?.id === targetId ? dragOverMarker.position : 'after'
    moveByDrag(draggingId, targetId, position)
    hapticTap()
    clearDragState()
  }

  const sceneById = new Map(scenes.map((s) => [s.id, s]))

  return (
    <div className="smart-home-scene-order-overlay" onClick={onClose}>
      <div className="smart-home-scene-order-modal" onClick={(e) => e.stopPropagation()}>
        <div className="smart-home-scene-order-header">
          <h3>調整情境順序</h3>
          <button
            type="button"
            className="smart-home-device-modal-back-btn"
            onClick={onClose}
            aria-label="關閉情境排序視窗"
          >
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>
        <div className="smart-home-scene-order-list">
          {orderedIds.map((id, index) => {
            const scene = sceneById.get(id)
            if (!scene) return null
            const iconName = normalizeSceneIconName(scene.icon)
            const tone = resolveSceneIconTone(iconName, index)
            const toneStyle = {
              '--scene-card-icon-bg': tone.bg,
              '--scene-card-icon-border': tone.border,
              '--scene-card-icon-fg': tone.fg,
              '--scene-card-icon-bg-active': tone.activeBg,
              '--scene-card-icon-border-active': tone.activeBorder,
              '--scene-card-icon-fg-active': tone.activeFg,
            } as CSSProperties
            const dragClassName = [
              'smart-home-scene-order-item',
              draggingId === id ? 'is-dragging' : '',
              dragOverMarker?.id === id
                ? dragOverMarker.position === 'before'
                  ? 'is-drag-over-before'
                  : 'is-drag-over-after'
                : '',
            ]
              .filter(Boolean)
              .join(' ')
            return (
              <div
                key={id}
                className={dragClassName}
                draggable
                onDragStart={() => handleDragStart(id)}
                onDragOver={(event) => handleDragOver(event, id)}
                onDrop={(event) => handleDrop(event, id)}
                onDragEnd={clearDragState}
              >
                <div className="smart-home-scene-icon" style={toneStyle}>
                  {renderSceneIconGlyph(iconName)}
                </div>
                <span className="smart-home-scene-order-name">{scene.label}</span>
                <span className="material-symbols-outlined smart-home-scene-order-handle" aria-hidden="true">
                  drag_indicator
                </span>
              </div>
            )
          })}
        </div>
        <div className="smart-home-scene-order-footer">
          <button
            type="button"
            className="smart-home-automation-btn smart-home-automation-cancel smart-home-scene-action-btn"
            onClick={onClose}
          >
            <span className="material-symbols-outlined">close</span>
            取消
          </button>
          <button
            type="button"
            className="smart-home-automation-btn smart-home-automation-primary smart-home-scene-action-btn"
            onClick={() => {
              hapticTap()
              onApply(orderedIds)
            }}
          >
            <span className="material-symbols-outlined">check</span>
            套用
          </button>
        </div>
      </div>
    </div>
  )
}

function DeviceOrderModal({
  devices,
  deviceTypeSlot1Map,
  onClose,
  onApply,
  hapticTap,
}: {
  devices: Device[]
  deviceTypeSlot1Map: Record<string, string>
  onClose: () => void
  onApply: (orderedDeviceSns: string[]) => void
  hapticTap: () => void
}) {
  const initialIds = useMemo(
    () => devices.map((device) => resolveDeviceSn(device)).filter(Boolean),
    [devices]
  )
  const [orderedIds, setOrderedIds] = useState<string[]>(initialIds)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dragOverMarker, setDragOverMarker] = useState<{ id: string; position: 'before' | 'after' } | null>(null)
  const deviceBySn = useMemo(
    () => new Map(devices.map((device) => [resolveDeviceSn(device), device])),
    [devices]
  )

  useEffect(() => {
    setOrderedIds(initialIds)
  }, [initialIds])

  const clearDragState = () => {
    setDraggingId(null)
    setDragOverMarker(null)
  }

  const moveByDrag = (fromId: string, toId: string, position: 'before' | 'after') => {
    if (fromId === toId) return
    setOrderedIds((prev) => {
      const withoutSource = prev.filter((id) => id !== fromId)
      const targetIndex = withoutSource.indexOf(toId)
      if (targetIndex < 0) return prev
      const insertIndex = position === 'before' ? targetIndex : targetIndex + 1
      const next = [...withoutSource]
      next.splice(insertIndex, 0, fromId)
      return next
    })
  }

  const handleDragStart = (id: string) => {
    setDraggingId(id)
    setDragOverMarker(null)
    hapticTap()
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>, targetId: string) => {
    if (!draggingId) return
    event.preventDefault()
    const bounds = event.currentTarget.getBoundingClientRect()
    const position = event.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after'
    setDragOverMarker((prev) => {
      if (prev && prev.id === targetId && prev.position === position) return prev
      return { id: targetId, position }
    })
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>, targetId: string) => {
    event.preventDefault()
    if (!draggingId) return
    const position = dragOverMarker?.id === targetId ? dragOverMarker.position : 'after'
    moveByDrag(draggingId, targetId, position)
    hapticTap()
    clearDragState()
  }

  const deviceName = (device: Device) =>
    device.nickname ?? device.displayName ?? device.name ?? device.deviceSN ?? device.sn ?? '—'
  const resolveDeviceOrderIconSlot1 = (device: Device): string | null => {
    const candidates = Array.from(new Set([
      ...buildDeviceTypeLookupCandidates(device.deviceType),
      ...buildDeviceTypeLookupCandidates(device.type),
    ]))
    for (const key of candidates) {
      const dataUrl = String(deviceTypeSlot1Map[key] || '').trim()
      if (dataUrl) return dataUrl
    }
    return null
  }
  const resolveDeviceOrderIcon = (device: Device): string => {
    const normalizedType = normalizeDeviceTypeLookupKey(device.deviceType ?? device.type ?? '')
    if (normalizedType === 'generic_switch' || normalizedType === 'genericswitch') return 'toggle_on'
    const typeIcon = getDeviceTypeIcon({ type: normalizedType })
    if (typeIcon && typeIcon !== 'power_settings_new') return typeIcon

    const iconName = getDeviceCardIcon(device, undefined).icon
    if (iconName === 'network_infra') return 'router'
    if (iconName === 'electrical_meter') return 'bolt'
    if (iconName === 'contact_open' || iconName === 'contact_close') return 'sensor_door'
    if (iconName === 'blinds') return 'blinds'
    if (iconName === 'sensors') return 'sensors'
    return 'devices'
  }

  return (
    <div className="smart-home-scene-order-overlay" onClick={onClose}>
      <div className="smart-home-scene-order-modal" onClick={(event) => event.stopPropagation()}>
        <div className="smart-home-scene-order-header">
          <h3>調整設備順序</h3>
          <button
            type="button"
            className="smart-home-device-modal-back-btn"
            onClick={onClose}
            aria-label="關閉設備排序視窗"
          >
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>
        <div className="smart-home-scene-order-list">
          {orderedIds.map((sn) => {
            const device = deviceBySn.get(sn)
            if (!device) return null
            const dragClassName = [
              'smart-home-scene-order-item',
              'smart-home-device-order-item',
              draggingId === sn ? 'is-dragging' : '',
              dragOverMarker?.id === sn
                ? dragOverMarker.position === 'before'
                  ? 'is-drag-over-before'
                  : 'is-drag-over-after'
                : '',
            ]
              .filter(Boolean)
              .join(' ')
            const slot1DataUrl = resolveDeviceOrderIconSlot1(device)
            return (
              <div
                key={sn}
                className={dragClassName}
                draggable
                onDragStart={() => handleDragStart(sn)}
                onDragOver={(event) => handleDragOver(event, sn)}
                onDrop={(event) => handleDrop(event, sn)}
                onDragEnd={clearDragState}
              >
                {slot1DataUrl ? (
                  <img
                    src={slot1DataUrl}
                    alt=""
                    className="smart-home-device-order-item-icon-image"
                  />
                ) : (
                  <span className="material-symbols-outlined smart-home-device-order-item-icon">
                    {resolveDeviceOrderIcon(device)}
                  </span>
                )}
                <div className="smart-home-device-order-item-main">
                  <span className="smart-home-scene-order-name">{deviceName(device)}</span>
                  <span className="smart-home-device-order-subtitle">
                    {formatDeviceTypeForDisplay(device.deviceType ?? device.type ?? 'Device')}
                  </span>
                </div>
                <span className="material-symbols-outlined smart-home-scene-order-handle" aria-hidden="true">
                  drag_indicator
                </span>
              </div>
            )
          })}
          {orderedIds.length === 0 && (
            <p className="smart-home-empty">目前沒有可排序設備</p>
          )}
        </div>
        <div className="smart-home-scene-order-footer">
          <button
            type="button"
            className="smart-home-automation-btn smart-home-automation-cancel smart-home-scene-action-btn"
            onClick={onClose}
          >
            <span className="material-symbols-outlined">close</span>
            取消
          </button>
          <button
            type="button"
            className="smart-home-automation-btn smart-home-automation-primary smart-home-scene-action-btn"
            onClick={() => {
              hapticTap()
              onApply(orderedIds)
            }}
          >
            <span className="material-symbols-outlined">check</span>
            套用
          </button>
        </div>
      </div>
    </div>
  )
}

function LightGroupOrderModal({
  groups,
  onClose,
  onApply,
  hapticTap,
}: {
  groups: LightControlGroup[]
  onClose: () => void
  onApply: (orderedGroupIds: string[]) => void
  hapticTap: () => void
}) {
  const initialIds = useMemo(() => groups.map((group) => group.id), [groups])
  const [orderedIds, setOrderedIds] = useState<string[]>(initialIds)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dragOverMarker, setDragOverMarker] = useState<{ id: string; position: 'before' | 'after' } | null>(null)
  const groupById = useMemo(
    () => new Map(groups.map((group) => [group.id, group])),
    [groups]
  )

  useEffect(() => {
    setOrderedIds(initialIds)
  }, [initialIds])

  const clearDragState = () => {
    setDraggingId(null)
    setDragOverMarker(null)
  }

  const moveByDrag = (fromId: string, toId: string, position: 'before' | 'after') => {
    if (fromId === toId) return
    setOrderedIds((prev) => {
      const withoutSource = prev.filter((id) => id !== fromId)
      const targetIndex = withoutSource.indexOf(toId)
      if (targetIndex < 0) return prev
      const insertIndex = position === 'before' ? targetIndex : targetIndex + 1
      const next = [...withoutSource]
      next.splice(insertIndex, 0, fromId)
      return next
    })
  }

  const handleDragStart = (id: string) => {
    setDraggingId(id)
    setDragOverMarker(null)
    hapticTap()
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>, targetId: string) => {
    if (!draggingId) return
    event.preventDefault()
    const bounds = event.currentTarget.getBoundingClientRect()
    const position = event.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after'
    setDragOverMarker((prev) => {
      if (prev && prev.id === targetId && prev.position === position) return prev
      return { id: targetId, position }
    })
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>, targetId: string) => {
    event.preventDefault()
    if (!draggingId) return
    const position = dragOverMarker?.id === targetId ? dragOverMarker.position : 'after'
    moveByDrag(draggingId, targetId, position)
    hapticTap()
    clearDragState()
  }

  return (
    <div className="smart-home-scene-order-overlay" onClick={onClose}>
      <div className="smart-home-scene-order-modal" onClick={(event) => event.stopPropagation()}>
        <div className="smart-home-scene-order-header">
          <h3>調整群組順序</h3>
          <button
            type="button"
            className="smart-home-device-modal-back-btn"
            onClick={onClose}
            aria-label="關閉群組排序視窗"
          >
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>
        <div className="smart-home-scene-order-list">
          {orderedIds.map((id) => {
            const group = groupById.get(id)
            if (!group) return null
            const dragClassName = [
              'smart-home-scene-order-item',
              'smart-home-device-order-item',
              draggingId === id ? 'is-dragging' : '',
              dragOverMarker?.id === id
                ? dragOverMarker.position === 'before'
                  ? 'is-drag-over-before'
                  : 'is-drag-over-after'
                : '',
            ]
              .filter(Boolean)
              .join(' ')
            return (
              <div
                key={id}
                className={dragClassName}
                draggable
                onDragStart={() => handleDragStart(id)}
                onDragOver={(event) => handleDragOver(event, id)}
                onDrop={(event) => handleDrop(event, id)}
                onDragEnd={clearDragState}
              >
                <span className="material-symbols-outlined smart-home-device-order-item-icon">
                  {group.icon || 'lightbulb'}
                </span>
                <div className="smart-home-device-order-item-main">
                  <span className="smart-home-scene-order-name">{group.name}</span>
                  <span className="smart-home-device-order-subtitle">{group.deviceSns.length} devices</span>
                </div>
                <span className="material-symbols-outlined smart-home-scene-order-handle" aria-hidden="true">
                  drag_indicator
                </span>
              </div>
            )
          })}
          {orderedIds.length === 0 && (
            <p className="smart-home-empty">目前沒有可排序群組</p>
          )}
        </div>
        <div className="smart-home-scene-order-footer">
          <button
            type="button"
            className="smart-home-automation-btn smart-home-automation-cancel smart-home-scene-action-btn"
            onClick={onClose}
          >
            <span className="material-symbols-outlined">close</span>
            取消
          </button>
          <button
            type="button"
            className="smart-home-automation-btn smart-home-automation-primary smart-home-scene-action-btn"
            onClick={() => {
              hapticTap()
              onApply(orderedIds)
            }}
          >
            <span className="material-symbols-outlined">check</span>
            套用
          </button>
        </div>
      </div>
    </div>
  )
}

function LightGroupEditorModal({
  devices,
  group,
  onClose,
  onSubmit,
  onDelete,
  hapticTap,
}: {
  devices: Device[]
  group?: LightControlGroup | null
  onClose: () => void
  onSubmit: (payload: { name: string; icon: string; deviceSns: string[] }) => void
  onDelete?: () => void
  hapticTap: () => void
}) {
  const isEdit = Boolean(group)
  const [step, setStep] = useState(1)
  const [name, setName] = useState(group?.name ?? '')
  const [icon, setIcon] = useState(group?.icon ?? (LIGHT_GROUP_ICON_OPTIONS[0] ?? 'lightbulb'))
  const [selectedDeviceSns, setSelectedDeviceSns] = useState<string[]>(() => group?.deviceSns ?? [])
  const [err, setErr] = useState<string | null>(null)
  const [isClosing, setIsClosing] = useState(false)
  const [dragY, setDragY] = useState(0)
  const isDragging = useRef(false)
  const [isDraggingState, setIsDraggingState] = useState(false)
  const startY = useRef(0)
  const step2ListRef = useRef<HTMLDivElement | null>(null)
  const deviceName = useCallback(
    (device: Device) => device.nickname ?? device.displayName ?? device.name ?? device.deviceSN ?? device.sn ?? '—',
    []
  )

  const orderedDevices = useMemo(() => {
    const selectedSet = new Set(selectedDeviceSns)
    return devices
      .map((device, index) => ({ device, index }))
      .sort((a, b) => {
        const aSelected = selectedSet.has(resolveDeviceSn(a.device))
        const bSelected = selectedSet.has(resolveDeviceSn(b.device))
        if (aSelected === bSelected) return a.index - b.index
        return aSelected ? -1 : 1
      })
      .map((entry) => entry.device)
  }, [devices, selectedDeviceSns])

  const getStep2ScrollContainer = (fromEl?: HTMLElement | null): HTMLElement | null => {
    const listEl = step2ListRef.current
    const sheetContentEl = (
      fromEl?.closest('.smart-home-automation-sheet-content')
      ?? listEl?.closest('.smart-home-automation-sheet-content')
    ) as HTMLElement | null
    if (sheetContentEl && sheetContentEl.scrollHeight > sheetContentEl.clientHeight + 1) {
      return sheetContentEl
    }
    if (listEl && listEl.scrollHeight > listEl.clientHeight + 1) {
      return listEl
    }
    return sheetContentEl ?? listEl ?? null
  }

  const toggleDevice = (sn: string, triggerEl?: HTMLElement | null) => {
    const scrollContainer = getStep2ScrollContainer(triggerEl)
    const scrollTopBefore = scrollContainer?.scrollTop ?? 0
    const activeEl = document.activeElement
    if (activeEl instanceof HTMLElement) activeEl.blur()
    if (triggerEl instanceof HTMLElement) triggerEl.blur()
    hapticTap()
    setSelectedDeviceSns((prev) => (prev.includes(sn) ? prev.filter((id) => id !== sn) : [...prev, sn]))
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const latestScrollContainer = getStep2ScrollContainer(triggerEl)
        if (!latestScrollContainer) return
        latestScrollContainer.scrollTop = scrollTopBefore
      })
    })
  }

  const handleClose = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      onClose()
    }, 250)
  }, [onClose])

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null
    if (target?.closest('button, input, select, textarea, a, [role="button"]')) return
    if (event.pointerType === 'mouse' && event.button !== 0) return
    isDragging.current = true
    setIsDraggingState(true)
    startY.current = event.clientY
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return
    const delta = Math.max(0, event.clientY - startY.current)
    setDragY(delta)
  }

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return
    isDragging.current = false
    setIsDraggingState(false)
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId)
    }
    if (dragY > 120) {
      handleClose()
    } else {
      setDragY(0)
    }
  }

  const handleHeaderBack = () => {
    hapticTap()
    if (step > 1) {
      setErr(null)
      setStep((prev) => Math.max(1, prev - 1))
      return
    }
    handleClose()
  }

  const handleSubmit = () => {
    if (!name.trim()) {
      setErr('請輸入群組名稱')
      return
    }
    if (selectedDeviceSns.length === 0) {
      setErr('請至少選擇一個燈具')
      return
    }
    hapticTap()
    onSubmit({
      name: name.trim(),
      icon: icon.trim() || 'lightbulb',
      deviceSns: selectedDeviceSns,
    })
  }

  const handleHeaderPrimary = () => {
    hapticTap()
    if (step === 1) {
      if (!name.trim()) {
        setErr('請輸入群組名稱')
        return
      }
      setErr(null)
      setStep(2)
      return
    }
    if (step === 2) {
      if (selectedDeviceSns.length === 0) {
        setErr('請至少選擇一個燈具')
        return
      }
      setErr(null)
      setStep(3)
      return
    }
    handleSubmit()
  }

  const handleDelete = () => {
    if (!isEdit || !onDelete) return
    hapticTap()
    onDelete()
  }

  const headerPrimaryLabel = step === 3 ? (isEdit ? '儲存' : '建立') : '下一步'
  const headerPrimaryDisabled = (step === 1 && !name.trim()) || (step === 2 && selectedDeviceSns.length === 0)
  const selectedDevices = selectedDeviceSns
    .map((sn) => devices.find((device) => resolveDeviceSn(device) === sn))
    .filter((device): device is Device => Boolean(device))

  return (
    <div className={`smart-home-automation-overlay ${isClosing ? 'is-closing' : ''}`} onClick={handleClose}>
      <div
        className={`smart-home-automation-modal glass ${isClosing ? 'is-closing' : ''}`}
        onClick={(event) => event.stopPropagation()}
        style={isClosing ? undefined : {
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: isDraggingState ? 'none' : 'transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <div
          className="smart-home-automation-sheet-top"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <div className="smart-home-automation-sheet-handle" aria-hidden="true" />
          <div className="smart-home-automation-sheet-header">
            <button
              type="button"
              className="smart-home-automation-sheet-close"
              onClick={handleHeaderBack}
              aria-label={step === 1 ? '關閉' : '返回'}
            >
              <span className="material-symbols-outlined">{step === 1 ? 'close' : 'chevron_left'}</span>
            </button>
            <h2 className="smart-home-automation-title">{isEdit ? '編輯控制群組' : '建立燈光群組'}</h2>
            <button
              type="button"
              className="smart-home-automation-sheet-next"
              disabled={headerPrimaryDisabled}
              onClick={handleHeaderPrimary}
            >
              {headerPrimaryLabel}
            </button>
          </div>
        </div>
        <div className="smart-home-automation-sheet-content">
          <div className="smart-home-scene-sheet-content">
            {step === 1 && (
              <section className="smart-home-scene-sheet-section">
                <div className="smart-home-scene-name-card">
                  <button
                    type="button"
                    className="smart-home-scene-name-icon active"
                    onClick={() => hapticTap()}
                    aria-label="群組圖示"
                  >
                    <span className="material-symbols-outlined">{icon}</span>
                  </button>
                  <div className="smart-home-scene-name-input-wrap">
                    <label>群組名稱</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder="例如：客廳主燈"
                      className="smart-home-automation-input"
                    />
                  </div>
                </div>
                <div className="smart-home-light-group-icon-selector" role="listbox" aria-label="選擇群組圖示">
                  {LIGHT_GROUP_ICON_OPTIONS.map((iconName) => {
                    const selected = icon === iconName
                    return (
                      <button
                        key={iconName}
                        type="button"
                        className={`smart-home-light-group-icon-option ${selected ? 'active' : ''}`}
                        onClick={() => {
                          hapticTap()
                          setIcon(iconName)
                        }}
                        aria-label={`Icon ${iconName}`}
                        aria-pressed={selected}
                      >
                        <span className="material-symbols-outlined">{iconName}</span>
                      </button>
                    )
                  })}
                </div>
                {isEdit && (
                  <button
                    type="button"
                    className="smart-home-automation-btn smart-home-automation-delete smart-home-scene-delete-btn"
                    onClick={handleDelete}
                  >
                    刪除
                  </button>
                )}
              </section>
            )}

            {step === 2 && (
              <div className="smart-home-automation-field smart-home-automation-field-fill">
                <label>選擇燈具</label>
                <div className="smart-home-automation-device-list" ref={step2ListRef}>
                  {orderedDevices.map((device) => {
                    const sn = resolveDeviceSn(device)
                    const selected = selectedDeviceSns.includes(sn)
                    return (
                      <button
                        key={sn}
                        data-light-group-device-sn={sn}
                        type="button"
                        className={`smart-home-automation-device-item glass ${selected ? 'active' : ''}`}
                        onClick={(event) => toggleDevice(sn, event.currentTarget)}
                      >
                        <span className="material-symbols-outlined">
                          {selected ? 'check_circle' : 'radio_button_unchecked'}
                        </span>
                        {deviceName(device)}
                      </button>
                    )
                  })}
                  {orderedDevices.length === 0 && (
                    <p className="smart-home-empty">目前沒有可加入群組的燈具</p>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <section className="smart-home-scene-sheet-section">
                <div className="smart-home-light-group-confirm-card">
                  <div className="smart-home-light-group-confirm-title">
                    <span className="material-symbols-outlined">{icon}</span>
                    <span>{name || '控制群組'}</span>
                  </div>
                  <p className="smart-home-light-group-confirm-subtitle">
                    共 {selectedDevices.length} 個設備
                  </p>
                  <div className="smart-home-light-group-confirm-list">
                    {selectedDevices.map((device) => (
                      <div key={resolveDeviceSn(device)} className="smart-home-light-group-confirm-item">
                        <span className="material-symbols-outlined">lightbulb</span>
                        <span>{deviceName(device)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
            {err && <p className="smart-home-automation-error">{err}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

function LightGroupControlModal({
  auth,
  group,
  allDevices,
  statusMap,
  selectedLocationId,
  readOnly = false,
  forcePowerButtonPreviewState = null,
  forcePowerButtonPreviewInstanceKey = '',
  onClose,
  hapticTap,
}: {
  auth: AuthCreds | null
  group: LightControlGroup
  allDevices: Device[]
  statusMap: Map<string, DeviceStatus>
  selectedLocationId: string | null
  readOnly?: boolean
  forcePowerButtonPreviewState?: 'on' | 'off' | null
  forcePowerButtonPreviewInstanceKey?: string
  onClose: () => void
  hapticTap: () => void
}) {
  const [listCollapsed, setListCollapsed] = useState(false)
  const [sending, setSending] = useState(false)
  const [feedback, setFeedback] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [localStateBySn, setLocalStateBySn] = useState<Record<string, Record<string, unknown>>>({})
  const localPendingOnOffBySnRef = useRef<Map<string, { desiredOn: boolean; expiresAt: number }>>(new Map())
  const [masterBrightnessDraft, setMasterBrightnessDraft] = useState(100)
  const [masterColorTempDraft, setMasterColorTempDraft] = useState(4200)

  const members = useMemo(
    () => group.deviceSns
      .map((sn) => allDevices.find((device) => resolveDeviceSn(device) === sn))
      .filter((device): device is Device => Boolean(device)),
    [allDevices, group.deviceSns]
  )

  const pruneLocalPendingOnOff = useCallback(() => {
    const now = Date.now()
    const pendingMap = localPendingOnOffBySnRef.current
    pendingMap.forEach((pending, sn) => {
      if (pending.expiresAt <= now) pendingMap.delete(sn)
    })
  }, [])

  const markLocalPendingOnOff = useCallback((sns: string[], desiredOn: boolean) => {
    if (sns.length === 0) return
    pruneLocalPendingOnOff()
    const expiresAt = Date.now() + PENDING_ONOFF_TTL_MS
    const pendingMap = localPendingOnOffBySnRef.current
    sns.forEach((sn) => {
      const normalizedSn = String(sn || '').trim()
      if (!normalizedSn) return
      pendingMap.set(normalizedSn, { desiredOn, expiresAt })
    })
  }, [pruneLocalPendingOnOff])

  const clearLocalPendingOnOff = useCallback((sns: string[]) => {
    if (sns.length === 0) return
    const pendingMap = localPendingOnOffBySnRef.current
    sns.forEach((sn) => pendingMap.delete(String(sn || '').trim()))
  }, [])

  const keepPendingOnOffFromLocal = useCallback(
    (sn: string, incoming: Record<string, unknown>, previousLocal: Record<string, unknown>): Record<string, unknown> => {
      pruneLocalPendingOnOff()
      const pending = localPendingOnOffBySnRef.current.get(sn)
      if (!pending) return incoming

      const incomingOn = extractOnOffValue(incoming)
      if (incomingOn == null) return incoming
      if (incomingOn === pending.desiredOn) {
        localPendingOnOffBySnRef.current.delete(sn)
        return incoming
      }

      const incomingWithoutOnOff = stripOnOffFields(incoming)
      const preservedOnOff = Object.fromEntries(
        Object.entries(previousLocal).filter(([key]) => NORMALIZED_ON_OFF_STATE_KEYS.has(normalizeStateKey(key)))
      )
      return { ...incomingWithoutOnOff, ...preservedOnOff }
    },
    [pruneLocalPendingOnOff]
  )

  useEffect(() => {
    setLocalStateBySn((prev) => {
      const seed: Record<string, Record<string, unknown>> = {}
      members.forEach((device) => {
        const sn = resolveDeviceSn(device)
        if (!sn) return
        const status = statusMap.get(sn)
        const attrs = (status?.attributes ?? status?.iotState ?? {}) as Record<string, unknown>
        const safeAttrs = keepPendingOnOffFromLocal(sn, attrs, prev[sn] ?? {})
        seed[sn] = {
          ...safeAttrs,
          ...(status?.online != null ? { online: status.online } : {}),
        }
      })
      return seed
    })
    setFeedback(null)
    setError(null)
  }, [group.id, keepPendingOnOffFromLocal, members, statusMap])

  useEffect(() => {
    if (!auth || !selectedLocationId) return
    const sns = members
      .map((device) => resolveDeviceSn(device))
      .map((sn) => String(sn || '').trim())
      .filter(Boolean)
    if (sns.length === 0) return

    let cancelled = false
    queryStatusWithAuth(auth, selectedLocationId, sns)
      .then((latest) => {
        if (cancelled || latest.length === 0) return
        setLocalStateBySn((prev) => {
          const next = { ...prev }
          latest.forEach((status) => {
            const sn = String(status.sn || '').trim()
            if (!sn) return
            const attrs = (status.attributes ?? status.iotState ?? {}) as Record<string, unknown>
            const safeAttrs = keepPendingOnOffFromLocal(sn, attrs, prev[sn] ?? {})
            next[sn] = {
              ...(next[sn] ?? {}),
              ...safeAttrs,
              ...(status.online != null ? { online: status.online } : {}),
            }
          })
          return next
        })
      })
      .catch(() => {
        // ignore one-shot sync failures on modal open
      })
    return () => {
      cancelled = true
    }
  }, [auth, keepPendingOnOffFromLocal, members, selectedLocationId])

  const patchLocalStates = useCallback((sns: string[], patch: Record<string, unknown>) => {
    if (sns.length === 0) return
    setLocalStateBySn((prev) => {
      const next = { ...prev }
      sns.forEach((sn) => {
        next[sn] = { ...(next[sn] ?? {}), ...patch }
      })
      return next
    })
  }, [])

  const getEffectiveState = useCallback((sn: string): Record<string, unknown> => {
    const status = statusMap.get(sn)
    const base = (status?.attributes ?? status?.iotState ?? {}) as Record<string, unknown>
    const local = localStateBySn[sn] ?? {}
    return { ...base, ...local }
  }, [localStateBySn, statusMap])

  const getOnOff = useCallback((sn: string): boolean => {
    const value = readStatusValue(getEffectiveState(sn), ['OnOff', 'on', 'powerState', 'power', 'isOn'])
    return toBooleanValue(value) === true
  }, [getEffectiveState])

  const getBrightness = useCallback((sn: string): number => {
    const value = toNumberValue(readStatusValue(getEffectiveState(sn), ['brightness', 'Brightness']))
    if (value == null || !Number.isFinite(value)) return 100
    return Math.max(0, Math.min(100, Math.round(value)))
  }, [getEffectiveState])

  const getColorTemp = useCallback((sn: string): number | null => {
    const state = getEffectiveState(sn)
    const directValue = toNumberValue(
      readStatusValue(state, [
        'colorTemperature',
        'ColorTemperature',
        'colorTemp',
        'ColorTemp',
        'color_temperature',
        'temperatureK',
        'TemperatureK',
        'colourTemperature',
        'temperatureKelvin',
        'kelvin',
        'ct',
        'temperature',
      ])
    )
    if (directValue != null && Number.isFinite(directValue)) {
      return Math.max(2700, Math.min(6500, Math.round(directValue)))
    }

    const nestedContainers = [
      readStatusValue(state, ['color', 'Color']),
      readStatusValue(state, ['colorAbsolute', 'ColorAbsolute']),
      readStatusValue(state, ['colorSetting', 'ColorSetting']),
    ]
    for (const container of nestedContainers) {
      if (!isRecord(container)) continue
      const nestedValue = toNumberValue(
        readStatusValue(container, [
          'temperature',
          'colorTemperature',
          'colorTemp',
          'color_temperature',
          'temperatureK',
          'TemperatureK',
          'temperatureKelvin',
          'kelvin',
          'ct',
        ])
      )
      if (nestedValue != null && Number.isFinite(nestedValue)) {
        return Math.max(2700, Math.min(6500, Math.round(nestedValue)))
      }
    }

    return null
  }, [getEffectiveState])

  // Rule: any device ON => group master ON
  const anyOn = members.some((device) => getOnOff(resolveDeviceSn(device)))
  const normalizedForcedInstance = String(forcePowerButtonPreviewInstanceKey || '').trim()
  const forcedPowerIsOn = forcePowerButtonPreviewState == null
    ? null
    : forcePowerButtonPreviewState === 'on'
  const forceMasterState =
    forcedPowerIsOn != null
    && (normalizedForcedInstance.length === 0 || normalizedForcedInstance === 'master')
  const effectiveAllOn = forceMasterState && forcedPowerIsOn != null
    ? forcedPowerIsOn
    : anyOn
  const avgBrightness = members.length > 0
    ? Math.round(members.reduce((sum, device) => sum + getBrightness(resolveDeviceSn(device)), 0) / members.length)
    : 100
  const colorTemps = members
    .map((device) => getColorTemp(resolveDeviceSn(device)))
    .filter((value): value is number => value != null && Number.isFinite(value))
  const avgColorTemp = colorTemps.length > 0
    ? Math.round(colorTemps.reduce((sum, value) => sum + value, 0) / colorTemps.length)
    : 4200

  useEffect(() => {
    setMasterBrightnessDraft(avgBrightness)
    setMasterColorTempDraft(avgColorTemp)
  }, [group.id, avgBrightness, avgColorTemp])

  const sendCommand = useCallback(async (
    sns: string[],
    command: string,
    params: Record<string, unknown> | undefined,
    optimisticPatch: Record<string, unknown>,
    successMessage: string
  ) => {
    if (sns.length === 0) return
    if (!auth || readOnly) return
    hapticTap()
    setSending(true)
    setError(null)
    setFeedback('指令傳送中...')
    const optimisticOn = extractOnOffValue(optimisticPatch)
    if (optimisticOn != null) {
      markLocalPendingOnOff(sns, optimisticOn)
    }
    patchLocalStates(sns, optimisticPatch)
    try {
      await executeCommandWithAuth(auth, sns, command, params)
      setFeedback(successMessage)
    } catch (commandError) {
      if (optimisticOn != null) clearLocalPendingOnOff(sns)
      setError(commandError instanceof Error ? commandError.message : '指令傳送失敗')
      setFeedback(null)
    } finally {
      setSending(false)
    }
  }, [
    auth,
    clearLocalPendingOnOff,
    hapticTap,
    markLocalPendingOnOff,
    patchLocalStates,
    readOnly,
  ])

  const groupDeviceSns = members.map((device) => resolveDeviceSn(device)).filter(Boolean)
  const renderDeviceName = (device: Device) =>
    device.nickname ?? device.displayName ?? device.name ?? device.deviceSN ?? device.sn ?? '—'

  return (
    <div
      className="smart-home-light-group-control-overlay"
      data-template-css-slot="groupModalOverlay"
      data-template-css-slot-label="Group Modal Overlay"
      onClick={onClose}
    >
      <div
        className="smart-home-light-group-control-modal"
        data-template-css-slot="groupModalPanel"
        data-template-css-slot-label="Group Modal Panel"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="smart-home-light-group-control-header"
          data-template-css-slot="groupModalHeader"
          data-template-css-slot-label="Group Modal Header"
        >
          <button
            type="button"
            className="smart-home-light-group-header-btn"
            data-template-css-slot="groupModalHeaderButton"
            data-template-css-slot-label="Group Modal Header Button"
            onClick={onClose}
            aria-label="返回"
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <h2
            data-template-css-slot="groupModalTitle"
            data-template-css-slot-label="Group Modal Title"
          >
            {group.name}
          </h2>
          <span className="smart-home-light-group-header-spacer" aria-hidden="true" />
        </div>

        <div
          className="smart-home-light-group-control-content no-scrollbar"
          data-template-css-slot="groupModalContent"
          data-template-css-slot-label="Group Modal Content"
        >
          <section
            className="smart-home-light-group-master-card"
            data-template-css-slot="groupModalMasterCard"
            data-template-css-slot-label="Group Modal Master Card"
          >
            <div className="smart-home-light-group-master-row">
              <div>
                <h3
                  data-template-css-slot="groupModalMasterTitle"
                  data-template-css-slot-label="Group Modal Master Title"
                >
                  Master Control
                </h3>
                <p
                  data-template-css-slot="groupModalMasterSubtitle"
                  data-template-css-slot-label="Group Modal Master Subtitle"
                >
                  {members.length} devices connected
                </p>
              </div>
              <button
                type="button"
                className={`smart-home-light-group-power-btn smart-home-light-group-power-btn-master ${effectiveAllOn ? 'is-on' : 'is-off'}`}
                data-template-css-slot="groupModalPowerButton"
                data-template-css-slot-label="Group Modal Power Button"
                data-template-css-instance="master"
                data-template-css-state={effectiveAllOn ? 'on' : 'off'}
                disabled={sending || readOnly || groupDeviceSns.length === 0}
                aria-label={effectiveAllOn ? 'Turn group off' : 'Turn group on'}
                onClick={() => {
                  const nextOn = !effectiveAllOn
                  void sendCommand(
                    groupDeviceSns,
                    nextOn ? 'on' : 'off',
                    { on: nextOn },
                    { OnOff: nextOn, on: nextOn, isOn: nextOn },
                    nextOn ? '已開啟群組燈具' : '已關閉群組燈具'
                  )
                }}
              >
                <span className="material-symbols-outlined">lightbulb</span>
              </button>
            </div>

            <div
              className="smart-home-light-group-master-control"
              data-template-css-slot="groupModalControlRow"
              data-template-css-slot-label="Group Modal Control Row"
            >
              <div className="smart-home-light-group-master-label-row">
                <span className="smart-home-light-group-master-label">
                  <span className="material-symbols-outlined">brightness_medium</span>
                  Brightness
                </span>
                <strong>{masterBrightnessDraft}%</strong>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={masterBrightnessDraft}
                disabled={sending || readOnly || groupDeviceSns.length === 0}
                onChange={(event) => {
                  const next = Number(event.target.value)
                  setMasterBrightnessDraft(next)
                  patchLocalStates(groupDeviceSns, { brightness: next, Brightness: next })
                }}
                onPointerUp={() => {
                  void sendCommand(
                    groupDeviceSns,
                    'setBrightness',
                    { brightness: masterBrightnessDraft },
                    { brightness: masterBrightnessDraft, Brightness: masterBrightnessDraft },
                    '已更新群組亮度'
                  )
                }}
              />
            </div>

            <div
              className="smart-home-light-group-master-control"
              data-template-css-slot="groupModalControlRow"
              data-template-css-slot-label="Group Modal Control Row"
            >
              <div className="smart-home-light-group-master-label-row">
                <span className="smart-home-light-group-master-label">
                  <span className="material-symbols-outlined">thermostat</span>
                  Temperature
                </span>
                <strong>{masterColorTempDraft}K</strong>
              </div>
              <input
                type="range"
                min={2700}
                max={6500}
                step={50}
                value={masterColorTempDraft}
                disabled={sending || readOnly || groupDeviceSns.length === 0}
                onChange={(event) => {
                  const next = Number(event.target.value)
                  setMasterColorTempDraft(next)
                  patchLocalStates(groupDeviceSns, {
                    colorTemperature: next,
                    colorTemp: next,
                    color_temperature: next,
                    temperature: next,
                  })
                }}
                onPointerUp={() => {
                  void sendCommand(
                    groupDeviceSns,
                    'setColorTemperature',
                    { temperature: masterColorTempDraft, colorTemp: masterColorTempDraft },
                    {
                      colorTemperature: masterColorTempDraft,
                      colorTemp: masterColorTempDraft,
                      color_temperature: masterColorTempDraft,
                      temperature: masterColorTempDraft,
                    },
                    '已更新群組色溫'
                  )
                }}
              />
            </div>
          </section>

          <div
            className="smart-home-light-group-list-header"
            data-template-css-slot="groupModalListHeader"
            data-template-css-slot-label="Group Modal List Header"
          >
            <h4
              data-template-css-slot="groupModalListTitle"
              data-template-css-slot-label="Group Modal List Title"
            >
              Devices in Group ({members.length})
            </h4>
            <button
              type="button"
              className="smart-home-light-group-collapse-btn"
              data-template-css-slot="groupModalCollapseButton"
              data-template-css-slot-label="Group Modal Collapse Button"
              data-template-css-state={listCollapsed ? 'off' : 'on'}
              onClick={() => {
                hapticTap()
                setListCollapsed((prev) => !prev)
              }}
              aria-label={listCollapsed ? '展開設備列表' : '收合設備列表'}
            >
              <span className="material-symbols-outlined">
                {listCollapsed ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}
              </span>
            </button>
          </div>

          {!listCollapsed && (
            <div
              className="smart-home-light-group-device-list"
              data-template-css-slot="groupModalDeviceList"
              data-template-css-slot-label="Group Modal Device List"
            >
              {members.map((device) => {
                const sn = resolveDeviceSn(device)
                const on = getOnOff(sn)
                const instanceKey = `device:${sn}`
                const forceThisDevice =
                  forcedPowerIsOn != null
                  && (normalizedForcedInstance.length === 0 || normalizedForcedInstance === instanceKey)
                const effectiveOn = forceThisDevice && forcedPowerIsOn != null
                  ? forcedPowerIsOn
                  : on
                const brightness = getBrightness(sn)
                const online = toBooleanValue(readStatusValue(getEffectiveState(sn), ['online'])) ?? true
                const typeLabel = formatDeviceTypeForDisplay(device.deviceType ?? device.type ?? 'Light')
                return (
                  <div
                    key={sn}
                    className={`smart-home-light-group-device-item ${online ? '' : 'is-offline'}`}
                    data-template-css-slot="groupModalDeviceCard"
                    data-template-css-slot-label="Group Modal Device Card"
                  >
                    <div className="smart-home-light-group-device-main">
                      <div className="smart-home-light-group-device-meta">
                        <div
                          className="smart-home-light-group-device-icon"
                          data-template-css-slot="groupModalDeviceIcon"
                          data-template-css-slot-label="Group Modal Device Icon"
                        >
                          <span className="material-symbols-outlined">lightbulb</span>
                        </div>
                        <div>
                          <p
                            className="smart-home-light-group-device-title"
                            data-template-css-slot="groupModalDeviceTitle"
                            data-template-css-slot-label="Group Modal Device Title"
                          >
                            {renderDeviceName(device)}
                          </p>
                          <p
                            className="smart-home-light-group-device-subtitle"
                            data-template-css-slot="groupModalDeviceSubtitle"
                            data-template-css-slot-label="Group Modal Device Subtitle"
                          >
                            {typeLabel}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`smart-home-light-group-power-btn smart-home-light-group-power-btn-compact ${effectiveOn ? 'is-on' : 'is-off'}`}
                        data-template-css-slot="groupModalPowerButton"
                        data-template-css-slot-label="Group Modal Power Button"
                        data-template-css-instance={instanceKey}
                        data-template-css-state={effectiveOn ? 'on' : 'off'}
                        disabled={sending || readOnly}
                        aria-label={effectiveOn ? 'Turn device off' : 'Turn device on'}
                        onClick={() => {
                          const nextOn = !effectiveOn
                          void sendCommand(
                            [sn],
                            nextOn ? 'on' : 'off',
                            { on: nextOn },
                            { OnOff: nextOn, on: nextOn, isOn: nextOn },
                            nextOn ? '已開啟燈具' : '已關閉燈具'
                          )
                        }}
                      >
                        <span className="material-symbols-outlined">power_settings_new</span>
                      </button>
                    </div>

                    <div
                      className="smart-home-light-group-device-brightness"
                      data-template-css-slot="groupModalDeviceBrightness"
                      data-template-css-slot-label="Group Modal Device Brightness"
                    >
                      <span className="material-symbols-outlined">sunny</span>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={brightness}
                        disabled={sending || readOnly}
                        onChange={(event) => {
                          const next = Number(event.target.value)
                          patchLocalStates([sn], { brightness: next, Brightness: next })
                        }}
                        onPointerUp={() => {
                          void sendCommand(
                            [sn],
                            'setBrightness',
                            { brightness },
                            { brightness, Brightness: brightness },
                            '已更新設備亮度'
                          )
                        }}
                      />
                      <span>{brightness}%</span>
                    </div>
                  </div>
                )
              })}
              {members.length === 0 && (
                <p
                  className="smart-home-empty"
                  data-template-css-slot="groupModalEmpty"
                  data-template-css-slot-label="Group Modal Empty"
                >
                  此群組目前沒有可控制燈具
                </p>
              )}
            </div>
          )}

          {feedback && (
            <p
              className="smart-home-scene-feedback"
              data-template-css-slot="groupModalFeedback"
              data-template-css-slot-label="Group Modal Feedback"
            >
              {feedback}
            </p>
          )}
          {error && (
            <p
              className="smart-home-automation-error"
              data-template-css-slot="groupModalError"
              data-template-css-slot-label="Group Modal Error"
            >
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export interface SmartHomeDashboardTemplateProps {
  /** When true (AppFactory preview), use credentials from user profile, no auth modal */
  isAppFactoryPreview?: boolean
  /** Override auth for public QR preview links */
  authOverride?: AuthCreds | null
  /** Shared preview mode tag for badges/logic */
  previewMode?: 'api_key_preview' | 'public_demo' | 'oauth_preview'
  /** Force mock data and disable real command execution */
  forceMockData?: boolean
  /** Host (TemplateDetail iframe parent) 注入的 modal overrides，避免 preview 端讀取不同步 */
  modalOverridesFromHost?: Record<string, DeviceTypeModalOverrideForContext>
  /** Host 注入的 App Template Editor mode（live/edit） */
  templateEditorMode?: boolean
  /** Editor 測試用：強制群控 icon 顯示 on/off，不依賴真實設備狀態 */
  groupIconPreviewState?: 'live' | 'on' | 'off'
  /** Host 注入的目前編輯 slot key（給 preview 強制顯示狀態用） */
  editorSelectedSlotKey?: string
  /** Host 注入的目前編輯 state（給 preview 強制顯示狀態用） */
  editorSelectedState?: TemplateCssState | string
  /** Host 注入的目前編輯 slot instance（給 preview 精準定位單一元件） */
  editorSelectedSlotInstanceKey?: string
  /** Host 注入的 App Template slot CSS overrides */
  templateItemCssOverrides?: Record<string, string>
  /** Host 注入的 App Template slot Framer preset overrides */
  templateItemFramerPresetOverrides?: Record<string, FramePreset>
  /** Host 注入的 Framer preset 變數覆蓋（來自 Themes > Framer） */
  framerPresetOverrides?: FramerPresetOverrides
  /** Host 注入的 custom CSS（用於跨 origin 場景如 marketplace preview，Firestore 不可用時） */
  customCssFromHost?: string
  sections?: {
    credentials?: { appId?: string; apiKey?: string }
    header?: {
      avatarImage?: string
      weatherLabel?: string
      weatherValue?: string
      weatherIcon?: string
      notificationDot?: boolean
    }
    quickScenes?: { id: string; label: string; icon: string; active?: boolean }[]
    bottomNav?: { id: string; label: string; icon: string; active?: boolean }[]
    headerBrand?: string
    login?: { styleId?: string }
    modal?: {
      deviceTypes?: Record<string, ModalConfig>
      defaultConfig?: ModalConfig
    }
    card?: {
      deviceTypes?: Record<string, {
        enabledAttributes?: string[]
        enabledStates?: string[]
        deviceTypeImage?: string
        cardMediaImage?: string
      }>
      defaultConfig?: {
        enabledAttributes?: string[]
        enabledStates?: string[]
        deviceTypeImage?: string
        cardMediaImage?: string
      }
    }
    editor?: {
      templateItemCss?: Record<string, string>
      templateItemFramerPreset?: Record<string, FramePreset>
    }
  }
  /** Store 連結，品牌商可在 AppSuite 填入自己的網址，預設 https://store.ultronsmart.com */
  storeLink?: string
  tokens?: typeof DEFAULT_TOKENS
}

export function SmartHomeDashboardTemplate({
  sections,
  storeLink = DEFAULT_STORE_LINK,
  tokens = DEFAULT_TOKENS,
  isAppFactoryPreview = false,
  authOverride = null,
  forceMockData = false,
  modalOverridesFromHost,
  templateEditorMode = false,
  groupIconPreviewState = 'live',
  editorSelectedSlotKey = '',
  editorSelectedState = 'on',
  editorSelectedSlotInstanceKey = '',
  templateItemCssOverrides,
  templateItemFramerPresetOverrides,
  framerPresetOverrides,
  customCssFromHost,
}: SmartHomeDashboardTemplateProps) {
  const colors = tokens.colors ?? DEFAULT_TOKENS.colors
  const spacing = tokens.spacing ?? DEFAULT_TOKENS.spacing
  const isPublicDemo = Boolean(forceMockData)
  const shouldForceGroupIconPreviewState = templateEditorMode && (groupIconPreviewState === 'on' || groupIconPreviewState === 'off')
  const normalizedEditorSelectedSlotToken = String(editorSelectedSlotKey || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
  const normalizedEditorSelectedState: TemplateCssState =
    editorSelectedState === 'off'
    || editorSelectedState === 'selected'
    || editorSelectedState === 'unselected'
      ? editorSelectedState
      : 'on'
  const forceGroupModalPowerButtonState: 'on' | 'off' | null =
    templateEditorMode && normalizedEditorSelectedSlotToken === 'groupmodalpowerbutton'
      ? (normalizedEditorSelectedState === 'off' ? 'off' : 'on')
      : null
  const forceGroupModalPowerButtonInstanceKey =
    templateEditorMode && normalizedEditorSelectedSlotToken === 'groupmodalpowerbutton'
      ? String(editorSelectedSlotInstanceKey || '').trim()
      : ''

  const [auth, setAuth] = useState<AuthCreds | null>(() => {
    if (isPublicDemo) return null
    if (isValidAuthOverride(authOverride)) {
      return authOverride
    }
    const stored = loadStoredAuth()
    return stored ? { apiKey: stored.apiKey, appId: stored.appId } : null
  })

  useEffect(() => {
    if (isPublicDemo) {
      setAuth(null)
      return
    }
    if (isValidAuthOverride(authOverride)) {
      setAuth(authOverride)
      return
    }
    if (isAppFactoryPreview) {
      getCredentialFromUserProfile().then((c) => {
        if (c?.appId && c?.apiKey) setAuth({ apiKey: c.apiKey, appId: c.appId })
      })
    }
  }, [isAppFactoryPreview, authOverride, isPublicDemo])

  useEffect(() => {
    if (isAppFactoryPreview || isPublicDemo) return
    const hash = window.location.hash?.slice(1)
    if (!hash) return
    const params = new URLSearchParams(hash)
    const accessToken = params.get('access_token')
    const appId = params.get('app_id')
    if (accessToken && appId) {
      setAuth({ accessToken, appId })
      try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
      } catch (_e) {
        // ignore
      }
    }
  }, [isAppFactoryPreview, isPublicDemo])

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'appfactory-credentials-cleared') setAuth(null)
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [])
  const [credentialLoading, setCredentialLoading] = useState(false)

  const handlePreviewLogin = useCallback(async () => {
    if (
      isPublicDemo ||
      !isAppFactoryPreview ||
      isValidAuthOverride(authOverride)
    ) {
      return
    }
    setCredentialLoading(true)
    try {
      const c = await getCredentialFromUserProfile()
      if (c?.appId && c?.apiKey) {
        setAuth(c)
      }
    } finally {
      setCredentialLoading(false)
    }
  }, [isAppFactoryPreview, authOverride, isPublicDemo])
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false)
  const [activeNav, setActiveNav] = useState<'home' | 'auto' | 'store'>('home')
  const accountDropdownRef = useRef<HTMLDivElement>(null)
  const [locations, setLocations] = useState<Location[]>([])
  const [devices, setDevices] = useState<Device[]>([])
  const [deviceStatuses, setDeviceStatuses] = useState<DeviceStatus[]>([])
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null)
  const [dataLoading, setDataLoading] = useState(false)
  const [shadowRefreshing, setShadowRefreshing] = useState(false)
  const [pullRefreshDistance, setPullRefreshDistance] = useState(0)
  const [pullRefreshDesktopTracking, setPullRefreshDesktopTracking] = useState(false)
  const [dataError, setDataError] = useState<string | null>(null)
  const [homeDeviceModal, setHomeDeviceModal] = useState<Device | null>(null)
  const lastNavRef = useRef<'home' | 'auto' | 'store'>('home')
  const devicesRef = useRef<Device[]>([])
  const wsRef = useRef<Socket | null>(null)
  const wsAuthedRef = useRef(false)
  const wsSubscribedSnsRef = useRef<Set<string>>(new Set())
  const [wsReady, setWsReady] = useState(false)
  const [realtimeNotificationDot, setRealtimeNotificationDot] = useState(false)
  const pendingOnOffBySnRef = useRef<Map<string, { desiredOn: boolean; expiresAt: number }>>(new Map())
  const pullRefreshStartYRef = useRef<number | null>(null)
  const pullRefreshActiveRef = useRef(false)
  const pullRefreshReadyRef = useRef(false)

  const pruneExpiredPendingOnOff = useCallback(() => {
    const now = Date.now()
    const pendingMap = pendingOnOffBySnRef.current
    pendingMap.forEach((pending, sn) => {
      if (pending.expiresAt <= now) pendingMap.delete(sn)
    })
  }, [])

  const markPendingOnOff = useCallback((sns: string[], desiredOn: boolean) => {
    if (sns.length === 0) return
    pruneExpiredPendingOnOff()
    const expiresAt = Date.now() + PENDING_ONOFF_TTL_MS
    const pendingMap = pendingOnOffBySnRef.current
    sns.forEach((sn) => {
      const normalizedSn = String(sn || '').trim()
      if (!normalizedSn) return
      pendingMap.set(normalizedSn, { desiredOn, expiresAt })
    })
  }, [pruneExpiredPendingOnOff])

  const clearPendingOnOff = useCallback((sns: string[]) => {
    if (sns.length === 0) return
    const pendingMap = pendingOnOffBySnRef.current
    sns.forEach((sn) => pendingMap.delete(String(sn || '').trim()))
  }, [])

  const sanitizeRealtimePatchForPending = useCallback((sn: string, patch: Record<string, unknown>) => {
    pruneExpiredPendingOnOff()
    const pending = pendingOnOffBySnRef.current.get(sn)
    if (!pending) return patch

    const incomingOn = extractOnOffValue(patch)
    if (incomingOn == null) return patch
    if (incomingOn === pending.desiredOn) {
      pendingOnOffBySnRef.current.delete(sn)
      return patch
    }

    const stripped = stripOnOffFields(patch)
    if (Object.keys(stripped).length === 0) return null
    return stripped
  }, [pruneExpiredPendingOnOff])

  const resolvePendingSnsForRealtimePayload = useCallback((payload: unknown): string[] => {
    pruneExpiredPendingOnOff()
    const candidates = collectRealtimeSnCandidates(payload)
    if (candidates.length === 0) return []
    const pendingSns = Array.from(pendingOnOffBySnRef.current.keys())
    if (pendingSns.length === 0) return []
    const candidateBases = new Set(
      candidates
        .map((candidate) => candidate.split('#')[0])
        .map((base) => String(base || '').trim())
        .filter(Boolean)
    )
    if (candidateBases.size === 0) return []
    return pendingSns.filter((sn) => candidateBases.has(String(sn || '').split('#')[0]))
  }, [pruneExpiredPendingOnOff])

  useEffect(() => {
    devicesRef.current = devices
  }, [devices])

  const getDashboardScrollContainers = useCallback(() => {
    if (typeof document === 'undefined') return [] as Array<{ selector: (typeof DASHBOARD_SCROLL_CONTAINER_SELECTORS)[number]; element: HTMLElement }>
    return DASHBOARD_SCROLL_CONTAINER_SELECTORS
      .map((selector) => ({ selector, element: document.querySelector<HTMLElement>(selector) }))
      .filter((item): item is { selector: (typeof DASHBOARD_SCROLL_CONTAINER_SELECTORS)[number]; element: HTMLElement } => Boolean(item.element))
  }, [])

  const captureDashboardScrollSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    const containerTops: DashboardScrollSnapshot['containerTops'] = {}
    for (const { selector, element } of getDashboardScrollContainers()) {
      containerTops[selector] = element.scrollTop || 0
    }
    pendingDashboardScrollSnapshot = {
      nav: activeNav,
      containerTops,
      rootTop: document.scrollingElement?.scrollTop ?? 0,
      winTop: window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0,
    }
  }, [activeNav, getDashboardScrollContainers])

  const restoreDashboardScrollSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return false
    const snapshot = pendingDashboardScrollSnapshot
    if (!snapshot || snapshot.nav !== activeNav) return false
    const applyRestore = () => {
      for (const { selector, element } of getDashboardScrollContainers()) {
        const nextTop = snapshot.containerTops[selector]
        if (typeof nextTop === 'number') element.scrollTop = nextTop
      }
      if (document.scrollingElement) document.scrollingElement.scrollTop = snapshot.rootTop
      window.scrollTo(0, snapshot.winTop)
    }
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(applyRestore)
    })
    pendingDashboardScrollSnapshot = null
    return true
  }, [activeNav, getDashboardScrollContainers])

  const resetDashboardScrollTop = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    pendingDashboardScrollSnapshot = null
    for (const { element } of getDashboardScrollContainers()) {
      element.scrollTop = 0
    }
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0
    window.scrollTo(0, 0)
  }, [getDashboardScrollContainers])

  const isDashboardAtTop = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return true
    const rootTop = document.scrollingElement?.scrollTop ?? 0
    const winTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0
    const containerTop = getDashboardScrollContainers().reduce((max, item) => Math.max(max, item.element.scrollTop || 0), 0)
    return Math.max(rootTop, winTop, containerTop) <= 2
  }, [getDashboardScrollContainers])

  const resetPullRefreshGesture = useCallback(() => {
    pullRefreshStartYRef.current = null
    pullRefreshActiveRef.current = false
    pullRefreshReadyRef.current = false
    setPullRefreshDesktopTracking(false)
    setPullRefreshDistance(0)
  }, [])

  // 首次進入 App：Home 從起點開始
  useEffect(() => {
    if (hasInitializedDashboardEntry) return
    hasInitializedDashboardEntry = true
    resetDashboardScrollTop()
  }, [resetDashboardScrollTop])

  // 切換 Tab（Home / Auto / Store）時：每個分頁都回到起點
  useEffect(() => {
    if (lastNavRef.current === activeNav) return
    lastNavRef.current = activeNav
    resetDashboardScrollTop()
  }, [activeNav, resetDashboardScrollTop])

  // 關閉 device modal 後：恢復進入 modal 前的 dashboard scroll 位置
  useEffect(() => {
    if (homeDeviceModal) return
    restoreDashboardScrollSnapshot()
  }, [homeDeviceModal, restoreDashboardScrollSnapshot])

  /** 預覽時：modal 開啟/關閉時通知 parent，讓預覽區可全螢幕（移除 phone frame） */
  useEffect(() => {
    if (!isAppFactoryPreview || typeof window === 'undefined') return
    try {
      window.parent.postMessage(
        { type: homeDeviceModal ? 'appfactory-device-modal-open' : 'appfactory-device-modal-closed' },
        '*'
      )
    } catch (_e) {
      // ignore
    }
    return () => {
      try { window.parent.postMessage({ type: 'appfactory-device-modal-closed' }, '*') } catch (_e) { /* ignore */ }
    }
  }, [isAppFactoryPreview, homeDeviceModal])

  const [modalOverrides, setModalOverrides] = useState<Record<string, DeviceTypeModalOverrideForContext>>(
    modalOverridesFromHost ?? {}
  )
  useEffect(() => {
    if (!modalOverridesFromHost) return
    setModalOverrides(modalOverridesFromHost)
  }, [modalOverridesFromHost])
  const reloadModalOverrides = useCallback(() => {
    if (modalOverridesFromHost) {
      setModalOverrides(modalOverridesFromHost)
      return
    }
    listDeviceTypeModalOverrides()
      .then(setModalOverrides)
      .catch(() => setModalOverrides({}))
  }, [modalOverridesFromHost])
  useEffect(() => {
    if (modalOverridesFromHost) return
    reloadModalOverrides()
    window.addEventListener('appfactory-templates-refresh', reloadModalOverrides)
    const unsubscribe =
      firebaseAuth
        ? onAuthStateChanged(firebaseAuth, () => {
            // preview-embed 可能先於 auth 初始化，auth ready 後重抓一次 override
            reloadModalOverrides()
          })
        : () => {}
    return () => {
      window.removeEventListener('appfactory-templates-refresh', reloadModalOverrides)
      unsubscribe()
    }
  }, [reloadModalOverrides, modalOverridesFromHost])
  const [customCss, setCustomCss] = useState(customCssFromHost || '')
  const reloadCustomCss = useCallback(() => {
    if (customCssFromHost != null) return // Host 提供時不從 Firestore 載入
    getCustomCss()
      .then((css) => setCustomCss(css || ''))
      .catch(() => setCustomCss(''))
  }, [customCssFromHost])
  useEffect(() => {
    if (customCssFromHost != null) {
      setCustomCss(customCssFromHost)
      return
    }
    reloadCustomCss()
    window.addEventListener('appfactory-templates-refresh', reloadCustomCss)
    const unsubscribe =
      firebaseAuth
        ? onAuthStateChanged(firebaseAuth, () => {
            reloadCustomCss()
          })
        : () => {}
    return () => {
      window.removeEventListener('appfactory-templates-refresh', reloadCustomCss)
      unsubscribe()
    }
  }, [reloadCustomCss, customCssFromHost])

  // Design Tokens (Phase 5) - load from Firestore, generate CSS variables
  const [designTokens, setDesignTokens] = useState<DesignTokens>({ colors: {} })
  const reloadDesignTokens = useCallback(() => {
    getDesignTokens()
      .then((t) => setDesignTokens(t ?? { colors: {} }))
      .catch(() => {})
  }, [])
  useEffect(() => {
    reloadDesignTokens()
    window.addEventListener('appfactory-templates-refresh', reloadDesignTokens)
    return () => {
      window.removeEventListener('appfactory-templates-refresh', reloadDesignTokens)
    }
  }, [reloadDesignTokens])
  const designTokenCssVars = useMemo(() => {
    const vars: string[] = []
    for (const [name, value] of Object.entries(designTokens.colors || {})) {
      vars.push(`  --token-${name}: ${value};`)
    }
    for (const [name, value] of Object.entries(designTokens.radius || {})) {
      vars.push(`  --token-radius-${name}: ${value};`)
    }
    for (const [name, value] of Object.entries(designTokens.spacing || {})) {
      vars.push(`  --token-spacing-${name}: ${value};`)
    }
    return vars.length > 0 ? `:root {\n${vars.join('\n')}\n}` : ''
  }, [designTokens])

  const [deviceTypeConfigs, setDeviceTypeConfigs] = useState<
    Record<
      string,
      {
        enabledTraits: string[]
        enabledStates: string[]
        stateDisplayOverrides?: Record<string, { icon?: string; label?: string; unit?: string }>
      }
    >
  >({})
  const [deviceTypeSlot1Map, setDeviceTypeSlot1Map] = useState<Record<string, string>>({})

  useEffect(() => {
    const types = [
      ...new Set(
        devices
          .map((d) => resolveModalTemplateDeviceType(d.deviceType ?? d.type ?? '', d.traits as Array<string | unknown>))
          .filter(Boolean)
      ),
    ]
    if (types.length === 0) return
    let cancelled = false
    Promise.all(types.map((t) => loadDeviceTypeTemplateConfig(t)))
      .then((configs) => {
        if (cancelled) return
        const map: Record<
          string,
          {
            enabledTraits: string[]
            enabledStates: string[]
            stateDisplayOverrides?: Record<string, { icon?: string; label?: string; unit?: string }>
          }
        > = {}
        types.forEach((t, i) => {
          const c = configs[i]
          const hasStateDisplayOverrides =
            Boolean(c?.stateDisplayOverrides) && Object.keys(c?.stateDisplayOverrides || {}).length > 0
          if (c?.enabledTraits?.length || c?.enabledStates?.length || hasStateDisplayOverrides) {
            map[t] = {
              enabledTraits: c.enabledTraits ?? [],
              enabledStates: c.enabledStates ?? [],
              stateDisplayOverrides: c.stateDisplayOverrides ?? {},
            }
          }
        })
        setDeviceTypeConfigs(map)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [devices])

  useEffect(() => {
    let cancelled = false
    listDeviceTypesMeta()
      .then((metaList) => {
        if (cancelled) return
        const slotByType: Record<string, string> = {}
        const resolvedByType: Record<string, string> = {}
        metaList.forEach((meta) => {
          const typeCandidates = buildDeviceTypeLookupCandidates(meta.deviceType)
          if (typeCandidates.length === 0) return

          const slot1 = String(meta.iconSlots?.find((slot) => slot.id === 'slot1')?.dataUrl || '').trim()
          if (slot1) {
            typeCandidates.forEach((candidate) => {
              slotByType[candidate] = slot1
            })
          }

          const resolved = normalizeDeviceTypeLookupKey(meta.resolvedDeviceTypeV1)
          if (resolved) {
            typeCandidates.forEach((candidate) => {
              resolvedByType[candidate] = resolved
            })
          }
        })

        Object.entries(resolvedByType).forEach(([typeKey, resolvedTypeKey]) => {
          if (!slotByType[typeKey] && slotByType[resolvedTypeKey]) {
            slotByType[typeKey] = slotByType[resolvedTypeKey]
          }
        })
        setDeviceTypeSlot1Map(slotByType)
      })
      .catch(() => {
        if (cancelled) return
        setDeviceTypeSlot1Map({})
      })
    return () => {
      cancelled = true
    }
  }, [])

  const fallbackScenes = sections?.quickScenes ?? DEFAULT_QUICK_SCENES
  const [sceneItems, setSceneItems] = useState<QuickSceneItem[]>(() =>
    fallbackScenes.map((scene) => ({
      id: scene.id,
      sceneId: scene.id,
      label: scene.label,
      icon: normalizeSceneIconName(scene.icon),
      active: scene.active,
      devices: [],
    }))
  )
  const [showSceneOrderModal, setShowSceneOrderModal] = useState(false)
  const [showSceneEditorModal, setShowSceneEditorModal] = useState(false)
  const [sceneEditorTarget, setSceneEditorTarget] = useState<QuickSceneItem | null>(null)
  const [sceneEditorMode, setSceneEditorMode] = useState<'create' | 'edit'>('create')
  const [runningSceneId, setRunningSceneId] = useState<string | null>(null)
  const runningSceneTimerRef = useRef<number | null>(null)
  const sceneLongPressTimerRef = useRef<number | null>(null)
  const sceneLongPressTriggeredRef = useRef(false)
  const lightGroupLongPressTimerRef = useRef<number | null>(null)
  const lightGroupLongPressTriggeredRef = useRef(false)
  const storageScopeAppId = auth?.appId ?? (isPublicDemo ? 'public_demo' : '')
  const storageScopeKey = storageScopeAppId && selectedLocationId ? `${storageScopeAppId}:${selectedLocationId}` : ''
  const [storageHydratedKey, setStorageHydratedKey] = useState('')
  const [lightGroups, setLightGroups] = useState<LightControlGroup[]>([])
  const [deviceOrderIds, setDeviceOrderIds] = useState<string[]>([])
  const [showDeviceOrderModal, setShowDeviceOrderModal] = useState(false)
  const [showLightGroupOrderModal, setShowLightGroupOrderModal] = useState(false)
  const [showLightGroupEditorModal, setShowLightGroupEditorModal] = useState(false)
  const [lightGroupEditorMode, setLightGroupEditorMode] = useState<'create' | 'edit'>('create')
  const [lightGroupEditorTargetId, setLightGroupEditorTargetId] = useState<string | null>(null)
  const [activeLightGroupId, setActiveLightGroupId] = useState<string | null>(null)

  const resolveModalConfigForDevice = useCallback((targetDevice: Device): ModalConfig => {
    const rawType = String(targetDevice.deviceType ?? targetDevice.type ?? '')
    const rawTraits = targetDevice.traits as Array<string | unknown>
    const type = resolveModalTemplateDeviceType(rawType, rawTraits)
    const dt = sections?.modal?.deviceTypes
    const configCandidates = [
      type,
      rawType,
      type === 'ac_unit' ? 'hvac' : '',
      type === 'hvac' ? 'ac_unit' : '',
    ]
    const { config: configForType } = resolveModalConfigByDeviceType(dt, configCandidates)
    const baseConfig = configForType
      ? { ...configForType }
      : { ...(sections?.modal?.defaultConfig ?? {}) }
    const fallbackConfig = deviceTypeConfigs[type] ?? {
      enabledTraits: [],
      enabledStates: [],
      stateDisplayOverrides: {},
    }
    const baseStateDisplayOverrides = (baseConfig as { stateDisplayOverrides?: Record<string, unknown> })
      .stateDisplayOverrides
    if (
      (!baseStateDisplayOverrides || Object.keys(baseStateDisplayOverrides).length === 0) &&
      fallbackConfig.stateDisplayOverrides &&
      Object.keys(fallbackConfig.stateDisplayOverrides).length > 0
    ) {
      const mutableBaseConfig = baseConfig as {
        stateDisplayOverrides?: Record<string, { icon?: string; label?: string; unit?: string }>
      }
      mutableBaseConfig.stateDisplayOverrides = fallbackConfig.stateDisplayOverrides
    }
    const { config: resolvedModalConfig } = resolveDeviceModalRenderConfig({
      rawDeviceType: rawType,
      rawTraits,
      baseConfig,
      modalOverrides,
      fallbackEnabledTraits: fallbackConfig.enabledTraits,
      fallbackEnabledStates: fallbackConfig.enabledStates,
      fallbackEnabledCharts: [],
      forceAcDefaultStyle: true,
    })
    return resolvedModalConfig
  }, [deviceTypeConfigs, modalOverrides, sections?.modal?.defaultConfig, sections?.modal?.deviceTypes])

  const resolveDeviceStateBySN = useCallback((sn: string): Record<string, unknown> => {
    const status = deviceStatuses.find((item) => item.sn === sn)
    return (status?.attributes ?? status?.iotState ?? {}) as Record<string, unknown>
  }, [deviceStatuses])

  const handleDeviceNicknameUpdated = useCallback((deviceSN: string, nickname: string) => {
    const nextNickname = String(nickname || '').trim()
    if (!nextNickname) return
    setDevices((prev) =>
      prev.map((item) => {
        const itemSn = item.deviceSN ?? item.sn
        if (itemSn !== deviceSN) return item
        return {
          ...item,
          nickname: nextNickname,
          displayName: nextNickname,
          name: nextNickname,
        }
      })
    )
    setHomeDeviceModal((prev) => {
      if (!prev) return prev
      const prevSn = prev.deviceSN ?? prev.sn
      if (prevSn !== deviceSN) return prev
      return {
        ...prev,
        nickname: nextNickname,
        displayName: nextNickname,
        name: nextNickname,
      }
    })
  }, [])

  const reloadScenes = useCallback(async () => {
    if (!auth) {
      setSceneItems(buildSceneItemsFromPayload(null, fallbackScenes))
      return
    }
    if (!selectedLocationId) {
      setSceneItems([])
      return
    }
    try {
      const payload = await queryScenesWithAuth(auth, true, selectedLocationId)
      const next = buildSceneItemsFromPayload(payload, fallbackScenes, selectedLocationId, false)
      const ordered = applySceneOrder(next, loadStoredSceneOrder(auth.appId, selectedLocationId))
      setSceneItems(ordered)
    } catch {
      setSceneItems([])
    }
  }, [auth, fallbackScenes, selectedLocationId])

  useEffect(() => {
    void reloadScenes()
  }, [reloadScenes])

  useEffect(() => {
    if (!storageScopeKey || !selectedLocationId || !storageScopeAppId) {
      setLightGroups([])
      setDeviceOrderIds([])
      setStorageHydratedKey('')
      return
    }
    setLightGroups(loadStoredLightGroups(storageScopeAppId, selectedLocationId))
    setDeviceOrderIds(loadStoredDeviceOrder(storageScopeAppId, selectedLocationId) ?? [])
    setStorageHydratedKey(storageScopeKey)
  }, [selectedLocationId, storageScopeAppId, storageScopeKey])

  useEffect(() => {
    if (!selectedLocationId || !storageScopeAppId) return
    if (!storageHydratedKey || storageHydratedKey !== storageScopeKey) return
    saveStoredDeviceOrder(storageScopeAppId, selectedLocationId, deviceOrderIds)
  }, [deviceOrderIds, selectedLocationId, storageHydratedKey, storageScopeAppId, storageScopeKey])

  const handleOpenSceneCreate = useCallback(() => {
    hapticTap()
    setSceneEditorMode('create')
    setSceneEditorTarget(null)
    setShowSceneEditorModal(true)
  }, [])

  const handleOpenSceneOrder = useCallback(() => {
    hapticTap()
    setShowSceneOrderModal(true)
  }, [])

  const handleOpenDeviceOrder = useCallback(() => {
    hapticTap()
    setShowDeviceOrderModal(true)
  }, [])

  const handleOpenLightGroupOrder = useCallback(() => {
    hapticTap()
    setShowLightGroupOrderModal(true)
  }, [])

  const handleApplyDeviceOrder = useCallback((orderedDeviceSns: string[]) => {
    setDeviceOrderIds(orderedDeviceSns)
    setShowDeviceOrderModal(false)
  }, [])

  const handleApplyLightGroupOrder = useCallback((orderedGroupIds: string[]) => {
    setLightGroups((prev) => applyLightGroupOrder(prev, orderedGroupIds))
    setShowLightGroupOrderModal(false)
  }, [])

  const handleApplySceneOrder = useCallback((orderedIds: string[]) => {
    setSceneItems((prev) => {
      const ordered = applySceneOrder(prev, orderedIds)
      if (auth && selectedLocationId) {
        saveSceneOrder(auth.appId, selectedLocationId, ordered.map((s) => s.id))
      }
      return ordered
    })
    setShowSceneOrderModal(false)
  }, [auth, selectedLocationId])

  const handleOpenSceneEdit = useCallback((scene: QuickSceneItem) => {
    hapticTap()
    setSceneEditorMode('edit')
    setSceneEditorTarget(scene)
    setShowSceneEditorModal(true)
  }, [])

  const handleOpenLightGroupCreate = useCallback(() => {
    hapticTap()
    setLightGroupEditorMode('create')
    setLightGroupEditorTargetId(null)
    setShowLightGroupEditorModal(true)
  }, [hapticTap])

  const handleOpenLightGroupEdit = useCallback((group: LightControlGroup) => {
    hapticTap()
    setLightGroupEditorMode('edit')
    setLightGroupEditorTargetId(group.id)
    setShowLightGroupEditorModal(true)
  }, [hapticTap])

  const handleCreateLightGroup = useCallback((payload: { name: string; icon: string; deviceSns: string[] }) => {
    const now = Date.now()
    const uniqueDeviceSns = Array.from(new Set(payload.deviceSns.map((sn) => String(sn || '').trim()).filter(Boolean)))
    if (uniqueDeviceSns.length === 0) return
    setLightGroups((prev) => {
      const next = prev
        .map((group) => ({
          ...group,
          deviceSns: (group.deviceSns ?? []).filter((sn) => !uniqueDeviceSns.includes(sn)),
        }))
        .filter((group) => group.deviceSns.length > 0)
      next.push({
        id: `light-group-${now}-${Math.random().toString(36).slice(2, 8)}`,
        name: payload.name.trim() || '燈光群組',
        icon: payload.icon || 'lightbulb',
        deviceSns: uniqueDeviceSns,
        createdAt: now,
        updatedAt: now,
      })
      return next
    })
    setShowLightGroupEditorModal(false)
    setLightGroupEditorMode('create')
    setLightGroupEditorTargetId(null)
  }, [])

  const handleSaveLightGroup = useCallback((groupId: string, payload: { name: string; icon: string; deviceSns: string[] }) => {
    const now = Date.now()
    const uniqueDeviceSns = Array.from(new Set(payload.deviceSns.map((sn) => String(sn || '').trim()).filter(Boolean)))
    if (!groupId || uniqueDeviceSns.length === 0) return
    setLightGroups((prev) => {
      const next = prev
        .map((group) => {
          if (group.id === groupId) {
            return {
              ...group,
              name: payload.name.trim() || group.name || '控制群組',
              icon: payload.icon || group.icon || 'lightbulb',
              deviceSns: uniqueDeviceSns,
              updatedAt: now,
            }
          }
          return {
            ...group,
            deviceSns: (group.deviceSns ?? []).filter((sn) => !uniqueDeviceSns.includes(sn)),
          }
        })
        .filter((group) => group.id === groupId || group.deviceSns.length > 0)
      return next
    })
    setShowLightGroupEditorModal(false)
    setLightGroupEditorMode('create')
    setLightGroupEditorTargetId(null)
  }, [])

  const handleDeleteLightGroup = useCallback((groupId: string) => {
    if (!groupId) return
    setLightGroups((prev) => prev.filter((group) => group.id !== groupId))
    setActiveLightGroupId((prev) => (prev === groupId ? null : prev))
    setShowLightGroupEditorModal(false)
    setLightGroupEditorMode('create')
    setLightGroupEditorTargetId(null)
  }, [])

  const startLightGroupLongPress = useCallback((group: LightControlGroup) => {
    if (lightGroupLongPressTimerRef.current != null) {
      window.clearTimeout(lightGroupLongPressTimerRef.current)
    }
    lightGroupLongPressTriggeredRef.current = false
    lightGroupLongPressTimerRef.current = window.setTimeout(() => {
      lightGroupLongPressTriggeredRef.current = true
      handleOpenLightGroupEdit(group)
    }, 500)
  }, [handleOpenLightGroupEdit])

  const cancelLightGroupLongPress = useCallback(() => {
    if (lightGroupLongPressTimerRef.current != null) {
      window.clearTimeout(lightGroupLongPressTimerRef.current)
      lightGroupLongPressTimerRef.current = null
    }
  }, [])

  const handleLightGroupPressEnd = useCallback((groupId: string) => {
    cancelLightGroupLongPress()
    if (lightGroupLongPressTriggeredRef.current) {
      lightGroupLongPressTriggeredRef.current = false
      return
    }
    hapticTap()
    setActiveLightGroupId(groupId)
  }, [cancelLightGroupLongPress, hapticTap])

  const handleLightGroupIconToggle = useCallback(async (group: LightControlGroup, currentlyOn: boolean) => {
    if (templateEditorMode || isPublicDemo || !auth) return
    const sns = group.deviceSns
      .map((sn) => String(sn || '').trim())
      .filter(Boolean)
    if (sns.length === 0) return

    const nextOn = !currentlyOn
    hapticTap()
    markPendingOnOff(sns, nextOn)
    try {
      await executeCommandWithAuth(auth, sns, nextOn ? 'on' : 'off', { on: nextOn })
      const optimisticPatch: Record<string, unknown> = {
        on: nextOn,
        OnOff: nextOn,
        isOn: nextOn,
        powerState: nextOn ? 'on' : 'off',
        power: nextOn ? 'on' : 'off',
      }
      setDeviceStatuses((prev) => {
        const next = [...prev]
        sns.forEach((sn) => {
          const idx = next.findIndex((item) => item.sn === sn)
          if (idx < 0) {
            next.push({ sn, attributes: { ...optimisticPatch }, iotState: { ...optimisticPatch } })
            return
          }
          const current = next[idx]
          const currentAttrs = isRecord(current.attributes) ? current.attributes : {}
          const currentState = isRecord(current.iotState) ? current.iotState : {}
          next[idx] = {
            ...current,
            attributes: { ...currentAttrs, ...optimisticPatch },
            iotState: { ...currentState, ...optimisticPatch },
          }
        })
        return next
      })
    } catch (_) {
      clearPendingOnOff(sns)
      // ignore; websocket event will reconcile.
    }
  }, [auth, clearPendingOnOff, hapticTap, isPublicDemo, markPendingOnOff, templateEditorMode])

  const handleSceneRun = useCallback(async (scene: QuickSceneItem) => {
    if (templateEditorMode) return
    hapticTap()
    if (!auth) return
    if (!scene.sceneId) return
    try {
      setRunningSceneId(scene.id)
      await runSceneWithAuth(auth, scene.sceneId, scene.groupId ?? selectedLocationId ?? undefined)
      if (runningSceneTimerRef.current != null) {
        window.clearTimeout(runningSceneTimerRef.current)
      }
      runningSceneTimerRef.current = window.setTimeout(() => {
        setRunningSceneId((prev) => (prev === scene.id ? null : prev))
        runningSceneTimerRef.current = null
      }, 1400)
    } catch (e) {
      console.warn('[SceneRun] failed', e)
      setRunningSceneId(null)
    }
  }, [auth, selectedLocationId, templateEditorMode])

  const startSceneLongPress = useCallback((scene: QuickSceneItem) => {
    if (sceneLongPressTimerRef.current != null) {
      window.clearTimeout(sceneLongPressTimerRef.current)
    }
    sceneLongPressTriggeredRef.current = false
    sceneLongPressTimerRef.current = window.setTimeout(() => {
      sceneLongPressTriggeredRef.current = true
      handleOpenSceneEdit(scene)
    }, 500)
  }, [handleOpenSceneEdit])

  const cancelSceneLongPress = useCallback(() => {
    if (sceneLongPressTimerRef.current != null) {
      window.clearTimeout(sceneLongPressTimerRef.current)
      sceneLongPressTimerRef.current = null
    }
  }, [])

  const handleScenePressEnd = useCallback((scene: QuickSceneItem) => {
    cancelSceneLongPress()
    if (sceneLongPressTriggeredRef.current) {
      sceneLongPressTriggeredRef.current = false
      return
    }
    void handleSceneRun(scene)
  }, [cancelSceneLongPress, handleSceneRun])

  useEffect(() => () => {
    if (sceneLongPressTimerRef.current != null) {
      window.clearTimeout(sceneLongPressTimerRef.current)
    }
    if (lightGroupLongPressTimerRef.current != null) {
      window.clearTimeout(lightGroupLongPressTimerRef.current)
    }
    if (runningSceneTimerRef.current != null) {
      window.clearTimeout(runningSceneTimerRef.current)
    }
  }, [])

  const fetchLocations = useCallback(async () => {
    if (isPublicDemo) {
      const mock = buildPublicDemoMockData()
      setLocations(mock.locations)
      return mock.locations
    }
    if (!auth) return []
    const locs = await queryLocationsWithAuth(auth)
    setLocations(locs)
    return locs
  }, [auth, isPublicDemo])

  const fetchDevicesForLocation = useCallback(
    async (locId: string) => {
      if (isPublicDemo) {
        const mock = buildPublicDemoMockData()
        setSelectedLocationId(locId || mock.locations[0]?.id || null)
        setDevices(mock.devices)
        setDeviceStatuses(mock.statuses)
        setDataLoading(false)
        setDataError(null)
        return
      }
      if (!auth) return
      setDataLoading(true)
      setDataError(null)
      try {
        const devs = await queryDevicesWithAuth(auth, locId, true)
        setDevices(devs)
        const sns = devs.map((d) => d.deviceSN ?? d.sn).filter(Boolean)
        const statuses = sns.length > 0 ? await queryStatusWithAuth(auth, locId, sns) : []
        setDeviceStatuses(statuses)
      } catch (e) {
        setDataError(e instanceof Error ? e.message : 'Failed to load')
      } finally {
        setDataLoading(false)
      }
    },
    [auth, isPublicDemo]
  )

  const refreshShadowStatuses = useCallback(async () => {
    if (isPublicDemo) {
      const mock = buildPublicDemoMockData()
      setDeviceStatuses(mock.statuses)
      setDataError(null)
      return true
    }
    if (!auth || !selectedLocationId) return false
    const sns = devices.map((d) => d.deviceSN ?? d.sn).filter(Boolean)
    setShadowRefreshing(true)
    try {
      const statuses = sns.length > 0 ? await queryStatusWithAuth(auth, selectedLocationId, sns) : []
      setDeviceStatuses(statuses)
      setDataError(null)
      return true
    } catch (e) {
      setDataError(e instanceof Error ? e.message : 'Failed to fetch')
      return false
    } finally {
      setShadowRefreshing(false)
    }
  }, [auth, devices, isPublicDemo, selectedLocationId])

  useEffect(() => {
    if (isPublicDemo) {
      const mock = buildPublicDemoMockData()
      setLocations(mock.locations)
      setDevices(mock.devices)
      setDeviceStatuses(mock.statuses)
      const firstLocationId = mock.locations[0]?.id ?? mock.locations[0]?.groupId ?? null
      setSelectedLocationId(firstLocationId)
      setDataLoading(false)
      setDataError(null)
      return
    }
    if (!auth) {
      setLocations([])
      setDevices([])
      setDeviceStatuses([])
      setSelectedLocationId(null)
      return
    }
    fetchLocations().then((locs) => {
      const savedLocId = loadStoredSelectedLocation(auth.appId)
      const savedExists = savedLocId && locs.some((l) => (l.id ?? l.groupId) === savedLocId)
      const locId = savedExists ? savedLocId : locs[0]?.id ?? locs[0]?.groupId ?? null
      setSelectedLocationId(locId)
    })
  }, [auth, fetchLocations, isPublicDemo])

  useEffect(() => {
    if (auth && selectedLocationId) {
      fetchDevicesForLocation(selectedLocationId)
    }
  }, [auth, selectedLocationId, fetchDevicesForLocation])

  useEffect(() => {
    if (!auth) {
      if (wsRef.current) {
        wsRef.current.disconnect()
        wsRef.current = null
      }
      wsAuthedRef.current = false
      wsSubscribedSnsRef.current = new Set()
      setWsReady(false)
      return
    }

    const socketOrigin = toSocketOrigin(getCurrentApiUrl())
    const socketAuthPayload =
      'accessToken' in auth
        ? { accessToken: auth.accessToken, appId: auth.appId }
        : { apiKey: auth.apiKey, appId: auth.appId }

    const socket = io(socketOrigin, {
      path: '/ws',
      transports: ['websocket', 'polling'],
      auth: socketAuthPayload,
      autoConnect: true,
    })

    const handleAuthenticated = (payload: unknown) => {
      const ok = isRecord(payload) && payload.success === true
      wsAuthedRef.current = ok
      setWsReady(ok)
    }

    const handleDisconnect = () => {
      wsAuthedRef.current = false
      setWsReady(false)
    }

    const handleRealtimeStateUpdate = (payload: unknown) => {
      const matched = collectRealtimeSns(payload, devicesRef.current)
      const fallbackPending = matched.length === 0 ? resolvePendingSnsForRealtimePayload(payload) : []
      const sns = matched.length > 0 ? matched : fallbackPending
      if (sns.length === 0) return
      const patch = collectRealtimeAttributes(payload)
      const patchKeys = Object.keys(patch)
      if (patchKeys.length === 0) return

      setDeviceStatuses((prev) => {
        const next = [...prev]
        const indexMap = new Map<string, number>()
        next.forEach((item, index) => indexMap.set(item.sn, index))

        sns.forEach((sn) => {
          const safePatch = sanitizeRealtimePatchForPending(sn, patch)
          if (!safePatch) return
          const idx = indexMap.get(sn)
          if (idx == null) {
            next.push({
              sn,
              online: typeof safePatch.online === 'boolean' ? safePatch.online : undefined,
              attributes: { ...safePatch },
              iotState: { ...safePatch },
            })
            indexMap.set(sn, next.length - 1)
            return
          }

          const current = next[idx]
          const currentAttrs = isRecord(current.attributes) ? current.attributes : {}
          const currentState = isRecord(current.iotState) ? current.iotState : {}
          next[idx] = {
            ...current,
            online: typeof safePatch.online === 'boolean' ? safePatch.online : current.online,
            attributes: { ...currentAttrs, ...safePatch },
            iotState: { ...currentState, ...safePatch },
          }
        })

        return next
      })

      setRealtimeNotificationDot(true)
    }

    const handleRealtimeNotify = () => {
      setRealtimeNotificationDot(true)
    }

    socket.on('connect', () => {
      // Ensure auth re-validation after reconnect.
      socket.emit('authenticate', socketAuthPayload)
    })
    socket.on('authenticated', handleAuthenticated)
    socket.on('disconnect', handleDisconnect)
    socket.on('device:update', handleRealtimeStateUpdate)
    socket.on('webhook:event', handleRealtimeStateUpdate)
    socket.on('climate-alert', handleRealtimeNotify)
    socket.on('system:notification', handleRealtimeNotify)

    wsRef.current = socket
    wsSubscribedSnsRef.current = new Set()
    wsAuthedRef.current = false
    setWsReady(false)

    return () => {
      socket.off('authenticated', handleAuthenticated)
      socket.off('disconnect', handleDisconnect)
      socket.off('device:update', handleRealtimeStateUpdate)
      socket.off('webhook:event', handleRealtimeStateUpdate)
      socket.off('climate-alert', handleRealtimeNotify)
      socket.off('system:notification', handleRealtimeNotify)
      socket.disconnect()
      if (wsRef.current === socket) {
        wsRef.current = null
      }
      wsAuthedRef.current = false
      wsSubscribedSnsRef.current = new Set()
      setWsReady(false)
    }
  }, [auth, resolvePendingSnsForRealtimePayload, sanitizeRealtimePatchForPending])

  useEffect(() => {
    const socket = wsRef.current
    if (!socket || !wsReady || !wsAuthedRef.current) return

    const prev = wsSubscribedSnsRef.current
    const next = new Set<string>()
    if (selectedLocationId) {
      devices.forEach((d) => {
        const sn = String(d.deviceSN ?? d.sn ?? '').trim()
        if (!sn) return
        next.add(sn)
        const baseSN = sn.split('#')[0]
        if (baseSN) next.add(baseSN)
      })
    }

    const toUnsubscribe = Array.from(prev).filter((sn) => !next.has(sn))
    const toSubscribe = Array.from(next).filter((sn) => !prev.has(sn))

    if (toUnsubscribe.length > 0) {
      socket.emit('unsubscribe-devices', { devices: toUnsubscribe })
    }
    if (toSubscribe.length > 0) {
      socket.emit('subscribe-devices', { devices: toSubscribe })
    }

    wsSubscribedSnsRef.current = next
  }, [devices, selectedLocationId, wsReady])

  const handleLogout = () => {
    setAuth(null)
    clearAuth()
    setAccountDropdownOpen(false)
  }

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target as Node)) {
        setAccountDropdownOpen(false)
      }
    }
    if (accountDropdownOpen) document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [accountDropdownOpen])

  const header = sections?.header ?? {}
  const avatarImage = header.avatarImage ?? DEFAULT_AVATAR
  const notificationDot = header.notificationDot ?? true
  const showNotificationDot = notificationDot || realtimeNotificationDot

  const [weather, setWeather] = useState<{
    label: string
    value: string
    icon: string
  } | null>(null)

  useEffect(() => {
    fetchWeatherByIP()
      .then(setWeather)
      .catch(() => {
        const fallback =
          header.weatherLabel != null && header.weatherValue != null && header.weatherIcon != null
            ? { label: header.weatherLabel, value: header.weatherValue, icon: header.weatherIcon }
            : { label: 'OUTSIDE', value: '—', icon: 'wb_sunny' }
        setWeather(fallback)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const weatherLabel = (weather?.label ?? header.weatherLabel ?? 'OUTSIDE').toUpperCase()
  const weatherValue = weather?.value ?? header.weatherValue ?? '—'
  const weatherIcon = weather?.icon ?? header.weatherIcon ?? 'wb_sunny'

  const scenes = sceneItems
  const normalizedLightGroups = useMemo(
    () => sanitizeLightGroupsForDevices(lightGroups, devices),
    [lightGroups, devices]
  )
  const groupedLightSnSet = useMemo(
    () => new Set(normalizedLightGroups.flatMap((group) => group.deviceSns)),
    [normalizedLightGroups]
  )
  const visibleDevices = useMemo(() => {
    const filtered = devices.filter((device) => {
      const sn = resolveDeviceSn(device)
      if (!isLightCandidateDevice(device)) return true
      return !groupedLightSnSet.has(sn)
    })
    return applyDeviceOrder(filtered, deviceOrderIds)
  }, [deviceOrderIds, devices, groupedLightSnSet])
  const lightGroupById = useMemo(
    () => new Map(normalizedLightGroups.map((group) => [group.id, group])),
    [normalizedLightGroups]
  )
  const lightGroupEditorTarget = lightGroupEditorTargetId ? (lightGroupById.get(lightGroupEditorTargetId) ?? null) : null
  const lightGroupEditorSelectableDevices = useMemo(() => {
    const targetSnSet = new Set(lightGroupEditorTarget?.deviceSns ?? [])
    return devices.filter((device) => {
      if (!isLightCandidateDevice(device)) return false
      const sn = resolveDeviceSn(device)
      if (lightGroupEditorMode === 'edit') {
        return !groupedLightSnSet.has(sn) || targetSnSet.has(sn)
      }
      return !groupedLightSnSet.has(sn)
    })
  }, [devices, groupedLightSnSet, lightGroupEditorMode, lightGroupEditorTarget])
  const activeLightGroup = activeLightGroupId ? (lightGroupById.get(activeLightGroupId) ?? null) : null

  const navItems = sections?.bottomNav ?? [
    { id: 'home', label: 'Home', icon: 'home', active: true },
    { id: 'auto', label: 'Auto', icon: 'time_auto', active: false },
    { id: 'store', label: 'Store', icon: 'shopping_bag', active: false },
  ]
  const statusMap = useMemo(
    () => new Map(deviceStatuses.map((s) => [s.sn, s])),
    [deviceStatuses]
  )
  const deviceKey = (d: Device) => d.deviceSN ?? d.sn

  const selectedLocation = locations.find((l) => (l.id ?? l.groupId) === selectedLocationId)
  const locationName = selectedLocation?.name || selectedLocation?.id || selectedLocation?.groupId || 'Sweet Home'
  const templateSectionItemCss = useMemo(() => {
    const maybeMap = (sections as { editor?: { templateItemCss?: unknown } })?.editor?.templateItemCss
    if (!maybeMap || typeof maybeMap !== 'object' || Array.isArray(maybeMap)) return {}
    return maybeMap as Record<string, string>
  }, [sections])
  const templateSectionItemFramerPreset = useMemo(() => {
    const maybeMap =
      (sections as { editor?: { templateItemFramerPreset?: unknown } })?.editor?.templateItemFramerPreset
    if (!maybeMap || typeof maybeMap !== 'object' || Array.isArray(maybeMap)) return {}
    return maybeMap as Record<string, FramePreset>
  }, [sections])
  const mergedTemplateItemCss = useMemo(
    () => ({ ...templateSectionItemCss, ...(templateItemCssOverrides ?? {}) }),
    [templateItemCssOverrides, templateSectionItemCss]
  )
  const mergedTemplateItemFramerPreset = useMemo(
    () => ({ ...templateSectionItemFramerPreset, ...(templateItemFramerPresetOverrides ?? {}) }),
    [templateItemFramerPresetOverrides, templateSectionItemFramerPreset]
  )
  const runtimeMergedTemplateItemCss = useMemo(() => {
    const nextMap: Record<string, string> = { ...mergedTemplateItemCss }
    const keys = new Set([
      ...Object.keys(mergedTemplateItemCss),
      ...Object.keys(mergedTemplateItemFramerPreset),
    ])
    keys.forEach((key) => {
      const parsedKey = parseTemplateItemCssKey(key)
      const preset = mergedTemplateItemFramerPreset[key]
      const sourceCss = ensureTemplateRootCssBlock(String(nextMap[key] || ''))
      if (parsedKey && !canApplyFramerPresetToTemplateSlot(parsedKey.slotKey)) {
        nextMap[key] = sourceCss
        return
      }
      nextMap[key] = applyTemplateFramerPresetRuntimeCss(
        sourceCss,
        preset ?? 'default',
        framerPresetOverrides
      )
    })
    return nextMap
  }, [mergedTemplateItemCss, mergedTemplateItemFramerPreset, framerPresetOverrides])
  const runtimeTemplateItemCss = useMemo(
    () => buildTemplateItemCssRuntime(runtimeMergedTemplateItemCss),
    [runtimeMergedTemplateItemCss]
  )
  const groupGridCardStyleByState = useMemo(() => {
    const resolveStateStyle = (state: 'on' | 'off'): CSSProperties => {
      const preferredExactKeys = [
        `${TEMPLATE_ITEM_KEY}::groupGrid@${state}`,
        `${TEMPLATE_ITEM_KEY}::groupgrid@${state}`,
      ]
      const exactKey = preferredExactKeys.find((key) => Object.prototype.hasOwnProperty.call(runtimeMergedTemplateItemCss, key))
      let entry: [string, string] | undefined
      if (exactKey) {
        entry = [exactKey, String(runtimeMergedTemplateItemCss[exactKey] || '')]
      } else {
        const candidates = Object.entries(runtimeMergedTemplateItemCss).filter(([key]) => {
          const parsed = parseTemplateItemCssKey(key)
          if (!parsed) return false
          const token = normalizeTemplateSlotToken(parsed.slotKey)
          return token === 'groupgrid' && parsed.cssState === state
        })
        entry = candidates[candidates.length - 1]
      }
      if (!entry) return {}
      const [, rawCss] = entry
      const normalizedSource = ensureTemplateRootCssBlock(String(rawCss || ''))
      const body = readTemplateRootCssBody(normalizedSource)
      const background = toInlineStyleValue(readTemplateCssDeclaration(body, 'background'))
      const backgroundColor = toInlineStyleValue(readTemplateCssDeclaration(body, 'background-color'))
      const border = toInlineStyleValue(readTemplateCssDeclaration(body, 'border'))
      const borderColor = toInlineStyleValue(readTemplateCssDeclaration(body, 'border-color'))
      const borderStyle = toInlineStyleValue(readTemplateCssDeclaration(body, 'border-style'))
      const borderWidth = toInlineStyleValue(readTemplateCssDeclaration(body, 'border-width'))
      const boxShadow = toInlineStyleValue(readTemplateCssDeclaration(body, 'box-shadow'))
      const style: CSSProperties = {}
      if (background) style.background = background
      if (backgroundColor) style.backgroundColor = backgroundColor
      if (border) style.border = border
      if (borderColor) style.borderColor = borderColor
      if (borderStyle) style.borderStyle = borderStyle
      if (borderWidth) style.borderWidth = borderWidth
      if (boxShadow) style.boxShadow = boxShadow
      return style
    }
    return {
      on: resolveStateStyle('on'),
      off: resolveStateStyle('off'),
    }
  }, [runtimeMergedTemplateItemCss])

  const postTemplateSlotSelected = useCallback(
    (target: HTMLElement, event: { clientX: number; clientY: number }) => {
      if (!isAppFactoryPreview || !templateEditorMode || typeof window === 'undefined') return
      const rawSlotKey = String(target.dataset.templateCssSlot || '').trim()
      if (!rawSlotKey) return
      const slotInstanceKey = String(target.dataset.templateCssInstance || '').trim()
      const normalizedRawSlot = normalizeTemplateSlotToken(rawSlotKey)
      const slotKey =
        normalizedRawSlot === 'scenecard'
          ? 'sceneGrid'
          : (normalizedRawSlot === 'groupcard' || normalizedRawSlot === 'groupcardicon')
            ? 'groupGrid'
            : normalizedRawSlot === 'devicecard'
              ? 'deviceGrid'
              : rawSlotKey
      const slotLabel =
        slotKey === 'sceneGrid'
          ? 'Scene Grid'
          : slotKey === 'groupGrid'
            ? 'Group Grid'
            : slotKey === 'deviceGrid'
              ? 'Device Grid'
              : String(target.dataset.templateCssSlotLabel || slotKey).trim()
      const slotStateToken = String(target.dataset.templateCssState || 'on').trim().toLowerCase()
      const slotState: TemplateCssState =
        slotStateToken === 'off' || slotStateToken === 'selected' || slotStateToken === 'unselected'
          ? slotStateToken
          : 'on'
      try {
        window.parent.postMessage(
          {
            type: 'appfactory-preview-template-slot-selected',
            payload: {
              itemKey: TEMPLATE_ITEM_KEY,
              itemKind: 'template',
              itemId: 'dashboard',
              itemStyleId: 'TemplateStyle01',
              slotKey,
              slotLabel,
              slotState,
              slotInstanceKey,
              clientX: event.clientX,
              clientY: event.clientY,
            },
          },
          window.location.origin
        )
      } catch {
        // ignore
      }
    },
    [isAppFactoryPreview, templateEditorMode]
  )

  const handleTemplateEditorContextMenuCapture = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!templateEditorMode) return
      const target = event.target as HTMLElement | null
      let slotTarget = target?.closest('[data-template-css-slot]') as HTMLElement | null
      if (!event.shiftKey) {
        // Grid-only editing: always pick the nearest grid slot.
        const groupGrid = target?.closest('[data-template-css-slot="groupGrid"]') as HTMLElement | null
        if (groupGrid) {
          slotTarget = groupGrid
        } else {
          const sceneGrid = target?.closest('[data-template-css-slot="sceneGrid"]') as HTMLElement | null
          if (sceneGrid) {
            slotTarget = sceneGrid
          } else {
            const deviceGrid = target?.closest('[data-template-css-slot="deviceGrid"]') as HTMLElement | null
            if (deviceGrid) {
              slotTarget = deviceGrid
            }
          }
        }

        const navFrame = target?.closest('[data-template-css-slot="bottomNavFrame"]') as HTMLElement | null
        if (navFrame) {
          slotTarget = navFrame
        } else {
          const navContainer = target?.closest('[data-template-css-slot="bottomNav"]') as HTMLElement | null
          if (navContainer) {
            slotTarget = navContainer
          }
        }
      }
      if (!slotTarget) return
      event.preventDefault()
      event.stopPropagation()
      postTemplateSlotSelected(slotTarget, { clientX: event.clientX, clientY: event.clientY })
    },
    [postTemplateSlotSelected, templateEditorMode]
  )

  const [headerCollapsed, setHeaderCollapsed] = useState(false)
  const [autoScrollLocked, setAutoScrollLocked] = useState(false)

  useEffect(() => {
    if (!selectedLocationId || !storageScopeAppId) return
    if (!storageHydratedKey || storageHydratedKey !== storageScopeKey) return
    saveStoredLightGroups(storageScopeAppId, selectedLocationId, normalizedLightGroups)
  }, [
    normalizedLightGroups,
    selectedLocationId,
    storageHydratedKey,
    storageScopeAppId,
    storageScopeKey,
  ])

  useEffect(() => {
    if (!activeLightGroupId) return
    if (lightGroupById.has(activeLightGroupId)) return
    setActiveLightGroupId(null)
  }, [activeLightGroupId, lightGroupById])

  useEffect(() => {
    if (!lightGroupEditorTargetId) return
    if (lightGroupById.has(lightGroupEditorTargetId)) return
    setLightGroupEditorTargetId(null)
    setLightGroupEditorMode('create')
    setShowLightGroupEditorModal(false)
  }, [lightGroupEditorTargetId, lightGroupById])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    let rafId = 0
    const scrollContainers = [
      document.querySelector<HTMLElement>('.appfactory-preview-embed-viewport'),
      document.querySelector<HTMLElement>('.appfactory-share-preview-content'),
      document.querySelector<HTMLElement>('.appfactory-preview-viewport'),
      document.querySelector<HTMLElement>('.device-type-modal-preview-modal-wrap'),
    ].filter((el): el is HTMLElement => Boolean(el))

    const computeScrollTop = () => {
      const rootTop = document.scrollingElement?.scrollTop ?? 0
      const winTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0
      const containerTop = scrollContainers.reduce((max, el) => Math.max(max, el.scrollTop || 0), 0)
      return Math.max(winTop, rootTop, containerTop)
    }

    const updateCollapsedState = () => {
      rafId = 0
      setHeaderCollapsed(computeScrollTop() > 6)
    }

    const onScroll = () => {
      if (rafId) return
      rafId = window.requestAnimationFrame(updateCollapsedState)
    }

    updateCollapsedState()
    window.addEventListener('scroll', onScroll, { passive: true })
    document.addEventListener('scroll', onScroll, { passive: true, capture: true })
    window.addEventListener('resize', onScroll, { passive: true })
    scrollContainers.forEach((el) => el.addEventListener('scroll', onScroll, { passive: true }))
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', onScroll)
      document.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      scrollContainers.forEach((el) => el.removeEventListener('scroll', onScroll))
    }
  }, [activeNav, homeDeviceModal])

  const handleNavClick = (n: { id: string; label: string; icon: string; active?: boolean }) => {
    hapticTap()
    if (n.id === 'store') {
      setActiveNav('store')
    } else if (n.id === 'auto') {
      setActiveNav('auto')
    } else if (n.id === 'home') {
      setActiveNav('home')
    }
  }

  const showStoreOverlay = activeNav === 'store'
  const handleOpenHomeDeviceModal = useCallback((device: Device) => {
    captureDashboardScrollSnapshot()
    setHomeDeviceModal(device)
  }, [captureDashboardScrollSnapshot])
  const handleCloseHomeDeviceModal = useCallback(() => {
    setHomeDeviceModal(null)
  }, [])
  const pullRefreshReady = pullRefreshDistance >= PULL_REFRESH_TRIGGER_PX
  const pullRefreshProgress = Math.min(1, pullRefreshDistance / PULL_REFRESH_TRIGGER_PX)
  const hasHomeOverlayOpen = Boolean(
    homeDeviceModal
    || showSceneOrderModal
    || showSceneEditorModal
    || showDeviceOrderModal
    || showLightGroupOrderModal
    || showLightGroupEditorModal
    || activeLightGroup
  )
  const canPullRefresh = (
    activeNav === 'home'
    && !showStoreOverlay
    && !hasHomeOverlayOpen
    && (Boolean(auth) || isPublicDemo)
  )
  const shouldShowPullRefreshUI = canPullRefresh && (pullRefreshDistance > 0 || shadowRefreshing)

  const updatePullRefreshDistanceByCurrentY = useCallback((currentY: number) => {
    if (!pullRefreshActiveRef.current) return
    const startY = pullRefreshStartYRef.current
    if (startY == null) return
    const rawDelta = currentY - startY
    if (rawDelta <= 0) {
      setPullRefreshDistance(0)
      pullRefreshReadyRef.current = false
      return
    }
    const nextDistance = Math.min(PULL_REFRESH_MAX_PX, rawDelta * 0.52)
    setPullRefreshDistance(nextDistance)
    pullRefreshReadyRef.current = nextDistance >= PULL_REFRESH_TRIGGER_PX
  }, [])

  const completePullRefreshGesture = useCallback(() => {
    if (!pullRefreshActiveRef.current) return
    const shouldRefresh = pullRefreshReadyRef.current
    resetPullRefreshGesture()
    if (shouldRefresh) void refreshShadowStatuses()
  }, [refreshShadowStatuses, resetPullRefreshGesture])

  const handlePullRefreshTouchStartCapture = useCallback((event: ReactTouchEvent<HTMLDivElement>) => {
    if (!canPullRefresh || dataLoading || shadowRefreshing) return
    if (event.touches.length !== 1) return
    if (!isDashboardAtTop()) return
    pullRefreshStartYRef.current = event.touches[0]?.clientY ?? null
    pullRefreshActiveRef.current = pullRefreshStartYRef.current != null
    pullRefreshReadyRef.current = false
  }, [canPullRefresh, dataLoading, isDashboardAtTop, shadowRefreshing])

  const handlePullRefreshTouchMoveCapture = useCallback((event: ReactTouchEvent<HTMLDivElement>) => {
    if (!pullRefreshActiveRef.current) return
    if (event.touches.length !== 1) {
      resetPullRefreshGesture()
      return
    }
    const startY = pullRefreshStartYRef.current
    if (startY == null) return
    const currentY = event.touches[0]?.clientY ?? startY
    updatePullRefreshDistanceByCurrentY(currentY)
    if (currentY > startY) event.preventDefault()
  }, [resetPullRefreshGesture, updatePullRefreshDistanceByCurrentY])

  const handlePullRefreshTouchEndCapture = useCallback(() => {
    completePullRefreshGesture()
  }, [completePullRefreshGesture])

  const handlePullRefreshPointerDownCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse') return
    if (!canPullRefresh || dataLoading || shadowRefreshing) return
    if (event.button !== 0) return
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
    if (!isDashboardAtTop()) return
    if (pullRefreshActiveRef.current) return
    pullRefreshStartYRef.current = event.clientY
    pullRefreshActiveRef.current = true
    pullRefreshReadyRef.current = false
    setPullRefreshDesktopTracking(true)
  }, [canPullRefresh, dataLoading, isDashboardAtTop, shadowRefreshing])

  const handlePullRefreshPointerMoveCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse') return
    if (!pullRefreshActiveRef.current) return
    if ((event.buttons & 1) !== 1) {
      resetPullRefreshGesture()
      return
    }
    const startY = pullRefreshStartYRef.current
    if (startY == null) return
    updatePullRefreshDistanceByCurrentY(event.clientY)
    if (event.clientY > startY) event.preventDefault()
  }, [resetPullRefreshGesture, updatePullRefreshDistanceByCurrentY])

  const handlePullRefreshPointerUpCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse') return
    completePullRefreshGesture()
  }, [completePullRefreshGesture])

  const handlePullRefreshMouseDownCapture = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (!canPullRefresh || dataLoading || shadowRefreshing) return
    if (event.button !== 0) return
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
    if (!isDashboardAtTop()) return
    if (pullRefreshActiveRef.current) return
    pullRefreshStartYRef.current = event.clientY
    pullRefreshActiveRef.current = true
    pullRefreshReadyRef.current = false
    setPullRefreshDesktopTracking(true)
  }, [canPullRefresh, dataLoading, isDashboardAtTop, shadowRefreshing])

  const handlePullRefreshMouseMoveCapture = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (!pullRefreshActiveRef.current) return
    if ((event.buttons & 1) !== 1) {
      resetPullRefreshGesture()
      return
    }
    const startY = pullRefreshStartYRef.current
    if (startY == null) return
    updatePullRefreshDistanceByCurrentY(event.clientY)
    if (event.clientY > startY) event.preventDefault()
  }, [resetPullRefreshGesture, updatePullRefreshDistanceByCurrentY])

  const handlePullRefreshMouseUpCapture = useCallback(() => {
    completePullRefreshGesture()
  }, [completePullRefreshGesture])

  useEffect(() => {
    if (!pullRefreshDesktopTracking || typeof window === 'undefined') return
    const handleWindowMouseMove = (event: MouseEvent) => {
      if (!pullRefreshActiveRef.current) return
      if ((event.buttons & 1) !== 1) {
        resetPullRefreshGesture()
        return
      }
      const startY = pullRefreshStartYRef.current
      updatePullRefreshDistanceByCurrentY(event.clientY)
      if (startY != null && event.clientY > startY) event.preventDefault()
    }
    const handleWindowMouseUp = () => {
      completePullRefreshGesture()
    }
    window.addEventListener('mousemove', handleWindowMouseMove, { passive: false })
    window.addEventListener('mouseup', handleWindowMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove)
      window.removeEventListener('mouseup', handleWindowMouseUp)
    }
  }, [completePullRefreshGesture, pullRefreshDesktopTracking, resetPullRefreshGesture, updatePullRefreshDistanceByCurrentY])

  useEffect(() => {
    if (!canPullRefresh) resetPullRefreshGesture()
  }, [canPullRefresh, resetPullRefreshGesture])

  // Preview 登出後：顯示所選 Login Page 樣式（與 device modal 相同：early return 切換 view）
  if (!auth && isAppFactoryPreview && !isPublicDemo) {
    const loginStyleId = (sections as { login?: { styleId?: string } })?.login?.styleId || 'redesigned-sign-in'
    const LoginComponent = getLoginStyleComponent(loginStyleId)
    if (LoginComponent) {
      return (
        <div className="smart-home-dashboard-template smart-home-login-fullscreen">
          {designTokenCssVars && (
            <style id="appfactory-design-tokens-runtime" dangerouslySetInnerHTML={{ __html: designTokenCssVars }} />
          )}
          {customCss && (
            <style id="appfactory-custom-css-runtime" dangerouslySetInnerHTML={{ __html: customCss }} />
          )}
          <LoginComponent onLogin={handlePreviewLogin} />
        </div>
      )
    }
  }

  // 點擊 device card 後：切換到 device modal 全螢幕（與 Login 相同：early return，非 overlay）
  if (homeDeviceModal && (auth || isPublicDemo)) {
    const homeModalRawDt = String(homeDeviceModal.deviceType ?? homeDeviceModal.type ?? '')
    const homeModalOverride = modalOverrides[homeModalRawDt]
      ?? modalOverrides[homeModalRawDt.toLowerCase()]
      ?? modalOverrides[homeModalRawDt.toUpperCase()]
    // In AppSuite editor preview mode (modalOverridesFromHost is defined), any device type
    // without a configured styleId is disabled — show placeholder instead of built-in modal.
    const homeModalDisabled = modalOverridesFromHost !== undefined && (
      !homeModalOverride || homeModalOverride.disabled === true || !homeModalOverride.styleId
    )
    return (
      <div className="smart-home-dashboard-template smart-home-login-fullscreen">
        {designTokenCssVars && (
          <style id="appfactory-design-tokens-runtime" dangerouslySetInnerHTML={{ __html: designTokenCssVars }} />
        )}
        {customCss && (
          <style id="appfactory-custom-css-runtime" dangerouslySetInnerHTML={{ __html: customCss }} />
        )}
        {homeModalDisabled ? (
          <div className="smart-home-device-modal-unsupported">
            <button
              type="button"
              className="smart-home-device-modal-back-btn"
              onClick={handleCloseHomeDeviceModal}
              aria-label="Back"
            >
              <span className="material-symbols-rounded">arrow_back</span>
            </button>
            <span className="material-symbols-rounded smart-home-device-modal-unsupported-icon">devices_off</span>
            <p>暫時不支持</p>
          </div>
        ) : (
          <DeviceModal
            device={homeDeviceModal}
            auth={auth}
            readOnly={isPublicDemo || templateEditorMode || !auth}
            selectedLocationId={selectedLocationId}
            mode="control"
            wrapMode={isAppFactoryPreview ? 'preview' : 'overlay'}
            onClose={handleCloseHomeDeviceModal}
            hapticTap={hapticTap}
            modalConfig={resolveModalConfigForDevice(homeDeviceModal)}
            deviceState={resolveDeviceStateBySN(homeDeviceModal.deviceSN ?? homeDeviceModal.sn)}
            onDeviceNicknameUpdated={handleDeviceNicknameUpdated}
          />
        )}
      </div>
    )
  }

  return (
    <div
      className={`smart-home-dashboard-template ${showStoreOverlay ? 'store-overlay-active' : ''} ${isAppFactoryPreview ? 'smart-home-preview-shell' : ''} ${activeNav === 'auto' && autoScrollLocked ? 'smart-home-auto-empty-lock' : ''} ${templateEditorMode ? 'is-template-editor-mode' : ''}`}
      style={{
        '--bg': colors.background,
        '--bg-accent': colors.backgroundAccent,
        '--card-glass': colors.cardGlass,
        '--card-border': colors.cardBorder,
        '--text-primary': colors.textPrimary,
        '--text-muted': colors.textMuted,
        '--primary': colors.primary,
        '--page-x': `${spacing.pageX ?? 24}px`,
        '--page-top': `${spacing.pageTop ?? 56}px`,
        '--section-gap': `${spacing.sectionGap ?? 28}px`,
        '--pull-refresh-offset': `${Math.round(pullRefreshDistance)}px`,
      } as React.CSSProperties}
      onContextMenuCapture={handleTemplateEditorContextMenuCapture}
      onPointerDownCapture={handlePullRefreshPointerDownCapture}
      onPointerMoveCapture={handlePullRefreshPointerMoveCapture}
      onPointerUpCapture={handlePullRefreshPointerUpCapture}
      onPointerCancelCapture={resetPullRefreshGesture}
      onMouseDownCapture={handlePullRefreshMouseDownCapture}
      onMouseMoveCapture={handlePullRefreshMouseMoveCapture}
      onMouseUpCapture={handlePullRefreshMouseUpCapture}
      onMouseLeave={resetPullRefreshGesture}
      onTouchStartCapture={handlePullRefreshTouchStartCapture}
      onTouchMoveCapture={handlePullRefreshTouchMoveCapture}
      onTouchEndCapture={handlePullRefreshTouchEndCapture}
      onTouchCancelCapture={resetPullRefreshGesture}
    >
      {designTokenCssVars && (
        <style id="appfactory-design-tokens-runtime" dangerouslySetInnerHTML={{ __html: designTokenCssVars }} />
      )}
      {customCss && (
        <style id="appfactory-custom-css-runtime" dangerouslySetInnerHTML={{ __html: customCss }} />
      )}
      {runtimeTemplateItemCss && (
        <style id="appfactory-template-item-css-runtime" dangerouslySetInnerHTML={{ __html: runtimeTemplateItemCss }} />
      )}
      {showStoreOverlay && (
        <>
          <div className="smart-home-store-iframe-wrap">
            <iframe
              src={storeLink}
              title="Store"
              className="smart-home-store-iframe"
            />
          </div>
          <button
            type="button"
            className="smart-home-back-to-app glass"
            onClick={() => {
              hapticTap()
              setActiveNav('home')
            }}
          >
            <span className="material-symbols-outlined">arrow_back</span>
            Back to App
          </button>
        </>
      )}
      {!showStoreOverlay && (
      <>
      <header
        className={`smart-home-header smart-home-header-fixed ${headerCollapsed ? 'is-collapsed' : ''}`}
        data-template-css-slot="header"
        data-template-css-slot-label="Header"
      >
        <div className="smart-home-account-wrap" ref={accountDropdownRef}>
          <button
            type="button"
            className="smart-home-user-pill glass"
            onClick={() => {
              hapticTap()
              setAccountDropdownOpen((o) => !o)
            }}
          >
            <span className="material-symbols-outlined smart-home-icon">menu</span>
            <div className="smart-home-avatar">
              <img src={avatarImage} alt="Profile" />
            </div>
          </button>
          {accountDropdownOpen && (
            <div className="smart-home-account-dropdown glass">
              {!auth ? (
                isPublicDemo ? (
                  <button
                    type="button"
                    className="smart-home-account-item"
                    onClick={() => {
                      hapticTap()
                      setAccountDropdownOpen(false)
                    }}
                  >
                    Demo Mode
                  </button>
                ) : isAppFactoryPreview ? (
                  <button
                    type="button"
                    className="smart-home-account-item"
                    onClick={() => {
                      hapticTap()
                      setAccountDropdownOpen(false)
                      void handlePreviewLogin()
                    }}
                    disabled={credentialLoading}
                  >
                    {credentialLoading ? '載入中...' : '登入'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="smart-home-account-item"
                    onClick={() => {
                      hapticTap()
                      setAccountDropdownOpen(false)
                      const appId = (sections?.credentials as { appId?: string })?.appId ?? 'KLMGeDMy'
                      const returnUrl = window.location.href.split('#')[0]
                      window.location.href = `${getCurrentApiUrl()}/auth/login?app_id=${appId}&return_url=${encodeURIComponent(returnUrl)}`
                    }}
                  >
                    登入 (OAuth)
                  </button>
                )
              ) : (
                <>
                  {locations.map((loc) => {
                    const locId = loc.id ?? loc.groupId ?? ''
                    const name = loc.name || loc.id || loc.groupId || 'Unknown'
                    return (
                      <button
                        key={locId}
                        type="button"
                        className={`smart-home-account-item ${selectedLocationId === locId ? 'active' : ''}`}
                        onClick={() => {
                          hapticTap()
                          setSelectedLocationId(locId)
                          if (auth?.appId) saveSelectedLocation(auth.appId, locId)
                          setAccountDropdownOpen(false)
                        }}
                      >
                        {name}
                      </button>
                    )
                  })}
                  <div className="smart-home-account-divider" />
                  <button
                    type="button"
                    className="smart-home-account-item smart-home-account-logout"
                    onClick={() => {
                      hapticTap()
                      handleLogout()
                    }}
                  >
                    登出
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        <div className="smart-home-header-right">
          <div className="smart-home-header-center">
            <div
              className={`smart-home-weather smart-home-header-swap ${
                headerCollapsed ? 'fade-out' : 'fade-in'
              }`}
              data-template-css-slot="headerMeta"
              data-template-css-slot-label="Header Meta"
            >
              <div className="smart-home-weather-label">{weatherLabel}</div>
              <div className="smart-home-weather-value">
                <span className="material-symbols-outlined smart-home-weather-icon">{weatherIcon}</span>
                {weatherValue}
              </div>
            </div>
            <div
              className={`smart-home-location-in-header smart-home-header-swap ${
                headerCollapsed ? 'fade-in' : 'fade-out'
              }`}
              data-template-css-slot="headerBrand"
              data-template-css-slot-label="Header Brand"
            >
              {locationName}
            </div>
          </div>
          <div
            className={`smart-home-noti glass ${wsReady ? 'ws-online' : 'ws-offline'}`}
            role="button"
            tabIndex={0}
            onClick={() => {
              hapticTap()
              setRealtimeNotificationDot(false)
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              hapticTap()
              setRealtimeNotificationDot(false)
            }}
          >
            <span className="material-symbols-outlined smart-home-icon">notifications</span>
            {showNotificationDot && <span className="smart-home-noti-dot" />}
          </div>
        </div>
      </header>
      <div className="smart-home-header-spacer" aria-hidden="true" />


      <main
        className={`smart-home-content-grid ${shouldShowPullRefreshUI ? 'is-pull-refreshing' : ''}`}
        data-template-css-slot="dashboardGrid"
        data-template-css-slot-label="Dashboard Grid"
      >
      {canPullRefresh && (
        <div
          className={`smart-home-pull-refresh-indicator ${shouldShowPullRefreshUI ? 'is-visible' : ''}`}
          style={{ '--pull-refresh-progress': String(pullRefreshProgress) } as React.CSSProperties}
          aria-live="polite"
        >
          <span className={`material-symbols-outlined ${shadowRefreshing ? 'is-refreshing' : ''}`}>
            {shadowRefreshing ? 'autorenew' : 'south'}
          </span>
          <span>
            {shadowRefreshing ? '更新中...' : (pullRefreshReady ? '放開更新' : '下拉更新')}
          </span>
        </div>
      )}
      {activeNav === 'home' && (
      <>
      <section className="smart-home-section smart-home-scenes-section">
        <div className="smart-home-section-header">
          <h2 className="smart-home-section-title">情境</h2>
          <div className="smart-home-scenes-header-actions">
            <button
              type="button"
              className="smart-home-add-scene glass smart-home-scene-order-trigger"
              onClick={handleOpenSceneOrder}
              aria-label="Reorder scenes"
            >
              <span className="material-symbols-outlined smart-home-icon">reorder</span>
            </button>
            <button
              type="button"
              className="smart-home-add-scene glass"
              onClick={handleOpenSceneCreate}
              aria-label="Add scene"
            >
              <span className="material-symbols-outlined smart-home-icon">add</span>
            </button>
          </div>
        </div>
        <div className="smart-home-scenes-bleed">
          <div
            className="smart-home-scenes smart-home-scenes-grid no-scrollbar"
            data-template-css-slot="sceneGrid"
            data-template-css-slot-label="Scene Grid"
          >
            {scenes.map((s, i) => (
              (() => {
                const normalizedIcon = normalizeSceneIconName(s.icon)
                const tone = resolveSceneIconTone(normalizedIcon, i)
                const sceneIconToneStyle = {
                  '--scene-card-icon-bg': tone.bg,
                  '--scene-card-icon-border': tone.border,
                  '--scene-card-icon-fg': tone.fg,
                  '--scene-card-icon-bg-active': tone.activeBg,
                  '--scene-card-icon-border-active': tone.activeBorder,
                  '--scene-card-icon-fg-active': tone.activeFg,
                } as CSSProperties

                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`smart-home-scene-card smart-home-scene-card-grid glass ${s.active || i === 0 ? 'active' : ''} ${runningSceneId === s.id ? 'running' : ''}`}
                    data-template-css-slot="sceneCard"
                    data-template-css-slot-label="Scene Card"
                    onMouseDown={() => startSceneLongPress(s)}
                    onMouseUp={() => handleScenePressEnd(s)}
                    onMouseLeave={cancelSceneLongPress}
                    onTouchStart={() => startSceneLongPress(s)}
                    onTouchEnd={() => handleScenePressEnd(s)}
                    onTouchCancel={cancelSceneLongPress}
                    onKeyDown={(e) => e.key === 'Enter' && (void handleSceneRun(s))}
                  >
                    <div className="smart-home-scene-icon" style={sceneIconToneStyle}>
                      {renderSceneIconGlyph(normalizedIcon)}
                    </div>
                    <span className="smart-home-scene-label">{s.label}</span>
                  </button>
                )
              })()
            ))}
          </div>
        </div>
      </section>

      <section className="smart-home-section smart-home-light-groups-section">
        <div className="smart-home-section-header">
          <h2 className="smart-home-section-title">群控</h2>
          <div className="smart-home-scenes-header-actions">
            <button
              type="button"
              className="smart-home-add-scene glass smart-home-scene-order-trigger"
              onClick={handleOpenLightGroupOrder}
              aria-label="排序控制群組"
              disabled={normalizedLightGroups.length <= 1}
            >
              <span className="material-symbols-outlined smart-home-icon">reorder</span>
            </button>
            <button
              type="button"
              className="smart-home-add-scene glass"
              onClick={handleOpenLightGroupCreate}
              aria-label="建立控制群組"
              disabled={!auth && !isPublicDemo}
            >
              <span className="material-symbols-outlined smart-home-icon">add</span>
            </button>
          </div>
        </div>
        {normalizedLightGroups.length > 0 && (
          <div
            className="smart-home-scenes-bleed"
          >
            <div
              className="smart-home-scenes no-scrollbar smart-home-light-group-scroll"
            >
              <div
                className="smart-home-light-group-cards"
              >
                <div
                  className="smart-home-light-group-track"
                data-template-css-slot="groupGrid"
                data-template-css-slot-label="Group Grid"
                >
                  {normalizedLightGroups.map((group) => (
                    (() => {
                      const groupDeviceSns = group.deviceSns
                        .map((sn) => String(sn || '').trim())
                        .filter(Boolean)
                      const groupOn = groupDeviceSns.some((sn) => {
                        const status = statusMap.get(sn)
                        const record = (status?.attributes ?? status?.iotState ?? {}) as Record<string, unknown>
                        return toBooleanValue(readStatusValue(record, ['OnOff', 'on', 'powerState', 'power', 'isOn'])) === true
                      })
                      const effectiveGroupOn = shouldForceGroupIconPreviewState
                        ? groupIconPreviewState === 'on'
                        : groupOn
                      return (
                    <button
                      key={group.id}
                      type="button"
                      className="smart-home-scene-card glass smart-home-light-group-card"
                      style={effectiveGroupOn ? groupGridCardStyleByState.on : groupGridCardStyleByState.off}
                      data-template-css-slot="groupCard"
                      data-template-css-slot-label="Group Card"
                      data-template-css-state={effectiveGroupOn ? 'on' : 'off'}
                      onMouseDown={(event) => {
                        if (event.button !== 0) return
                        startLightGroupLongPress(group)
                      }}
                      onMouseUp={(event) => {
                        if (event.button !== 0) return
                        handleLightGroupPressEnd(group.id)
                      }}
                      onMouseLeave={cancelLightGroupLongPress}
                      onTouchStart={() => startLightGroupLongPress(group)}
                      onTouchEnd={() => handleLightGroupPressEnd(group.id)}
                      onTouchCancel={cancelLightGroupLongPress}
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter') return
                        event.preventDefault()
                        hapticTap()
                        setActiveLightGroupId(group.id)
                      }}
                    >
                    <div
                      role="button"
                      tabIndex={0}
                      className={`smart-home-light-group-icon ${effectiveGroupOn ? 'is-on' : 'is-off'}`}
                      data-template-css-slot="groupCardIcon"
                      data-template-css-slot-label="Group Card Icon"
                      data-template-css-state={effectiveGroupOn ? 'on' : 'off'}
                      aria-label={effectiveGroupOn ? 'Turn group off' : 'Turn group on'}
                      onMouseDown={(event) => {
                        event.stopPropagation()
                        cancelLightGroupLongPress()
                      }}
                      onTouchStart={(event) => {
                        event.stopPropagation()
                        cancelLightGroupLongPress()
                      }}
                      onMouseUp={(event) => {
                        event.stopPropagation()
                      }}
                      onTouchEnd={(event) => {
                        event.stopPropagation()
                      }}
                      onTouchCancel={(event) => {
                        event.stopPropagation()
                      }}
                      onClick={(event) => {
                        event.stopPropagation()
                        void handleLightGroupIconToggle(group, groupOn)
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return
                        event.preventDefault()
                        event.stopPropagation()
                        void handleLightGroupIconToggle(group, groupOn)
                      }}
                    >
                      <span className="material-symbols-outlined">{group.icon || 'lightbulb'}</span>
                    </div>
                    <div className="smart-home-light-group-card-main">
                      <span className="smart-home-scene-label">{group.name}</span>
                      <span className="smart-home-light-group-card-count">{group.deviceSns.length} devices</span>
                    </div>
                    </button>
                      )
                    })()
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="smart-home-section smart-home-devices">
        <div className="smart-home-section-header">
          <h2 className="smart-home-section-title">
            {locationName}
          </h2>
          <div className="smart-home-scenes-header-actions">
            <button
              type="button"
              className="smart-home-add-scene glass smart-home-scene-order-trigger"
              onClick={handleOpenDeviceOrder}
              aria-label="排序設備"
              disabled={visibleDevices.length <= 1}
            >
              <span className="material-symbols-outlined smart-home-icon">reorder</span>
            </button>
          </div>
        </div>
        {!auth && !isPublicDemo ? (
          <p className="smart-home-empty">請先登入以載入設備列表</p>
        ) : dataLoading ? (
          <p className="smart-home-loading">載入中...</p>
        ) : dataError ? (
          <p className="smart-home-error">{dataError}</p>
        ) : visibleDevices.length === 0 ? (
          <p className="smart-home-empty">尚無設備</p>
        ) : (
          <div
            className="smart-home-cards"
            data-template-css-slot="deviceGrid"
            data-template-css-slot-label="Device Grid"
          >
            {visibleDevices.map((d) => {
              const sn = deviceKey(d)
              const status = statusMap.get(sn)
              const cardIcon = getDeviceCardIcon(d, status)
              const statusRecord = (status?.attributes ?? status?.iotState ?? {}) as Record<string, unknown>
              const onOff = readStatusValue(statusRecord, ['OnOff', 'on', 'powerState', 'power'])
              const enabled = onOff === true || onOff === 1 || onOff === 'on'
              const type = (d.deviceType ?? d.type ?? '').toLowerCase()
              const cardConfig =
                (type && sections?.card?.deviceTypes?.[type]) ?? sections?.card?.defaultConfig
              const enabledAttrs = new Set((cardConfig?.enabledAttributes ?? []).map((k) => normalizeStateKey(k)))
              const enabledStatesSet = new Set((cardConfig?.enabledStates ?? []).map((k) => normalizeStateKey(k)))
              const ON_OFF_KEYS = new Set(['onoff', 'on', 'off', 'powerstate', 'power'])
              const configuredMeta = Object.entries(statusRecord)
                .filter(([k]) => !ON_OFF_KEYS.has(normalizeStateKey(k)))
                .filter(([k]) => enabledAttrs.has(normalizeStateKey(k)) || enabledStatesSet.has(normalizeStateKey(k)))
                .map(([k, v]) => formatStatusPair(k, v))
                .filter((pair): pair is CardMetaPair => pair !== null)
                .map((pair) => normalizeMetaPairByLabel(pair))
                .slice(0, 2)
              const fallbackMetaKeys = [
                'thermostatMode',
                'fanSpeed',
                'temperature',
                'humidity',
                'colorTemperature',
                'OpenClosePercent',
                'watt',
                'watt_hour',
                'lux',
                'battery',
              ]
              const fallbackMeta = fallbackMetaKeys
                .map((key) => formatStatusPair(key, readStatusValue(statusRecord, [key])))
                .filter((pair): pair is CardMetaPair => pair !== null)
                .map((pair) => normalizeMetaPairByLabel(pair))
              const primaryPair = resolveCardPrimaryByDevice(type, statusRecord, enabled)
              const primaryLabelNorm = normalizePairText(primaryPair.label)
              const primaryValueNorm = normalizePairText(primaryPair.value)
              const isPrimaryBattery = primaryLabelNorm === 'battery'
              const hidePrimaryPowerOnly = (
                !primaryPair.label
                && (primaryValueNorm === 'on' || primaryValueNorm === 'off')
              )
              const baseMetaPairs = (configuredMeta.length > 0 ? configuredMeta : fallbackMeta)
                .filter((pair) => {
                  const pairLabelNorm = normalizePairText(pair.label)
                  const pairValueNorm = normalizePairText(pair.value)
                  // 避免重複顯示與主值相同的資訊（例如 OPEN 50% 重複兩次）
                  return !(pairValueNorm === primaryValueNorm || (pairLabelNorm === primaryLabelNorm && pairValueNorm === primaryValueNorm))
                })
              const batteryPair = buildBatteryMetaPair(statusRecord)
              const metaPairs = (
                batteryPair && primaryLabelNorm !== 'battery'
                  ? [batteryPair, ...baseMetaPairs]
                  : baseMetaPairs
              )
                .filter((pair, index, arr) => {
                  const key = `${normalizePairText(pair.label)}:${normalizePairText(pair.value)}`
                  return arr.findIndex((x) => `${normalizePairText(x.label)}:${normalizePairText(x.value)}` === key) === index
                })
                .slice(0, 2)
              const stateBadge = resolveStateBadge(d, statusRecord, enabled)
              const displayName = d.nickname ?? d.displayName ?? d.name ?? d.sn
              const overrideImage = String(cardConfig?.deviceTypeImage || cardConfig?.cardMediaImage || '').trim()
              const img = getDeviceImage(d, overrideImage)
              const isPowerIcon = cardIcon.icon === 'power' || cardIcon.icon === 'power_off'
              const useGenericSwitchIcon = Boolean(cardIcon.useGenericSwitchIcon)
              const isPowerOn = isPowerIcon && enabled
              const isNetworkInfra = isNetworkInfraLike(d)
              const quickExec =
                canQuickExecute(d) && !isNetworkInfra && !!auth && !isPublicDemo && !templateEditorMode
              const deviceSN = d.deviceSN ?? d.sn
              const quickActionLabel = isNetworkInfra
                ? null
                : (
                  isPowerIcon
                    ? (enabled ? 'Turn Off' : 'Turn On')
                    : (cardIcon.icon === 'blinds'
                        ? (stateBadge.tone === 'open' ? 'CLOSE' : 'OPEN')
                        : (quickExec ? 'Action' : resolvePassiveSensorActionLabel(d, statusRecord)))
                )
              const actionIconTone = isPowerIcon ? null : resolveActionIconTone(cardIcon.icon, quickActionLabel, stateBadge.tone)
              const normalizedQuickActionLabel = String(quickActionLabel || '').trim().toUpperCase()
              const shouldShowActionGlow = (
                stateBadge.tone === 'on'
                || stateBadge.tone === 'open'
                || stateBadge.tone === 'armed'
                || normalizedQuickActionLabel === 'DETECTED'
              )
              const isOfflineCard = stateBadge.tone === 'offline'
              const shouldShowCardGlow = Boolean(cardIcon.showGlow) && !isOfflineCard

              const handleQuickExecute = async (e?: React.MouseEvent | React.KeyboardEvent) => {
                e?.stopPropagation()
                if (!auth || !quickExec || isPublicDemo || templateEditorMode) return
                hapticTap()
                let pendingOnOffSns: string[] = []
                try {
                  let optimisticPatch: Record<string, unknown> | null = null
                  if (isPowerIcon) {
                    const nextOn = !enabled
                    pendingOnOffSns = [deviceSN]
                    markPendingOnOff(pendingOnOffSns, nextOn)
                    await executeCommandWithAuth(auth, [deviceSN], enabled ? 'off' : 'on', { on: nextOn })
                    optimisticPatch = { on: nextOn, OnOff: nextOn, isOn: nextOn }
                  } else if (cardIcon.icon === 'blinds') {
                    const openPct = resolveOpenPercentValue(statusRecord) ?? 50
                    const targetPct = openPct > 50 ? 0 : 100
                    await executeCommandWithAuth(auth, [deviceSN], 'OpenClose', { openPercent: targetPct })
                    optimisticPatch = {
                      openPercent: targetPct,
                    }
                  }
                  // Do a local, partial patch to avoid full-page reload feel.
                  if (optimisticPatch) {
                    setDeviceStatuses((prev) => {
                      const index = prev.findIndex((item) => item.sn === deviceSN)
                      if (index < 0) {
                        return [
                          ...prev,
                          {
                            sn: deviceSN,
                            attributes: { ...optimisticPatch },
                            iotState: { ...optimisticPatch },
                          },
                        ]
                      }
                      const next = [...prev]
                      const current = next[index]
                      const currentAttrs = isRecord(current.attributes) ? current.attributes : {}
                      const currentState = isRecord(current.iotState) ? current.iotState : {}
                      next[index] = {
                        ...current,
                        attributes: { ...currentAttrs, ...optimisticPatch },
                        iotState: { ...currentState, ...optimisticPatch },
                      }
                      return next
                    })
                  }
                } catch (_) {
                  if (pendingOnOffSns.length > 0) clearPendingOnOff(pendingOnOffSns)
                  // Keep current UI state; websocket event will reconcile.
                }
              }

              return (
                <div
                  key={sn}
                  role="button"
                  tabIndex={0}
                  className={`smart-home-room-card ${shouldShowCardGlow ? 'active-card-glow' : ''}${isOfflineCard ? ' is-offline' : ''}`}
                  data-template-css-slot="deviceCard"
                  data-template-css-slot-label="Device Card"
                  onClick={() => {
                    hapticTap()
                    handleOpenHomeDeviceModal(d)
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && (hapticTap(), handleOpenHomeDeviceModal(d))}
                >
                  <img src={img} alt="" className="smart-home-room-card-bg" />
                  <div className="smart-home-room-card-gradient" />
                  <div className="smart-home-room-card-content">
                    <span className={`smart-home-room-card-state-chip tone-${stateBadge.tone}${stateBadge.text === 'OCCUPIED' ? ' is-occupied' : ''}`}>
                      {stateBadge.text}
                    </span>
                    <div className="smart-home-room-card-main">
                      <div className="smart-home-room-card-title-row">
                        <h3 className="smart-home-room-card-title">{displayName}</h3>
                      </div>
                      {!hidePrimaryPowerOnly ? (
                        <div className="smart-home-room-card-primary">
                          {isPrimaryBattery ? (
                            <img src={batteryFrameIcon} alt="" className="smart-home-room-card-primary-icon" />
                          ) : null}
                          <span className="smart-home-room-card-primary-value">{primaryPair.value}</span>
                          {!isPrimaryBattery && primaryPair.label ? (
                            <span className="smart-home-room-card-primary-label">{primaryPair.label}</span>
                          ) : null}
                        </div>
                      ) : null}
                      {metaPairs.length > 0 ? (
                        <div className="smart-home-room-card-meta">
                          {metaPairs.map((pair, index) => (
                            <span key={`${pair.label}-${index}`} className="smart-home-room-card-meta-chip">
                              {pair.icon === 'battery' ? (
                                <img src={batteryFrameIcon} alt="" className="smart-home-room-card-meta-icon" />
                              ) : (
                                <span className="smart-home-room-card-meta-label">{pair.label}</span>
                              )}
                              <span className={`smart-home-room-card-meta-value${pair.emphasizeValue ? ' is-emphasized' : ''}`}>
                                {pair.value}
                              </span>
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div
                      role={quickExec ? 'button' : undefined}
                      tabIndex={quickExec ? 0 : undefined}
                      className="smart-home-room-card-action"
                    >
                      <div
                        className={`smart-home-power-btn ${isPowerOn ? 'power-btn-glow power-on' : isPowerIcon ? 'power-off' : ''}${useGenericSwitchIcon ? ' generic-switch-solid' : ''}${!isPowerIcon && actionIconTone ? ` icon-tone-${actionIconTone}` : ''}${shouldShowActionGlow ? ' has-active-glow' : ''} ${quickExec ? 'smart-home-power-btn--clickable' : ''}`}
                        title={isPowerIcon ? (enabled ? '關閉' : '開啟') : cardIcon.icon === 'blinds' ? '開/關窗簾' : undefined}
                        onClick={quickExec ? handleQuickExecute : undefined}
                        onKeyDown={quickExec ? (e) => e.key === 'Enter' && handleQuickExecute(e) : undefined}
                      >
                        {isPowerIcon ? (
                          useGenericSwitchIcon ? (
                            <img
                              src={motionPlayIcon}
                              alt=""
                              className="smart-home-power-btn-icon is-generic-switch-solid"
                            />
                          ) : (
                            enabled ? (
                              <span className="material-symbols-outlined smart-home-power-btn-symbol is-solid">
                                power_settings_new
                              </span>
                            ) : (
                              <img
                                src={powerSettingsNewIcon}
                                alt=""
                                className="smart-home-power-btn-icon is-off"
                              />
                            )
                          )
                        ) : (
                          cardIcon.icon === 'contact_open' || cardIcon.icon === 'contact_close' ? (
                            <img
                              src={cardIcon.icon === 'contact_open' ? doorOpenIcon : doorFrontIcon}
                              alt=""
                              className={`smart-home-power-btn-icon is-contact-${cardIcon.icon === 'contact_open' ? 'open' : 'close'}`}
                            />
                          ) : cardIcon.icon === 'electrical_meter' ? (
                            <img
                              src={boltIcon}
                              alt=""
                              className="smart-home-power-btn-icon is-electrical-meter"
                            />
                          ) : cardIcon.icon === 'network_infra' ? (
                            <img
                              src={hubIcon}
                              alt=""
                              className="smart-home-power-btn-icon is-network-infra"
                            />
                          ) : cardIcon.icon === 'sensors' && normalizedQuickActionLabel === 'OCCUPIED' ? (
                            <img
                              src={sensorOccupiedIcon}
                              alt=""
                              className="smart-home-power-btn-icon is-occupied-sensor"
                            />
                          ) : (
                            <span className={`material-symbols-outlined ${actionIconTone === 'hollow' ? 'smart-home-card-icon-hollow' : 'smart-home-card-icon-solid'}`}>
                              {cardIcon.icon}
                            </span>
                          )
                        )}
                      </div>
                      {quickActionLabel ? (
                        <span className="smart-home-room-card-action-label">{quickActionLabel}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>
      </>
      )}

      {activeNav === 'auto' && (
        <AutoRoutinesContent
          auth={auth}
          selectedLocationId={selectedLocationId}
          devices={devices}
          scenes={scenes}
          hapticTap={hapticTap}
          resolveModalConfigForDevice={resolveModalConfigForDevice}
          resolveDeviceStateBySN={resolveDeviceStateBySN}
          onEmptyScrollLockChange={setAutoScrollLocked}
        />
      )}

      {auth && showSceneEditorModal && (
        <SceneEditorModal
          auth={auth}
          scene={sceneEditorMode === 'edit' ? sceneEditorTarget : null}
          devices={devices}
          selectedLocationId={selectedLocationId}
          onClose={() => {
            setShowSceneEditorModal(false)
            setSceneEditorMode('create')
            setSceneEditorTarget(null)
          }}
          onSaved={() => {
            setShowSceneEditorModal(false)
            setSceneEditorMode('create')
            setSceneEditorTarget(null)
            void reloadScenes()
          }}
          hapticTap={hapticTap}
          resolveModalConfigForDevice={resolveModalConfigForDevice}
          resolveDeviceStateBySN={resolveDeviceStateBySN}
        />
      )}

      {activeNav === 'home' && showSceneOrderModal && (
        <SceneOrderModal
          scenes={scenes}
          onClose={() => setShowSceneOrderModal(false)}
          onApply={handleApplySceneOrder}
          hapticTap={hapticTap}
        />
      )}

      {activeNav === 'home' && showDeviceOrderModal && (
        <DeviceOrderModal
          devices={visibleDevices}
          deviceTypeSlot1Map={deviceTypeSlot1Map}
          onClose={() => setShowDeviceOrderModal(false)}
          onApply={handleApplyDeviceOrder}
          hapticTap={hapticTap}
        />
      )}

      {activeNav === 'home' && showLightGroupOrderModal && (
        <LightGroupOrderModal
          groups={normalizedLightGroups}
          onClose={() => setShowLightGroupOrderModal(false)}
          onApply={handleApplyLightGroupOrder}
          hapticTap={hapticTap}
        />
      )}

      {activeNav === 'home' && showLightGroupEditorModal && (
        <LightGroupEditorModal
          devices={lightGroupEditorSelectableDevices}
          group={lightGroupEditorMode === 'edit' ? lightGroupEditorTarget : null}
          onClose={() => {
            setShowLightGroupEditorModal(false)
            setLightGroupEditorMode('create')
            setLightGroupEditorTargetId(null)
          }}
          onSubmit={(payload) => {
            if (lightGroupEditorMode === 'edit' && lightGroupEditorTargetId) {
              handleSaveLightGroup(lightGroupEditorTargetId, payload)
              return
            }
            handleCreateLightGroup(payload)
          }}
          onDelete={
            lightGroupEditorMode === 'edit' && lightGroupEditorTargetId
              ? () => handleDeleteLightGroup(lightGroupEditorTargetId)
              : undefined
          }
          hapticTap={hapticTap}
        />
      )}

      {activeNav === 'home' && activeLightGroup && (
        <LightGroupControlModal
          auth={auth}
          group={activeLightGroup}
          allDevices={devices}
          statusMap={statusMap}
          selectedLocationId={selectedLocationId}
          readOnly={isPublicDemo || templateEditorMode || !auth}
          forcePowerButtonPreviewState={forceGroupModalPowerButtonState}
          forcePowerButtonPreviewInstanceKey={forceGroupModalPowerButtonInstanceKey}
          onClose={() => setActiveLightGroupId(null)}
          hapticTap={hapticTap}
        />
      )}

      </main>

      <nav
        className="smart-home-nav-wrap"
        data-template-css-slot="bottomNav"
        data-template-css-slot-label="Bottom Nav"
      >
        <div
          className="smart-home-nav nav-glass"
          data-template-css-slot="bottomNavFrame"
          data-template-css-slot-label="Bottom Nav Frame"
        >
          {navItems.map((n) => (
            <button
              key={n.id}
              type="button"
              className={`smart-home-nav-item ${activeNav === n.id ? 'active' : ''}`}
              data-template-css-slot="bottomNavItem"
              data-template-css-slot-label="Bottom Nav Item"
              data-template-css-state={activeNav === n.id ? 'selected' : 'unselected'}
              onClick={() => handleNavClick(n)}
            >
              <span
                className="material-symbols-outlined"
                data-template-css-slot="bottomNavIcon"
                data-template-css-slot-label="Bottom Nav Icon"
                data-template-css-state={activeNav === n.id ? 'selected' : 'unselected'}
              >
                {n.icon}
              </span>
              <span
                className="smart-home-nav-label"
                data-template-css-slot="bottomNavLabel"
                data-template-css-slot-label="Bottom Nav Label"
                data-template-css-state={activeNav === n.id ? 'selected' : 'unselected'}
              >
                {n.label}
              </span>
            </button>
          ))}
        </div>
      </nav>
      </>
      )}
    </div>
  )
}

export default SmartHomeDashboardTemplate
